/* =====================================================================
   THE ROOM ENGINE — E1

   ONE WebGL2 context, one scene, one camera. The desk, the sheet and the
   ground are objects in one world measured in CSS pixels; the ground is a
   rigid body that stands up out of the paper when the sheet is tilted, so
   "look up from the desk" is a CAMERA MOVE and nothing else (ROOM-PLAN §3).

   What is here: the real field from the real record (built in a worker,
   uploaded R16F as FLOAT, sampled on its own lattice); contours, coast,
   hachure, sea, depth bands, foreshore and the tint stack in one fragment
   shader; the existing furniture SVG rastered once to a texture at device
   resolution; a flat grid displaced in the VERTEX shader (no CPU mould);
   placeholder marks; drag pan, continuous wheel zoom, shift-drag tilt and
   turn; the hash unchanged.

   What is NOT here, by the plan: lettering, landmarks, books, the DOM
   overlay, sky wash, haze, ripples, the beyond. E2-E8.

   WHERE THE PRIOR ART IS. The comments through this directory cite
   `relief.js:NNN` and `survey-map.js:NNN` — the old plate, which was this
   engine's spec. Those two files, the baked tile set and the DOM sheet
   left the repo at THE SWITCH (poc/room-switch); they are still readable
   at the tag **survey/v32** (branch `poc/survey-v17`), and that tag is
   also what the furniture generator `room-e9/build-furniture.js` has to
   be served from, because it reads the old sheet's DOM.
   ===================================================================== */
(function () {
'use strict';

var T0 = performance.now();
/* ==== PT1 (2026-09-21): THE BOOT ON THE CONSOLE. Tommy: "with hard
   refreshes, it seems to load quite quickly often, but sometimes it can
   just hang a long time. I want to understand what it is." So every
   milestone of the load prints a line as it happens, stamped in ms since
   the page began to load (performance.now() is measured from navigation
   start), and `ready` prints a summary: the navigation's own timings, the
   slowest resources, the fonts, and S.boot. A hang shows as the last line
   printed before the gap. Console only; nothing on the sheet. ==== */
function bootLog(what, extra) {
  try { console.log('[boot +' + performance.now().toFixed(0) + ' ms] ' + what + (extra ? '  (' + extra + ')' : '')); }
  catch (e) { }
}
function bootSummary() {
  try {
    var nav = performance.getEntriesByType('navigation')[0];
    if (nav) console.log('[boot] navigation: ttfb ' + nav.responseStart.toFixed(0) + ' ms, html ' + nav.responseEnd.toFixed(0)
      + ', dom interactive ' + nav.domInteractive.toFixed(0) + ', DCL ' + nav.domContentLoadedEventEnd.toFixed(0)
      + ', load ' + (nav.loadEventEnd || 0).toFixed(0) + (nav.transferSize === 0 ? '  (html from cache)' : ''));
    var rs = performance.getEntriesByType('resource').slice().sort(function (a, b) { return b.duration - a.duration; }).slice(0, 8);
    var lines = rs.map(function (r) {
      return '    ' + r.duration.toFixed(0) + ' ms  from +' + r.startTime.toFixed(0) + '  '
        + (r.transferSize ? (r.transferSize / 1024).toFixed(0) + ' KB' : 'cache') + '  ' + r.name.replace(location.origin, '');
    });
    console.log('[boot] slowest resources:\n' + lines.join('\n'));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { bootLog('fonts ready'); });
    console.log('[boot] S.boot ' + JSON.stringify(S.boot));
  } catch (e) { }
}
bootLog('engine.js evaluated');
var Q = new URLSearchParams(location.search);
var F = window.RoomField, C = F.C;
var clamp = F.clamp;
var DEG = Math.PI / 180;
var RELIEF_SKIRT = 26;                 // survey-map.js:163
var EXAG = C.VERTICAL_EXAGGERATION;    // relief.js:89 — 20
/* PT1: the funnel's hachure — the ring it is on and the tick's length, both
   in disc radii, and one tick every thirty degrees of the whirl's angle */
var HACH_R = '0.42', HACH_L = '0.055';
var HACH_A = (Math.PI / 6).toFixed(6);
/* R26: the 190 rung is awake; R43: DOM rides the paper's top face. */
F.wakeRung190();
/* R43 geometry diagnostic: put DOM back on the bare z=0 paper plane. */
var PAPER_H = Q.get('ph') !== '0';
var MAX_TILT = C.MAX_TILT;             // 75 degrees
/* ==== R25 (4): THE CLAMP IS ON THE TOTAL PITCH, NOT ON THE GESTURE.
   Tommy, of the pitched edge pose: "you can rotate under the map fyi".
   He is right, and here is why. The map is laid back on the paper by
   S.tilt, so its plane's normal in world is (0, sin t, cos t); the eye is
   at R.(0, -sin p, cos p) from the point it looks at, so its height above
   THE MAP'S OWN PLANE is R.cos(p + t). It goes negative — the eye passes
   under the map — when p + t > 90 degrees, and at the rest pitch of 22
   the old clamp let t reach 75, which is 97.
   MAX_TILT is 75 and the grazing angle at no pitch is 90, so the clamp has
   always carried 15 degrees of margin; this keeps exactly that margin by
   spending the rest pitch out of the SAME 90. At p0 = 0 it returns
   MAX_TILT to the bit and nothing about the default moves. ==== */
function tiltCap() {
  return Math.max(MAX_TILT * 0.25, MAX_TILT - (S.cam.p0 || 0));
}
var FOV = 22 * DEG; // ROOM-PLAN §3
/* PH3: the engine held in the hand (base.njk's head script: every touch phone, PS1) */
var PHONE = /\bphone\b/.test((typeof document !== "undefined" && document.documentElement ? document.documentElement.className : ""));
/* TB2: a phone path on a tablet's glass (base.njk) */
var TABLET = PHONE && /\btablet\b/.test(document.documentElement.className);
var R17_EDGE_DEG = 22;
/* R25 (1): THE PULL-BACK, MODERATED. R17 shipped 1.20 and Tommy's verdict
   was that it was desk-maxxing — "keep the slant but lose the huge amount
   of desk that shrinks the paper". The number below is settled in
   REPORT-room-R25.md against 1.06 / 1.10 / 1.14 at the fit: the foot of
   the sheet is nearly the portal's width and the pitch's own
   foreshortening leaves the board showing beside and above the far edge. */
/* R27 (1): 1.10 -> 1.02. R25's own table said what stopped the foot at
   88 % of the window, and it was not the sides: the books hang BOOK_SHOW
   past the sheet's south edge and the arris, the face and a floor band all
   have to fit under them, so the BOTTOM of the frame was the binding
   constraint while a band of bare desk sat above the head carrying
   nothing. Tommy: "we're still losing quite a bit of horizontal space and
   a LOT of space at the top? i guess we're not exactly struggling for
   vertical space?" R17_EDGE_AIM rides the whole composition up and the
   room that frees at the bottom is spent on the width. */
var R17_EDGE_K = 1.02;
/* ==== R27 (1): THE AIM, IN CSS PIXELS DOWN THE PAGE.
   The camera looks at the middle of the glass; the point it looks at is
   drawn at the middle of the picture. Move the aim DOWN the page and the
   whole composition rides UP the frame — the head of the sheet toward the
   top, and every css pixel of desk that was above it handed to the bottom,
   where the books, the arris, the face and the floor band live. The chosen
   50 px was settled in REPORT-room-R27.md against 0/20/40/60/80 at three
   pull-backs. */
var R17_EDGE_AIM = 50;
/* ==== R33 (d), ON BY DEFAULT AT R42 (c): THE AIM-LIFT IS TAKEN OFF THE
   GROUND UNDER THE HASH'S OWN POINT, not off the field and the window.

   The old rule is a figure of the FIELD (a third of the highest ground)
   capped by the WINDOW (a fifth of its depth), and neither of them knows
   where the reader is looking: zoomed in, the cap collapses — at zoom 235
   the window is 60 map units deep, so the cap allows 12 while the ground
   under the aim stands at 49 — and the point the hash names WALKS OFF THE
   TOP OF THE PLATE as the sheet is tilted (R33: screen y 277 -> 135 -> 47
   at tilt 0/30/53). Taking the lift off the ground the hash names holds it
   (277 -> 206 -> 206). R33 built this, measured it and left it OFF because
   it re-composes every tilted view; R42 (c) put the re-composition under
   the eighteen views of the two sweeps and shipped it. ==== */
/* R27 OPEN: the solid edge lamp remains an explicit, off-by-default
   judgement with its measured placement controls.
   LS1 (2026-10-01): AND IT IS ON. Off, the map view drew the lamp's SHADOW
   and no lamp: a dark smudge at the top-left edge at the usual window, and
   in a narrow one (developer tools docked at the side) the whole disc on
   bare oak with nothing to cast it (Tommy: "you get the shadow of the lamp
   just as a big circle, and it's a bit odd"). R27's slab was the old puck;
   BL1's lamp reads as a lamp at every crop. `?edgelamp=0` is the old way. */
var R17_EDGE_LAMP = !(Q.get('edgelamp') === '0' || Q.get('edgelamp') === 'off');
var ORTHO = Q.get('proj') === 'ortho'; // projection diagnostic
var BOOK_SHOW = 64;                    // survey-map.js:246
var HACH_MODE = (function (q) {
  if (q === '0' || q === 'off') return 0;
  return 1;
})(Q.get('hach'));
/* the two fading generations of contour, each over ONE octave of zoom
   centred on the band boundary the sheet steps at (survey-map.js:6142) */
var LEV_B = Math.log(1.55) / Math.LN2, LEV_C = Math.log(3.40) / Math.LN2;
/* ?lev=0,0 pins the far band at any zoom (what the LOD classes drew there),
   ?lev=1,1 pins every level. A measuring switch, and the pair Tommy can
   open to see what the fade has added at the zoom he is at. */
var LEV_FORCE = (function (q) { return q ? q.split(',').map(Number) : null; })(Q.get('lev'));
var LET_ON = Q.get('let') !== '0';
var LET_CUT = Q.get('let') !== 'nocut';
var LET_UP = Q.get('letup') === 'cam';
/* R1 (b): the screen separation, in device px, at which a generation of
   contours stops being lines and starts being a moire. ?sep=0 pins the old
   behaviour for the pair; ?sep=lo,hi to move it. */
var SEP = (function (q) {
  if (q === '0' || q === 'off') return [-1, 0];
  var a = (q || '').split(',').map(Number);
  return (a.length === 2 && a[0] >= 0 && a[1] > a[0]) ? a : [0.8, 1.8];
})(Q.get('sep'));
/* R1 (b): ?grad=iso pins E2's isotropic sqrt(det J) for the pair. */
var GRAD_ISO = Q.get('grad') === 'iso';
var DAY_ON = Q.get('day') !== '0';
var DAY_GRID = DAY_ON && Q.get('daygrid') !== '0';

/* ---- state ------------------------------------------------------- */
var S = {
  gl: null, cv: null, fld: null, furn: null,
  view: { x: 0, y: 0, w: 0, h: 0 },
  tilt: 0, yaw: 0,
  /* THE CAMERA. `pitch`/`k` are E1's orbit over the glass and are left
     alone; `room` is E8's blend from the desk pose to the room pose and
     `want` is where it is headed (what the hash and `Room.inRoom` read). */
  /* R17 (c): the chosen fit is pulled back and pitched down so the table's
     front edge shows. It is a REST POSE, not a gesture — `p0`/`k0` are what
     "the camera has not left the desk" now means. */
  cam: { pitch: R17_EDGE_DEG * DEG, k: R17_EDGE_K,
         aim: R17_EDGE_AIM,
         p0: R17_EDGE_DEG * DEG, k0: R17_EDGE_K,
         room: 0, want: 0,
         /* X1: and the third pose — turned to the laptop. `lap` blends on
            TOP of whatever the camera is already doing, so Esc puts the
            eye back exactly where it stood, seated or standing. */
         lap: 0, lapWant: 0,
         /* US1: upright off the map on the phone, seated at the real desk (upSeat) */
         seat: 0,
         /* R62: AND THE HEAD, WHICH IS NOT A POSE AT ALL. `lookYaw` and
            `lookPitch` are radians off whichever pose the camera is in —
            the eye does not move, only the way it is pointed — and they
            are scaled by `cam.room`, so a look exists only in the room
            and unwinds itself as the reader sits down. */
         lookYaw: 0, lookPitch: 0 },
  levForce: null,
  css: { w: 0, h: 0 }, dpr: 1,
  layout: null, drawn: 0, lastDrawMs: 0
};
S.levForce = LEV_FORCE; S.hachMode = HACH_MODE;
window.Room = S;

/* =================================================================
   THE SHADERS
   ================================================================= */
var FIELD_GLSL = `
uniform sampler2D uField;
uniform vec4 uLat;     // X0 Y0 DX DY: sheet units of field cell 0 and the cell size
/* LX1: the spacing the field was BUILT at, which the hachure's cell, the
   hill shading's normal and the gradient's step key off — so upsampling
   the lattice (field.js upsampleX) re-deals no stroke and moves no edge
   by a width. Equal to uLat.zw at ?upx=1. */
uniform vec4 uLat0;    // DX0 DY0 0 0
uniform vec2 uN;       // NX NY
/* ==== R35: THE MARGIN IS A TEXEL OFFSET, NOT A NEW ORIGIN. field.js grows
   the field north and west; uLat.xy stays the RECORD's own lattice origin
   and uMarg carries the margin in WHOLE CELLS, added after the division so
   it is exact. Moving the origin instead costs the fit twice — it re-deals
   the hachure's scatter (a hash of the cell index taken against the
   origin) and it changes the float32 rounding of every (s - X0)/D on the
   plate. uMarg is (0,0) until a field with a margin is bound. ==== */
uniform vec2 uMarg;
vec2 uvOf(vec2 s){ return (vec2((s.x-uLat.x)/uLat.z, (s.y-uLat.y)/uLat.w)+uMarg+0.5)/uN; }
/* ==== JG1 (2026-09-26): THE ONE READ OF THE GROUND'S HEIGHT.
   Tommy, of a tilted steep face: "these lines aren't smooth ... they're
   still pretty jaggedy. I'd like to see if we could do a much better job."
   The ink was already drawn on a smooth surface — the fragment read the
   field through E7's Catmull-Rom — but the GROUND ITSELF stood on the
   bilinear read, and on a mesh four lattice rows to a cell. A contour is
   where the smooth field equals its level; the eye sees it at the height
   the MESH gives that point, and wherever the two disagree the line is
   lifted or dropped by the difference, with the mesh's period: the
   zigzag. So every height on the plate — the mesh, the ink, the marks,
   the names, the boat — reads THIS one function (Catmull-Rom: it passes
   through every sample and is C1 between them), and day.js bedAt mirrors
   it in JS (field.js fieldAtJS). A function nobody calls is not emitted,
   so a program that never asks for a height pays nothing for it.

   NINE BILINEAR TAPS, NOT SIXTEEN POINT ONES. The two middle weights of
   each axis are both positive, so they fold into ONE linear fetch at
   w2/(w1+w2) between their texels; the outer two are read at texel
   centres, where the filter is the texel itself. CLAMP_TO_EDGE is the
   index clamp. Measured on his RTX 3070 Ti (headless Chrome over WSL's
   D3D12, 3840 x 2160, Room.perf(60), medians of 7): the tilted view with
   the finer mesh below 7.47 ms at sixteen taps, 6.38 at nine; the flat
   fit 5.30 against 5.07-5.19. The filter's own weight precision (1/256
   of a texel on this card) moves a value by at most 1/512 of the step
   between two samples — 0.005 map units of a contour's position. ==== */
vec4 crw(float f){
  float f2 = f*f, f3 = f2*f;
  return vec4(-0.5*f3 + f2 - 0.5*f, 1.5*f3 - 2.5*f2 + 1.0,
              -1.5*f3 + 2.0*f2 + 0.5*f, 0.5*f3 - 0.5*f2);
}
float fieldAt(vec2 s){
  vec2 tc = vec2((s.x-uLat.x)/uLat.z, (s.y-uLat.y)/uLat.w) + uMarg;
  vec2 i1 = floor(tc), f = tc - i1;
  vec4 wx = crw(f.x), wy = crw(f.y);
  vec2 w12 = vec2(wx.y + wx.z, wy.y + wy.z);
  vec2 p0 = (i1 - 0.5)/uN, p3 = (i1 + 2.5)/uN, pm = (i1 + 0.5 + vec2(wx.z, wy.z)/w12)/uN;
  float r0 = wx.x*texture(uField, vec2(p0.x, p0.y)).r + w12.x*texture(uField, vec2(pm.x, p0.y)).r + wx.w*texture(uField, vec2(p3.x, p0.y)).r;
  float rm = wx.x*texture(uField, vec2(p0.x, pm.y)).r + w12.x*texture(uField, vec2(pm.x, pm.y)).r + wx.w*texture(uField, vec2(p3.x, pm.y)).r;
  float r3 = wx.x*texture(uField, vec2(p0.x, p3.y)).r + w12.x*texture(uField, vec2(pm.x, p3.y)).r + wx.w*texture(uField, vec2(p3.x, p3.y)).r;
  return wy.x*r0 + w12.y*rm + wy.w*r3;
}
/* ==== R4: THE GROUND RISES. The whole field is pulled down to the datum
   and let back up over the reveal, so the contour levels and the tint
   bands SWEEP up the land as it comes — the survey being drawn, not a
   picture appearing. It is a flattening, not a raising, so that the
   DEFAULT VALUE OF THE UNIFORM (0,0) is exactly today's picture: any
   program that includes this chunk and never sets uFlat draws as it
   always did. .x is how much is taken away, .y the datum it falls to. */
uniform vec2 uFlat;
float riseOf(float b){ return uFlat.x <= 0.0 ? b : mix(b, uFlat.y, uFlat.x); }
`;

/* the front and the surveyed region, ANALYTICALLY (ROOM-PLAN §1) */
/* ==== LP4, 2026-09-22: AND IT IS IN TWO HALVES, because the ground is
   four programs now and only three of them have any use for the downs,
   the seabed and the funnel. FRONT_CORE is the front, the mould's own
   mask and the west gate — a page of arithmetic on the map point;
   FRONT_TAIL is field.js's terra and seabed chunks, which are the bulk.
   `FRONT_GLSL` is the two of them concatenated in the order they were
   written in, so every program that asks for it gets the same string it
   always got, to the byte. ==== */
/* ==== FE1, 2026-09-30: THE EDGE INTO THE UNSURVEYED. Tommy: "can we do
   something to make the edge into the unsurveyed a bit nicer? you see the
   straight line i mean?" (#1622,582,339). The front is today's WEEK COLUMN
   (e0..e1, 40 map units) and its days are ROWS (78 each). Three straight
   lines made the box: (1) the rows still to come stopped at e0, one
   vertical line down the whole plate; (2) the days done this week ran on
   to e1, with one horizontal step where they met the rows to come; (3)
   the done days faded out over the same two-day run everywhere, so their
   east side was a vertical band at e1 - fade .. e1.
   NOW the line follows the days: e0 on the rows to come, e1 on the days
   done, joined by a slope across the row edge instead of a step; it
   wanders a little, as a hand-drawn boundary does, and is inked (the FEAT
   block in the ground's last pass); the ground runs full colour up to it.
   Colour only: the field's scarp still stands at e1 on every row
   (field.js CUT). Arithmetic on the map point, no texture read. Tommy
   picked it over a noise-broken wash with no line (?edge=b), 2026-09-30,
   and the switch went with the loser. ==== */
var FE_GLSL = `
/* the days-shaped front: how far east the survey stands on row y */
float feRamp(float y, float r){
  float yb = uFrontC.z > uFrontC.x + 0.5 ? uFrontC.z : uFrontC.w;   /* the row edge inside the plate */
  float into = uFrontC.z > uFrontC.x + 0.5 ? y - yb : yb - y;       /* + into the days done */
  return smoothstep(-r, r, into);
}
float feWob(float y){
  return 1.25*sin(y*0.0713 + 1.3) + 0.75*sin(y*0.1731 + 4.1) + 0.22*sin(y*0.2913 + 0.7);
}
float feHash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7)))*43758.5453); }
float feNoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f*f*(3.0 - 2.0*f);
  return mix(mix(feHash(i), feHash(i + vec2(1.0, 0.0)), f.x),
             mix(feHash(i + vec2(0.0, 1.0)), feHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
/* the edge line, e0 .. e1 - 2.5 so the wobble never reaches the scarp */
float feEdgeX(float y){ return mix(uFront.y, uFront.z - 2.5, feRamp(y, 26.0)) + feWob(y); }
/* no derivatives here: this chunk is in the vertex shaders too. The edge
   is antialiased over half a map unit and the ink (FEAT, below) is drawn
   over it at a device pixel and a bit, so the fit's aliasing is covered. */
float feSurv(vec2 s){ return 1.0 - smoothstep(-0.5, 0.5, s.x - feEdgeX(s.y)); }
float feTerraFrom(vec2 s){ return feEdgeX(s.y) - 1.0; }
`;
var FRONT_CORE = `
uniform vec4 uFront;   // a0, e0, e1, fade
uniform vec4 uFrontC;  // c0, c1, pLo, pHi
uniform vec2 uFrontK;  // mode (0 none, 1 open, 2 closed), east neat line
` + FE_GLSL + `float surveyedAt(vec2 s){
  if (s.x < uFront.x) return 0.0;
  if (s.y < uFrontC.x || s.y > uFrontC.y) return 0.0;
  if (uFrontK.x < 0.5) return 0.0;
  if (uFrontK.x > 1.5) return s.x < uFrontK.y ? 1.0 : 0.0;
  return feSurv(s);   /* FE1: the days-shaped, inked front */
}
/* what the MOULD stands on: bedAt's mask (survey-map.js:2106), the skirt
   at the ends of the record and the conjecture east of the front */
uniform vec4 uMould;   // recordW, recordE, plateC0, plateC1
uniform vec4 uMouldF;  // tCol(along), frontRowEdge(across), turned, keep
/* ==== R35: THE FRONT IS A SCARP OF THIS SURFACE, not a cut in the mask.
   uScarp is (the cut's x, the run the ground falls over east of it). The
   FIELD does the falling now — field.js eases the bed to the datum over
   that run, which the mesh resolves as a steep face of its own triangles
   instead of the one-cell CHORD the 0.8-unit mask step left it (R28 OPEN
   2). What is left of the mask's cut is a backstop AT THE SCARP'S FOOT,
   where the field is already at the datum and it has nothing left to do.
   uScarp.y is 0 until a field is bound, and 0 is the line the mask cut on
   before, so any program that never receives it draws as it did. ==== */
uniform vec2 uScarp;
/* ==== R41: THE WEST GATE. The OPEN_SEA run (field.js BEY.OPEN_SEA x a
   plate-height, 409.5 map units here) measured WEST of the surveyed
   start, uFront.x. It is exactly 0 at the start and east of it, so every
   expression it touches is the expression it was, to the bit, everywhere
   the record and its north/south crossfade are drawn. uWestSea is the
   run in map units and its DEFAULT VALUE IS 0 — any program that never
   receives it, and ?seamat=0, draw as R39 drew. ==== */
uniform float uWestSea;
float smst(float a, float b, float v){ float t = clamp((v-a)/(b-a),0.0,1.0); return t*t*(3.0-2.0*t); }
float westSea(vec2 s){
  return uWestSea > 0.0 ? smst(0.0, uWestSea, max(uFront.x - s.x, 0.0)) : 0.0;
}
/* ==== LP2, 2026-09-22: THE MOULD AND THE DOWNS ARE BAKED =============

   Direct3D's shader compiler inlines every function at every call site,
   so a chunk shared by twenty programs is not shared at all once it is
   handed to the driver: the lettering's vertex shader calls groundAt
   from eleven places and each one got its own copy of this mask, of
   mouldMaskT, and of the whole terra/downs/funnel/pace chain behind
   terraOf. Timed alone on Tommy's RTX 3070 Ti that program took 8.5 s
   to link and the ground 3.4 s; stubbing mouldMaskT alone took the
   lettering to 1.9 s and stubbing terraOf to 3.2 s.

   None of it varies per frame. Every term reads the map point and
   uniforms setCommon works out from the record, the funnel and the
   paced days — so it is drawn ONCE into an offscreen RGBA16F texture
   over the field's own rectangle (ensureBake, below) and every
   consumer reads it:

       uBake.r  terraOf(s)
       uBake.g  mouldMask(s)

   and nothing else. mouldMaskT is the mask plus a terraW, a smst and a
   mix — four instructions on the map point — so it is DERIVED at its
   call sites and the texture stays two channels wide instead of four,
   which on this record is 35 MB and not 69.

   downsAt and paceVec are NOT baked. The ground's material reads
   both as vec3 — the value AND its two map derivatives — and a bilinear
   read of a baked derivative facets the hill shading at every texel
   boundary, which is a picture fault to buy a compile. They stay
   analytic, in the one program that calls them.

   WITHOUT EXT_color_buffer_float there is no bake and BAKED is never
   defined, so every branch below is the analytic one and nothing is
   lost. It is a capability fallback, not a switch. ==================== */
#ifdef BAKED
uniform sampler2D uBake;
float mouldMask(vec2 s){ return texture(uBake, uvOf(s)).g; }
#else
float mouldMask(vec2 s){
  /* ==== R41: AND THE SKIRT DOES NOT CUT THE OPEN SEA.

     This was the STEP Tommy kept seeing — measured at map x -8901, which
     is front.west - margin.run - 26 to the unit. R35 moved the skirt out
     to the margin's own edge on the argument that "the field is already at
     the datum there and it has nothing left to do". That is true on LAND.
     It stopped being true at sea when R39 held the west bed at the floor:
     the mask took a bed of -2.13 down to 0 over 26 map units, which at the
     exaggeration is a 42-unit cliff in the open sea — and above the water
     plane, so west of it the plane had no sea to lie on and the ground
     showed its own untinted, unhazed blue. Hence "a flat saturated blue to
     the west, the record's sea pale and washed to the east".

     So west of the surveyed start the skirt is released — west AND across,
     because the sea runs north and south out there too. The FIELD is the
     truth about where the ground stands, and west of the line the field is
     already exactly what a zero-commit month of 2022 is. The east skirt,
     the front's own rows and the scarp are untouched. ==== */
  float wW = westSea(s);
  float m = mix(smst(-26.0, 0.0, s.x - uMould.x), 1.0, wW)
          * smst(-26.0, 0.0, uMould.y - s.x)
          * mix(smst(-26.0, 0.0, min(s.y - uMould.z, uMould.w - s.y)), 1.0, wW);
  if (m <= 0.0) return 0.0;
  if (uFrontK.x > 1.5 || uFrontK.x < 0.5) return m;
  /* AU2 (2026-10-02): the rows still to come are lowered toward the
     conjecture, and they used to be lowered by a STEP at the row edge — a
     boolean on s.y. On the flat sheet nobody could see it; stood up, it is
     a cliff along the row edge one mesh row wide, and from near and tilted
     it read as a vertical crack in the ground with the paper of Terra
     Incognita through it (the audit's A2, #1700,431,200,90,30). The front's
     own line already crosses that edge on a slope (FE1 feRamp, 26 units
     each side); the mould now does the same, so the ground and its ink
     turn the corner together. day.js mouldMask is the same expression. */
  float into = uMouldF.z > 0.5 ? s.y - uMouldF.y : uMouldF.y - s.y;
  float sr = smoothstep(-26.0, 26.0, into);
  float e = clamp((s.x - (uMouldF.x - 40.0)) / 40.0, 0.0, 1.0);
  m *= mix(uMouldF.w + (1.0 - uMouldF.w) * (1.0 - e), 1.0, sr);
  m *= 1.0 - smst(uMouldF.x + uScarp.y, uMouldF.x + uScarp.y + 0.8, s.x);
  return m;
}
#endif
`;
var FRONT_TAIL = F.TERRA_GLSL + F.SEABED_GLSL + `
/* ==== R40: AND THE MOULD DOES NOT END AT THE FOOT OF THE SCARP ANY MORE.

   Two of mouldMask's terms were EAST CUTS: the 26-unit taper at the
   record's own east neat line, and the backstop 0.8 units past the
   scarp's foot that R35 left standing. Between them the height of
   everything east of the foot was multiplied by 0 — which is why Terra
   Incognita could only ever be a plane however high its field stood.

   The downs are ground, so east of the foot the mould is the NORTH-SOUTH
   skirt and nothing else: the same skirt the margin already stands on, at
   the same place (uMould.z/.w, the margin's own rect), so the downs reach
   exactly as far north and south as Terra Incognita's paper and its
   rhumbs do (water.js :243) and no further.

   terraW is 0 at and west of the cut, so the mix below is the identity there and
   every program that includes this chunk — the marks, the lettering, the
   day cell — draws the mask it drew, to the bit. ==== */
/* LP2: NOT a channel of its own. Every term of this but mouldMask is
   arithmetic on the map point and two uniforms — a terraW, a smst and a
   mix — so where the mask is a texture read this whole function is one
   read and four instructions, and the bake stays two channels wide
   instead of four. The body is untouched: only what mouldMask means
   underneath it changes. */
float mouldMaskT(vec2 s){
  float tw = terraW(s.x);
  float mNS = smst(-26.0, 0.0, min(s.y - uMould.z, uMould.w - s.y));
  if (tw <= 0.0) return mouldMask(s);
  if (tw >= 1.0) return mNS;
  return mix(mouldMask(s), mNS, tw);
}
`;
var FRONT_GLSL = FRONT_CORE + FRONT_TAIL;

/* the rigid body: map units -> sheet units -> world (css px), and the
   camera. ONE camera; the room move is a different camera pose. */
/* ==== FD2 (a), 2026-09-20: THE PAPER UNDER THE MAP ===================
   objects.js owns the sheet's own surface and the light on it; this is
   that arithmetic, compiled into every program that draws into the window
   so the map is ink on THIS piece of paper and not a picture on the plane
   it is lying on. The stub is for the harnesses that load a map module
   without the desk. ==== */
var PAPER_INK_GLSL = (window.RoomObjects && RoomObjects.paperInkGLSL)
  ? RoomObjects.paperInkGLSL()
  : 'float paperSurf(vec2 p){ return 0.0; }\nvec3 paperInk(vec3 c, vec2 p){ return c; }\n';

var XFORM_GLSL = PAPER_INK_GLSL + `
uniform vec4 uView;    // view x y w h (sheet units of the map)
uniform vec4 uPlate;   // plate x y w h (sheet units of the paper)
uniform vec3 uAtt;     // cos tilt, sin tilt, k (plate units per map unit)
uniform vec2 uYaw;     // cos, sin
uniform float uLift;
uniform vec3 uSheet;   // ox, oy (css px), kSheet
uniform mat4 uVP;
vec3 mapToSheet(vec2 m, float h){
  vec2 d = (m - (uView.xy + uView.zw*0.5)) * uAtt.z;
  vec2 r = vec2(d.x*uYaw.x - d.y*uYaw.y, d.x*uYaw.y + d.y*uYaw.x);
  float z = h * uAtt.z;
  float v = r.y*uAtt.x - z*uAtt.y + uLift*uAtt.y;
  float up = r.y*uAtt.y + z*uAtt.x;
  return vec3(uPlate.x + uPlate.z*0.5 + r.x, uPlate.y + uPlate.w*0.5 + v, up);
}
/* ==== FD2 (a), 2026-09-20. Tommy: "the folds of the sheet no longer
   appear through the map, as well as the shape of the journal. they
   essentially appear the second you engage the folding process, even if
   you've not moved the sheet yet."

   They appeared because a folded sheet takes its ink off R58's raster and
   is therefore ON the paper, and the flat one took it off this line,
   which put it on the paper's PLANE. A map point's own relief still
   stands off the sheet; what it stands off is the sheet's face. ==== */
vec4 sheetToClip(vec3 p){
  float z = p.z + paperSurf(p.xy);
  return uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), z*uSheet.z, 1.0);
}
/* ==== R13 (b): AND NOTHING IS SET WHERE NO GROUND IS DRAWN.
   The ground is the mesh's rect cut by the record's; a name, a mark or a
   figure is placed on a map point and has never asked whether the ground
   under it was reached. R13 (a) makes the mesh cover the window at the
   clamp, so at the desk this now answers ONE at every point in the
   portal — it is the invariant made explicit rather than a second
   opinion, and it is what keeps "Mare Otiosum set over bare paper"
   (room-rev3/R13-before-words.png, the top-left corner) from coming back
   if a layout, a camera or a wider clamp ever outruns the reach again.
   Eased out over a fiftieth of the rect so a pan across an octave step
   cannot pop a word. uGReach is (0,0,0,0) until a rect is set, and zero
   means "no opinion", so any program that never receives it draws as it
   did. ==== */
uniform vec4 uGReach;
float reachOf(vec2 m){
  if (uGReach.z <= 0.0) return 1.0;
  vec2 q = max(uGReach.xy - m, m - (uGReach.xy + uGReach.zw));
  float band = 0.02 * min(uGReach.z, uGReach.w);
  return 1.0 - smoothstep(-band, 0.0, max(q.x, q.y));
}
`;

var GROUND_VS = `#version 300 es
precision highp float; precision highp sampler2D;
layout(location = 0) in vec2 a;
/* ==== LP4: AND THE FOUR PASSES AGREE ABOUT WHERE THIS VERTEX IS.
   The ground is drawn four times over the same mesh with the same source
   here and four different fragment shaders, and three of those draws
   share a depth buffer whose LEQUAL test is what picks the nearest
   fragment where the country folds over itself. Four separate compiles of
   one vertex shader are free to schedule their arithmetic differently —
   a fused multiply-add in one and not in the next is enough — and a
   position that differs by a last bit between two passes is a depth test
   that keeps a different surface in each. The invariant qualifier is the
   one thing in the language that forbids it. ==== */
invariant gl_Position;
out vec2 vMap; out vec2 vSheet; out float vBed;
/* JG2: the field this vertex was lifted by, the height one unit of field
   lifts it, and the height itself — linear across the triangle, i.e. the
   MESH's own values at every fragment (GROUND_FS seenField) */
out vec3 vSeen;
/* ==== R10 (b): how far back this vertex stands, for the BLANK PAPER
   alone. depthOut is linear in the map point and in the height, and both
   are linear across a triangle, so a varying is exact here. ==== */
out float vDep;
uniform vec4 uGrid;    // the map rect the grid covers
uniform float uRelief;
` + FIELD_GLSL + FRONT_GLSL + XFORM_GLSL
  + (window.RoomWater && RoomWater.GLSL_DEPTH ? RoomWater.GLSL_DEPTH
     : 'float depthOut(vec2 m, float h){ return 0.0; }') + `
void main(){
  vec2 m = uGrid.xy + a * uGrid.zw;
  /* R40: the field carries the record; terraOf carries the downs east of
     the cut, and nothing at all west of it.

     AT THE PLAN THIS COSTS NOTHING AND CHANGES NOTHING. uRelief is 0
     there, so h is 0 whatever the downs say — exactly, by the multiply —
     and vBed is not read by the ground fragment, so the flat sheet is the
     flat sheet it was to the bit (measured: the fit's diff against the
     base is 0 pixels at 1.75 and at 1.25). It was worth checking whether
     the two octaves — eight hashes a VERTEX over a mesh that is 1024 x 384
     at the fit — should be gated on uRelief as well. They should not: a
     single 40-frame reading said 6.94 ms against 8.55 and looked like a
     real cost, but three consecutive 60-frame readings show that was the
     warm-up and not the work. Steady state, d3d12 at realDpr 1.75: the
     fit 1.667 ms before against 1.635 after, tilt 50 1.717 against 1.645,
     the terra view 1.590 against 1.597. A gate that buys nothing is a
     branch to read and a place for the two paths to drift. */
  /* R46: and seabedOf carries the open sea's floor WEST of the record,
     and nothing at all east of it (?west=bed; uSeabed.x is 0 otherwise,
     so this is the multiply-by-nothing it was). */
  /* X14 (g): + the voyage's own ground (field.js voyVec) */
  /* JG1: the field through the one smooth read (FIELD_GLSL fieldAt) — the
     surface the ink is drawn on — and not at all when the sheet is flat,
     where the height is multiplied by uRelief = 0 and the nine fetches
     would buy nothing (vBed is not read by the ground fragment). */
  float fb = uRelief > 0.0 ? fieldAt(m) : 0.0;
  float bed = riseOf(fb + terraOf(m) + seabedOf(m) + voyVec(m).x);
  float hk = mouldMaskT(m) * ${EXAG}.0 * uRelief;
  float h = bed * hk;
  vec3 p = mapToSheet(m, h);
  vMap = m; vSheet = p.xy; vBed = bed; vDep = depthOut(m, h);
  vSeen = vec3(fb, hk * (uFlat.x <= 0.0 ? 1.0 : 1.0 - uFlat.x), h);
  gl_Position = sheetToClip(p);
}`;

/* THE GROUND. look.html's shader (ROOM-PLAN §1, the proof-of-look), with
   the surveyed region and the front carried analytically, the hachure in
   the sheet's own sepia, and the film of the window over the top. */
/* ==== LP4, 2026-09-22: AND IT IS FOUR PROGRAMS, DRAWN IN FOUR PASSES.

   MEASURED ON TOMMY'S CARD (RTX 3070 Ti, Chrome 153, ANGLE Direct3D 11,
   KHR_parallel_shader_compile on). Direct3D's compiler inlines everything
   and its time grows faster than the code does, so ONE program is
   expensive and several small ones are cheap AND compile in parallel on
   separate cores: this fragment took 3.4-5 s alone, the same source with
   its surveyed block cut out took 1.1 s, and the room's twenty-five
   programs of 10-24 KB take 0.1-0.4 s each. Bisecting the surveyed block
   found no single feature to cut — the tint loop, the hachures, the
   strata, the reveal, the day's glow, the horizons and the haze each cost
   0.3-0.7 s. So the ground is not made smaller; it is CUT UP, along its
   own structure, and each piece is drawn the moment its own program is
   ready. Tommy: "If you see the map draw, then other items come onto it,
   etc. That's better than what it currently does, which is just sitting
   as a white screen for ages!"

   THE FOUR PASSES, in the order they are drawn:

     ground-mat   the material: the land's tint bands, the coast, the
                  foreshore, the sea's three depths. It starts at the
                  paper and writes the surveyed colour `g` — which is
                  exactly the `wash` the one-program version snapshots.
     ground-line  the ink cut on that wash: the hachure, the depth
                  contours, the waterlining, the swell, the three
                  generations of contour, the coast's two strokes, the
                  day grid and the marker horizons — then R35's give-out
                  with the distance, and the hill shading.
     ground-feat  everything that is NOT the survey: the rhumbs, the
                  downs' light, the well, the conjecture's paper, the
                  scarp's bedding, the haze, the reveal and the three
                  pencil marks. It is the pass that mixes the surveyed
                  colour in, by `surv`.
     ground-post  what is applied to the FINAL colour, and nothing else:
                  the plate's alpha at the horizon, the film, the paper's
                  own ink and the lantern's temperature.

   HOW THE FOUR COMPOSE, AND WHY IT IS EXACT. Passes mat, line and feat
   render into an offscreen colour attachment the size of the canvas
   (RGBA16F where EXT_color_buffer_float is there, RGBA8 otherwise), and
   each one after the first READS the colour the pass before it left, at
   its own `gl_FragCoord.xy`, with `texelFetch` — so the arithmetic is not
   re-expressed as a blend at all: it is the SAME chain of `mix`es, in the
   same order, with `col` carried through a texture instead of a register.
   That is what makes it exact, including the two steps a blend cannot
   express — the day grid's multiply, and `mix(wash, g, bLines)`, which
   reads a snapshot taken before the ink — and it is why `wash` is simply
   mat's output and needs no second buffer.

   WHERE IT IS NOT EXACT. Three places, all of them rounding.
   (1) The offscreen is half float, so each hop rounds the colour to about
       1/2048 — under half a step of 8-bit, three hops deep. Without
       EXT_color_buffer_float it is RGBA8 and each hop rounds to 1/255.
   (2) mat and line each compute the prologue (the bed, the Jacobian, the
       gradient) from the same source text, but they are separate compiles
       and a driver may fuse a multiply-add in one and not in the other,
       so an antialiased edge can land a last bit from where one program
       put it.
   (3) The mesh's own depth. The three offscreen passes share a depth
       renderbuffer of their own, cleared before mat and tested LEQUAL by
       all three, so the fragment that survives is the nearest one — the
       same one the real framebuffer's depth test keeps — and GROUND_VS
       declares `invariant gl_Position` so the four programs agree about
       where that is to the bit.

   THE ISSUE ORDER IS NOT THE DRAW ORDER. At the context's creation the
   links go out post, mat, line, feat: post is the smallest and nothing
   shows without it, and mat is the first thing a reader sees. With
   KHR_parallel_shader_compile a pass whose program the poller has not
   collected yet is SKIPPED for that frame and the passes after it draw
   over what is there, so the sheet fills in — the material, then the ink,
   then the features. Without the extension there is nothing to poll and
   the first question blocks (R6's finding), so the old order stands and
   all four are collected before the first draw. ==== */
var GROUND_ISSUE = ['post', 'mat', 'line', 'feat'];   /* the LINK order */
var GROUND_ORDER = ['mat', 'line', 'feat', 'post'];   /* the DRAW order */
function GROUND_FS(part) {
  /* R35: the beyond's own constants, from the file that owns them */
  var BEY = (window.RoomWater && RoomWater.BEY) ||
            { FLAT_RUN: 0.34, FLATTEN: 0.34, SINK: 1 + 1/6, REACH: 2.2, FADE: 0.28, HATCH: 9 };
  /* R40 (b): what fraction of the record's hill shading the downs take.
     `?downshade=` to pair the pictures; 0 draws them unlit. */
  var DSH = (function (q) { return q === null ? 0.60 : Math.max(0, +q || 0); })(Q.get('downshade'));
  DSH = DSH.toFixed(3);
  var MAT = part === 'mat', LINE = part === 'line',
      FEAT = part === 'feat', POST = part === 'post';
  /* the pieces a pass has no use for are not in its source at all: the
     D3D compiler's time is in what it can reach, and a chunk it cannot
     reach is a chunk it does not inline. */
  function K(on, s) { return on ? s : ''; }
  /* LP4: the colour the passes before this one left, at this fragment's
     own pixel. NEAREST, no filter and no offset: the offscreen is the
     canvas's own size and every pass draws the same mesh through the same
     vertex shader, so the texel is this fragment's own. */
  var PREV = 'uniform sampler2D uPrev;\n' +
             'vec3 prevCol(){ return texelFetch(uPrev, ivec2(gl_FragCoord.xy), 0).rgb; }\n';
  return `#version 300 es
precision highp float; precision highp sampler2D;
in vec2 vMap; in vec2 vSheet; in float vBed;
in float vDep;          /* R10 (b) */
in vec3 vSeen;          /* JG2 */
out vec4 o;
uniform float uCoast, uStep, uDpr, uShade, uFilm, uPxPerSheet, uHach;
uniform vec4 uHachC;    // cut0, q1, q2 — the home year's slope quantiles — and the mode
uniform vec2 uLev;      // the two fading generations of contour
uniform vec4 uDayG;     // E6: the day cell — cw, px, ch, py (0 = no grid)
uniform float uDayGA;   // R9 (a): how much of the grid there is, 0..1
uniform float uInk;     // DEVICE px per sheet unit AT THE FIT: the size the ink holds
uniform vec3 uSun;     // x, y (screen-ish), z
uniform vec4 uPlate;
uniform vec4 uLampS;    // R48: the lantern in sheet units: x, y, and the pool's two radii
uniform float uLampN;   // and the night, which is nought by day
uniform float uRelief;  // R35: the scarp is a cut only when the ground stands up
/* R36: THE MARKER HORIZONS — the map x of each, and how many there are.
   The list is horizons.json's and nothing here knows what is in it: the
   array is eight long because a uniform array must have a length, and the
   sheet would be a graticule long before eight. */
#define MAXH 8
uniform float uHorzX[MAXH];
uniform float uHorzN;
/* THE GAP A CONTOUR FIGURE CUTS IN ITS OWN LINE (E3). The SVG can only
   overprint a paper-coloured halo, because the figure is a separate node
   drawn after the path; here the line is told where the figure is and
   simply STOPS, which is what an engraver did and what the reference
   sheets show. Up to sixteen boxes, in MAP units, each with its own
   rotation — the same boxes the lettering laid out. */
/* R4: the reveal — mode (0 none/rise, 1 ink west->east, 2 fade), t,
   the ink edge in MAP units and its softness */
uniform vec4 uReveal;
uniform int uNGap;
uniform vec4 uGapB[16];
uniform vec2 uGapR[16];
uniform float uGapF;
` + FIELD_GLSL + FRONT_CORE + K(!POST, FRONT_TAIL) + K(POST, PAPER_INK_GLSL) + `
/* E7's smoothed reconstruction is FIELD_GLSL's fieldAt now (JG1) */
` + K(MAT || LINE, `
/* ==== JG2 (2026-09-26): THE FIELD WHERE THE EYE MEETS THE GROUND, NOT
   WHERE THE MESH IS. Tommy, at #1385,568,706,48,-57: "contours are better
   but some are now perfectly smooth and others resemble what they did
   before". JG1's note says it: a line is inked where the smooth field
   equals its level, and seen at the height the MESH gives that point. The
   mesh is linear across each facet, so on a steep curved face every line
   is lifted and dropped by (mesh - field) and kinks at every facet edge,
   in columns straight across the stacked lines. Measured at his hash, in
   device px on his screen, over the contours on the Sound's banks and the
   strip beyond them (every level, front-facing ground, the exact camera):
   0.36 rms, 1.1 at the 99th percentile, 1.4 worst, on the finest mesh the
   ladder gives that view (1.25 x 3.55 map units a facet); the mesh the
   view draws until that one is built (5 x 7.1) is four to ten times that.
   A finer mesh only halves it per octave, and every octave doubles the
   vertex work of four passes.

   So the fragment finds the ground its own pixel is looking at. Every
   point of the pixel's ray is (s, h) + t (s - eye, h - eye.h) in map
   units and height (uEyeM is the camera's eye carried back through
   mapToSheet, setCommon), and the true surface crosses it where
   hk f(s + t d) - h_mesh - t d.z = 0. One Newton step from t = 0 — the
   mesh's own field and lift ride in vSeen — and the field there is
   f + t (grad f . d): no fetch, the gradient is the central difference
   the ink reads anyway. Measured the same way: 0.005 rms, 0.04 worst.
   Where the ground turns away from the eye the step is undefined (the ray
   runs along the face), so it fades out between a quarter and a half of
   the flat ground's rate and is capped at three map units. FLAT, hk is 0
   and the value is the field's own, to the bit. ==== */
uniform vec3 uEyeM;
float seenField(vec2 s, float f, vec2 gf){
  if (uRelief <= 0.0) return f;
  vec3 d = vec3(s, vSeen.z) - uEyeM;
  float gd = dot(gf, d.xy);
  float den = vSeen.y*gd - d.z;
  float w = smoothstep(0.25, 0.5, den / max(-d.z, 1e-6));
  if (w <= 0.0) return f;
  float len = max(length(d.xy), 1e-6);
  float t = clamp(-vSeen.y*(f - vSeen.x)/den*w*len, -3.0, 3.0) / len;
  return f + t*gd;
}
`) + `
/* ==== R35: ONE GROUND — AND HOW FAR OUTSIDE THE LINE THIS FRAGMENT IS.

   Until this item the ground DISCARDED past the record's rect and a second
   drawing (water.js's beyond, its own grid, its own program, its own
   material) had everything outside. It could only ever EXTRUDE — every
   point out there read the record's boundary point nearest it — so a curve
   at the edge came out a straight band, and between the two meshes a
   curtain had to hide a height mismatch that can only be biased, never
   made exact. The field carries the continuation now (field.js), the mesh
   already covers the window at the clamp (R13), so the ground simply draws
   it: ONE surface, ONE program, ONE material.

   Two distances, and they are not the same distance (R31):
   - bOut, the true distance out. What is DRAWN gives out with it — the
     contour lines, the hachure, the depth lines, the coast — because out
     there the survey is one edge read through a window as wide as the
     distance, and a wash is an honest thing to make of that while a ruled
     line is not.
   - bAcr, the ACROSS overshoot alone. The MATERIAL gives out with that, so
     due west the sea is the sea and due east the blank paper is the blank
     paper however far the pan runs, and north and south R16's and R20's
     fade is untouched to the float.
   Inside the line both are 0, bMat and bLines are exactly 1, and every
   expression below collapses to the one that was there. ==== */
uniform vec4 uPlateM;   // the RECORD rect, MAP units (front.west .. front.east)
/* ==== D2 (2): AND THE SHEET REACHES FURTHER EAST THAN THE RECORD'S OWN
   TWO PLATE-HEIGHTS. Tommy ruled the funnel out of the default view
   ("we probably do still need some hints ... a bit obvious, you just see
   it on the map right there"), and the reader cannot pan to a thing the
   paper stops short of: the alpha rim discards the ground at REACH x a
   plate-height, which is 1,201 map units, and mid-2030 is seven thousand
   further. So the EAST overshoot alone is scaled: uEastK is 1 everywhere
   the funnel is not asked for, and the ratio of the old reach to the new
   one when it is. West, north and south are untouched, and so is every
   fragment inside the line (dE is 0 there). Default 0 reads as 1, so a
   program that never receives it draws what it drew. ==== */
uniform float uEastK;
float bOut, bAcr, bMat, bLines;
void beyOf(vec2 s){
  vec2 lo = uPlateM.xy, hi = lo + uPlateM.zw;
  /* the three overshoots, apart: west of the record, east of it, across */
  float dW = max(lo.x - s.x, 0.0), dE = max(s.x - hi.x, 0.0);
  float dA = max(max(lo.y - s.y, s.y - hi.y), 0.0);
  /* ==== R41: WEST OF THE SURVEYED START THE SEA IS SURVEYED SEA.

     Tommy's rule: "west of the record the sea is SURVEYED zero-commit sea,
     the same sea as 2022, for ever, in every direction the field says is
     sea." Two things in here said otherwise:
     - bAcr, the ACROSS overshoot, is what the MATERIAL gives out with, and
       it never asked whether it was west of the record — so beyond a
       plate-height and a sixth north or south the ground was conjecture
       paper whatever the bed said, which is why the west sea was a BAND
       between two paper margins (R39's own OPEN 1). It is gated on the
       west run now: at the start it is the across overshoot entire, three
       quarters of a plate-height west it is nothing.
     - the WEST overshoot itself. It is dropped when the gate is on, so the
       record's rect has no western edge: bOut and bAcr are both 0 due
       west, if (bOut > 0.0) is false, and the ground draws the sea, the
       depth contours, the waterlining and the coast exactly as it draws
       them inside the line — and the alpha's REACH rim, which used to
       discard the ground 1,201 map units out, never comes into it. That
       rim was the reason the west sea did not reach the edge of the
       portal. Dropping it is CONTINUOUS: at the start dW is 0 and both
       readings agree, and westward bOut only falls.
     At uWestSea = 0 this is length(d) and d.y to the bit. ==== */
  float wW = westSea(s);
  float dx = dE*(uEastK > 0.0 ? uEastK : 1.0) + (uWestSea > 0.0 ? 0.0 : dW);
  float dy = dA * (1.0 - wW);
  bOut = length(vec2(dx, dy)); bAcr = dy;
  float PA = uPlateM.w;
  bMat   = (1.0 - smst(0.0, ${BEY.FLAT_RUN.toFixed(4)}*PA, bAcr)*${(1 - BEY.FLATTEN).toFixed(4)})
         * (1.0 - smst(0.0, ${BEY.SINK.toFixed(4)}*PA, bAcr));
  bLines = (1.0 - smst(0.0, ${BEY.FLAT_RUN.toFixed(4)}*PA, bOut)*${(1 - BEY.FLATTEN).toFixed(4)})
         * (1.0 - smst(0.0, ${BEY.SINK.toFixed(4)}*PA, bOut));
}
` + K(!MAT, PREV) + K(FEAT, `
/* the conjecture's own paper and its rules — a shade off the sheet's paper
   on purpose, at 45 degrees, nine sheet units apart (relief.js
   hatchTexture :4549). Struck per fragment it has no mip, so where the
   rules fall closer than three device pixels the stroke gives way to its
   own area fraction — the tone the mip would have arrived at. */
vec3 conjPaper(vec2 s, float ink){
  vec3 c = vec3(0.90196, 0.85882, 0.75294);       // #e6dbc0
  float d = (s.x + s.y) * 0.70710678;
  float per = ${BEY.HATCH.toFixed(2)};
  float dh = abs(fract(d/per + 0.5) - 0.5)*per;
  float wpx = max(1.20*ink, 0.9);
  float cov = clamp((wpx*0.5 - dh*ink) + 0.5, 0.0, min(1.0, wpx));
  float vis = clamp(per/max(fwidth(d)*4.5, 1e-4), 0.0, 1.0);
  float hatch = (cov*vis + (1.20/per)*(1.0 - vis))*0.62;
  return mix(c, vec3(0.61569, 0.52157, 0.36078), hatch);
}
`) + K(LINE, `
/* ==== /E7 ============================================================== */
float gapAt(vec2 m){
  float g = 0.0;
  for (int i = 0; i < 16 + uZero; i++){
    if (i >= uNGap) break;
    vec2 d = m - uGapB[i].xy;
    vec2 r = vec2(d.x*uGapR[i].x + d.y*uGapR[i].y, -d.x*uGapR[i].y + d.y*uGapR[i].x);
    vec2 q = abs(r) - uGapB[i].zw;
    g = max(g, 1.0 - smst(-uGapF, 0.0, max(q.x, q.y)));
  }
  return g;
}
`) + `
const vec3 PAPER = vec3(0.94509804,0.90588235,0.81960784); // #f1e7d1
const vec3 CIDX  = vec3(0.49019608,0.26274510,0.12549020); // #7d4320
const vec3 CINT  = vec3(0.61176471,0.37254902,0.18039216); // #9c5f2e
const vec3 CCOAST= vec3(0.42745098,0.27058824,0.12549020); // #6d4520
const vec3 SEPIA = vec3(0.60392157,0.41960784,0.25490196); // #9a6b41
const vec3 SEA   = vec3(0.63921569,0.72549020,0.74117647); // #a3b9bd
const vec3 SEA1  = vec3(0.55686275,0.66274510,0.69803922); // #8ea9b2
const vec3 SEA2  = vec3(0.43529412,0.58431373,0.63529412); // #6f95a2
const vec3 FORE  = vec3(0.84705882,0.82352941,0.69019608); // #d8d2b0
const vec3 SEALN = vec3(0.29019608,0.43921569,0.47450980); // #4a7079  --sea-line
const vec3 CUNDER= vec3(0.18431373,0.30196078,0.33333333); // #2f4d55  .sv-coast-under
` + K(MAT, `
/* ==== R26: THE LADDER IS NOT WRITTEN HERE. It is field.js's C, written
   into this string the way ${EXAG} is, so the ground, the beyond and the
   ramp cannot drift apart. ================================================ */
` + F.TINT_GLSL('tintCol', 'tintBand')) + K(LINE || FEAT, `
/* A LINE THINNER THAN A PIXEL MUST NOT INK A WHOLE ONE. The coverage of a
   stroke w device pixels wide whose centre is d from this pixel's centre is
   clamp(w/2 - d + 0.5, 0, 1) only while w >= 1; below that the half-pixel
   term inks w/2 + 0.5 where the true area is w, so a 0.42 px hairline came
   out 0.71 dark at ratio 1 and 0.46 at ratio 2 - the same ink measured 12.5
   % heavier on a 1x screen than on a 2x one. Clamping the ceiling to w is
   what an SVG hairline does, and it is what makes the ink the same PHYSICAL
   weight at every device ratio. */
float inkOf(float w, float d){ return clamp(w*0.5-d+0.5, 0.0, min(1.0, w)); }
`) + K(LINE, `
float cover(float val, float grad, float period, float offset, float w, float minLevel){
  float g=(val-offset)/period;
  float n=floor(g+0.5); float lv=n*period+offset;
  if(lv<=minLevel) return 0.0;
  float dpx=abs(val-lv)/max(grad,1e-7);
  return inkOf(w, dpx);
}
`) + K(POST, `
/* THE FILM — one varnish over the whole plate (styles.css, .canvas-plot::after).
   It is a layer of the WINDOW, so it is a function of where the fragment
   falls in the plate, and it is here rather than as a DOM layer because a
   mix-blend-mode element over a canvas is a second composite pass. */
vec3 film(vec3 col, vec2 s){
  if (uFilm <= 0.0) return col;
  vec2 q = (s - uPlate.xy) / uPlate.zw;
  /* radial-gradient(125% 155% at 50% 44%, ...): the ending shape's radii
     are 125% of the box WIDTH and 155% of its HEIGHT, and the stops are
     fractions of that ellipse — so the gradient position is already
     normalised and must NOT be doubled. */
  float r = length(vec2((q.x-0.5)/1.25, (q.y-0.44)/1.55));
  vec4 rad;
  if (r < 0.34) rad = vec4(1.0,1.0,1.0,0.0);
  else if (r < 0.66) rad = mix(vec4(1.0,1.0,1.0,0.0), vec4(0.494,0.361,0.180,0.16), (r-0.34)/0.32);
  else if (r < 0.86) rad = mix(vec4(0.494,0.361,0.180,0.16), vec4(0.408,0.290,0.141,0.34), (r-0.66)/0.20);
  else if (r < 1.0)  rad = mix(vec4(0.408,0.290,0.141,0.34), vec4(0.329,0.227,0.102,0.52), (r-0.86)/0.14);
  else rad = vec4(0.329,0.227,0.102,0.52);
  float t = clamp(q.y, 0.0, 1.0);
  vec4 lin = mix(vec4(0.776,0.612,0.361,0.20), vec4(0.698,0.525,0.290,0.26), t);
  float a = rad.a + lin.a*(1.0-rad.a);
  vec3 c = a > 0.0 ? (rad.rgb*rad.a + lin.rgb*lin.a*(1.0-rad.a))/a : vec3(1.0);
  float f = a * uFilm;
  return col * (1.0 - f) + col * c * f;
}
/* ==== R48: AND THE PLATE TAKES THE LANTERN'S TEMPERATURE TOO =========
   R42 (a) warmed the sheet, the card and frame 1's paper on the oak's
   lamp pool at night and left the plate INSIDE the neat line — which is
   this fragment's — cold, so the neat line was a temperature seam of
   about 10 of r-b at night (measured at the top neat line, the day
   picture subtracted: room-rev6/R48-before-night-fit.png). It is ONE
   piece of paper: the same five lines, the same two uniforms, the same
   pool in the same SHEET units, so the pool does not bend at the line
   and does not swim under a pan. The night scales the whole term, and
   uLampN is exactly 0 by day, so the daylight picture is the one it was
   to the bit (measured: the day fit's diff against the base is 0).
   Six copies now: objects.js (the sheet), day.js (the card),
   engine.js's frame-1 sheet, here, and — R52 — marks.js (one copy for its
   three programs) and lettering.js, which are the print over this ground.

   WHAT WAS STILL COLD WHEN R48 SHIPPED, AND WHAT R52 DID. The sea, the
   tints, the contours, the hachure and the scarp's bedding are all this
   fragment's, so they take the lantern with it. The MARKS (marks.js) and
   the NAMES (lettering.js) are their own programs drawn over this one and
   were NOT warmed: over the TERRA INCOGNITA band at 23:00 the paper gained
   15.6 of r-b and the glyphs' ink 0.8 (room-rev6/R48-after-night-fit.png,
   device rows 440-510, columns 1790-2360). It read as a name a shade less
   golden than the paper it is set on rather than as an edge, which is why
   it was not R48's named fault — but on the sheet the printed furniture IS
   warmed (objects.js applies lampTemp to its own print), so the plate's
   print should be too.
   DONE, R52: marks.js (ONE copy for its three programs — the families, the
   buildings, the sprites) and lettering.js (the names, ink and paper halo
   together) now carry these same five lines and the same two uniforms off
   lampSheet(), applied last, one mix each. SIX copies of the rule now.
   Measured after, on the same band: the paper gains 16 of r-b and the
   name's ink 8, against 1 before — the residual is the multiply's own
   arithmetic (the ink is already a warm brown and has little blue left to
   take away), the same thing R48's x=1400 column showed. The compass rose
   goes 1 -> 6 against the paper's 10; the field boundaries at a zoom pose
   track the paper exactly (REPORT-room-R52). The day picture still diffs
   0 at the fit, the room pose and a zoom. ==================== */
vec3 lampTemp(vec3 c, vec2 wp){
  if (uLampN <= 0.0) return c;
  vec2 d = (wp - uLampS.xy)/uLampS.zw;
  float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
  vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
  /* LK1: and the lamp is the light. The paper outside the pool falls into
     shadow at night (to half), and into a dusk dimness before it. */
  float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
  return c*mix(vec3(1.0), t*lit, uLampN);
}
`) + K(LINE, `
/* ================= THE INK =========================================
   A stroke lattice that is GLUED TO THE GROUND (so nothing swims under a
   pan) and whose spacing, length and width are held in DEVICE pixels (so
   the ink is the same size at every zoom and every device ratio — the
   property patternTransform / calc(Npx / var(--z)) exists to fake).
   The two are only compatible if the lattice SUBDIVIDES: the cell is the
   sheet's own stride-2 hachure cell halved once per zoom octave, and the
   two generations either side of the octave cross-fade, so no stroke ever
   appears or vanishes. Every anchor is jittered by a hash of its own cell
   index — unjittered strokes comb into moire on smooth ground, which is
   the one way this looks like a computer drew it (ROOM-PLAN §2). */
vec2 hash22(vec2 p){          /* Dave Hoskins' hash - no sin(), which is
                                 where mobile GL loses its precision */
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
float hachAt(vec2 s, mat2 Sx, vec2 dirS, float cx, float cy, float lenPx,
             float wPx, float wgt, float lvl){
  if (wgt <= 0.003) return 0.0;
  /* R35: uLat.xy is the RECORD's lattice origin and stays so — the
     scatter below is a hash of the CELL INDEX, and an index taken against
     a moving origin re-deals every stroke on the plate. See uMarg. */
  vec2 c = vec2((s.x - uLat.x)/cx, (s.y - uLat.y)/cy);
  vec2 i0 = floor(c);
  float acc = 0.0;
  for (int dy = -1; dy <= 1 + uZero; dy++){
    for (int dx = -1; dx <= 1 + uZero; dx++){
      vec2 ij = i0 + vec2(float(dx), float(dy));
      vec2 j = hash22(ij + vec2(lvl*13.17, lvl*7.31)) - 0.5;
      /* the sheet staggers every other row by half a cell; the hash then
         scatters within the cell (:2541, jx/jy) */
      float stag = fract(ij.y*0.5) > 0.25 ? 0.5 : 0.0;
      vec2 a = vec2((ij.x + 0.5 + stag + j.x*0.84)*cx + uLat.x,
                    (ij.y + 0.5 + j.y*0.84)*cy + uLat.y);
      vec2 q = Sx * (s - a);                       // device px, anchor -> fragment
      float al = clamp(dot(q, dirS), 0.0, lenPx);
      float d = length(q - dirS*al);
      acc = max(acc, inkOf(wPx, d));
    }
  }
  return acc*wgt;
}
/* ==== E7 (water.js): the waterlining of the sea ======================== */
` + (window.RoomWater ? RoomWater.FS_SEA : 'vec3 waterLining(vec3 g,float v,float gr,float d,float m){return g;}') + `
/* ==== /E7 ============================================================== */
`) + K(FEAT || POST, `
/* ==== R10 (b) (water.js): the distance's two ramps, for the blank paper = */
` + (window.RoomWater && RoomWater.GLSL_RAMPS ? RoomWater.GLSL_RAMPS
     : 'uniform vec4 uDist; uniform vec3 uHazeCol;\nfloat skyOf(float d){return 0.0;}\nfloat hazeOf(float d){return 0.0;}') + `
/* ==== /R10 ============================================================= */
`) + K(FEAT, `
/* ==== CE1 (Tommy, 2026-10-08): THE CLIFF IS PLAIN PAPER. R35/R36 painted
   the scarp at the front as cut earth, brown bedding by a slope rule;
   Tommy: "I expected we'd still have a cliff, but it would be the colour
   of the unsurveyed land, rather than the weird brown effect". He picked
   it (then ?earth=0) over a long slope with no cliff (?terra=land). The
   face is the downs' own paper; the bedding and its switch are gone. */
/* ==== R3 (a) (water.js): the winds over Terra Incognita ================= */
` + (window.RoomWater && RoomWater.FS_TERRA ? RoomWater.FS_TERRA : 'vec3 rhumbs(vec3 c, vec2 s){ return c; }') + `
/* ==== /R3 ============================================================== */
/* ==== D1 (4): THE INSTRUMENT'S LINE OF SIGHT was pencil on the paper here,
   with the limb (D2 (4)) beside it. PH1 (2026-09-25) took both off the
   ground: printed on the paper they lay down the scarp's face when the
   sheet was tilted (Tommy: "the lines just stretch down the cliff face").
   The line is drawn IN THE AIR now and the limb is an upright plate — see
   doomSightDraw and doomDial. ==== */
const vec3 PENCIL = vec3(0.42, 0.38, 0.33);
/* ==== D2 (1): THE WELL IS DRAWN, NOT ONLY SHAPED.

   Tommy's two references are the wireframe GRAVITY WELL (a square grid
   warping down into a throat, the lines crowding as they descend, with a
   spiral drift) and the black-hole/white-hole hourglass. Terra Incognita
   draws no contour and no hachure — the survey has not been there — so
   there is no generation of lines for the funnel to bend. This lays one:
   a faint SQUARE LATTICE in the sheet's own pencil, over the downs and
   nothing else, and the funnel warps it.

   THE CROWDING IS THE SURFACE'S OWN ARC LENGTH. The lattice is drawn at
   a fixed spacing along the GROUND, and the ground down the throat is
   nearly vertical, so a map unit of radius carries many units of surface
   — which is exactly why the classic picture's lines close up as they go
   in. rr is that arc length, near enough: the map distance plus the drop,
   which is right to a few per cent on a wall this steep and costs a
   multiply. Out on the flat downs the drop is 0 and rr is r, so the
   lattice is a plain square grid there.

   THE DRIFT IS A UNIFORM. uDoomK.x is an angle the engine advances with
   the clock; it is multiplied by (1-t)^2 so it dies at the rim and the
   downs do not turn. No loop of its own: the doom block asks for a frame
   four times a second while the funnel is on screen.
   PT1 2026-09-20: and the engine hands that angle over ALREADY SIGNED by
   the rise (dsgn, at the upload), so the pit and the peak whirl opposite
   ways — at the plan that handedness is the only thing that tells them
   apart, the relief being zero there.

   AND THE LINES GIVE OUT WHEN THEY CLOSE (R1 b): where the lattice's own
   separation falls under about three device pixels it is not a generation
   of lines, it is a tone, so it fades out instead of aliasing. ==== */
/* ==== D2 (3): THE PACED DAY'S OWN BOUNDARY. One rule on the cell's four
   sides, a device pixel and a half wide through fwidth — which is why it
   is here and not in field.js beside paceVec: fwidth is a FRAGMENT's
   function, and that chunk is included by nine vertex programs. Nothing
   at all where no day is paced (uPaceK.x is 0 until the party moves). */
float paceRule(vec2 s){
  if (uPaceK.x <= 0.0) return 0.0;
  vec2 w = fwidth(s)*1.5 + 1e-4;
  float a = 0.0;
  for (int i = 0; i < 7 + uZero; i++){
    if (uPace[i].z <= 0.0) continue;
    float dx = abs(s.x - uPace[i].x), dy = abs(s.y - uPace[i].y);
    if (dx > uPaceK.x + w.x || dy > uPaceK.y + w.y) continue;
    float lx = (1.0 - smst(0.0, w.x, abs(dx - uPaceK.x)))*step(dy, uPaceK.y + w.y);
    float ly = (1.0 - smst(0.0, w.y, abs(dy - uPaceK.y)))*step(dx, uPaceK.x + w.x);
    a = max(a, max(lx, ly));
  }
  return a;
}
uniform vec4 uDoomK;   /* D2: the drift's angle, 1/depth, the ink, the lattice */
vec3 wellInk(vec3 c, vec2 s){
  /* TWO GATES BEFORE ANY OF THE WORK. The first is uniform — no funnel
     asked for, or a flat sheet — so it costs a branch the whole draw
     takes together. The second is the RECORD: west of the scarp's foot
     there is no Terra Incognita to rule, and the record is most of the
     window, so an atan and a pair of derivatives a fragment there is the
     whole cost of this term for nothing. The quads that straddle the
     foot diverge on it, and their derivatives are the poorer for it —
     which changes nothing, because every term below is multiplied by tw
     and tw is 0 to three decimal places exactly there. */
  if (uDoomK.z <= 0.0) return c;
  float tw = terraW(s.x);
  if (tw <= 0.002) return c;
  vec2 d = s - uDoom.xy;
  float R = max(uDoom.z, 1.0);
  float r = length(d);
  float t = clamp(r/R, 0.0, 1.0);
  float f = doomF(t);
  float rr = r + abs(uDoom.w)*f*${EXAG}.0;
  float gate = 1.0 - smst(0.88, 1.0, t);
  float a = atan(d.y, d.x) + uDoomK.x*gate*gate;
  vec2 q = mix(s, uDoom.xy + vec2(cos(a), sin(a))*rr, gate);
  vec2 fw = fwidth(q) + 1e-5;
  vec2 gg = abs(fract(q/uDoomK.w + 0.5) - 0.5)*uDoomK.w;
  vec2 dd = gg/fw;
  float line = 1.0 - smst(0.35, 1.30, min(dd.x, dd.y));
  line *= smst(1.7, 3.6, uDoomK.w/max(max(fw.x, fw.y), 1e-5));
  /* REVIEW 2026-09-19: AND ALL OF IT AT THE PLAN TOO. D2 gated the
     lattice, the throat and the spire on uRelief, reading Tommy's "cool
     or naff" as keep-it-hidden-flat; Tommy: "is there any reason we
     can't see the moving singularity when we're flat?" There is none: the
     crowding is the funnel's own arc length and the drift is time, and
     neither needs a tilt to be drawn. The fit is untouched — the pit is
     seven thousand map units east of anything the fit shows, and the
     record gate (tw) above still leaves every fragment on the record
     alone. What the plan loses is only the tilt's shading of the throat's
     walls, which is relief and not ink. */
  float rel = tw;
  c = mix(c, PENCIL, line*uDoomK.z*rel*(0.20 + 0.62*gate));
  /* ==== PT1 2026-09-20: THE HACHURE, and it is the second thing that
     tells the two apart. A depression contour on a real sheet carries
     short ticks on its DOWNHILL side, and the survey's reader knows it:
     they run INWARD round the pit and OUTWARD round the peak. Tommy:
     "and maybe yes to the ticks outward."

     ONE RING ONLY. The whirl's rings are the square lattice wrapped, so
     their radius runs with the angle and the throat crowds them until
     they give out; a tick on every ring would be a texture. So the ticks
     sit on the ring NEAREST THE HOLE that the lattice still resolves, at
     0.42 of the disc, twelve of them, and they are carried on the WHIRL'S
     OWN ANGLE (a, the drift included), so they turn with the lattice they
     belong to instead of standing still on the paper under it.

     THE SAME INK AS THE LATTICE. The same 0.35..1.30 profile across the
     tick, the same give-out when it closes to a dot, and the same
     0.20 + 0.62*gate strength — hachure is a contour's own line, not a
     heavier mark, and nothing here is meant to be the first thing seen.

     hsig is the rise normalised to -1..+1 (the pit at -1), which is what
     points the tick; it also fades the ticks out through the crossing,
     where there is neither a pit nor a peak to hachure and the whirl is
     straight, so nothing snaps round. ==== */
  vec2 fs = fwidth(s) + 1e-5;
  vec2 rad = d/max(r, 1e-4);
  float pwT = length(fs*vec2(-rad.y, rad.x));  /* a pixel ACROSS the tick */
  float pwR = length(fs*rad);                  /* and a pixel ALONG it */
  float hsig = clamp(uDoom.w*uDoomK.y, -1.0, 1.0);
  float RT = R*${HACH_R}, LT = R*${HACH_L};
  float ga = abs(fract(a/${HACH_A} + 0.5) - 0.5)*${HACH_A}*max(r, 1.0);
  float along = (r - RT)*(hsig >= 0.0 ? 1.0 : -1.0);
  float hend = max(pwR*1.1, LT*0.15);
  float hach = (1.0 - smst(0.35, 1.30, ga/pwT))
             * smst(-hend, 0.0, along)*(1.0 - smst(LT - hend, LT, along))
             * smst(1.7, 3.6, LT/pwR)
             * smst(0.03, 0.30, abs(hsig));
  c = mix(c, PENCIL, hach*uDoomK.z*rel*(0.20 + 0.62*gate));
  /* and the throat is DARK and the spire is LIT — one number, eased from
     the one to the other, so there is no second drawing to cross to */
  float amt = clamp(f*uDoom.w*uDoomK.y, -1.0, 1.0);
  c = mix(c, vec3(0.055, 0.050, 0.046), max(-amt, 0.0)*0.93*rel);
  c = mix(c, vec3(1.000, 0.972, 0.902), max( amt, 0.0)*0.72*rel);
  return c;
}
`) + K(MAT, `
/* ==== MF1 (2026-09-26): AND THE FOUR FRAGMENT SHADERS READ EVERY VARYING.
   Tommy, of the slow lifeboat: "weird jitter in the edge underneath ...
   it's only in the edging underneath the boat, never above". It was
   single frames of blue, short rules and flecks, in the hatched south
   margin, and only while the ground stood in relief (the voyage draws at
   uRelief 0.84). LP4's invariant gl_Position does not hold on his card:
   ANGLE builds each program's vertex shader for D3D with only the outputs
   its own fragment shader reads, so mat, line and feat were three
   different compiles of GROUND_VS and put a vertex a last bit apart.
   Where the relief folds over itself that is enough for mat's near
   surface to cover a pixel that feat's copy just misses; feat's fragment
   there is the far surface, fails LEQUAL against mat's depth, and the
   pixel keeps mat's sea blue, the one colour the margin's hatch
   (conjPaper, in feat) is not. Measured on his Chrome, sailing the
   lifeboat through 2022: 12 flashed frames in 120 shots; none with feat's
   test at ALWAYS, none with neither mat nor line writing depth, none with
   the field read held constant; no depth bias fixed it (the far surface
   is far). So every pass reads all five varyings, behind a branch that
   never runs (uZero is never set), and the four vertex shaders are one
   compile. ==== */
void main(){
  if (uZero > 0) { o = vec4(vMap, vSheet) + vec4(vBed, vDep, vSeen.xy) + vec4(vSeen.z); return; }   /* MF1 */
  vec2 s = vMap;
  /* ==== R35: how far outside the neat line this fragment stands ======== */
  beyOf(s);
  /* AND WHAT IS SURVEYED OUT THERE IS WHAT THE LINE IT LEFT WAS. The
     margin is the record's own field continued, so its surveyedness is
     the record's at the boundary point nearest it: north and south of the
     surveyed weeks it is ground, north and south of the unsurveyed east it
     is Terra Incognita's paper, and the front's own five-day fade carries
     across the line with it. Inside the line the clamp is the identity and
     this is surveyedAt(s) to the bit. */
  vec2 sCl = clamp(s, uPlateM.xy, uPlateM.xy + uPlateM.zw);
  /* ==== D2 (3): AND A DAY THE PARTY HAS PACED IS SURVEYED GROUND. The
     pad already RAISES it (field.js paceVec); this is the same pad read
     as surveyedness, so the cell takes the record's own colour ramp at
     its paced height instead of Terra Incognita's grey, with the record's
     own contours and hachure on it. It is read at the CLAMPED point for
     the same reason surveyedAt is: north and south of the record's rows
     the sheet is the boundary row continued, so the edge that runs off
     into the distance takes the surveyed colour too instead of going off
     grey beside coloured ground (Tommy: "it should also change the
     edge"). ==== */
  float surv = max(surveyedAt(sCl), paceMask(sCl));
  vec3 g = PAPER;
  if (surv > 0.001) {
    /* THE VALUE IS SAMPLED HERE, NOT INTERPOLATED FROM THE VERTEX. vBed is
       the grid's own vertex height, linear across each triangle, so every
       level crossing was a polyline on the GRID's facets — a staircase of
       one facet (about 6 device px) wherever a tint edge ran near an axis,
       which is what x4 and x8 showed against 8102's smooth edge. Sampling
       the field per fragment puts the level set back on the FIELD's own
       bilinear lattice, which is exactly the surface 8102's marching
       squares walks (survey-map.js contour()), and costs one fetch. */
    /* ==== R46: THE OPEN SEA'S FLOOR, WEST OF JANUARY 2022. The array
       cannot hold it (the lattice stops 130 map units west of the line and
       the voyage's 1986 is 74,000 further), so it is evaluated here — the
       value AND its two map derivatives, because the depth tints, the
       coast and the eight waterlines all measure themselves through the
       field's own gradient and a value that moves under a gradient that
       does not is a band edge of the wrong width. Zero east of the line
       and zero without ?west=bed, to the bit. ==== */
    /* X14 (g): and the voyage's ground with it, value AND gradient */
    /* D1: and the ground the party has levelled, value AND gradient —
       the geometry rises through terraOf either way, and the tints, the
       contours and the waterlines are all measured through the field's
       own gradient, so a pad the fragment did not know about would raise
       the ground and leave its contours lying where they were. */
    vec3 sbv = seabedVec(s) + voyVec(s) + paceVec(s);
    /* JG2: the gradient first, because the value is read where the eye
       meets the ground (seenField) and that step is along it */
    vec2 gF = vec2(
      (texture(uField, uvOf(s+vec2(uLat0.x,0.))).r - texture(uField, uvOf(s-vec2(uLat0.x,0.))).r)/(2.0*uLat0.x),
      (texture(uField, uvOf(s+vec2(0.,uLat0.y))).r - texture(uField, uvOf(s-vec2(0.,uLat0.y))).r)/(2.0*uLat0.y));
    float bed = riseOf(seenField(s, fieldAt(s), gF) + sbv.x);   /* E7: the smoothed reconstruction; R4: through the rise */
    float v = bed + uCoast;
    /* THE LOCAL MAP -> DEVICE PIXEL JACOBIAN, once: the ink, the level set
       and the hachure all measure themselves in device pixels through it,
       and it is exact under any tilt, turn or perspective because vMap is
       linear across a triangle. */
    mat2 J = mat2(dFdx(s), dFdy(s));
    float det = J[0].x*J[1].y - J[0].y*J[1].x;
    mat2 Sx = mat2(J[1].y, -J[0].y, -J[1].x, J[0].x)*(1.0/(abs(det) > 1e-12 ? det : 1e-12));
    float pxPerU = sqrt(abs(1.0/(abs(det) > 1e-12 ? det : 1e-12)));
    /* THE GRADIENT IS THE FIELD'S OWN CENTRAL DIFFERENCE, NOT dFdx OF THE
       SAMPLE. A derivative is computed once per 2x2 quad from one pair of
       pixels, so where a level crossing ran near a texel boundary the whole
       quad took the wrong width and the tint edges came out dotted at x4
       and x8. The central difference over the field's own lattice is the
       slope 8102's contour walker uses, it is smooth across texels, and
       the hachure needs it anyway. */
    vec2 g2 = gF;                 /* JG2: read above, before the value */
    g2 += sbv.yz;                 /* R46: the seabed's own slope, analytic */
    float mag = length(g2);
    /* THE LINE'S WIDTH IS MEASURED ALONG THE LINE'S OWN NORMAL, NOT ALONG
       AN AVERAGE (R1 b). Every edge on this plate — the contours, the tint
       band boundaries, the coast, the depth lines — is drawn by turning
       "how far is this fragment from the level" into DEVICE PIXELS, and E2
       did that with one isotropic number: mag / pxPerU, where pxPerU is the
       square root of the Jacobian's determinant, i.e. the GEOMETRIC MEAN of
       the two screen scales.

       On the flat sheet the map-to-screen map is a scaled rotation and the
       mean is exact. Under a tilt it is not even close: the map's y is
       foreshortened by cos(tilt) while the ground's own slope, magnified
       twentyfold, adds sin(tilt) x dh/dy to the same screen direction — so
       a slope facing the camera is STRETCHED (0.276 + 0.96 at tilt 74) and
       the slope behind it is CRUSHED (0.276 - 0.96, through zero where the
       surface turns edge-on). The two screen scales differ by a factor of
       five and more, and the geometric mean is wrong by its square root in
       BOTH directions at once. So a contour running across the fall of the
       land was drawn two or three times too wide and one running with it
       too thin, and — because the anisotropy is a function of the ground's
       own slope under the camera — those errors changed every frame of a
       tilt. That is the crawl: the lines were not moving, their WIDTHS were.

       The right number costs two dot products. v is a field on the map and
       the fragment derivatives dFdx(s), dFdy(s) are the columns of J = ds/dp
       (map units per device pixel), so dv/dp = J^T grad_map(v), and its
       length is the value the level set changes by per device pixel, along
       the direction that matters. Where J is a scaled rotation this is
       exactly mag * sqrt(det J) = mag / pxPerU, so the FLAT SHEET IS
       UNTOUCHED to the last bit (measured: tilt 0 diff 0 px). */
    float grad = max(${GRAD_ISO ? 'mag/max(pxPerU,1e-7)' : 'length(vec2(dot(J[0], g2), dot(J[1], g2)))'}, 1e-7);
    if (bed > 0.0) {
      /* the last band's edge is antialiased the way the filled path it
         stands for is; the ones under it are covered and cost nothing */
      g = mix(g, tintCol(0), 0.88);
      /* R26: the walk reaches the ladder's top rung now. It stopped at
         44 — 151 commits — which was under the record's own peak, so
         every day over 125 came out the same brown whatever it held. */
      for (int li=2; li<=${F.TINT_NLEV} + uZero; li+=2) {
        float lv = float(li)*uStep;
        if (lv <= uCoast*1.02) continue;
        float w = clamp((v - lv)/max(grad,1e-7) + 0.5, 0.0, 1.0);
        if (w <= 0.0) break;
        g = mix(g, tintCol(tintBand(lv*lv)), 0.88*w);
      }
    } else {
      g = mix(g, SEA, 0.78);
      /* THE FORESHORE IS THE SHALLOWS, NOT THE DEEP. It is
         contour(coastField, COAST*0.965) — a region filled ABOVE that
         level, i.e. the strip between it and the coast — and the probe's
         look.html had the test the other way round, which flooded the
         whole sea with it. Visible as a broad muddy band offshore through
         the whole of 2024.
         EVERY EDGE OF EVERY FILL IS ONE DEVICE PIXEL WIDE: these were hard
         steps in E1, and a filled region in an SVG has an antialiased edge
         where a comparison has none. */
      g = mix(g, FORE, 0.7 *clamp((v - uCoast*0.965)/max(grad,1e-7) + 0.5, 0.0, 1.0));
      g = mix(g, SEA1, 0.72*clamp((-0.47979658 - bed)/max(grad,1e-7) + 0.5, 0.0, 1.0));
      g = mix(g, SEA2, 0.74*clamp((-1.17666850 - bed)/max(grad,1e-7) + 0.5, 0.0, 1.0));
    }
  }
  o = vec4(g, 1.0);
}`) + K(LINE, `
void main(){
  if (uZero > 0) { o = vec4(vMap, vSheet) + vec4(vBed, vDep, vSeen.xy) + vec4(vSeen.z); return; }   /* MF1 */
  vec2 s = vMap;
  /* ==== R35: how far outside the neat line this fragment stands ======== */
  beyOf(s);
  /* AND WHAT IS SURVEYED OUT THERE IS WHAT THE LINE IT LEFT WAS. The
     margin is the record's own field continued, so its surveyedness is
     the record's at the boundary point nearest it: north and south of the
     surveyed weeks it is ground, north and south of the unsurveyed east it
     is Terra Incognita's paper, and the front's own five-day fade carries
     across the line with it. Inside the line the clamp is the identity and
     this is surveyedAt(s) to the bit. */
  vec2 sCl = clamp(s, uPlateM.xy, uPlateM.xy + uPlateM.zw);
  /* ==== D2 (3): AND A DAY THE PARTY HAS PACED IS SURVEYED GROUND. The
     pad already RAISES it (field.js paceVec); this is the same pad read
     as surveyedness, so the cell takes the record's own colour ramp at
     its paced height instead of Terra Incognita's grey, with the record's
     own contours and hachure on it. It is read at the CLAMPED point for
     the same reason surveyedAt is: north and south of the record's rows
     the sheet is the boundary row continued, so the edge that runs off
     into the distance takes the surveyed colour too instead of going off
     grey beside coloured ground (Tommy: "it should also change the
     edge"). ==== */
  float surv = max(surveyedAt(sCl), paceMask(sCl));
  /* LP4: the wash mat left, which is this chain's own starting colour and
     the snapshot R35's give-out mixes back to */
  vec3 g = prevCol();
  if (surv > 0.001) {
    /* THE VALUE IS SAMPLED HERE, NOT INTERPOLATED FROM THE VERTEX. vBed is
       the grid's own vertex height, linear across each triangle, so every
       level crossing was a polyline on the GRID's facets — a staircase of
       one facet (about 6 device px) wherever a tint edge ran near an axis,
       which is what x4 and x8 showed against 8102's smooth edge. Sampling
       the field per fragment puts the level set back on the FIELD's own
       bilinear lattice, which is exactly the surface 8102's marching
       squares walks (survey-map.js contour()), and costs one fetch. */
    /* ==== R46: THE OPEN SEA'S FLOOR, WEST OF JANUARY 2022. The array
       cannot hold it (the lattice stops 130 map units west of the line and
       the voyage's 1986 is 74,000 further), so it is evaluated here — the
       value AND its two map derivatives, because the depth tints, the
       coast and the eight waterlines all measure themselves through the
       field's own gradient and a value that moves under a gradient that
       does not is a band edge of the wrong width. Zero east of the line
       and zero without ?west=bed, to the bit. ==== */
    /* X14 (g): and the voyage's ground with it, value AND gradient */
    /* D1: and the ground the party has levelled, value AND gradient —
       the geometry rises through terraOf either way, and the tints, the
       contours and the waterlines are all measured through the field's
       own gradient, so a pad the fragment did not know about would raise
       the ground and leave its contours lying where they were. */
    vec3 sbv = seabedVec(s) + voyVec(s) + paceVec(s);
    /* JG2: the gradient first, because the value is read where the eye
       meets the ground (seenField) and that step is along it */
    vec2 gF = vec2(
      (texture(uField, uvOf(s+vec2(uLat0.x,0.))).r - texture(uField, uvOf(s-vec2(uLat0.x,0.))).r)/(2.0*uLat0.x),
      (texture(uField, uvOf(s+vec2(0.,uLat0.y))).r - texture(uField, uvOf(s-vec2(0.,uLat0.y))).r)/(2.0*uLat0.y));
    float bed = riseOf(seenField(s, fieldAt(s), gF) + sbv.x);   /* E7: the smoothed reconstruction; R4: through the rise */
    float v = bed + uCoast;
    /* THE LOCAL MAP -> DEVICE PIXEL JACOBIAN, once: the ink, the level set
       and the hachure all measure themselves in device pixels through it,
       and it is exact under any tilt, turn or perspective because vMap is
       linear across a triangle. */
    mat2 J = mat2(dFdx(s), dFdy(s));
    float det = J[0].x*J[1].y - J[0].y*J[1].x;
    mat2 Sx = mat2(J[1].y, -J[0].y, -J[1].x, J[0].x)*(1.0/(abs(det) > 1e-12 ? det : 1e-12));
    float pxPerU = sqrt(abs(1.0/(abs(det) > 1e-12 ? det : 1e-12)));
    /* THE GRADIENT IS THE FIELD'S OWN CENTRAL DIFFERENCE, NOT dFdx OF THE
       SAMPLE. A derivative is computed once per 2x2 quad from one pair of
       pixels, so where a level crossing ran near a texel boundary the whole
       quad took the wrong width and the tint edges came out dotted at x4
       and x8. The central difference over the field's own lattice is the
       slope 8102's contour walker uses, it is smooth across texels, and
       the hachure needs it anyway. */
    vec2 g2 = gF;                 /* JG2: read above, before the value */
    g2 += sbv.yz;                 /* R46: the seabed's own slope, analytic */
    float mag = length(g2);
    /* THE LINE'S WIDTH IS MEASURED ALONG THE LINE'S OWN NORMAL, NOT ALONG
       AN AVERAGE (R1 b). Every edge on this plate — the contours, the tint
       band boundaries, the coast, the depth lines — is drawn by turning
       "how far is this fragment from the level" into DEVICE PIXELS, and E2
       did that with one isotropic number: mag / pxPerU, where pxPerU is the
       square root of the Jacobian's determinant, i.e. the GEOMETRIC MEAN of
       the two screen scales.

       On the flat sheet the map-to-screen map is a scaled rotation and the
       mean is exact. Under a tilt it is not even close: the map's y is
       foreshortened by cos(tilt) while the ground's own slope, magnified
       twentyfold, adds sin(tilt) x dh/dy to the same screen direction — so
       a slope facing the camera is STRETCHED (0.276 + 0.96 at tilt 74) and
       the slope behind it is CRUSHED (0.276 - 0.96, through zero where the
       surface turns edge-on). The two screen scales differ by a factor of
       five and more, and the geometric mean is wrong by its square root in
       BOTH directions at once. So a contour running across the fall of the
       land was drawn two or three times too wide and one running with it
       too thin, and — because the anisotropy is a function of the ground's
       own slope under the camera — those errors changed every frame of a
       tilt. That is the crawl: the lines were not moving, their WIDTHS were.

       The right number costs two dot products. v is a field on the map and
       the fragment derivatives dFdx(s), dFdy(s) are the columns of J = ds/dp
       (map units per device pixel), so dv/dp = J^T grad_map(v), and its
       length is the value the level set changes by per device pixel, along
       the direction that matters. Where J is a scaled rotation this is
       exactly mag * sqrt(det J) = mag / pxPerU, so the FLAT SHEET IS
       UNTOUCHED to the last bit (measured: tilt 0 diff 0 px). */
    float grad = max(${GRAD_ISO ? 'mag/max(pxPerU,1e-7)' : 'length(vec2(dot(J[0], g2), dot(J[1], g2)))'}, 1e-7);
    /* R35: THE WASH, BEFORE ANY INK IS CUT ON IT. Outside the line the
       drawn lines give out with the true distance (bLines) and the tone
       under them does not — out there the survey is one edge read through
       a window as wide as the distance, and a ruled contour is a
       measurement while a tint is a tone. Inside, bLines is exactly 1 and
       the snapshot is never used. */
    vec3 wash = g;
    /* THE HACHURE. The central difference in SHEET units, as the sheet's
       own generator takes it (survey-map.js:2521) — not the screen's,
       which is not defined once the ground is turned. */
    if (v > uCoast*1.02 && uHachC.w > 0.5) {
      if (mag > 0.0022) {
        /* the flattest 26 % of the home year's ground carries no stroke at
           all (:2536). The cut is softened over a fifth of itself so the
           edge of the hachured country is not a contour of its own. */
        float onset = smoothstep(uHachC.x*0.82, uHachC.x*1.18, mag);
        if (onset > 0.004) {
          float lenU = mag < uHachC.y ? 7.0 : (mag < uHachC.z ? 5.4 : 4.0);
          float wPx = (mag < uHachC.y ? 0.42 : (mag < uHachC.z ? 0.62 : 0.85))*uDpr;
          float op = (mag < uHachC.y ? 0.20 : (mag < uHachC.z ? 0.30 : 0.42))*onset;
          /* the lattice is in the GROUND and the ink is in DEVICE
             PIXELS, both through the one Jacobian above, so this holds
             under any tilt, turn or perspective */
          vec2 dirS = normalize(Sx*(-g2/mag) + 1e-9);
          float lenPx = lenU*uInk;
          /* the octave, PER PIXEL (so the far ground under a tilt is
             combed as finely as the near, not four times as finely) */
          float f = log2(max(pxPerU, 1e-6)/uInk);
          float L = floor(f), t = f - L;
          float cx = 2.0*uLat0.x*exp2(-L), cy = 2.0*uLat0.y*exp2(-L);
          /* the ink per unit area is held across the octave: the coarse
             generation is 4x sparser than the fine one, so the two
             weights are normalised by what they lay down together */
          float N = (1.0-t)*exp2(-2.0*t) + t*exp2(2.0-2.0*t);
          float amt = max(hachAt(s, Sx, dirS, cx, cy, lenPx, wPx, (1.0-t)/N, L),
                          hachAt(s, Sx, dirS, cx*0.5, cy*0.5, lenPx, wPx, t/N, L+1.0));
          g = mix(g, SEPIA, amt*op);
        }
      }
    }
    /* the band boundaries: depth contours, in the water's own ink
       (.sv-depth-contour, .85 / .5) */
    if (bed < 0.0) {
      float dd = min(abs(bed + 0.47979658), abs(bed + 1.17666850))/max(grad,1e-7);
      g = mix(g, SEALN, inkOf(0.85*uDpr, dd)*0.5);
      /* ==== E7: and the water lining, as Blaeu and the Admiralty cut it = */
      g = waterLining(g, v, grad, uDpr, uLev.x);
      /* ==== R46: AND THE SWELL, WEST OF JANUARY 2022 (?west=swell). The
         eight waterlines above are lines of equal depth and are the
         record's; the swell is a stated CONVENTION over the sea nobody
         surveyed, and it is drawn here, in the same ink, through the same
         Jacobian, so that it is part of the DRAWING and not a film over
         it — at plan as much as at tilt, which ink on the water plane
         could never be (the plane is not drawn at plan at all). uSwell.x
         is 0 without the switch and seaW is exactly 0 at and east of the
         surveyed start, so the sheet is the sheet. ==== */
      g = mix(g, SEALN, swellInk(s, J, uDpr));
      /* ==== /E7 ======================================================== */
    }
    /* THE LEVEL SET IS CONTINUOUS. The sheet re-thresholds in three steps
       - index contours alone at far, every fourth added at mid, all of
       them at near (survey-map.js:2598, .lod-mid/.lod-near) — and every
       boundary crossed makes a third of the lines on the plate appear at
       once. Here the same three generations FADE, each over one whole zoom
       octave centred on the band boundary the sheet uses (z = 1.55 and
       3.40 of the fit, survey-map.js:6142), so no contour ever pops and
       the fit is still the fit: at z = 1 uLev is (0, 0) and what is drawn
       is exactly E1's, the index line and the one between it. */
    /* AND A GENERATION WHOSE LINES FALL CLOSER TOGETHER THAN THEIR OWN
       STROKE IS NOT A GENERATION OF LINES (R1 b). Under a grazing tilt the
       far ground is minified to the point where a whole contour interval
       lands inside one fragment: the sampled height jumps an interval
       between neighbouring pixels, every one of them is "on" a line, and
       what is drawn is a moire that re-deals itself every frame. The
       engraver stops cutting at that distance; the hachure's own octave
       already does this for the strokes. The fade is per generation and on
       the only number that can tell — the screen separation of ITS OWN
       lines, period / grad, in device pixels — and the band is set BELOW
       the stroke widths it carries (0.55 and 1.05 px), so it can only take
       lines that have already merged. MEASURED: at tilt 0 the tightest
       separation anywhere on the plate (the 2026 front's crowded north
       edge, and the December fault's drag) is above 1.8 px, so the flat
       sheet is untouched — 0 pixels over 24/255 against 8119, against the
       14 a 1.5/3.2 band cost. ?sep=0 pins the old behaviour for the pair. */
    float P = 8.0*uStep;
    float keep = uNGap > 0 ? 1.0 - gapAt(vMap) : 1.0;
    float sepP = smst(${SEP[0].toFixed(3)}, ${SEP[1].toFixed(3)}, P/grad);
    g = mix(g, CINT, cover(v, grad, P, 4.0*uStep, 0.55*uDpr, uCoast*1.02)*0.45*keep*sepP);
    if (uLev.x > 0.002)
      g = mix(g, CINT, cover(v, grad, 4.0*uStep, 2.0*uStep, 0.55*uDpr, uCoast*1.02)*0.45*uLev.x*keep
                       *smst(${SEP[0].toFixed(3)}, ${SEP[1].toFixed(3)}, 4.0*uStep/grad));
    if (uLev.y > 0.002)
      g = mix(g, CINT, cover(v, grad, 2.0*uStep, uStep, 0.55*uDpr, uCoast*1.02)*0.45*uLev.y*keep
                       *smst(${SEP[0].toFixed(3)}, ${SEP[1].toFixed(3)}, 2.0*uStep/grad));
    g = mix(g, CIDX, cover(v, grad, P, 0.0, 1.05*uDpr, uCoast*1.02)*0.82*keep*sepP);
    /* the coast, on its soft under-stroke (.sv-coast-under 2.6 @ .3,
       then .sv-coast 1.6) */
    float dC = abs(v-uCoast)/max(grad,1e-7);
    g = mix(g, CUNDER, inkOf(2.6*uDpr, dC)*0.3);
    g = mix(g, CCOAST, inkOf(1.6*uDpr, dC));
    /* ==== E6: THE DAY GRID, AT THE NEAR BAND ==========================
       The sheet rules every day's cell on the ground close in and nowhere
       else (.sv-daygrid lod-near, survey-map.js:3344; .sv-daygrid-line
       is #7d6d59, .9 wide, .55 opaque, dashed 6 4). The LOD class made a
       third of the plate's ink appear at one zoom step; here it FADES IN
       over the same octave the near band opens on, on uLev.y — E2's rule,
       and the grid is the last thing on the plate that still stepped. */
    if (uDayG.x > 0.0 && uDayGA > 0.002) {
      vec2 e0 = Sx*vec2(1.0, 0.0), e1 = Sx*vec2(0.0, 1.0);
      vec2 dv = Sx*(s - vec2(uDayG.y, uDayG.w));
      float gx = (s.x - uDayG.y)/uDayG.x;
      float gy = (s.y - uDayG.w)/uDayG.z;
      float dxp = abs(gx - floor(gx + 0.5))*uDayG.x*length(e0);
      float dyp = abs(gy - floor(gy + 0.5))*uDayG.z*length(e1);
      /* the dash is measured ALONG each rule, in device pixels, as a
         non-scaling-stroke dasharray is */
      float py0 = fract(dv.y/10.0)*10.0, px0 = fract(dv.x/10.0)*10.0;
      float dsY = clamp(min(py0, 6.0 - py0) + 0.5, 0.0, 1.0);
      float dsX = clamp(min(px0, 6.0 - px0) + 0.5, 0.0, 1.0);
      /* ==== R9 (a): THE GRID IS THE FAINTEST THING ON THE SHEET.
         It was a 0.9 px rule mixed 0.55 of the way to #7d6d59 — against
         the paper that is a THIRTY PER CENT darkening, heavier than an
         index contour, and at zoom it turned the plate into graph paper
         and crossed every hill as a ruled diagonal. It exists so the
         hand can tell one day's cell from the next, not to be read.
         So: a 0.55 px hairline, and instead of its own ink it DARKENS
         WHATEVER IS UNDER IT by four per cent, which can never compete
         with a line that has a colour of its own and which keeps the
         tint stack's own hue. uDayGA carries the fade (below). ==== */
      float amt = max(inkOf(0.55*uDpr, dxp)*dsY, inkOf(0.55*uDpr, dyp)*dsX);
      g *= 1.0 - 0.04*amt*uDayGA;
    }
    /* ==== /E6 ========================================================= */
    /* ==== R36: THE MARKER HORIZONS, ON THE GROUND.
       A model's release is a dated bed, and on the map a dated bed is a
       MERIDIAN: a fine pecked hairline down the column of its day, no
       heavier than a contour and in the contour's own ink at sixty per
       cent — a graticule line, not a feature of the country. It is drawn
       HERE, with the ground and before the shading, so it lies ON the
       ground at any tilt and it runs exactly as far as the field does:
       across the plate inside the neat lines, and on through the margin
       the record's own field is continued into (R35), which is the only
       place Opus 4.5's column — five columns west of the neat line — can
       be seen at all. THE PECK IS MEASURED IN DEVICE PIXELS, along the
       line, the way the day grid's is and the way a non-scaling dasharray
       would be — because the STROKE is (0.55 px, the contour's own, held
       at a readable weight however close the eye comes). A peck that
       scaled with the paper while its stroke did not came out 50 css px
       long at zoom 400 and read as a solid rule with a gap in it
       (room-rev6/R36-after-fable5-z400.png); in device pixels the line
       reads the same at the fit, at zoom 400 and at any tilt. ==== */
    if (uHorzN > 0.5) {
      float pxU = length(Sx*vec2(1.0, 0.0));     /* device px per map unit across */
      float per = 16.0*uDpr, ph = fract((Sx*s).y/per)*per;
      float peck = clamp(min(ph, 10.0*uDpr - ph) + 0.5, 0.0, 1.0);
      for (int hi = 0; hi < MAXH + uZero; hi++) {
        if (float(hi) >= uHorzN) break;
        g = mix(g, CINT, inkOf(0.55*uDpr, abs(s.x - uHorzX[hi])*pxU)*peck*0.60);
      }
    }
    /* R35: and the ink gives out with the distance, the wash does not */
    if (bOut > 0.0) g = mix(wash, g, bLines);
    /* the hill shading comes in with the tilt (relief.js shadeOf) */
    if (uShade > 0.001 && bed > 0.0) {
      vec3 n = normalize(vec3(-g2.x*uLat0.x*${EXAG}.0, -g2.y*uLat0.y*${EXAG}.0, uLat0.x));
      float l = clamp(dot(n, normalize(uSun)), 0.0, 1.0);
      g *= mix(1.0, 0.55 + 0.75*l, uShade);
    }
  }
  o = vec4(g, 1.0);
}`) + K(FEAT, `
void main(){
  if (uZero > 0) { o = vec4(vMap, vSheet) + vec4(vBed, vDep, vSeen.xy) + vec4(vSeen.z); return; }   /* MF1 */
  vec2 s = vMap;
  /* ==== R35: how far outside the neat line this fragment stands ======== */
  beyOf(s);
  /* AND WHAT IS SURVEYED OUT THERE IS WHAT THE LINE IT LEFT WAS. The
     margin is the record's own field continued, so its surveyedness is
     the record's at the boundary point nearest it: north and south of the
     surveyed weeks it is ground, north and south of the unsurveyed east it
     is Terra Incognita's paper, and the front's own five-day fade carries
     across the line with it. Inside the line the clamp is the identity and
     this is surveyedAt(s) to the bit. */
  vec2 sCl = clamp(s, uPlateM.xy, uPlateM.xy + uPlateM.zw);
  /* ==== D2 (3): AND A DAY THE PARTY HAS PACED IS SURVEYED GROUND. The
     pad already RAISES it (field.js paceVec); this is the same pad read
     as surveyedness, so the cell takes the record's own colour ramp at
     its paced height instead of Terra Incognita's grey, with the record's
     own contours and hachure on it. It is read at the CLAMPED point for
     the same reason surveyedAt is: north and south of the record's rows
     the sheet is the boundary row continued, so the edge that runs off
     into the distance takes the surveyed colour too instead of going off
     grey beside coloured ground (Tommy: "it should also change the
     edge"). ==== */
  float surv = max(surveyedAt(sCl), paceMask(sCl));
  /* ==== R3 (a): THE PAPER EAST OF THE FRONT IS NOT BLANK. The winds are
     the GROUND the survey has not reached, so they are laid first and the
     surveyed ground is mixed OVER them by surv — which is what puts the
     front's own five-day fade over the winds instead of beside them. ==== */
  vec3 col = rhumbs(PAPER, s);
  /* ==== R40 (b): AND THE DOWNS TAKE THE LIGHT, FAINTLY. Terra Incognita
     is UNSURVEYED, so it gets no contour, no hachure and no waterline —
     the survey has not been there and a ruled line would be a lie. But
     the country out there has a shape now, and a shape that takes no
     light at all reads as flat paper with the ground silhouetted behind
     it. So the ground program's OWN hill-shading term falls on it, at
     ${DSH} of the record's strength, on the downs' own analytic slope.

     It comes in through uShade, which is the record's own tilt gate
     (shadeOf: 0 below a degree of tilt, 1 at fifteen). At PLAN uShade is
     0 and uRelief is 0 — the sheet is flat, there is nothing to shade,
     and the fit cannot move by so much as a pixel east of the front. ==== */
  if (uShade > 0.001) {
    float tw = terraW(s.x);
    if (tw > 0.002) {
      vec3 dn = downsAt(s);
      vec3 nT = normalize(vec3(-dn.y*uLat0.x*${EXAG}.0, -dn.z*uLat0.y*${EXAG}.0, uLat0.x));
      float lT = clamp(dot(nT, normalize(uSun)), 0.0, 1.0);
      col *= mix(1.0, 0.55 + 0.75*lT, uShade*${DSH}*tw);
    }
  }
  col = wellInk(col, s);    /* D2 (1): the lattice, the throat, the spire */
  /* LP4: and the surveyed colour the two passes before this one drew */
  if (surv > 0.001) col = mix(col, prevCol(), surv);
  {                                   /* FE1: the inked edge, a device pixel and a bit, pressed harder in places */
    float feD = sCl.x - feEdgeX(sCl.y);
    float fePx = abs(feD)/max(fwidth(feD), 1e-4);
    float feOn = (uFrontK.x > 0.5 && uFrontK.x < 1.5 && sCl.y >= uFrontC.x && sCl.y <= uFrontC.y && sCl.x >= uFront.x) ? 1.0 : 0.0;
    float fePress = 0.55 + 0.35*feNoise(vec2(sCl.y*0.09, 3.7));
    col = mix(col, CCOAST, (1.0 - smoothstep(0.55, 1.35, fePx))*fePress*feOn*0.8);
  }
  /* ==== R10 (b): AND THE BLANK PAPER INSIDE THE NEAT LINE TAKES THE
     DISTANCE TOO. The ruling is that nothing SURVEYED is ever hidden —
     Terra Incognita is not surveyed, it is the paper the survey has not
     reached, and it was the one thing in the picture that stood at full
     contrast however far back it lay. Panned east of the front at tilt 55
     that put a hard pale RECTANGLE, the record's own rect, in the middle
     of a hazed conjecture (room-rev2/s-east-t55.png, x 840 y 300-515), with
     the rhumbs stopping dead on its edge. It now recedes by exactly the
     terms the conjecture uses — the contrast eases first (dk x .42), then
     the wash (uHazeCol x sk^2) — weighted by how much of the fragment is
     paper, so the front's own five-day fade carries the change with it and
     no surveyed ground is touched. uDist.z is min(tilt/45,1) x .95, which
     is 0 at plan: the flat sheet cannot move. ==== */
  /* ==== R35: AND THE RECORD'S MATERIAL CROSSFADES INTO THE
     CONJECTURE'S OWN PAPER, over the two runs R16 gave the wash, on the
     ACROSS overshoot — so at the line the beyond IS the record, a
     plate-height and a sixth north or south it is paper whichever it left,
     and due west and due east it never finishes (R31). ==== */
  if (bOut > 0.0) col = mix(conjPaper(s, uPxPerSheet), col, bMat);
  /* the distance treatment is the conjecture's and the blank paper's, and
     it arrives ACROSS THE LINE rather than at it: inside, bMat is 1 and
     this is R10 (b)'s own 1 - surv to the bit. */
  float pap = max(1.0 - surv, 1.0 - bMat);
  if (uDist.z > 0.001 && pap > 0.002) {
    float pdk = hazeOf(vDep);
    col = mix(col, vec3(dot(col, vec3(0.28, 0.60, 0.12))), pdk*0.42*pap);
    float psk = skyOf(vDep);
    col = mix(col, uHazeCol, uDist.z*psk*psk*pap);
  }
  /* ==== R4: THE ALTERNATIVE REVEALS. The rise needs nothing here - the
     ground itself comes up through uFlat and the levels sweep with it.
     The ink walks a soft edge west to east across the view and leaves
     bare paper ahead of it; the fade is the whole window washing in.
     Both are ?reveal= switches for Tommy's eye, and both cost one mix. */
  if (uReveal.x > 0.5) {
    float rv = uReveal.x < 1.5
      ? 1.0 - smoothstep(uReveal.z - uReveal.w, uReveal.z, vMap.x)
      : uReveal.y;
    col = mix(PAPER, col, rv);
  }
  /* ==== R35: AND THE PAPER ENDS WHERE THE WASH HAS TAKEN IT, and nowhere
     else — it arrives at the colour already painted behind it, so there is
     no edge to see (relief.js :5124). Inside the line a is exactly 1 and
     nothing surveyed is ever hidden. ==== */
  /* (the ray and the limb that were inked here are in the air since PH1) */
  /* D2 (3): the paced day's own boundary, last and over everything, so
     the reader can see where the day ends */
  col = mix(col, PENCIL, paceRule(s)*0.45);
  o = vec4(col, 1.0);
}`) + K(POST, `
void main(){
  if (uZero > 0) { o = vec4(vMap, vSheet) + vec4(vBed, vDep, vSeen.xy) + vec4(vSeen.z); return; }   /* MF1 */
  vec2 s = vMap;
  /* ==== R35: how far outside the neat line this fragment stands ======== */
  beyOf(s);
  vec3 col = prevCol();
  float a = 1.0;
  if (bOut > 0.0) {
    float REACH = ${BEY.REACH.toFixed(2)}*uPlateM.w;
    float rim = clamp(1.0 - (bOut - ${(1 - BEY.FADE).toFixed(4)}*REACH)/max(1.0, ${BEY.FADE.toFixed(4)}*REACH), 0.0, 1.0);
    float gone = uDist.z * smst(0.86, 1.0, skyOf(vDep));
    a = max(rim, 1.0 - gone);
    if (a < 0.004) discard;
  }
  /* R48: the lantern, last, exactly as the sheet's own fragment takes it */
  /* FD2 (a): ink on paper takes the paper's light. On the ONE frame that
     strikes R58's print raster paperInk is the identity and the lantern is
     left off with it — the sheet applies both when it samples the ink. */
  vec3 gcol = paperInk(film(col, vSheet), vSheet);
  o = vec4(lampTemp(gcol, vSheet), a);   /* LB1: the lantern is in the print */
${SURF?'  o = bOut > 0.0 ? vec4(0.85,0.0,0.85,1.0) : vec4(0.0,0.8,0.0,1.0);':''}
}`);
}

/* the desk, and the sheet with its furniture */
var FLAT_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a;
out vec2 vUv; out vec2 vSheet;
uniform vec4 uRect;    // sheet units
uniform vec3 uSheet; uniform mat4 uVP; uniform float uZ;
/* R17 (c): a quad that HANGS. uDrop is nought in every draw but the desk's
   own front edge, which is this same quad standing on end — the only way
   to get a vertical face out of the flat mesh without a second one. */
uniform float uDrop;
void main(){
  vec2 p = uRect.xy + a*uRect.zw;
  vUv = a; vSheet = p;
  gl_Position = uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), uZ - uDrop*a.y, 1.0);
}`;

var DESK_FS = `#version 300 es
precision highp float;
in vec2 vUv; in vec2 vSheet;
out vec4 o;
uniform vec3 uSun;
float h21(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
void main(){
  vec2 q = vUv;
  vec3 wood = mix(vec3(0.114,0.078,0.051), vec3(0.278,0.200,0.122),
                  0.5 + 0.5*sin(q.y*180.0 + sin(q.x*7.0)*3.0));
  wood = mix(vec3(0.192,0.141,0.102), wood, 0.55);
  float wax = exp(-6.0*length((q - vec2(0.5 - uSun.x*0.18, 0.42 - uSun.y*0.18))*vec2(1.0,1.6)));
  wood += vec3(0.10,0.075,0.04)*wax;
  wood *= 0.93 + 0.07*h21(floor(gl_FragCoord.xy));
  o = vec4(wood, 1.0);
}`;

var SHEET_FS = `#version 300 es
precision highp float; precision highp sampler2D;
in vec2 vUv; in vec2 vSheet;
out vec4 o;
uniform sampler2D uFurn;
uniform float uHasFurn;
uniform vec4 uLampS;       // R42 (a): the lantern in sheet units: x, y, and the pool's two radii
uniform float uLampN;      // and the night, which is nought by day
/* R42 (a): FRAME 1 IS ALSO AT NIGHT. This is the sheet only until
   objects.js's paper is compiled, but that is the FIRST PICTURE, and a
   first picture that is cool paper giving way to warm paper is a pop. The
   same five lines as objects.js's SHEET_FS and day.js's card. */
vec3 lampTemp(vec3 c, vec2 wp){
  if (uLampN <= 0.0) return c;
  vec2 d = (wp - uLampS.xy)/uLampS.zw;
  float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
  vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
  /* LK1: and the lamp is the light. The paper outside the pool falls into
     shadow at night (to half), and into a dusk dimness before it. */
  float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
  return c*mix(vec3(1.0), t*lit, uLampN);
}
void main(){
  vec3 col = vec3(0.94509804,0.90588235,0.81960784);
  if (uHasFurn > 0.5) {
    vec4 f = texture(uFurn, vUv);
    col = mix(col, f.rgb/max(f.a,0.001), f.a);
  }
  o = vec4(lampTemp(col, vSheet), 1.0);
}`;

/* =================================================================
   GL plumbing
   ================================================================= */
/* ==== R4: THE COMPILE IS ASKED FOR EARLY AND COLLECTED LATE ==========

   A shader compile is main-thread work in the driver, and every query
   about a shader or a program — COMPILE_STATUS, LINK_STATUS, the uniform
   list — STALLS until that work is finished. So the eleven programs the
   ground needs used to cost their full compile in a straight line,
   wherever they were asked for.

   `sh` and `progLink` now only ISSUE the work. `progFinish` is what
   stalls, and it is called either by the poller (when
   KHR_parallel_shader_compile says the driver is done, so nothing waits)
   or by the first caller that actually needs the uniforms. Between the
   two, the driver compiles on its own threads while the WORKER builds
   the field: the two heaviest things in the load overlap instead of
   queueing (ROOM-REVIEW-1, R4 third pass, SHADER ORDER).

   PROGS memoises by name, which is what lets a module's programs be
   asked for twice — once by the precompile after frame 1, once by its
   own attach when the field lands — and compiled once.
   ==================================================================== */
var PROGS = {}, LINK_ONLY = false, KHR = null;

/* ==== LP1: THE LOOPS ARE NOT UNROLLED =================================

   Direct3D's HLSL compiler (which ANGLE hands every one of these shaders
   to on Windows) FULLY UNROLLS a loop whose trip count it can count. On
   Tommy's RTX 3070 Ti a cold load spent 12-21 s in compile alone: the
   ground's fragment carries a `li` loop of 53 turns inside an `hi` loop
   of MAXH, which unrolls to 424 copies of the body, and the sheet's R7
   arm — the SAME source as sheet-e4 but with `#define R7 1` — cost
   13.4 s against e4's 76 ms for three eight-turn loops around texture
   reads.

   `uZero` is an int uniform that is NEVER set, so it reads 0 on every
   program: the picture is arithmetically identical. Folded into a loop's
   bound (`i < 7 + uZero`) it makes the trip count unknowable at compile
   time, and the compiler emits ONE copy of the body with a real loop.

   WHERE IT IS DECLARED. The sources are concatenations — a per-module
   HEAD, the shared FIELD + FRONT + XFORM chunks, a per-module BODY, and
   in several places a generated `const float EXAGC` string — and a
   uniform declared twice in one source is a compile error. Rather than
   pick a chunk and hope every path through the twenty-odd builders
   includes it exactly once, the declaration is injected HERE, at the one
   place in the engine that ever calls `shaderSource`: after the
   `#version` line (which must stay first) and before anything else. One
   copy per stage, by construction, for every program the room builds.
   ==================================================================== */
/* `highp` is SPOKEN, not defaulted: an int with no precision qualifier is
   highp in a vertex shader and mediump in a fragment one, and the two
   stages then disagree about the same uniform's precision — which is a
   LINK error ("Precisions of uniform 'uZero' differ"), and it took out
   ground, sheet-r7, letter and the three marks on the first run. */
var ZERO_DECL =
  'uniform highp int uZero;  /* LP1: never set, so 0. Its only job is to\n' +
  '                             stop the D3D compiler unrolling the loops\n' +
  '                             below. */\n';
/* ==== LP2: AND `BAKED` IS INJECTED IN THE SAME BREATH ================
   Whether the chain is read out of a texture or computed is a property of
   the CONTEXT (is `EXT_color_buffer_float` there), not of any one module,
   and the shared chunks are strings built at script load — before there
   is a context to ask. So the decision is made once, at bring-up
   (`BAKE_ON`), and carried into every source at the one place the engine
   ever calls `shaderSource`, exactly as `uZero` is. The bake program's
   own source `#undef`s it on its first line, which is the only place the
   analytic chain is still compiled when the bake is on. ============== */
var BAKE_DECL = '#define BAKED 1  /* LP2: the terra/mould chain is a\n' +
                '                    texture read, not eleven inlined\n' +
                '                    copies of itself. */\n';
var BAKE_ON = false;
var COLD_NONCE = Q.get('cold') === '1' ? String(Date.now()) : '';
function zeroDecl(src) {
  var head = (BAKE_ON ? BAKE_DECL : '') + ZERO_DECL;
  /* `?cold=1` is the bench's knob: a nonce in every source makes each
     load a cache miss in the browser's program cache, so the cold
     compile can be measured on demand (LP3) */
  if (COLD_NONCE) head += '// cold ' + COLD_NONCE + '\n';
  var m = /^\s*#version[^\n]*\n/.exec(src);
  return m ? src.slice(0, m[0].length) + head + src.slice(m[0].length)
           : head + src;
}
function sh(gl, t, src, name) {
  var s = gl.createShader(t); gl.shaderSource(s, zeroDecl(src)); gl.compileShader(s);
  s.__name = name;
  return s;
}
/* R6 TRIED DEFERRING THE LINK and it is written down here so nobody tries
   it again. Asking a program anything — its status, a uniform — blocks
   until the driver has built it, so the theory was to link early, ask late
   and let ANGLE build on its own thread during the worker's field pass.
   Measured over eight loads on the d3d12 harness it was much WORSE: the
   first draw went from about 9 ms to about 257 ms, for the OLD desk as
   well as the new one (the first question asked of the GPU process after a
   boot's worth of queued uploads pays for all of it), and `ready` went from
   ~700 ms to ~1,000. The oak's extra 11 ms of compile is kept off frame 1
   by compiling it AFTER the first picture instead (RoomObjects.buildWood). */
function shCheck(gl, s) {
  if (gl.getShaderParameter(s, gl.COMPILE_STATUS)) return;
  if (gl.isContextLost()) return;   /* CL1: not a shader fault */
  console.error(s.__name + ':\n' + gl.getShaderInfoLog(s));
  (window.__roomErr = window.__roomErr || []).push(s.__name + ': ' + gl.getShaderInfoLog(s));
}
/* FW2: a program issued while this is set is one frame one draws; boot's
   `whenFrameOne` waits for exactly these before the first draw. */
var F1_ISSUE = false;
/* FW2 (c): set while `whenFrameOne` is waiting for those programs; draw()
   does nothing until it clears. terminal.js's boot turns the camera the
   moment its script lands, and `turnTo`'s own animation frame called
   draw() before frame one with the oak link-only — `O.drawDesk` read
   `pDesk.u.uVP` of a program with no `u`, the frame died and the terminal
   never showed (Tommy, ?term=1 on the preview, 2026-09-22). */
var F1_WAIT = false;
function progLink(gl, vs, fs, name) {
  var p = gl.createProgram();
  p.__sh = [sh(gl, gl.VERTEX_SHADER, vs, name + '.vs'),
            sh(gl, gl.FRAGMENT_SHADER, fs, name + '.fs')];
  gl.attachShader(p, p.__sh[0]); gl.attachShader(p, p.__sh[1]);
  gl.linkProgram(p);
  p.__name = name; p.__t0 = performance.now(); p.__f1 = F1_ISSUE;
  return p;
}
function progFinish(gl, p) {
  if (p.u) return p;
  shCheck(gl, p.__sh[0]); shCheck(gl, p.__sh[1]);
  /* PT1: the LINK_STATUS read is where the main thread waits for the
     driver's compile; a wait worth seeing is named on the console */
  var tLink = performance.now();
  var linked = gl.getProgramParameter(p, gl.LINK_STATUS);
  var stall = performance.now() - tLink;
  (S.progStall = S.progStall || {})[p.__name] = +stall.toFixed(1);
  if (stall > 40) bootLog('program ' + p.__name + ' stalled the thread', stall.toFixed(0) + ' ms waiting for the driver');
  if (!linked && !gl.isContextLost()) {   /* CL1 */
    console.error(p.__name + ' link: ' + gl.getProgramInfoLog(p));
    (window.__roomErr = window.__roomErr || []).push(p.__name + ' link: ' + gl.getProgramInfoLog(p));
  }
  p.u = {};
  var n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
  for (var i = 0; i < n; i++) {
    var u = gl.getActiveUniform(p, i), loc = gl.getUniformLocation(p, u.name);
    p.u[u.name] = loc;
    /* an ARRAY comes back as "uGap[0]" and every caller writes "uGap" */
    if (/\[0\]$/.test(u.name)) p.u[u.name.slice(0, -3)] = loc;
  }
  /* what this one program cost, from the linkProgram to the collection */
  (S.progMs = S.progMs || {})[p.__name] = +(performance.now() - p.__t0).toFixed(1);
  return p;
}
/* ==== LK3 (audit A8): THE WEATHER SHOWS ON THE DESK =====================
   Rain and fog looked exactly like a clear noon on the map: the shadows
   softened (sky.js --sun-soft/--sun-cast) and the oak darkened, and the
   paper did not care. Under a lid of cloud the light is flatter and
   cooler, and fog is a little haze in the room's own air. This is ONE
   constant-colour pass over the finished frame, blended as
       out = add + dst * tint        (blendFunc ONE, CONSTANT_COLOR)
   so it costs a single full-screen fill and no change to any of the big
   programs (their compile time grows with their size). It is SKIPPED
   outright when the sky is clear, so a clear day is the picture it was to
   the bit, and it gives way to the room's own weather lighting as the
   camera stands up. ==== */
var WXG_VS = `#version 300 es
void main(){ vec2 p = vec2(float((gl_VertexID & 1) << 2) - 1.0, float((gl_VertexID & 2) << 1) - 1.0); gl_Position = vec4(p, 0.0, 1.0); }`;
var WXG_FS = `#version 300 es
precision mediump float;
uniform vec3 uAdd;
out vec4 o;
void main(){ o = vec4(uAdd, 0.0); }`;
function weatherGrade(gl) {
  if (!window.Sky || !Sky.weather || !Sky.wash) return;
  var wx = Sky.weather(), wa = Sky.wash();
  var away = 1 - Math.max(S.cam ? (S.cam.room || 0) : 0, S.cam ? (S.cam.lap || 0) : 0);
  var dimN = 1 - 0.6 * (wa.night || 0);
  var w = Math.min(1, (wx.cloud || 0) * 0.55 + (wx.rain || 0) * 0.45) * dimN * away;
  var fg = (wx.fog ? 1 : 0) * dimN * away;
  if (w < 0.003 && fg < 0.003) return;
  var p = prog(gl, WXG_VS, WXG_FS, 'wx-grade');
  if (!p.u) return;
  /* flatter: the darks lift as the lights come down; cooler: the blue
     keeps more than the red. Fog lays the haze over the lot. */
  var tr = 1 - 0.105 * w, tg = 1 - 0.085 * w, tb = 1 - 0.055 * w;
  var ar = 0.032 * w, ag = 0.035 * w, ab = 0.040 * w;
  var hz = wa.hazeRgb || [222, 208, 184], kf = 0.16 * fg;
  ar = ar * (1 - kf) + kf * hz[0] / 255; ag = ag * (1 - kf) + kf * hz[1] / 255; ab = ab * (1 - kf) + kf * hz[2] / 255;
  tr *= 1 - kf; tg *= 1 - kf; tb *= 1 - kf;
  var bl = gl.isEnabled(gl.BLEND), dt = gl.isEnabled(gl.DEPTH_TEST),
      sc = gl.isEnabled(gl.SCISSOR_TEST), st = gl.isEnabled(gl.STENCIL_TEST);
  var bs = [gl.getParameter(gl.BLEND_SRC_RGB), gl.getParameter(gl.BLEND_DST_RGB),
            gl.getParameter(gl.BLEND_SRC_ALPHA), gl.getParameter(gl.BLEND_DST_ALPHA)];
  var dm = gl.getParameter(gl.DEPTH_WRITEMASK);
  gl.disable(gl.DEPTH_TEST); gl.disable(gl.SCISSOR_TEST); gl.disable(gl.STENCIL_TEST);
  gl.depthMask(false);
  gl.enable(gl.BLEND);
  gl.blendColor(tr, tg, tb, 1);
  gl.blendFuncSeparate(gl.ONE, gl.CONSTANT_COLOR, gl.ZERO, gl.ONE);
  gl.useProgram(p);
  gl.bindVertexArray(null);
  gl.uniform3f(p.u.uAdd, ar, ag, ab);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
  gl.blendFuncSeparate(bs[0], bs[1], bs[2], bs[3]);
  if (!bl) gl.disable(gl.BLEND);
  if (dt) gl.enable(gl.DEPTH_TEST);
  if (sc) gl.enable(gl.SCISSOR_TEST);
  if (st) gl.enable(gl.STENCIL_TEST);
  gl.depthMask(dm);
}

function prog(gl, vs, fs, name) {
  var p = PROGS[name] || (PROGS[name] = progLink(gl, vs, fs, name));
  return LINK_ONLY ? p : progFinish(gl, p);
}

/* =================================================================
   X6 (c) — THE RECORD'S GL OBJECTS, AND THE DISPOSE PASS

   `Room.load` re-runs `openWindow`, and `openWindow` re-attaches the
   three modules that are built out of the RECORD: the marks, the day and
   the lettering. Each `attach` cuts a fresh atlas, fills fresh instance
   buffers and wires fresh vertex arrays — and until this item nothing
   deleted the last set. Twenty handles in a session was twenty sets of
   everything on the GPU (X4 OPEN 3).

   WHOSE OBJECT IS IT. There is no way to ask WebGL what it is holding, so
   the engine keeps the ledger itself: the five `create*` calls are
   shadowed on the context (own properties over the prototype's, installed
   once at boot) and every object they hand back is filed under whatever
   tag is current. Outside a tagged call the tag is null and the object is
   PERMANENT — the field texture, the ramp, the grids, the desk, the books,
   the ruler, the oak — and the dispose pass never touches those, because
   nothing rebuilds them per record.

   The three tags are set by `owned()` around the three build functions,
   and that is enough: every per-record object of all three modules is
   created inside its own `attach` (marks' rose is cut at the end of
   attach, not lazily at draw — checked) so nothing
   escapes the window. `disposeGL(tag)` deletes the previous load's set
   immediately before the new one is built, which is the only safe moment:
   the module object that owns them is about to be replaced.

   PROGRAMS ARE NOT IN THIS. `prog()` memoises by name in PROGS for the
   life of the page and every module asks for its programs by name, so a
   second load compiles and creates nothing — there is no such thing as a
   per-record program. (Measured: `Room.glStats().progs` is flat across
   twenty loads.)
   ================================================================= */
var GLTAG = null;
var GLREG = { made: 0, deleted: 0, perm: 0, bag: {},
              kinds: { buffer: 0, texture: 0, vao: 0, fbo: 0, rbo: 0 } };
var GL_MAKE = { createBuffer: ['deleteBuffer', 'buffer'],
                createTexture: ['deleteTexture', 'texture'],
                createVertexArray: ['deleteVertexArray', 'vao'],
                createFramebuffer: ['deleteFramebuffer', 'fbo'],
                createRenderbuffer: ['deleteRenderbuffer', 'rbo'] };
function trackGL(gl) {
  Object.keys(GL_MAKE).forEach(function (c) {
    var base = gl[c], del = GL_MAKE[c][0], kind = GL_MAKE[c][1];
    if (typeof base !== 'function') return;
    gl[c] = function () {
      var o = base.call(gl);
      GLREG.made++; GLREG.kinds[kind]++;
      if (GLTAG) (GLREG.bag[GLTAG] || (GLREG.bag[GLTAG] = [])).push([o, del, kind]);
      else GLREG.perm++;
      return o;
    };
  });
}
/* everything created while `fn` runs belongs to `tag` */
function owned(tag, fn) {
  var was = GLTAG; GLTAG = tag;
  try { return fn(); } finally { GLTAG = was; }
}
/* and the pass itself: the previous load's set, deleted */
function disposeGL(tag) {
  var b = GLREG.bag[tag];
  if (!b || !b.length) return 0;
  var gl = S.gl, n = b.length, i;
  /* a deleted vertex array that is still BOUND unbinds itself and takes
     the element-array binding with it, which is exactly the state draw()
     already refuses to assume (see the note at the end of draw) — but the
     next attach binds its own before it fills anything, so this is belt
     and braces rather than a fix */
  gl.bindVertexArray(null);
  for (i = 0; i < n; i++) gl[b[i][1]](b[i][0]);
  GLREG.bag[tag] = [];
  GLREG.deleted += n;
  return n;
}
/* the debug hook the twenty-load run reads (X6 acceptance) */
Room.glStats = function () {
  var live = {}, tot = 0, k;
  for (k in GLREG.bag) { live[k] = GLREG.bag[k].length; tot += live[k]; }
  var np = 0; for (k in PROGS) np++;
  return { made: GLREG.made, deleted: GLREG.deleted, perm: GLREG.perm,
           live: live, tagged: tot, alive: GLREG.made - GLREG.deleted,
           kinds: GLREG.kinds, progs: np, loads: S.loads || 0 };
};
/* the programs the driver has finished with, collected off the critical
   path. Without the extension there is nothing to poll and nothing is
   collected here: the first caller pays, as it always did. */
function pollLinks() {
  var gl = S.gl, pend = [], k;
  for (k in PROGS) if (!PROGS[k].u) pend.push(PROGS[k]);
  if (!pend.length) { if (!S.boot.compiled) bootLog('every program linked'); S.boot.compiled = S.boot.compiled || +(performance.now() - S.tBoot).toFixed(1); return; }
  /* WL1: the OLDEST first, and stop at the first one not done — the
     driver links in the order it was asked and every query here is a
     round trip to the process doing the linking (with the room's twenty
     links pending this polled all twenty every 12 ms). */
  if (KHR) for (var i = 0; i < pend.length; i++) {
    if (!gl.getProgramParameter(pend[i], KHR.COMPLETION_STATUS_KHR)) break;
    progFinish(gl, pend[i]);
    /* LP4: a ground pass that has just landed has a frame to draw */
    if (pend[i].__part || pend[i].__redraw) invalidate();
  } else {
    /* NO EXTENSION, NO WAITING FOR ONE. Found by builder P2 (2026-09-20)
       under swiftshader, which has no KHR_parallel_shader_compile: this
       loop collected nothing, `sheet-r7` was never finished, `pLate.u`
       stayed unset and the curled corner never drew — on any GPU or
       browser without the extension, silently. Without it the only
       question is LINK_STATUS, which blocks until the link is done, so
       one program is collected a turn, oldest first, and the stall is
       spread over the turns instead of taken in one. */
    progFinish(gl, pend[0]);
    if (pend[0].__part || pend[0].__redraw) invalidate();
  }
  setTimeout(pollLinks, 12);
}

/* A room program may be ISSUED by the post-paint warmer without being
   collected.  place.js uses these three small hooks to issue one link per
   turn, ask KHR whether it is genuinely ready, and let an actual room entry
   promote the same memoised program to the ordinary blocking path. */
function roomProgramTools() {
  return {
    prog: prog,
    issue: function (gl, vs, fs, name) {
      var was = LINK_ONLY; LINK_ONLY = true;
      try { return prog(gl, vs, fs, name); }
      finally { LINK_ONLY = was; }
    },
    peek: function (name) { return PROGS[name] || null; },
    complete: function (p) {
      if (!p || p.u) return !!p;
      if (KHR && !S.gl.getProgramParameter(p, KHR.COMPLETION_STATUS_KHR)) return false;
      progFinish(S.gl, p);
      return true;
    },
    /* WL1: the blocking collection, for place.js's B2 fallback ONLY — the
       lane itself never calls it */
    finish: function (p) { return p ? progFinish(S.gl, p) : p; }
  };
}
function gridBuffer(gl, nx, ny) {
  var verts = new Float32Array((nx + 1) * (ny + 1) * 2), k = 0;
  for (var y = 0; y <= ny; y++) for (var x = 0; x <= nx; x++) { verts[k++] = x / nx; verts[k++] = y / ny; }
  var idx = new Uint32Array(nx * ny * 6); k = 0;
  for (var y2 = 0; y2 < ny; y2++) for (var x2 = 0; x2 < nx; x2++) {
    var a0 = y2 * (nx + 1) + x2, b0 = a0 + 1, c0 = a0 + nx + 1, d0 = c0 + 1;
    idx[k++] = a0; idx[k++] = c0; idx[k++] = b0; idx[k++] = b0; idx[k++] = c0; idx[k++] = d0;
  }
  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
  return { vb: vb, ib: ib, n: idx.length };
}

/* ---- matrices (column major) ------------------------------------- */
function mul(a, b) {
  var o = new Float32Array(16);
  for (var c = 0; c < 4; c++) for (var r = 0; r < 4; r++) {
    var s = 0; for (var k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
    o[c * 4 + r] = s;
  }
  return o;
}
function persp(fovy, asp, zn, zf) {
  var f = 1 / Math.tan(fovy / 2), o = new Float32Array(16);
  o[0] = f / asp; o[5] = f; o[10] = (zf + zn) / (zn - zf); o[11] = -1; o[14] = 2 * zf * zn / (zn - zf);
  return o;
}
function ortho(w, h, zn, zf) {
  var o = new Float32Array(16);
  o[0] = 2 / w; o[5] = 2 / h; o[10] = -2 / (zf - zn); o[14] = -(zf + zn) / (zf - zn); o[15] = 1;
  return o;
}
function lookAt(e, c, up) {
  var z = norm([e[0] - c[0], e[1] - c[1], e[2] - c[2]]);
  var x = norm(cross(up, z)), y = cross(z, x);
  return new Float32Array([
    x[0], y[0], z[0], 0, x[1], y[1], z[1], 0, x[2], y[2], z[2], 0,
    -(x[0] * e[0] + x[1] * e[1] + x[2] * e[2]),
    -(y[0] * e[0] + y[1] * e[1] + y[2] * e[2]),
    -(z[0] * e[0] + z[1] * e[1] + z[2] * e[2]), 1]);
}
function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
function norm(a) { var l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }

/* =================================================================
   LAYOUT — the sheet on the desk, exactly as survey-map.js lays it
   (deskFit :1476, layoutPortal :1495). The portal is not a DOM element
   any more; it is the plate rect of the sheet mesh.
   ================================================================= */
/* ==== RU3 (2026-10-04): UPRIGHT IN THE ROOM IS THE SIDEWAYS ROOM, CROPPED
   TALL. Tommy's recording of the folded map upright: "a complete basket
   case": the stand-up passed over the whole desk and looked down on the
   chair, the card read blurred, the desk ended in a dark band. RU1 had laid
   the room out for the upright glass, at the desk fit of a 393 px wide
   window: a third of the sideways scale, with the seated eye placed by the
   glass's 665 px height, so it stood nearly three times as high over the
   desk as it does on its side, and every pose was a different picture.
   So upright off the map the layout IS the sideways one (phone.js keeps
   the glass's size on its side, `landSize`), re-centred on the upright
   glass: the same sheet at the same scale, the same eye at every pose
   (vp() takes its distance from the sideways height, `ah`), and only the
   frame differs: vp() opens the field until the upright glass's width
   takes in RU3_SPAN of what the sideways one did across, with more desk
   above and below. `aw`/`ah` are the sideways glass. ==== */
var RU3_SPAN = 1.0, RU3_FOV_MAX = 112 * DEG;
/* MM1: the trial is on unless `?mm=0` */
var MM = !/[?&]mm=0(&|$)/.test(location.search), MM_REF = [2194, 1234];
/* MP1: the phone's world is laid at the plate across this many px (his iPhone on its side) */
var MP_REF_W = 852;
/* LY1: the glass row a point of the sheet (sheet units) is seen at, from
   the open map's own camera: the fold's framing, the room and the laptop
   are set aside for the measure (a turn or a resize can lay the glass out
   while any of them is up) */
function sideRow(x, y) {
  var L = S.layout, c = S.cam, r0 = c.room, l0 = c.lap;
  c.room = 0; c.lap = 0; S.rowOnly = true;
  var m = vp();
  c.room = r0; c.lap = l0; S.rowOnly = false;
  var wx = L.ox + x * L.k, wy = -(L.oy + y * L.k);
  var cy = m[1] * wx + m[5] * wy + m[13], cw = m[3] * wx + m[7] * wy + m[15];
  return cw > 1e-4 ? (0.5 - cy / cw * 0.5) * L.ch : null;
}
function layout() {
  var host = document.getElementById('canvas');
  var cw = host.clientWidth || 1, ch = host.clientHeight || 1;
  var cwR = cw, chR = ch;
  var UPR = PHONE && cw < ch && window.RoomPhone && RoomPhone.room && RoomPhone.room() && RoomPhone.landSize;
  if (UPR) { var lsz = RoomPhone.landSize(); cw = lsz[0]; ch = lsz[1]; }
  var sheet = S.fld.sheet;
  var m = clamp(Math.round(Math.min(cw, ch) * 0.045), 48, 120);
  var mS = Math.max(m, BOOK_SHOW + 24);
  var availH = Math.max(80, ch - m - mS);
  var k = Math.min(Math.max(80, cw - m * 2) / sheet.w, Math.max(80, ch - m - mS) / sheet.h);
  S.layout = {
    cw: cw, ch: ch, k: k,
    ox: (cw - sheet.w * k) / 2,
    oy: m + (availH - sheet.h * k) / 2
  };
  /* ==== MM1 (trial, 2026-10-04): ONE ROOM, BUILT ONCE. The world was laid
     out for the window: the sheet's scale `k` was the window's fit, and
     every object (the desk, the books, the room) is measured from it. So
     the room was a different size in every window, and the stand-in desk
     was fitted to what each window saw. Here the world is laid out ONCE, at
     MM_REF (Tommy's window), and a window changes only the camera: it moves
     back or in by `S.frameK` (vp) so the sheet is the size on the glass it
     was, and everything else is where the world puts it. The world is
     centred on the glass's middle (a translation, which changes nothing
     in it). At MM_REF frameK is 1 and every number is today's. ==== */
  S.frameK = 1;
  if (MM && !PHONE) {
    var rm = clamp(Math.round(Math.min(MM_REF[0], MM_REF[1]) * 0.045), 48, 120), rmS = Math.max(rm, BOOK_SHOW + 24);
    var kR = Math.min(Math.max(80, MM_REF[0] - rm * 2) / sheet.w, Math.max(80, MM_REF[1] - rm - rmS) / sheet.h);
    var fk = kR / k, rx0 = (cw - MM_REF[0]) / 2, ry0 = (ch - MM_REF[1]) / 2;
    /* the reference window's own layout, its middle on this glass's middle;
       `ref` is that window in the world, which is what the books are held
       inside (desk-books.js), so they lie where they lie at MM_REF */
    S.layout.k = k = kR;
    S.layout.ox = rx0 + (MM_REF[0] - sheet.w * kR) / 2;
    S.layout.oy = ry0 + rm + (Math.max(80, MM_REF[1] - rm - rmS) - sheet.h * kR) / 2;
    S.layout.ref = { x0: rx0, y0: ry0, x1: rx0 + MM_REF[0], y1: ry0 + MM_REF[1] };
    S.layout.gl = { x0: cw / 2 - cw / 2 * fk, y0: ch / 2 - ch / 2 * fk, x1: cw / 2 + cw / 2 * fk, y1: ch / 2 + ch / 2 * fk };   /* MP2 */
    S.frameK = fk;
  }
  if (PHONE && cw > ch) {
    /* PH10: SIDEWAYS THE PHONE IS THE MAP. Tommy: "why are we using our
       real estate so inefficiently?" (the plate was 9% of the screen). The
       plate is laid across the whole width — its own 3.88 to 1 is wider
       than any phone held sideways, so the width is what binds — and set
       in the middle of the height, where the paper's head shows above it
       as the edge of an object and the desk past that. The books, the
       laptop and the lamp are out of frame (desk-books.js lays the books
       off the glass). On the engine's own portal that is 56% of the
       screen at 852 x 393 and at 932 x 430. */
    var Pp = S.fld.plate;
    k = cw / Pp.w;
    var kPlate = k;                    /* MP3: the plate's own fit, before a pinch out widens it */
    var desk = S.layout;
    /* LY1 (2026-10-04): AND NO DESK ABOVE THE PAPER. Tommy: "why so much
       desk at the top (none is needed) leading to so much of the sheet being
       cut off". The plate in the middle of the height put the paper's head
       and a band of oak above it and cut the section off below; the sheet
       is taller than the glass, so it is laid with its head at the glass's
       top and the plate stays where it falls, wholly on the glass. A sheet
       shorter than the glass is centred. */
    var oyP = (ch - Pp.h * k) / 2 - Pp.y * k, shH = S.fld.sheet.h * k;
    var oyS = shH >= ch ? Math.min(0, Math.max(ch - shH, oyP)) : (ch - shH) / 2;
    if (oyS + (Pp.y + Pp.h) * k > ch) oyS = oyP;                 /* the plate is never cut */
    S.layout = { cw: cw, ch: ch, k: k, ox: -Pp.x * k, oy: oyS, side: true };
    /* the camera looks at the sheet down its own pitch (R17's edge pose),
       so the head laid at the glass's top in layout px lands lower on the
       glass: it is put there through the camera itself, twice over */
    if (oyS <= 0) {
      S.cam.aim = 0;
      for (var it = 0; it < 2; it++) {
        var hy = sideRow(S.fld.sheet.w / 2, 0), py = sideRow(S.fld.sheet.w / 2, Pp.y + Pp.h);
        if (hy == null || py == null || py - hy > ch) break;
        S.layout.oy -= hy;                               /* the plate's foot stays on the glass (above) */
      }
    }
    /* ==== EG3 (2026-10-02): AND A PINCH OUT PAST THE STOP SHOWS THE WHOLE
       SHEET. Tommy, of the eggs on a phone: the fold "should work fine,
       other than the fact that the folded corner is not currently on a
       landscape screen", and the name in the cartouche (a stranger's
       handle) is off it too. So past the widest view the plate allows, the
       pinch goes on into the PAPER: `S.wide` eases the framing from the
       plate across the glass (0) to the whole sheet laid as at a desk (1),
       the cartouche, the section and the corner on the glass; a pinch in
       comes back. The books stay off the glass (their spines are DOM). */
    /* TB2 (2026-10-06): A TABLET ON ITS SIDE IS THE DESK. Tommy: "Should
       tablets not just work the same as desktop?" Its glass is a small
       laptop's, so it starts where a phone's pinch out ends: the whole
       sheet on the oak with the books beside it, as a desk is laid. A pinch
       in still goes to the plate across the glass, and out again back. */
    if (TABLET && S.wide == null) S.wide = 1;
    var t = S.wide || 0;
    if (t > 0) {
      var e = t * t * (3 - 2 * t), kk = Math.exp(Math.log(k) + (Math.log(desk.k) - Math.log(k)) * e);
      /* the point of the sheet at the glass's middle moves straight from
         the one framing's to the other's, so the paper does not swing */
      var mx0 = (cw / 2 - S.layout.ox) / k, my0 = (ch / 2 - S.layout.oy) / k;
      var mx1 = (cw / 2 - desk.ox) / desk.k, my1 = (ch / 2 - desk.oy) / desk.k;
      var mx = mx0 + (mx1 - mx0) * e, my = my0 + (my1 - my0) * e;
      S.layout = { cw: cw, ch: ch, k: kk, ox: cw / 2 - mx * kk, oy: ch / 2 - my * kk, side: true, wide: t };
      k = kk;            /* the portal below is laid at the layout's own scale */
    }
  }
  /* ==== MP1 (2026-10-04): THE PHONE'S ROOM IS THE DESKTOP'S. Tommy, of MM1
     on the desktop, "we still need to bring it to phone". The phone's frame
     is worked out as before (the plate across the glass, the head at its
     top, the pinch out to the whole sheet), and then it is turned into a
     camera: the world is laid ONCE at the phone's own reference scale (the
     plate across MP_REF_W, his iPhone sideways, so that pixel-struck ink and
     lettering keep the size they have), the sheet scaled about the glass's
     middle and the camera moved back by the same `frameK`, which draws the
     open map exactly as it was. Everything measured in mm off the sheet (the
     desk, the room, the books) is then the desktop's room to the mm: the
     books lie inside the desktop's reference window, scaled (`ref`), and the
     real desk is always the desk (objects.js deskT). `gl` is the glass in
     the world, untilted, for what is laid off it. ==== */
  if (MM && PHONE) {
    var kP = MP_REF_W / S.fld.plate.w, fP = kP / k, tx = cw / 2, ty = ch / 2;
    S.frameK0 = kP / (kPlate || k);    /* MP3: the camera's distance at the plate's own framing */
    var rmR = clamp(Math.round(Math.min(MM_REF[0], MM_REF[1]) * 0.045), 48, 120), rmRS = Math.max(rmR, BOOK_SHOW + 24);
    var kRd = Math.min(Math.max(80, MM_REF[0] - rmR * 2) / sheet.w, Math.max(80, MM_REF[1] - rmR - rmRS) / sheet.h), sR = kP / kRd;
    var Lp = S.layout;
    Lp.ox = tx + (Lp.ox - tx) * fP; Lp.oy = ty + (Lp.oy - ty) * fP; Lp.k = k = kP;
    var rx = Lp.ox - sR * (MM_REF[0] - sheet.w * kRd) / 2;
    var ry = Lp.oy - sR * (rmR + (Math.max(80, MM_REF[1] - rmR - rmRS) - sheet.h * kRd) / 2);
    Lp.ref = { x0: rx, y0: ry, x1: rx + MM_REF[0] * sR, y1: ry + MM_REF[1] * sR };
    Lp.gl = { x0: tx - tx * fP, y0: ty - ty * fP, x1: tx + tx * fP, y1: ty + ty * fP };
    S.frameK = fP;
  }
  /* and R27's aim, which rides the desk's composition up its window to
     make room for the books' feet, would ride this one off the head */
  if (PHONE) S.cam.aim = S.layout.side ? 0 : R17_EDGE_AIM * (S.frameK || 1);
  if (UPR) {
    /* the sideways layout, its middle on the upright glass's middle */
    S.layout.ox += (cwR - cw) / 2; S.layout.oy += (chR - ch) / 2;
    [S.layout.ref, S.layout.gl].forEach(function (g) {     /* MP1: they move with the world */
      if (g) { g.x0 += (cwR - cw) / 2; g.x1 += (cwR - cw) / 2; g.y0 += (chR - ch) / 2; g.y1 += (chR - ch) / 2; }
    });
    S.layout.aw = cw; S.layout.ah = ch;
    S.layout.cw = cw = cwR; S.layout.ch = ch = chR;
  }
  S.css = { w: cw, h: ch };
  S.dpr = window.devicePixelRatio || 1;
  /* PH3: A PHONE DRAWS AT 2x AT MOST. A 3x iPhone is 2.25 times the
     pixels of 2x for a difference no eye at arm's length sees, on a GPU
     about a tenth of the desk's (notes/overnight-2026-10-02/mobile.md). */
  if (PHONE && S.dpr > 2) S.dpr = 2;
  if (+Q.get('dpr') > 0) S.dpr = +Q.get('dpr');   /* FP4: to time the frame at another density */
  var dw = Math.round(cw * S.dpr), dh = Math.round(ch * S.dpr);
  if (S.cv.width !== dw || S.cv.height !== dh) { S.cv.width = dw; S.cv.height = dh; }
  S.cv.style.width = cw + 'px'; S.cv.style.height = ch + 'px';
  /* THE WINDOW IS A WHOLE NUMBER OF DEVICE PIXELS, as it is today
     (survey-map.js onGrid / layoutPortal): the portal's rect is what the
     view's aspect is taken from, so the engine has to round it the same
     way or a link's view differs from 8102's in the third decimal. */
  var ss = Math.min(S.dpr, 2), grid = function (v) { return Math.round(v * ss) / ss; };
  var P = S.fld.plate;
  S.portal = { l: grid(S.layout.ox + P.x * k), t: grid(S.layout.oy + P.y * k),
               w: grid(P.w * k), h: grid(P.h * k) };
  /* ==== MP2 (2026-10-04): AND WHERE IT IS ON THE GLASS. `portal` is the
     plate in the world. Under MM1/MP1 the camera stands back by `frameK`,
     so a world px at the sheet is 1/frameK of a css px, and everything that
     met the portal as the on-screen plate (a pointer tested against it, a
     drag or a pinch geared by it, the zoom's pivot, ink weighed in device
     px, names culled at its edges) meets this instead: the same plate,
     shrunk about the glass's middle. At frameK 1 it is the portal itself.
     Like the 1:1 it replaces, it ignores the desk pose's small pitch. ==== */
  var fS = S.frameK || 1, Pw = S.portal;
  S.portalScr = fS === 1 ? Pw : { l: cw / 2 + (Pw.l - cw / 2) / fS, t: ch / 2 + (Pw.t - ch / 2) / fS,
                                  w: Pw.w / fS, h: Pw.h / fS };
}

/* the view's own aspect: the portal's, as today (survey-map viewRatio) */
function viewRatio() { return S.portal.h / S.portal.w; }

function fitWidth() { return S.fld.plate.w; }

/* ==== R3 (c): HOW FAR THE WORLD RUNS EAST AND WEST.
   The view used to be clamped to the record's own neat line, so the sheet
   was the end of the world. It is not: west of the first day the sea goes
   on and east of the front the conjecture does (water.js's beyond), so the
   view runs out over both and the plate is a WINDOW, not a fence.
   It is not literally endless, and the number is not taste: every
   procedural lattice in the shaders — the conjecture's 9-unit hatch, the
   hachure's octave cells, the day grid — is struck from a float32 map
   coordinate, and a 9-unit period held to a tenth of a unit needs about
   five significant figures spare. Past it the pan simply stops, as the
   zoom does.

   ==== X9: AND 24 STOPPED SHORT OF THE VOYAGE. Tommy: "the pan isn't
   unclamped, it maxes out at 1998, it can't reach 1986." He is right, and
   the arithmetic says exactly that. The plate is 2,120 map units wide and
   the record's west edge (`front.west`) is at -8,238; the sheet is C.cw =
   40 units to the week, so a year is 2,087.1 units.

     RUN 24 → 50,880 units → the view's west edge stops at -59,118,
              which is 24.97 years before the harbour's 29 Jul 2022:
              JULY 1997. Rounded off the ruler: 1998. X5's destination,
              12 April 1986, is at about -82,760 — twenty-three thousand
              units, eleven years, past the fence. The voyage could SAIL
              there (voyage.js's follow() writes S.view directly and does
              not clamp), but the first drag afterwards snapped the eye
              twelve years back east, which is a fence showing.

     RUN 40 → 84,800 units → the west edge stops at -93,038, 41.2 years
              back: MARCH 1981, with the view's own centre at NOVEMBER
              1982. 1986 is 10,000 units inside it and the drag after the
              arrival moves nothing.

   The float32 argument, redone for the new number: at |x| = 93,038 the
   exponent is 2^16, so one ulp is 2^-7 = 0.0078 units and a tenth of a
   unit is 12.8 of them — the hatch's phase, the hachure's cell index and
   the day grid are all struck a long way above the noise. In digits:
   93,038 resolved to 0.1 is 5.97 significant figures of float32's 7.22,
   about a factor of 18 in hand. (At RUN 24 it was 25.6 ulps and 5.77
   figures; this spends a little under half of the margin that was left,
   and there is still more than an order of magnitude of it.) 40 is where
   it stops because the voyage stops there: a fence past the end of the
   sea nobody can sail to is not a fence anybody meets. ==== */
/* ==== V3: AND THE WEST END IS NOT A NUMBER OF PLATE WIDTHS ANY MORE —
   IT IS THE EDGE OF THE PAPER. Tommy: *"cut the left-hand sides of the map
   off at January 1986. Is there any reason to go back to 1982? ... once you
   pan the map, you can see beyond where the map cuts off."* He is right on
   both counts: RUN 40 was chosen to clear 1986 by ten thousand units, and
   what is out there past the coast is sea nobody drew.

   So the chart is CUT at 1 January 1986 (field.js CUT_ISO), a coast stands
   at the cut, and the pan stops with the view's west edge ON the cut: the
   land runs off the map because the paper ends, which is the Leo Belgicus's
   own trick of 1583. The east is untouched — the conjecture still runs 40
   plate widths out to blank paper.

   THE FLOAT32 ARGUMENT, redone for the new number. X9's was that at
   |x| = 93,038 one ulp is 2^-7 = 0.0078 units, so a tenth of a unit is 12.8
   ulps and 93,038-to-0.1 is 5.97 of float32's 7.22 significant figures. The
   cut is at -83,341: the SAME binade (2^16 <= |x| < 2^17, ulp 0.0078), and
   5.92 figures instead of 5.97. It is smaller, so nothing is lost — the
   hatch's phase, the hachure's cell index and the day grid all keep the
   order of magnitude of margin X9 measured. ==== */
/* ==== V4: AND WEST OF THE RECORD THE EYE MAY STAND FURTHER BACK. Tommy,
   shown V3's coast: *"I don't really see what you've rendered here. You've
   just represented three okay-looking bits of land? What happened to the
   idea of making it a human female torso, like I'm being birthed out
   of it?!"* V3's own report named the reason and could not reach it: the
   widest view the chart allows is `fitWidth() * 1.25` = 2,650 x 682.8 map
   units, so a figure that fits one view is a few hundred units long — and
   the BERTH on it is a quay of stone 290 units long, because a quay is
   built to a boat and not to a coast. A body only four quays long is not
   a body; it is three bits of land with a harbour in them.

   So the maximum WIDTH of the view is no longer one number. West of the
   record it opens to `fitWidth() * VIEW_WIDE` — 8,480 x 2,184.8 map units,
   four plate widths — and the figure is drawn at that scale: 1,860 units
   from the neck to the thigh, twenty boat-lengths, with the quay a sixth
   of it. The sea is the cheapest thing on this sheet: it is the ground
   pass's own `else` branch, no atlas cell, no instance, no texture, so a
   wider window out there costs the same fragments it costs anywhere.

   THE RECORD ITSELF CANNOT BE ZOOMED WIDER THAN TODAY, and it is not a
   promise, it is arithmetic. `out` is how far the view's west edge lies
   west of the one place a TODAY-widest view would just touch the record
   (`front.west - narrow`). The extra width is `(WIDE - 1.25) * fitWidth()`
   = 5,830 units eased on over VIEW_RAMP = 12,000, and a smoothstep's
   steepest slope is 1.5/ramp, so the extra grows by at most 0.729 units
   per unit of `out` — always less than one. Therefore

       east edge = vx + narrow + extra(out) <= vx + narrow + out
                 = vx + narrow + (front.west - narrow - vx) = front.west,

   with equality only where extra is 0. Whenever the window is wider than
   today's 2,650, NO PART OF THE RECORD IS IN IT — the view's east edge is
   west of the record's west edge. At the fit and at the harbour `out` is
   negative and this function returns `fitWidth() * 1.25`, the number that
   was here before, to the bit.

   AND IT IS A RAMP AND NOT A FENCE (Tommy on X9: *"the pan isn't
   unclamped"*). Sail or pan east from the wide sea and the chart eases
   back to its own scale over 12,000 units — about six years of sheet —
   instead of stopping dead at a wall the reader cannot see. Because
   `maxViewW` depends on the view's WEST EDGE alone and not on its width,
   the clamp is monotone: it can only ever narrow, so it settles in one
   pass and cannot hunt between frames.

   THE FOUR PLACES THE WIDTH IS CAPPED all read this one function now: the
   clamp below, the wheel, `Room.centreOn` and `Room.easeTo`. The three
   outside this block are the same expression with `fitWidth() * 1.25`
   swapped for `maxViewW()` and nothing else — a cap written in four
   places is still one number, and leaving three of them at 1.25 would
   have meant the clamp permitted a width no gesture could ever ask for.
   They read the view's CURRENT west edge, so a programmatic jump from
   the record straight out to the wide sea takes two calls (pan, then
   zoom); `applyHash` writes `S.view` itself and needs neither. ==== */
var VIEW_RUN = 40;
var VIEW_WIDE = 4;        /* plate widths, out in the voyage's own sea */
var VIEW_RAMP = 12000;    /* map units of west edge the wide max eases on over */
function maxViewW(vx) {
  var narrow = fitWidth() * 1.25;
  if (!S.fld || !S.fld.front) return narrow;
  if (vx === undefined || vx === null) vx = S.view.x;
  var out = (S.fld.front.west - narrow) - vx;
  if (!(out > 0)) return narrow;
  var t = out > VIEW_RAMP ? 1 : out / VIEW_RAMP;
  t = t * t * (3 - 2 * t);
  return narrow + (fitWidth() * VIEW_WIDE - narrow) * t;
}
/* ==== AU2 (2026-10-02): THE NEAREST ZOOM IS TIED TO THE TILT. Flat, the
   floor is a 26th of the plate, as it was. Stood up, the relief is drawn at
   the zoom's scale too, so at the near floor and the tilt cap the hills
   stood taller than the eye and the camera was inside them (the audit's
   #1700,431,200,90,0). The floor rises with the square of the tilt's share
   of its cap, to a 5.3rd of the plate at the cap (400 map units on this
   record), which is the nearest the full tilt still reads as country. ==== */
function minViewW() {
  var P = S.fld.plate, f = clamp(S.tilt / Math.max(tiltCap(), 1e-3), 0, 1);
  return P.w / 26 + (P.w / 5.3 - P.w / 26) * f * f;
}
function clampView() {
  var P = S.fld.plate, v = S.view;
  var maxW = maxViewW(v.x), minW = minViewW();
  var w0 = v.w, w1 = clamp(v.w, minW, maxW);
  if (w1 !== w0) {          /* AU2: a floor raised by a tilt widens about the centre */
    var vcx = v.x + v.w / 2, vcy = v.y + v.h / 2;
    v.w = w1; v.h = v.w * viewRatio();
    v.x = vcx - v.w / 2; v.y = vcy - v.h / 2;
  }
  v.w = w1;
  v.h = v.w * viewRatio();
  var lo = P.y, hi = P.y + P.h - v.h;
  /* West of the record the voyage has its own wider, finite sea. Let a
     stopped boat and a freely panned chart use that whole basin; follow()
     and clampView() therefore agree instead of snapping back to the old
     plate-height strip on the first hand movement.

     ==== VF1 (Tommy, 2026-09-23): "the Voyages Sea is much more expansive
     north and south than our normal grounds. If you're not aligned in
     the middle ... once you get beyond a certain proportion of this
     non-land (basically when you pan east enough), I think it re-centres
     you. It looks like it pops in with the 2022 stuff, but actually it's
     just moved you up." It did: the sea's fence and the plate's were two
     rects and the view's CENTRE crossing the record's west edge picked
     one or the other, so at #-9141,2051,2650 one more screen of pan east
     put the view 1,620 units north in a frame. The fence now EASES from
     the sea's band to the plate's over the view's own width as the view
     comes onto the record — the same easing the width cap has had since
     VIEW_RAMP, and the plate's fence to the unit once the whole view is
     east of the edge. It is worked on the view's centre line: the plate's
     band is a single point when the view is taller than the plate (the
     old `hi < lo` case) and the ease lands on that point. And the fence
     never hides the boat: while she is out in the sea the band is
     widened to hold her, so a pan back to a stopped boat is always
     possible. ==== */
  if (window.RoomField && RoomField.voyGround && S.fld.front) {
    /* V3: the half-width is the file's own constant. `voyGround.fair` is
       the slot the cut travels to the shader in and is no longer 2,700. */
    var VY = RoomField.VOY, west0 = S.fld.front.west;
    var ym = (S.fld.front.c0 + S.fld.front.c1) / 2, half = VY.FAIR + VY.RUN;
    var cx = v.x + v.w / 2;
    /* 0 with the view's east edge at the record's west edge or further
       west, 1 with the view's west edge on it; a smoothstep between */
    var ft = clamp((cx - (west0 - v.w / 2)) / v.w, 0, 1);
    ft = ft * ft * (3 - 2 * ft);
    if (ft < 1) {
      var pLo = hi < lo ? P.y + P.h / 2 : lo + v.h / 2;   /* the plate's band, centre line */
      var pHi = hi < lo ? P.y + P.h / 2 : hi + v.h / 2;
      var bLo = ym - half + v.h / 2, bHi = ym + half - v.h / 2;
      var VS = window.RoomVoyage && RoomVoyage.state ? RoomVoyage.state() : null;
      if (VS && VS.x < west0 && (VS.state === 'sailing' || VS.state === 'stopped' || VS.state === 'aground')) {
        bLo = Math.min(bLo, VS.y); bHi = Math.max(bHi, VS.y);
      }
      var cLo = bLo + (pLo - bLo) * ft, cHi = bHi + (pHi - bHi) * ft;
      lo = cLo - v.h / 2; hi = cHi - v.h / 2;
    }
  }
  /* ==== FR1 (2): AND WHILE THE INSTRUMENT IS UP THE FENCE CARRIES THE
     READER ALONG THE LINE OF SIGHT.

     Tommy, 2026-09-21: "when you do the alignment part, the line that
     draws out: if you pan too far to try and follow the line, once the
     alignment gets off the page or you get too far, the line completely
     disappears." It is not the ray and it never was. Measured on this
     branch (room-e9/fr1-0921): with the view placed on the ray by hand
     the line is drawn WHOLE, dash for dash, out to its cross-hair two and
     a half thousand map units north of the record's own rows, on ground
     the sheet draws out there (fr1-0921/f2-x9000.png). What the reader
     cannot do is GO there. The fence above is the plate's own rect, and
     at the fit the plate is exactly the view's own height — so
     `[P.y, P.y + P.h - v.h]` is a single point and the view cannot move
     north or south by one map unit at all. Act two sets the instrument up
     twenty degrees off the truth, which takes the ray off the top of
     those rows 118 map units east of the station: the pan east that
     follows it is a pan along a band the line left almost at once.

     So while the sight is up the north and south limits open to the
     LINE'S OWN CORRIDOR — the y it runs through over its run, and not a
     unit further. Once the pointer is in its notch that corridor is the
     station to the throat, which lies inside the record's own rows, so
     act three hands back the fence it was given. Nothing else moves: the
     east and west ends are the record's as they were, the width cap is
     untouched, and with no pit on the sheet this is the clamp that was
     here. ==== */
  /* AL1: the corridor is now the band BOTH lines run through (doomLines'
     yLo..yHi), out to the paper's reach past the pit */
  var DS = S.doom;
  if (DS && DS.on && (DS.act === 2 || DS.act === 3) && DS.yHi >= DS.yLo) {
    lo = Math.min(lo, DS.yLo - v.h / 2);
    hi = Math.max(hi, DS.yHi - v.h / 2);
  }
  v.y = hi < lo ? (P.y + P.h / 2 - v.h / 2) : clamp(v.y, lo, hi);
  /* the record's own two ends — the limits the view used to stop at */
  var west = S.fld.front.west, east = P.x + P.w, run = P.w * VIEW_RUN;
  /* V3: and the west limit is the paper's edge when the sheet knows where
     it is cut (it does not before a real field has landed, and then the
     old fence stands). */
  var cut = (window.RoomField && RoomField.cutX) ? RoomField.cutX(S.fld) : 0;
  var wlim = cut < -1 ? cut : west - run;
  v.x = clamp(v.x, wlim, east + run - v.w);
}

/* =================================================================
   THE DRAW — one pass, four draws, every frame. Nothing decides
   whether to redraw (ROOM-PLAN §6).
   ================================================================= */
/* ==== MM1 (trial): A SHOT NEVER SEES PAST THE DESK. The world is one
   room now, and its desk is the real one (1400 x 700 mm) with no stand-in
   fitted to the window. Every desk view's footprint on the desk is smaller
   than the desk (measured: at most 1298 mm across and 785 mm deep, at
   2560x1080 and 1280x1000), but not centred on it: the seat sees past the
   front edge, the folded view past the west end. So the desk pose is
   panned until what it sees is on the oak, and brought in only where it
   is deeper than the desk. North the bound is the wall's foot (the desk's
   back edge and the curtain are drawn); south the front edge is allowed
   MM_EDGE mm of its own face, which is what Tommy's window has always
   shown. The room pose eases from the panned pose, so it is continuous. */
var MM_EDGE = 20, MM_WALL = 150;
function onDesk(eye, c, up, asp, fv) {
  var O = window.RoomObjects;
  if (!O || !O.deskRect || !O.DESK_MM) return;
  var L = S.layout, f = S.fld, mm = f.sheet.w * L.k / 840;
  var cx = L.ox + f.sheet.w * L.k / 2, cy = L.oy + f.sheet.h * L.k / 2, D = O.DESK_MM;
  var bx0 = cx + D.x0 * mm, bx1 = cx + D.x1 * mm;
  var by0 = cy + (D.n - MM_WALL) * mm, by1 = O.deskRect(S, 1);
  by1 = by1.y + by1.h + MM_EDGE * mm;
  var tv = Math.tan((fv || FOV) / 2), th = tv * asp;
  for (var it = 0; it < 4; it++) {
    var fw = norm([c[0] - eye[0], c[1] - eye[1], c[2] - eye[2]]);
    var rt = norm([fw[1] * up[2] - fw[2] * up[1], fw[2] * up[0] - fw[0] * up[2], fw[0] * up[1] - fw[1] * up[0]]);
    var uu = [rt[1] * fw[2] - rt[2] * fw[1], rt[2] * fw[0] - rt[0] * fw[2], rt[0] * fw[1] - rt[1] * fw[0]];
    var x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, ok = true;
    for (var q = 0; q < 4; q++) {
      var nx = q & 1 ? 1 : -1, ny = q & 2 ? 1 : -1;
      var d = [fw[0] + rt[0] * nx * th + uu[0] * ny * tv, fw[1] + rt[1] * nx * th + uu[1] * ny * tv, fw[2] + rt[2] * nx * th + uu[2] * ny * tv];
      if (!(d[2] < -1e-6)) { ok = false; break; }
      var t = -eye[2] / d[2], wx = eye[0] + d[0] * t, wy = -(eye[1] + d[1] * t);
      x0 = Math.min(x0, wx); x1 = Math.max(x1, wx); y0 = Math.min(y0, wy); y1 = Math.max(y1, wy);
    }
    if (!ok) return;
    var sc = Math.min(1, (bx1 - bx0) / (x1 - x0), (by1 - by0) / (y1 - y0));
    if (sc < 0.999) {
      for (var a = 0; a < 3; a++) eye[a] = c[a] + (eye[a] - c[a]) * sc;
      continue;
    }
    var dx = x0 < bx0 ? bx0 - x0 : x1 > bx1 ? bx1 - x1 : 0;
    var dy = y0 < by0 ? by0 - y0 : y1 > by1 ? by1 - y1 : 0;
    if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) return;
    eye[0] += dx; c[0] += dx; eye[1] -= dy; c[1] -= dy;
  }
}
function vp() {
  var L = S.layout, cw = L.cw, ch = L.ch;
  /* the desk pose: the camera is over the middle of the glass at the
     distance that makes the sheet plane 1:1 in css pixels.
     RU3: upright in the room, at the sideways glass's distance */
  var Ok = window.RoomObjects, pk = (Ok && Ok.foldOn && Ok.foldGeom) ? F.smoothstep(0.55, 1, Ok.foldT || 0) : 0;
  /* ==== MP3 (2026-10-04): THE FOLDED PACKET IS ONE SHOT ON THE PHONE. A
     pinch out to the whole sheet is the camera standing back (MP1), and a
     fold made from there kept that distance into LK8's framing, so onDesk
     then brought it right in: the packet filled the glass and a thumb's
     swipe no longer unfolded it. As the packet lands the camera's distance
     goes back to the plate's own, so the packet is the same shot whichever
     view it was folded from. ==== */
  var fk = S.frameK || 1;
  if (PHONE && S.frameK0 && pk > 0 && !S.capPrint && !S.rowOnly) fk += (S.frameK0 - fk) * pk;
  var D = ((L.ah || ch) / 2) / Math.tan(FOV / 2) * fk;   /* MM1 */
  /* R27 (1): the aim rides DOWN the page, so the picture rides UP. At no
     switch `cam.aim` is 0 and these are the three numbers they were. */
  var c = [cw / 2, -(ch / 2 + (S.cam.aim || 0)), 0];
  var p = S.cam.pitch, R = D * S.cam.k;
  /* ==== LK8 (audit A17): THE FOLDED PACKET IS WHAT YOU ARE LOOKING AT.
     R59 keeps the reader seated over the packet they have just made, and
     the frame was mostly empty oak with the packet lying on the Field Book
     in a corner. Over the fold's last stretch the eye eases part of the way
     towards the packet (the sheet's south-west quarter, which is where
     every fold lands) and a little closer, so the packet, the card and the
     books are the picture. A pure function of the fold's progress, so the
     unfold walks it back, and at foldT 0 not one number here changes. */
  if (pk > 0 && !S.capPrint && !S.rowOnly) {
    var gk = Ok.foldGeom(), pw = gk.c2 * L.k, ph = (gk.h - gk.cy) * L.k;
    var pcx = L.ox + gk.c2 * 0.5 * L.k, pcy = L.oy + (gk.cy + gk.h) * 0.5 * L.k;
    var reach = 0.6 * pk;                                   /* towards, not on to */
    /* ==== MP4 (2026-10-04): UPRIGHT, THE PACKET AND THE CARD FILL THE GLASS.
       Tommy, of the upright folded phone: "the folded map and cards are just
       tiny ... zoom in on the folded map and cards and let the desk edge and
       floor show". So upright the eye goes all the way over the packet and
       the card's rest beside it (day.js restBox) and comes in until the two
       take the glass's width, a twelfth to spare, through RU3's own field.
       What is past the desk's edge shows (onDesk is off upright). ==== */
    var cbU = PHONE && L.ah && S.card && S.card.restBox ? S.card.restBox() : null;
    if (cbU) {
      var ux0 = Math.min(0, cbU.x0) - 60, ux1 = Math.max(gk.c2, cbU.x1);   /* and the Field Book's spine under the packet's west edge */
      var uy0 = Math.min(gk.cy, cbU.y0), uy1 = Math.max(gk.h, cbU.y1);
      var ucx = L.ox + (ux0 + ux1) / 2 * L.k, ucy = L.oy + (uy0 + uy1) / 2 * L.k;
      var tvU = Math.tan(Math.min(RU3_FOV_MAX, 2 * Math.atan(Math.tan(FOV / 2) * RU3_SPAN * (L.aw / L.ah) * (ch / cw))) / 2);
      var thU = tvU * cw / ch;
      var Rt = Math.max((ux1 - ux0) * L.k * 0.54 / thU, (uy1 - uy0) * L.k * 0.54 / tvU);
      c = [c[0] + (ucx - c[0]) * pk, c[1] + (-ucy - c[1]) * pk, 0];
      R += (Rt - R) * pk;
    } else {
      c = [c[0] + (pcx - c[0]) * reach, c[1] + (-pcy - c[1]) * pk, 0];   /* level with it */
      var fitR = Math.max(ph / (0.55 * (L.ah || ch) * fk), pw / (0.55 * (L.aw || cw) * fk));   /* the packet at 55% of the frame (RU3: the sideways frame's; MM1: in the world) */
      R *= 1 + (Math.min(1, Math.max(0.62, fitR)) - 1) * reach;
    }
  }
  var eye = [c[0], c[1] - R * Math.sin(p), R * Math.cos(p)];
  var up = [0, Math.cos(p), Math.sin(p)];
  var fov = FOV, far = D * 8;
  /* MM1: and the shot stays on the desk (see onDesk) */
  /* MP1: the phone's too, upright through RU3's wider field */
  var fvD = L.ah ? Math.min(RU3_FOV_MAX, 2 * Math.atan(Math.tan(FOV / 2) * RU3_SPAN * (L.aw / L.ah) * (ch / cw))) : FOV;
  /* (not upright: a tall glass cannot hold the packet and the card and see
     only oak; upright frames the two and lets the floor show, MP4) */
  if (MM && !S.rowOnly && !L.ah) onDesk(eye, c, up, cw / ch, fvD);   /* and for the print (printSnap): it is struck through this camera */
  /* ==== E8: THE ROOM IS A CAMERA STATE (ROOM-PLAN section 3), not a
     bigger tilt clamp and not a second camera. `S.cam.room` walks 0 -> 1
     and the eye and the point it looks at walk with it, from over the
     middle of the glass to standing back from the desk looking across it
     at the wall. There is no roll anywhere on the way: the eye and the
     target share an x, so the view direction stays in the y-z plane and
     the up vector is that direction turned a quarter turn — which at
     room 0 is exactly [0,1,0], the same three numbers as before.

     THE FIELD EASES WITH IT. E1's 22 degrees is what keeps the flat
     sheet 1:1 and it is kept (REPORT-room-E1 (h)); a ROOM seen through
     22 degrees is a keyhole — at this eye the frame would be under a
     metre wide at the wall — so the field opens to RoomPlace's as the
     camera leaves and closes again exactly on the way back. At room 0
     every number in this function is the number it was. ==== */
  var r = S.cam.room || 0;
  if (r > 0 && window.RoomPlace) {
    var rp = RoomPlace.pose(S);
    eye = [eye[0] + (rp.eye[0] - eye[0]) * r,
           eye[1] + (rp.eye[1] - eye[1]) * r,
           eye[2] + (rp.eye[2] - eye[2]) * r];
    c = [c[0] + (rp.target[0] - c[0]) * r,
         c[1] + (rp.target[1] - c[1]) * r,
         c[2] + (rp.target[2] - c[2]) * r];
    var fw = norm([c[0] - eye[0], c[1] - eye[1], c[2] - eye[2]]);
    up = [0, -fw[2], fw[1]];
    /* ==== R62: AND THE HEAD TURNS, WHICH IS NOT A FOURTH POSE. A look is
       not a place: the eye stays exactly where the room pose put it and
       only the point it looks at swings round it, so nothing about this
       can walk the reader across the floor. The yaw is about the WORLD's
       own up (a person turning at the waist keeps the horizon level, a
       yaw about the camera's up would roll it) and the pitch is about the
       right that yaw leaves, which is the order a neck works in.

       IT IS SCALED BY `r`, which does three jobs at once: at the desk
       `r` is 0 and every number in this function is the number it was to
       the bit (the fit cannot move); the sit's own 1200 ms unwinds the
       look for nothing, which is the card's "no jump"; and at look 0 the
       two lines above stand as they are rather than go round the general
       basis, which is the same vector only to within an ulp. The angles
       are zeroed when the sit LANDS (`lookUp`), not when it starts. ==== */
    var ly = (S.cam.lookYaw || 0) * r, lp = (S.cam.lookPitch || 0) * r;
    if (ly || lp) {
      var Ld = Math.sqrt((c[0]-eye[0])*(c[0]-eye[0]) + (c[1]-eye[1])*(c[1]-eye[1]) +
                         (c[2]-eye[2])*(c[2]-eye[2]));
      var cy2 = Math.cos(ly), sy2 = Math.sin(ly);
      var f1 = [fw[0]*cy2 - fw[1]*sy2, fw[0]*sy2 + fw[1]*cy2, fw[2]];
      var rt = norm([f1[1], -f1[0], 0]);           /* cross(f1, world up) */
      var u1 = [rt[1]*f1[2] - rt[2]*f1[1], rt[2]*f1[0] - rt[0]*f1[2], rt[0]*f1[1] - rt[1]*f1[0]];
      var cp = Math.cos(lp), sp = Math.sin(lp);
      var f2 = norm([f1[0]*cp + u1[0]*sp, f1[1]*cp + u1[1]*sp, f1[2]*cp + u1[2]*sp]);
      c = [eye[0] + f2[0]*Ld, eye[1] + f2[1]*Ld, eye[2] + f2[2]*Ld];
      up = [rt[1]*f2[2] - rt[2]*f2[1], rt[2]*f2[0] - rt[0]*f2[2], rt[0]*f2[1] - rt[1]*f2[0]];
    }
    fov = FOV + (rp.fov - FOV) * r + (rp.fovWide || 0) * r * r * r;   /* RF1: place.js */
    far = D * (8 + 8 * r);
  }
  /* ==== X1: AND THE THIRD POSE, ON TOP. `cam.lap` walks 0 -> 1 from
     WHEREVER the camera stands — seated at the sheet or standing in the
     room — to RoomPlace's laptop pose, so a reader who opens the terminal
     from the room comes back to the room and one who opens it from the
     sheet comes back to the sheet. At lap 0 not one number above is
     touched, which is the whole of why the fit can still be diff 0 with a
     laptop in the scene. The up vector is BLENDED and lookAt squares it:
     at the desk pose up is [0,1,0] (a plan view, where world up is
     degenerate) and at the laptop it is world up, and there is no pose in
     between where the two are parallel. ==== */
  var lq = S.cam.lap || 0;
  if (lq > 0 && window.RoomPlace && RoomPlace.lapPose) {
    var lpp = RoomPlace.lapPose(S);
    if (lpp) {
      eye = [eye[0] + (lpp.eye[0] - eye[0]) * lq,
             eye[1] + (lpp.eye[1] - eye[1]) * lq,
             eye[2] + (lpp.eye[2] - eye[2]) * lq];
      c = [c[0] + (lpp.target[0] - c[0]) * lq,
           c[1] + (lpp.target[1] - c[1]) * lq,
           c[2] + (lpp.target[2] - c[2]) * lq];
      up = norm([up[0] * (1 - lq), up[1] * (1 - lq), up[2] * (1 - lq) + lq]);
      fov = fov + (lpp.fov - fov) * lq;
      far = Math.max(far, D * 10);
    }
  }
  /* ==== R60: AND THE BLENDED UP IS CHECKED BEFORE lookAt IS GIVEN IT.
     Tommy: Ctrl+` "moves to terminal but flips the map upside down". The
     one thing in this function that CAN turn a picture over is the line
     above: lookAt's basis is `f x up`, which is the zero vector — and the
     matrix NaN or mirrored — the instant the blended `up` lies along the
     way the camera is looking. The comment above ASSERTS the two are
     never parallel; this measures it. `S.upCos` is the cosine, every
     frame, one dot product, and it rides in `Room.x2()`: over the whole
     turn, from six starting poses, |cos| never passed 0.4437 — 63.7
     degrees of margin, and the guard never fired (REPORT-room-R60). It
     fires only past 0.9995, so every frame the engine draws today is the
     same matrix to the bit and the fit cannot move. ==== */
  var fwd = norm([c[0] - eye[0], c[1] - eye[1], c[2] - eye[2]]);
  var upCos = fwd[0] * up[0] + fwd[1] * up[1] + fwd[2] * up[2];
  S.upCos = upCos;
  if (Math.abs(upCos) > 0.9995) {
    /* the world axis the camera is LEAST pointed along, squared against
       the way it is looking: any up at all beats a degenerate one */
    var ax = Math.abs(fwd[0]) < Math.abs(fwd[1])
           ? (Math.abs(fwd[0]) < Math.abs(fwd[2]) ? [1, 0, 0] : [0, 0, 1])
           : (Math.abs(fwd[1]) < Math.abs(fwd[2]) ? [0, 1, 0] : [0, 0, 1]);
    var k2 = ax[0] * fwd[0] + ax[1] * fwd[1] + ax[2] * fwd[2];
    up = norm([ax[0] - fwd[0] * k2, ax[1] - fwd[1] * k2, ax[2] - fwd[2] * k2]);
    S.upFix = (S.upFix || 0) + 1;
  }
  S.camEye = eye;
  /* RU3: upright in the room the field is the sideways one opened until
     this glass's width takes in RU3_SPAN of what the sideways glass took in
     across (RU1 opened it by the glass's own shape, on top of place.js's
     own opening for a narrow window, and at a third of the scale). */
  if (L.ah) fov = Math.min(RU3_FOV_MAX, 2 * Math.atan(Math.tan(fov / 2) * RU3_SPAN * (L.aw / L.ah) * (ch / cw)));
  var proj = ORTHO ? ortho(cw, ch, -20000, 40000) : persp(fov, cw / ch, D * 0.02, far);
  if (ORTHO) { eye = [c[0], c[1], 10000]; up = [0, 1, 0]; }
  return mul(proj, lookAt(eye, c, up));
}

/* =====================================================================
   D1 — THE SURVEY FRONT IS BEING SURVEYED

   Tommy: *"the pit is DOOM, not the singularity — the former, if
   misaligned, leads to the latter"*, and today's ground is not levelled
   *"because it is being surveyed"*.

   So the camp on today's cell is a SURVEYOR AT WORK (marks.js), the
   reader paces the party over the seven days of the front, and each day
   paced is a day LEVELLED — the ground under it rises, and the first
   step is meant to be seen doing it. Out east, where the record is
   conjecture, the downs have a hole in them at mid-2030; when the party
   has paced the whole front it sets up the instrument, and a line of
   sight brought to bear on the hole turns it into a PEAK. Alignment in
   both senses.

   THE SEVEN DAYS OF THE FRONT are the seven DATES ending at the day the
   survey was carried to — not "today's column", which on a Friday holds
   six cells of this week and would have nothing for the E and W arrows
   to do. Laid boustrophedon they straddle two week columns, which is
   what makes all four arrows a real step and no diagonal necessary.
   ===================================================================== */
var DOOM_ISO   = '2030-07-01';   /* the essay's own mid-2030 */
/* ==== D2 (1): the funnel is EIGHTEEN columns across and it goes down
   DOOM_DEEP contour intervals — 22 of them, about 49 bed units, which at
   the exaggeration is 984 sheet units: nearly two plate-heights of hole
   against a record whose tallest day stands under a fifth of one. That is
   the absurdity Tommy asked for ("imagine more of a black hole"), and it
   is a ratio and not a picture: a taller record digs a deeper funnel.
   D2 (4): LIMB_R is the graduated arc's radius (a cell's width, less a
   sixth) and LIMB_HA its half-angle. ==== */
var DOOM_COLS  = 18;             /* the funnel, in week columns across */
var DOOM_DEEP  = 22;             /* and how many contour intervals down */
var LIMB_HA    = 32;             /* the limb's half-angle, in degrees */
/* ==== AL1 (2026-09-26): ACT TWO IS TRIANGULATION ON THE REAL MAP. Tommy:
   "there's no challenge in it at all" — the old limb turned the line 2.5
   degrees a click and snapped it to the truth within 3, so it could not be
   oversteered and it always locked in. Now the instrument is set up at
   BOTH ENDS OF THE FRONT (the column's top row and its bottom row, the
   longest baseline the front has) and each line is DRAGGED to bear on the
   pit: "you have to point one location dead centre at the pit and another
   location dead centre at the pit ... so they cross over in the pit". No
   steps, no snap, no aid on the limb ("it replaces investigation on the
   map and judgement with a small mechanical skill").

   THE GEOMETRY IS THE CHALLENGE. The two stations are 6 rows apart (468
   map units at this sheet) and the pit is ~7,900 units east, so the two
   lines meet at only ~3.4 degrees: an aim error of one map unit at the
   pit slides the crossing ~17 units (three days) east or west. From where
   we stand every line of evidence points the same way; the DATE is the
   hard part.

   DOOM_PREC is each bearing's half-angle — the instrument's precision,
   drawn as a thin wedge round the line. 0.075 degrees is ~10 map units
   either side of the line at the pit (a quarter of a week column), and
   with both lines dead on the throat the two wedges overlap in a diamond
   whose east-west extent is about two months either side of mid-2030:
   the date span lettered at the crossing.

   DOOM_FIX was the success test, an ellipse round the throat; AL2 below
   made the diamond itself the test. ==== */
/* ==== AL2 (2026-09-26): THE TARGET IS THE DIAMOND, THE DIAL IS THE HAND.
   Tommy: "i feel like i've got them right but the card hasn't 'kicked in',
   oh i finally got it but it was hard to tell i hadn't got it right and
   why", and "we might need to make the 'hit box' to trigger alignment a
   bit bigger". DOOM_FIX's ellipse was ~5 map units across — half the red
   diamond it sat invisibly inside. There is no second tolerance now: the
   pit is fixed when its centre lies inside the diamond as drawn
   (doomOnThroat), which is the same as each bearing passing within
   DOOM_PREC of the pit's centre. DOOM_PREC went 0.075 -> 0.12 degrees so
   the diamond is a fair target: ~16.5 map units either side of the line at
   the pit (two fifths of a week column), and along the lines about three
   months either side of mid-2030.
   And "dial-only": "you can drag the lines themselves? that makes it
   easier but maybe a little cheaty". The line is no longer taken by the
   hand; only the limb is. Its fine control is the reach: the limb turns
   the line one for one under the hand within DIAL_FINE css px of its
   centre, and a hand carried further out turns it by (DIAL_FINE / reach)
   squared of that — a vernier by leverage, the arm drawn while it is out
   (doomAim). ==== */
var DOOM_PREC  = 0.12;           /* each bearing's half-angle, degrees (AL1 had 0.075) */
var DOOM_TURN  = 80;             /* a bearing is held within this many degrees of east */
var STN_GRAB   = 18;             /* how near the hand must come to the other station's mark, css px */
var DOOM_FLY_MS = 1800;          /* the view goes to the pit over this long */
var DOOM_TILT  = 30;             /* and is tilted this far to watch it stand up, degrees: the
                                    peak is ~900 units tall on a 720-unit pit, and at the widest
                                    view the sheet allows out there (maxViewW, ~2,650) 30 degrees
                                    is the most that keeps its summit on the glass (measured at
                                    2194 x 1234: pit 0.38 down the glass, summit 0.10) */
var PACE_MS    = 400;            /* a day levels over this long */
var DRIFT_MS   = (function () { var v = parseInt(Q.get('drift'), 10); return isFinite(v) && v > 0 ? v : 0; })();
                                 /* the well's frame cadence: 0 is every display frame */
var DOOM_MS    = 2000;           /* the pit stands up over this long */
var DAY_MS_D   = 86400000;
/* ==== FR1 (1): THE CHEVRON'S OWN THREE NUMBERS, in the glass's units and
   not the paper's. CHEV_ART is the span of the sprite the atlas cuts (ten
   art units across, `doomDraw` below); CHEV_PX is the smallest it may be
   drawn on the glass and CHEV_GRAB the reach round it the hand is given.
   CHEV_CAP is the most the printed size may be blown up to hold CHEV_PX —
   a foreshortening steep enough to need more than that is one where the
   cell itself is a line, and a chevron three times its printed width is
   already most of a cell. ==== */
var CHEV_ART   = 10;             /* the sprite's span, in art units */
var CHEV_PX    = 12;             /* and the least it may measure, in css px */
var CHEV_GRAB  = 22;             /* how near the hand must come, in css px (PH1: was 14) */
var CHEV_CAP   = 3;
/* ==== PH1 (2026-09-25): THE HANDLES STAND UP. Tommy: "i also still find
   the alignment handles very tricky to see and use", "the pit alignment
   isn't awful in 2D but it's a mess in 3D" and "the person and device are
   cool but the lines just stretch down the cliff face". So the chevrons
   and the limb are upright plates like the surveyor (marks.js drawArt), a
   fixed size on the glass at any tilt or turn, and the line of sight is a
   line in the air from the instrument to the pit (doomSightDraw). A
   chevron points at the day it steps to AS THE SCREEN SHOWS IT, which is
   what REVIEW 2026-09-19 asked of the printed ones ("the arrow directions
   don't rotate when you rotate the map"). ==== */
var CHEV_OFF   = 28;             /* a chevron's least distance off the figure, css px */
var CHEV_BOX   = { x0: -6, y0: -6, x1: 6, y1: 6, k: 1.5 };      /* 18 css px; CS2 (2026-09-26): was k 2, Tommy "a little bit too brazen ... just a bit too big" */
var DIAL_R     = 44;             /* the limb's radius on the glass, css px */
var DIAL_FINE  = 57;             /* AL2: the hand takes the wheel within this reach of its centre, css px */
/* ==== AL7 (2026-09-27): A WHEEL, CRANKED. Tommy: "instead of the dotted
   lines for greater alignment granularity, couldn't we just make the
   normal range spin slower? maybe it becomes more like a wheel you turn to
   crank it in a particular direction?" The limb was one for one with a
   vernier by reach (the dotted arm). Now it is a wheel: the hand's travel
   round its centre spins it one for one, and a whole turn of the wheel
   turns the line six degrees (AL9 below made it a year). The pit is ~1.7 degrees off east from either
   station, about a third of a turn, and the wedge's DOOM_PREC either side
   is ~7 degrees of wheel, a few px of hand on the rim. No arm, no reach
   gearing. The index still stands where the line of sight crosses the rim.
   AL9 (2026-09-27): A TURN IS A YEAR, NOT SIX DEGREES. Tommy: "why not
   like a turn of the wheel for each year? ... because it's degrees the
   date change is uneven? it's such a tiny wheel turn between say 2030 and
   2034 whereas the duration of 2027 is like a full wheel". A line reaches
   the pit's row at x = sx + dy / tan(b), so a degree is weeks near the
   front and years out by the pit. The wheel is geared in the DATE the line
   lands on (AL6): each move is turned into bearing at the rate that moves
   that date a year a turn (doomCrank). Out past CRANK_YEARS from the front,
   and for a line that never reaches the row (both start due east), there
   is no date to gear by, so the wheel turns the bearing a constant amount a
   turn: exactly the turn that brings a due-east line in to CRANK_YEARS, so
   one turn from the start lands it on the dated stretch. The wedge's 0.12
   degrees at the pit is then about a quarter turn: three months. ==== */
var CRANK_YEARS = 6;             /* how far past the front a turn is a year */
var CRANK_MAX  = 20;             /* the most a turn may move the line, degrees (steep bearings) */
var DIAL_UP    = 11;             /* its centre, up the plate from the anchor: the level */
var DIAL_BOX   = { x0: -72, y0: -83, x1: 72, y1: 61, k: 1 };
var STN_BOX    = { x0: -10, y0: -15, x1: 11, y1: 10, k: 1 };     /* AL1: the other station's mark; AL3: the instrument */
var FIX_BOX    = { x0: -240, y0: -90, x1: 240, y1: 80, k: 1 };   /* AL2: the crossing's dates, lettered by the limb */
S.doom = { ready: 0, on: 0, t: -1, rise: 0, cx: 0, cy: 0, r: 0,
           sx: 0, sy: 0, dx: 1, dy: 0, sight: 0, srun: 0,
           days: [], cells: [], at: 0, act: 1, hov: 0, brg: 0, want: 0,
           paceH: 0, t0: 0, s0: 0, sight0: 0, paced: [0,0,0,0,0,0,0],
           /* D2: the drift's angle, the east reach, the frame-asking */
           spin: 0, spinT: 0, eastK: 1, sWant: 0,
           /* AL1: the two stations (north, south), which one the instrument
              is on, the line in the hand, the crossing, the band of y the
              lines run through, and the flight to the pit */
           stn: [], si: 0, drag: null, X: null, yLo: 0, yHi: 0, fly: 0,
           pace: new Float32Array(28) };
/* D2 (4): the limb's radius, in map units */
function limbR() { return C.cw * 0.84; }
/* ==== FR1 (1): WHERE A CHEVRON IS PRINTED, in one place. It was worked
   out inside `doomDraw` alone, so the hit test could only describe it in
   the cell's own fractions and the two could drift apart. Both read this
   now: the shared cell edge, half a cell off the party's own centre. ==== */
function doomArrowAt(d) {
  /* PH1: on the line to the next day's centre — the shared edge (half
     way) where that is CHEV_OFF css px off the figure or more, and further
     out along the same line where the cell is too small on the glass for
     that, so the plate never sits on the surveyor */
  var c = S.doom.cells[S.doom.at];
  var n = [c[0] + (d === 'E' ? C.cw : (d === 'W' ? -C.cw : 0)),
           c[1] + (d === 'S' ? C.ch : (d === 'N' ? -C.ch : 0))];
  /* the plate stands on the ground where it is put, so how far out it goes
     is measured on the ground; only its DIRECTION is level (PA1, below) */
  var f = 0.5, ts = S.day && S.day.toScreen, a = ts ? ts(c[0], c[1]) : null, b = ts ? ts(n[0], n[1]) : null;
  if (a && b) {
    var v = Math.sqrt((b[0] - a[0]) * (b[0] - a[0]) + (b[1] - a[1]) * (b[1] - a[1]));
    if (v > 1e-3) f = Math.min(3, Math.max(0.5, CHEV_OFF / v));
  }
  return [c[0] + (n[0] - c[0]) * f, c[1] + (n[1] - c[1]) * f];
}
/* ==== PA2 (2026-09-27): THE CHEVRONS STAND AT THE SURVEYOR'S HEIGHT.
   Tommy, at #1182,431,2120,50,36: "pacing arrows seem wrong from this
   angle?" A chevron stood on the GROUND at the shared edge of its cell —
   and the day he stands on is raised by the pacing while the next is not,
   so on a tilted sheet the step's arrow stood at the foot of the raised
   block's wall, half down the cliff, pointing into it. So a chevron is
   anchored on the surveyor's own cell and carried out ON THE GLASS along
   the level direction (doomArrowAng) by the same reach it had: half the
   cell's level length on the screen, never under CHEV_OFF css px and never
   over three cells. `doomArrowScr` is that point on the glass and its
   offset; the plate and the hit test both read it. ==== */
function doomArrowScr(d) {
  var c = S.doom.cells[S.doom.at], ts = S.day && S.day.toScreen;
  var n = [c[0] + (d === 'E' ? C.cw : (d === 'W' ? -C.cw : 0)),
           c[1] + (d === 'S' ? C.ch : (d === 'N' ? -C.ch : 0))];
  var a = ts ? ts(c[0], c[1]) : null, b = ts ? ts(n[0], n[1], c[0], c[1]) : null;
  if (!a) return null;
  var v = b ? Math.sqrt((b[0] - a[0]) * (b[0] - a[0]) + (b[1] - a[1]) * (b[1] - a[1])) : 0;
  var r = v > 1e-3 ? v * Math.min(3, Math.max(0.5, CHEV_OFF / v)) : CHEV_OFF;
  var th = doomArrowAng(d), ox = Math.cos(th) * r, oy = Math.sin(th) * r;
  return { x: a[0] + ox, y: a[1] + oy, off: [ox, oy] };
}
/* PH1: and which way that day lies on the glass, in radians (y down) */
function doomArrowAng(d) {
  var c = S.doom.cells[S.doom.at], ts = S.day && S.day.toScreen;
  var n = [c[0] + (d === 'E' ? C.cw : (d === 'W' ? -C.cw : 0)),
           c[1] + (d === 'S' ? C.ch : (d === 'N' ? -C.ch : 0))];
  /* PA1 (2026-09-26): both ends at the SURVEYOR's height. The days paced
     are raised and the next is not, so measured each at its own ground the
     line ran downhill and a tilted sheet printed the arrow pointing down;
     Tommy: "the arrow points down rather than purely across" */
  var a = ts ? ts(c[0], c[1]) : null, b = ts ? ts(n[0], n[1], c[0], c[1]) : null;
  if (a && b && (Math.abs(b[0] - a[0]) + Math.abs(b[1] - a[1])) > 1e-3) return Math.atan2(b[1] - a[1], b[0] - a[0]);
  return d === 'E' ? 0 : d === 'S' ? Math.PI / 2 : d === 'W' ? Math.PI : -Math.PI / 2;
}
/* ==== PH1: THE LIMB ON THE GLASS. Where the instrument's limb is drawn,
   in css px: the arc is centred on the level (DIAL_UP over the station's
   own projection), the index is the line of sight's direction ON THE
   SCREEN, and the two ends are the bearing turned LIMB_HA north and south,
   projected. AL1: the hooks that turned it are gone — the limb is the
   instrument's face and a handle to take the line by, and the bearing is
   dragged. One function, read by the plate and by the hit test. ==== */
function doomDial() {
  var D = S.doom, ts = S.day && S.day.toScreen;
  if (!ts || D.act !== 2 || !D.stn.length) return null;
  var c = D.cells[D.at], o = ts(c[0], c[1]);
  if (!o) return null;
  var far = 3 * C.cw, b = D.stn[D.si].brg * Math.PI / 180, ha = LIMB_HA * Math.PI / 180;
  /* ==== AL4 (2026-09-27): LEVEL, AND A FIXED ARC. Tommy, at
     #1397,-14,2120,50,50: "i've managed to get the left line stuck off the
     top of the page and can't get it back from this angle" and "the
     alignment wheel ... changes as you move it around? it's a different
     length in different positions?" The index and the arc's two ends were
     each a point three cells out projected at ITS OWN ground — and at a
     steep bearing that point is off the record, on ground far below the
     station, so the index pointed down-right under a line running up-right
     (measured: 48 degrees against the line's -80) and doomAim's gearing,
     read off the same projection, flipped sign and threw the bearing back
     to the stop. So the index is projected LEVEL, at the station's own
     height (PA1's rule for the chevrons), and the arc is LIMB_HA either
     side of it ON THE GLASS, the same length wherever it points. `ha` is
     no longer projected at all. ==== */
  function ang(t) {
    var q = ts(c[0] + Math.cos(t) * far, c[1] + Math.sin(t) * far, c[0], c[1]);
    return q ? Math.atan2(q[1] - o[1], q[0] - o[0]) : null;
  }
  var phi = ang(b);
  if (phi === null) return null;
  return { o: o, cx: o[0], cy: o[1] - DIAL_UP, phi: phi, d0: -ha, d1: ha };
}
/* AL6: the angle on the glass of a bearing from a cell, projected level
   at the cell's own height the way doomDial projects its index (AL4) */
function doomGlassAng(c, b) {
  var ts = S.day && S.day.toScreen, o = ts && ts(c[0], c[1]);
  if (!o) return null;
  var far = 3 * C.cw, q = ts(c[0] + Math.cos(b) * far, c[1] + Math.sin(b) * far, c[0], c[1]);
  return q ? Math.atan2(q[1] - o[1], q[0] - o[0]) : null;
}
/* ==== FR1 (1): AND HOW BIG, in map units per art unit. The printed size
   is three tenths of a cell and stays three tenths of a cell wherever the
   ink is big enough to see; where it is not — the sheet at the fit is
   about half a css pixel to the map unit, which makes the chevron five
   pixels across, and a tilt takes the north and south pair down further
   still — it is blown up until its SMALLEST side on the glass measures
   CHEV_PX. The px a map unit is worth is read the way voyage.js reads it
   for the corner arrow's pen: three `toScreen` calls for the local
   Jacobian, the worst of its two columns, so it holds under any tilt,
   turn or perspective and needs no camera arithmetic of its own. ==== */
function doomArrowK() {
  var k = C.cw * 0.03;
  var c = S.doom.cells[S.doom.at];
  if (!c || !S.day || !S.day.toScreen) return k;
  var s0 = S.day.toScreen(c[0], c[1]); if (!s0) return k;
  var se = S.day.toScreen(c[0] + 100, c[1]);
  var ss = S.day.toScreen(c[0], c[1] + 100);
  if (!se || !ss) return k;
  var ex = Math.sqrt((se[0] - s0[0]) * (se[0] - s0[0]) +
                     (se[1] - s0[1]) * (se[1] - s0[1])) / 100;
  var ey = Math.sqrt((ss[0] - s0[0]) * (ss[0] - s0[0]) +
                     (ss[1] - s0[1]) * (ss[1] - s0[1])) / 100;
  var per = Math.min(ex, ey);      /* css px to the map unit, the worst way */
  if (!(per > 1e-6)) return k;
  return Math.min(k * CHEV_CAP, Math.max(k, CHEV_PX / (CHEV_ART * per)));
}

function doomCell(n) {
  var w = Math.floor(n / 7), d = n - w * 7, r = (w & 1) ? 6 - d : d;
  return [C.px + (w + 0.5) * C.cw, C.py + (r + C.EDGE_ROWS + 0.5) * C.ch];
}
/* the day one step N, S, E or W of day n, or 0 if the front does not
   reach there: N and S are the rows of one column, E and W the same row
   of the next, and the boustrophedon turn is the (w & 1) above */
function doomStep(n, dir) {
  var w = Math.floor(n / 7), d = n - w * 7, r = (w & 1) ? 6 - d : d;
  var w2 = w, r2 = r;
  if (dir === 'N') r2 = r - 1; else if (dir === 'S') r2 = r + 1;
  else if (dir === 'E') w2 = w + 1; else w2 = w - 1;
  if (r2 < 0 || r2 > 6 || w2 < 0) return 0;
  var d2 = (w2 & 1) ? 6 - r2 : r2;
  var n2 = 7 * w2 + d2, D = S.doom;
  return (n2 >= D.days[0] && n2 <= D.days[6]) ? n2 : 0;
}
function doomInit() {
  var D = S.doom, f = S.fld;
  if (D.ready || !f || f.stub || !f.days || !f.days.epochMs || f.front.none) return;
  var last = f.days.surveyedTo || f.today;
  var n0 = Math.round((Date.parse(last + 'T00:00:00Z') - f.days.epochMs) / DAY_MS_D);
  if (!isFinite(n0) || n0 < 0) return;
  D.ready = 1; D.on = 1;
  /* REVIEW 2026-09-19, Tommy: "today I can't pace Sunday, so I can't pace
     the entire frontier. That means on a Monday I can't pace anywhere."
     The seven days of the front are TODAY'S COLUMN, Monday to Sunday, the
     days still to come included — they are the terra incognita the party
     is there to survey. Not the seven dates ending today. */
  var w0 = Math.floor(n0 / 7), i;
  for (i = 0; i < 7; i++) { D.days.push(7 * w0 + i); D.cells.push(doomCell(7 * w0 + i)); }
  D.at = n0 - 7 * w0;                         /* the party is on today */
  D.paced = [0, 0, 0, 0, 0, 0, 0];            /* when each day was levelled */
  /* AL1: THE TWO STATIONS are the front's two ends — the day in the
     column's top row and the day in its bottom row — and each keeps its
     own bearing (degrees, y down, so positive swings south) and whether it
     has been laid. Both start due east: every line from the front points
     the same way until it is aimed. */
  var iN = 0, iS = 0;
  for (i = 1; i < 7; i++) {
    if (D.cells[i][1] < D.cells[iN][1]) iN = i;
    if (D.cells[i][1] > D.cells[iS][1]) iS = i;
  }
  D.stn = [{ name: 'north', i: iN, brg: 0, laid: 0 }, { name: 'south', i: iS, brg: 0, laid: 0 }];
  D.si = 0;
  /* the pit: eight columns across, a depth of the order of the tallest
     day of the record, on the meridian of mid-2030 — or as far east as
     the drawn country goes, which at this record is a good deal short of
     it (the margin carries the ground 1 1/6 plate-heights past the neat
     line, about sixteen weeks, and mid-2030 is two hundred). */
  D.r = DOOM_COLS * C.cw / 2;
  D.rise = Math.max(DOOM_DEEP * TSTEP, 5 * (f.maxHeight || 0));
  D.paceH = Math.max(0.8 * TSTEP, 0.30 * (f.maxHeight || 0));
  var want = C.px + ((Date.parse(DOOM_ISO + 'T00:00:00Z') - f.days.epochMs) /
                     (7 * DAY_MS_D)) * C.cw;
  /* ==== D2 (2): AND IT STANDS ON MID-2030'S OWN COLUMN NOW. D1 pulled it
     back to the margin's own edge because the drawn country stopped
     there; the paper reaches as far as it is asked to (uEastK above), so
     the pit is where the essay says it is — about a hundred and forty
     week columns past the front, four plate-widths out, which is well
     clear of any view the reader is given for nothing. The floor under
     the max is for a record so long that mid-2030 is already inside it. */
  D.cx = Math.max(want, f.front.east + 1.3 * D.r);
  D.cy = (f.front.c0 + f.front.c1) / 2;
  /* how far the sheet's east overshoot has to be scaled for the paper to
     carry that far: the reach it has, over the reach it needs */
  /* the reach the sheet's alpha rim gives the paper east, in map units:
     GROUND_FS's own BEY.REACH (2.2) times a plate-height */
  var reach = 2.2 * (f.front.c1 - f.front.c0);
  var run = (D.cx + 1.7 * D.r) - f.front.east;
  D.eastK = run > reach ? reach / run : 1;
  doomRestore();
}
/* ==== RS1 (2026-09-28): THE PIT STAYS STOOD UP. Tommy: "i'd quite like to
   persist the room state in general". A pit that was aligned is `pit` in
   the room's blob (state.js): the two laid bearings. At init it stands as
   a peak at once — every day levelled, both lines laid and on the sheet,
   act three with its rise already over (t0 and paced long past, so
   doomTick reads them as done and doomLand never runs): no replay of the
   rise, no flight, no card (the mark is progress.js's, as it always was).
   The front is today's column, so a week later the stations have moved
   one column east; lines that no longer cross on the throat are aimed at
   its centre instead. An unfinished pit (acts one and two) is not kept. */
function doomRestore() {
  var D = S.doom, RSt = window.RoomState, saved = RSt && RSt.get('pit');
  if (!saved || !Array.isArray(saved.brg) || saved.brg.length !== 2) return;
  var i;
  for (i = 0; i < 7; i++) D.paced[i] = -1e9;
  for (i = 0; i < 2; i++) {
    D.stn[i].brg = Math.max(-DOOM_TURN, Math.min(DOOM_TURN, +saved.brg[i] || 0));
    D.stn[i].laid = 1; D.stn[i].wh = 0;
  }
  if (!doomOnThroat()) for (i = 0; i < 2; i++) {
    var c = D.cells[D.stn[i].i];
    D.stn[i].brg = Math.atan2(D.cy - c[1], D.cx - c[0]) * 180 / Math.PI;
  }
  D.act = 3; D.t0 = -1e9; D.t = 1; D.si = 0; D.at = D.stn[0].i; D.card = false;
  D.sight = 1; D.sight0 = 1; D.sWant = 1;
  doomLines();
}
function doomTick() {
  var D = S.doom;
  if (!D.ready) doomInit();
  if (!D.on) return D;
  var now = performance.now(), i, c, busy = 0;
  D.any = 0;
  for (i = 0; i < 7; i++) {
    var t0 = D.paced[i], h = 0;
    if (t0) D.any = 1;
    if (t0) {
      var u = Math.min(1, (now - t0) / PACE_MS);
      if (u < 1) busy = 1;
      h = D.paceH * u * u * (3 - 2 * u);
    }
    c = D.cells[i];
    D.pace[i * 4] = c[0]; D.pace[i * 4 + 1] = c[1];
    D.pace[i * 4 + 2] = h; D.pace[i * 4 + 3] = 0;
  }
  /* the pit standing up into the peak — AL1: once the view has ARRIVED on
     it (doomFly), so the reader sees it go from down to up. Until then
     D.t0 is 0 and the pit is still a pit. */
  if (D.act === 3 && D.t0) {
    var v = Math.min(1, (now - D.t0) / DOOM_MS);
    if (v < 1) busy = 1;
    D.t = -1 + 2 * (v * v * (3 - 2 * v));
  }
  /* and the ray, fading in when the instrument goes up — D2 (4): it goes
     SOLID at the notch, because that is the shot it took.
     FR3 2026-09-21: AND IT STAYS. It used to fade 2 s after the notch,
     once the funnel had turned itself inside out. Tommy: "for the
     alignment exercise, you don't necessarily know about the pit. So you
     may see the line go from dashed to solid, and you'll follow that and
     see what's at the end of it. That's how you may discover the pit,
     but then it goes and disappears. I kind of think the line should
     stay." So the observed bearing is on the sheet for as long as the
     pit is stood up, and the FR1 fence, which already opens the view to
     the line's corridor in act three, carries the reader to its end. */
  var wantS = (D.act === 2 || D.act === 3) ? 1 : 0;
  /* LF1: the running dashes want every frame — AL1: and only a line that
     is being laid runs; once both are laid nothing out here asks for one */
  if (D.act === 2 && D.sight > 0 && (!D.stn[0].laid || !D.stn[1].laid)) busy = 1;
  if (wantS !== D.sWant) { D.sWant = wantS; D.s0 = now; D.sight0 = D.sight; }
  if (D.sight !== wantS) {
    var w = Math.min(1, (now - D.s0) / (wantS ? 300 : 600));
    D.sight = wantS ? D.sight0 + (1 - D.sight0) * w : D.sight0 * (1 - w);
    if (w < 1) busy = 1; else D.sight = wantS;
  }
  /* ==== D2 (1): THE DRIFT. The lattice spirals into the throat on the
     clock and nothing else — one angle, handed to the shader, a turn in
     about three quarters of a minute.
     REVIEW 2026-09-19: AT THE DISPLAY'S OWN RATE. D2 asked for a frame
     four times a second on a timer, to keep a permanent loop off the
     sheet; Tommy: "it feels like it has a very low frame rate, is that
     deliberate?" It was, and it read as a fault. So while the funnel is
     anywhere in the view — and only then — every frame asks for the
     next, which is a full redraw of the sheet per display frame for as
     long as the pit is on screen (`Room.perf(60)` is the price on a
     given GPU). The tab's rAF stops on its own when the tab is hidden,
     and the loop ends the moment the view leaves the pit. `?drift=<ms>`
     keeps the timer at that cadence, for the comparison. ==== */
  D.spin = now * 0.00014;
  if (S.view) {
    var v = S.view, near = D.cx + D.r > v.x && D.cx - D.r < v.x + v.w &&
                           D.cy + D.r > v.y && D.cy - D.r < v.y + v.h;
    if (near) {
      if (DRIFT_MS > 0) {
        if (!D.spinT) D.spinT = setTimeout(function () { D.spinT = 0; invalidate(); }, DRIFT_MS);
      } else invalidate();
    }
  }
  /* AL1: the lines, their crossing and the band they run through — worked
     out here and nowhere else, and only while there are lines at all */
  if (D.act === 2 || D.act === 3) doomLines();
  if (busy) invalidate();
  return D;
}
/* ==== AL1: THE LINES AND WHERE THEY CROSS ==============================
   A line is shown for a station once it has been laid, and for the
   station the instrument stands on while it is being laid. Each runs from
   its station out to the paper's east reach (SL2 ended the old one ON the
   pit's centre, which gave the answer away; a line now has to be seen
   landing) — AL2: to the pan's own east fence, doomEastEnd.

   D.L[i] is { sx, sy, b (radians), ux, uy, len } or null. D.X is the
   crossing when both lines are shown and they meet east of the front on
   the paper: the centre-lines' crossing (x, y), the diamond the two
   wedges make (four corners, west first, going round), its east-west
   extent (lo, hi) and the dates that extent covers. Null when they
   diverge, meet behind the front or off the paper, or the diamond is
   open (the wedges are wider than the angle between the lines). */
/* ==== AL2 (2026-09-26): THE LINES RUN ON EAST. Tommy: "the lines only
   draw to mid-october 2030? shouldn't they draw further so you can
   crossover after the pit? does the eastward direction just draw into
   infinity?" and "you say the paper ends but i can pan east for years past
   2030 with a paper effect". It does not end: on a flat sheet the ground's
   rim never discards (a = max(rim, 1 - gone), and gone is 0 untilted), so
   the paper runs as far as the pan does — clampView's east fence, the
   record's east end plus VIEW_RUN plate-widths. A line now runs to that
   meridian (doomEastEnd), so a crossing well past the pit is drawn and can
   be panned to. North and south it goes no further than AL1's line did at
   any bearing (the old length), which keeps the fence's band (yLo..yHi)
   what it was. `lip` is how far out the line meets the pit's east lip: the
   line in the air comes down to the lip's height there and runs on level
   (doomAir), so a long line does not hang high over the pit. ==== */
function doomEastEnd() {
  var P = S.fld.plate;
  return P.x + P.w * (1 + VIEW_RUN);
}
function doomLine(i) {
  var D = S.doom, st = D.stn[i];
  /* AL3 (2026-09-27): in act two BOTH lines are shown from the first
     frame, laid or not. Tommy: "we should have both lines drawn
     immediately, not just the selected one so you can instantly see there
     are 2 lines and know to click on the other triangle after." So the
     crossing, and its dates by the limb, come up as soon as one line is
     turned across the other. */
  if (!st || !(st.laid || D.act === 2)) return null;
  var c = D.cells[st.i], b = st.brg * Math.PI / 180, ux = Math.cos(b), uy = Math.sin(b);
  var old = Math.max(C.cw, D.cx + 1.6 * D.r - c[0]);
  var len = Math.min((doomEastEnd() - c[0]) / Math.max(ux, 1e-6), Math.abs(uy) > 1e-9 ? old / Math.abs(uy) : Infinity);
  len = Math.max(old, len);
  var lip = Math.min(len, Math.max(C.cw, (D.cx + D.r * 1.05 - c[0]) / Math.max(ux, 1e-6)));
  return { sx: c[0], sy: c[1], b: b, ux: ux, uy: uy, len: len, lip: lip };
}
/* where two rays from (ax, ay) at angle ta and (bx, by) at angle tb meet,
   as [x, y, run along a, run along b], or null if they never do ahead */
function doomMeet(ax, ay, ta, bx, by, tb) {
  var ux = Math.cos(ta), uy = Math.sin(ta), vx = Math.cos(tb), vy = Math.sin(tb);
  var den = ux * vy - uy * vx;
  if (Math.abs(den) < 1e-12) return null;
  var wx = bx - ax, wy = by - ay;
  var ta_ = (wx * vy - wy * vx) / den, tb_ = (wx * uy - wy * ux) / den;
  if (!(ta_ > 0) || !(tb_ > 0)) return null;
  return [ax + ux * ta_, ay + uy * ta_, ta_, tb_];
}
function doomLines() {
  var D = S.doom, f = S.fld;
  var L0 = doomLine(0), L1 = doomLine(1);
  D.L = [L0, L1];
  /* the band of y the lines run through, for the fence (clampView) */
  var lo = Infinity, hi = -Infinity;
  [L0, L1].forEach(function (L) {
    if (!L) return;
    var ey = L.sy + L.uy * L.len;
    lo = Math.min(lo, L.sy, ey); hi = Math.max(hi, L.sy, ey);
  });
  D.yLo = lo; D.yHi = hi;
  if (L0) L0.hit = doomRowHit(L0);
  if (L1) L1.hit = doomRowHit(L1);
  D.X = null;
  if (!L0 || !L1) return;
  var m = doomMeet(L0.sx, L0.sy, L0.b, L1.sx, L1.sy, L1.b);
  var east = f.front.east;
  if (!m || m[0] <= east || m[2] > L0.len || m[3] > L1.len) return;
  var p = DOOM_PREC * Math.PI / 180;
  /* the diamond: north edge-line and south edge-line, taken round —
     (n+ s-) the west corner, (n+ s+), (n- s+) the east, (n- s-) */
  var pairs = [[p, -p], [p, p], [-p, p], [-p, -p]], poly = [], xlo = Infinity, xhi = -Infinity, k;
  for (k = 0; k < 4; k++) {
    var q = doomMeet(L0.sx, L0.sy, L0.b + pairs[k][0], L1.sx, L1.sy, L1.b + pairs[k][1]);
    if (!q || q[0] <= east || q[2] > L0.len || q[3] > L1.len) return;
    poly.push(q[0], q[1]);
    xlo = Math.min(xlo, q[0]); xhi = Math.max(xhi, q[0]);
  }
  D.X = { x: m[0], y: m[1], poly: poly, lo: xlo, hi: xhi,
          from: doomXISO(xlo), to: doomXISO(xhi), at: doomXISO(m[0]) };
  D.X.text = doomSpan(D.X.from, D.X.to);
}
/* ==== AL6 (2026-09-27): EACH LINE'S OWN DATE. Tommy: "maybe the date
   should be what date each line is hitting, rather than the crossover?"
   The pit sits on the middle row of the front's week (D.cy), so the day a
   line reaches that row is the day that line forecasts, and a line
   aimed true reads the pit's own day whatever the other line does. One
   day off the centre-line, lettered like a mark's date ("4 JUL 2030"):
   the wedge's own span there is half a year wide (the lines cross the row
   at a degree or two), which says nothing a turn of the limb can be
   judged by, and the day moves with the smallest turn. Null when the
   line runs level or turns away from the row (both start due east, so
   neither has a day until it is turned). The red diamond and its success
   test are unchanged; the success card still gives the crossing's span
   (pit.card_line_2). ==== */
function doomRowHit(L) {
  var D = S.doom, uy = Math.sin(L.b);
  if (Math.abs(uy) < 1e-9) return null;
  var t = (D.cy - L.sy) / uy;
  if (!(t >= 0 && t <= L.len)) return null;
  var x = L.sx + Math.cos(L.b) * t, at = doomXISO(x);
  return { x: x, at: at, text: +at.slice(8, 10) + ' ' + MONTHS[+at.slice(5, 7) - 1].slice(0, 3) + ' ' + at.slice(0, 4) };
}
/* a map x as the day it falls on, YYYY-MM-DD — the same column arithmetic
   doomInit put the pit on mid-2030 with */
function doomXISO(x) {
  var f = S.fld;
  var ms = f.days.epochMs + ((x - C.px) / C.cw) * 7 * DAY_MS_D;
  return new Date(ms).toISOString().slice(0, 10);
}
/* the crossing's dates as the sheet letters them: "May – Sep 2030", the
   year said once when both ends share it (the template is pit.fix) */
var DOOM_FIX_T = RoomCopy.t('pit.fix');
var DOOM_NEVER = RoomCopy.t('pit.never');
function doomSpan(a, b) {
  function mon(iso) { return MONTHS[+iso.slice(5, 7) - 1].slice(0, 3); }
  var ya = a.slice(0, 4), yb = b.slice(0, 4);
  if (ya === yb && a.slice(5, 7) === b.slice(5, 7)) return mon(b) + ' ' + yb;
  return DOOM_FIX_T.replace('{from}', ya === yb ? mon(a) : mon(a) + ' ' + ya)
                   .replace('{to}', mon(b) + ' ' + yb);
}
/* is the crossing on the throat? AL2: the pit's centre inside the red
   diamond as it is drawn — D.X.poly, the four corners doomLines worked out
   and doomSightDraw washes — and nothing else. A point is inside a convex
   quadrilateral when it is on the same side of all four edges. Needs both
   lines LAID. */
function doomOnThroat() {
  var D = S.doom;
  if (!D.stn[0].laid || !D.stn[1].laid) return false;
  doomLines();
  return doomInDiamond(D.cx, D.cy);
}
function doomInDiamond(x, y) {
  var X = S.doom.X;
  if (!X || !X.poly || X.poly.length !== 8) return false;
  var q = X.poly, k, neg = 0, pos = 0;
  for (k = 0; k < 4; k++) {
    var ax = q[2 * k], ay = q[2 * k + 1], bx = q[(2 * k + 2) % 8], by = q[(2 * k + 3) % 8];
    var cr = (bx - ax) * (y - ay) - (by - ay) * (x - ax);
    if (cr < 0) neg = 1; else if (cr > 0) pos = 1;
  }
  return !(neg && pos);
}
/* the cell a MAP POINT falls in, as (week, row) — the inverse of
   doomCell, and the hit test for the arrows. It is the cell grid and
   not the record, so it answers north of row 0 and south of row 6 too,
   which is what act two's turn arrows need. */
function doomCellAt(m) {
  if (!m) return null;
  var w = Math.floor((m.x - C.px) / C.cw);
  var r = Math.floor((m.y - C.py) / C.ch) - C.EDGE_ROWS;
  return (w < 0 || r < -1 || r > 7) ? null : [w, r];
}
function doomRow(n) {
  var w = Math.floor(n / 7), d = n - w * 7;
  return [w, (w & 1) ? 6 - d : d];
}
/* which of the arrows the hand is over: 'me' on the surveyor's own
   ground, 'N'/'S'/'E'/'W' on one of them, 0 anywhere else */
function doomHit(m) {
  var D = S.doom;
  if (!D.on || !m) return 0;
  /* ==== D2 (4): ACT TWO IS THE INSTRUMENT AND NOTHING ELSE. Tommy:
     "having the same arrows for walking and alignment is very confusing.
     Your arrow has just suddenly changed meaning unexpectedly." So the
     chevrons are gone the moment the seventh day is paced.
     AL1: and what is under the hand out here is, in this order, the OTHER
     STATION's mark ('stn': a press walks the instrument there) and the
     LIMB ('L0'/'L1', the line of the station it stands on), turned by the
     hand (doomGrab). AL2: the lines themselves are not handles any more.
     All found on the glass, where they are drawn. */
  if (D.act === 2) return doomHitAim(m) || 0;
  /* ==== FR1 (1): ACT ONE'S CHEVRONS ARE FOUND ON THE GLASS. Tommy,
     2026-09-21: "The arrows for pacing the frontier are quite tricky to
     use if you've panned to a particular angle." Everything below this is
     in MAP fractions — the cell's own outer third, and the whole of a
     neighbouring cell — and the map is not the glass: turn the sheet and
     the paper's north is no longer the screen's up, so the third the hand
     is over is not the third under the chevron it is aiming at; tilt it
     and the cell foreshortens until a third of it is a few device rows
     deep. The chevrons are sprites at known map points, so they are found
     where they are DRAWN — each one projected through the sheet's own
     `toScreen`, and the nearest within CHEV_GRAB css px of the hand is
     that chevron. The map rules stay underneath, so nothing the walk
     could do before it is any harder now, and this one answers first
     because it is the one the reader is aiming at.

     AND THE SURVEYOR KEEPS HIS OWN GROUND. A chevron takes only what is
     NEARER TO IT THAN TO THE FIGURE — measured on the glass, the same
     projection — so the middle of the cell is still the cell however far
     the pose squashes it. Without that rule a steep tilt, which can put
     the whole cell inside twenty css pixels, gives every point in it to
     a chevron and the day under the party can no longer be taken. ==== */
  var dirs4 = ['N', 'S', 'E', 'W'], j, near = 0, nd = CHEV_GRAB * CHEV_GRAB;
  var ptr = (S.day && S.day.toScreen) ? S.day.toScreen(m.x, m.y) : null;
  var cS = ptr ? S.day.toScreen(D.cells[D.at][0], D.cells[D.at][1]) : null;
  if (ptr && cS) {
    var mx = ptr[0] - cS[0], my = ptr[1] - cS[1], own = mx * mx + my * my;
    for (j = 0; j < 4; j++) {
      if (!doomStep(D.days[D.at], dirs4[j])) continue;
      var q = doomArrowScr(dirs4[j]); if (!q) continue;     /* PA2: where it is drawn */
      var qx = q.x - ptr[0], qy = q.y - ptr[1], q2 = qx * qx + qy * qy;
      if (q2 < nd && q2 < own) { nd = q2; near = dirs4[j]; }
    }
    if (near) return near;
  }
  var c = doomCellAt(m);
  if (!c) return 0;
  var wr = doomRow(D.days[D.at]);
  if (c[0] === wr[0] && c[1] === wr[1]) {
    /* REVIEW 2026-09-19, Tommy: "The buttons are quite fiddly … often I
       end up clicking to select the cell instead." The chevrons are drawn
       on the shared cell edge, so half of every chevron lies in the
       party's own cell. The outer third of that cell, toward a step that
       exists, is the arrow too. */
    var fx = (m.x - C.px) / C.cw - c[0], fy = (m.y - C.py) / C.ch - C.EDGE_ROWS - c[1];
    var edge = fy < 0.34 ? 'N' : (fy > 0.66 ? 'S' : (fx < 0.34 ? 'W' : (fx > 0.66 ? 'E' : 0)));
    if (edge && doomStep(D.days[D.at], edge)) return edge;
    return 'me';
  }
  var dirs = ['N', 'S', 'E', 'W'], i;
  for (i = 0; i < dirs.length; i++) {
    var n2 = doomStep(D.days[D.at], dirs[i]);
    if (!n2) continue;
    var w2 = doomRow(n2);
    if (w2[0] === c[0] && w2[1] === c[1]) return dirs[i];
  }
  return 0;
}
/* AL1: the act-two hit test, on the glass. `m` is the map point under
   the hand; everything is compared in css px through toScreen. */
function doomHitAim(m) {
  var D = S.doom, ts = S.day && S.day.toScreen;
  if (!ts || !m || !D.stn.length) return 0;
  var p = ts(m.x, m.y);
  if (!p) return 0;
  function d2(a) { return a ? (a[0] - p[0]) * (a[0] - p[0]) + (a[1] - p[1]) * (a[1] - p[1]) : Infinity; }
  var o = D.stn[1 - D.si], oc = D.cells[o.i];
  if (d2(ts(oc[0], oc[1])) < STN_GRAB * STN_GRAB) return 'stn';
  /* AL2: the limb, and only the limb — a line is no longer taken by the
     hand anywhere along it ("dial-only") */
  var g = doomDial();
  if (g && (p[0] - g.cx) * (p[0] - g.cx) + (p[1] - g.cy) * (p[1] - g.cy) < DIAL_FINE * DIAL_FINE) return 'L' + D.si;
  return 0;
}
/* a point `run` map units out along line Ln (at angle offset `da`
   radians off its bearing, for the wedge's edges), in CLIP space, at the
   height of a straight line through the air from the station's ground to
   the pit's lip at the line's far end — clip space is linear in the
   height at a fixed point, so the blend of the two readings is the point
   on that line */
function doomAir(Ln, run, da) {
  var b = Ln.b + (da || 0);
  return doomAirAt(Ln, Ln.sx + Math.cos(b) * run, Ln.sy + Math.sin(b) * run, run);
}
/* the same, for a map point (x, y) `run` out from the station — AL2: the
   height comes down to the lip's at Ln.lip and holds there */
function doomAirAt(Ln, x, y, run) {
  var D = S.doom, dd = S.day, t = Math.min(1, run / Math.max(1, Ln.lip));
  var P = dd.toClip(x, y, 0, Ln.sx, Ln.sy), Q = dd.toClip(x, y, 0, D.cx + D.r * 1.05, D.cy);
  return [P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t, P[2] + (Q[2] - P[2]) * t, P[3] + (Q[3] - P[3]) * t];
}
function doomHover(m) {
  var D = S.doom;
  if (!D.on || D.act === 3) { if (D.hov) { D.hov = 0; invalidate(); } return; }
  var h = doomHit(m);
  if (h !== D.hov) { D.hov = h; invalidate(); }
}
/* ==== WM2 — THE TWO ACTS OF THE PIT, AS TWO FUNCTIONS ================
   The party's whole business out east is two acts and they were both
   written inside the pointer handler, which is the one place a pair of
   hands can reach and an agent cannot. WM2 (2026-09-22) lifts them out
   WORD FOR WORD — `doomPace` is the act-one step, and act two is now
   AL1's `doomWalk` / `doomSet` — and the handlers call them, so there is
   ONE copy of each act. What is published (`Room.doomStep`,
   `Room.doomBearing`) goes through the same functions, which is what
   makes an agent's pacing the same pacing: the mark is struck by the act,
   not by the door the act came through.
   ==================================================================== */
/* ACT ONE: the party stands on the day. THE STEP IS WHAT LEVELS THE
   GROUND, so the day is surveyed the moment the party stands on it and
   the first step is seen to raise it. The day the party STARTED on —
   today, the front — is not levelled until it is stepped back onto:
   today's ground is rough because it is being surveyed, and the survey is
   not done with it. Seven levelled days end act one. */
function doomPace(n2) {
  var D = S.doom;
  var i = D.days.indexOf(n2);
  if (i < 0) return false;
  D.at = i;
  if (!D.paced[i]) D.paced[i] = performance.now();
  var all = 1, k;
  for (k = 0; k < 7; k++) if (!D.paced[k]) all = 0;
  /* AL1: and the instrument goes up on the NORTH station — the front's
     top row — with its line due east and nothing laid.
     AL10 (2026-09-27): not due east now. Tommy: "the 'top' wheel has the
     date but the 'bottom' wheel doesn't now, it's weird?" A due-east line
     never reaches the pit's row, so it has no date (AL6) until turned.
     Both lines start aimed at the row CRANK_YEARS past the front, crossing
     each other there, well east of the pit: each has a date from the
     first frame and a turn is a year from the first turn (AL9). */
  if (all) {
    D.act = 2; D.s0 = performance.now();
    D.stn[0].brg = doomStartBrg(0); D.stn[0].laid = 0; D.stn[1].brg = doomStartBrg(1); D.stn[1].laid = 0;
    D.stn[0].wh = 0; D.stn[1].wh = 0;
    D.si = 0; D.at = D.stn[0].i; D.drag = null;
  }
  return true;
}
/* ==== AL1: ACT TWO — THE INSTRUMENT IS WALKED, AIMED AND LAID ==========
   Four functions, and every door goes through them: the hand (doomPress,
   doomGrab/doomAim/doomLetGo from the pointer handlers) and the agent
   (`Room.doomBearing`). The mark is struck by the act, not by the door. */
/* the instrument (and the surveyor with it) to station i */
function doomWalk(i) {
  var D = S.doom;
  if (D.act !== 2 || (i !== 0 && i !== 1)) return false;
  D.si = i; D.at = D.stn[i].i;
  invalidate();
  return true;
}
/* station i's bearing set to `deg` (degrees, y down), held within
   DOOM_TURN of east; `lay` lays it and asks whether the pit is fixed */
function doomSet(i, deg, lay) {
  var D = S.doom;
  if (D.act !== 2) return false;
  var st = D.stn[i];
  st.brg = Math.max(-DOOM_TURN, Math.min(DOOM_TURN, deg));
  st.laid = lay ? 1 : 0;
  invalidate();
  if (lay && doomOnThroat()) doomFixed();
  return true;
}
/* THE PIT IS FIXED: both lines laid and crossing on the throat. The mark
   is struck here, once; the view is flown to the pit and tilted, and the
   pit stands up (and the card comes up, the first time) only when the
   view has arrived. */
function doomFixed() {
  var D = S.doom;
  doomLines();
  D.act = 3; D.t0 = 0; D.t = -1; D.s0 = performance.now();
  D.sight0 = D.sight; D.hov = 0; D.drag = null;
  /* RS1: the aligned pit is kept, by its two bearings (doomRestore) */
  if (window.RoomState) RoomState.set('pit', { brg: [D.stn[0].brg, D.stn[1].brg] });
  var first = false;
  try { first = !!(window.Room && Room.progress && Room.progress.mark && Room.progress.mark('doom')); }
  catch (e) { }
  /* D2 (5): THE FIRST TIME ONLY, the record says what has happened.
     Tommy, 2026-09-19: "when you align it successfully a card should
     pop-up like on the voyage to tell you what's happened". `mark`
     answers true once, on the strike that first put it on paper, so
     a reload that finds the pit already stood up says nothing. */
  D.card = first;
  doomFly();
}
/* ==== AL1: THE VIEW GOES TO THE PIT. Tommy: "refocus the screen on the
   pit and position the card such that you can see the pit change from
   down to up", tilted into 3D. The pan, the zoom, the tilt and the turn
   are eased together (the home move's cubic, the zoom taken
   geometrically). The view is as wide as the sheet allows out there and
   as far north as the lines' corridor lets it go (the fence clamps it),
   which puts the pit right of centre, a little over a third of the way
   down the glass, with the summit's room above it and the card in the
   top-left corner clear of both. A hand on the glass during the flight
   ends it where it is (doomLand). ==== */
function doomFly() {
  var D = S.doom;
  cancelHome();
  doomFlyStop();
  var w1 = Math.max(minViewW(), Math.min(maxViewW(), 7.4 * D.r));
  var h1 = w1 * viewRatio();
  var to = { x: D.cx - 0.12 * w1, y: D.cy - 0.4 * h1, w: w1,
             tilt: Math.min(DOOM_TILT * DEG, tiltCap()), yaw: 0 };
  var yaw0 = S.yaw - Math.round(S.yaw / (2 * Math.PI)) * 2 * Math.PI;
  var from = { x: S.view.x + S.view.w / 2, y: S.view.y + S.view.h / 2, w: S.view.w,
               tilt: S.tilt, yaw: yaw0 };
  var ratio = to.w / from.w, t0 = performance.now();
  S.yaw = yaw0;
  function step() {
    var e = Math.max(0, Math.min(1, (performance.now() - t0) / DOOM_FLY_MS));
    e = e < 0.5 ? 4 * e * e * e : 1 - Math.pow(-2 * e + 2, 3) / 2;
    S.view.w = from.w * Math.pow(ratio, e); S.view.h = S.view.w * viewRatio();
    S.view.x = (from.x + (to.x - from.x) * e) - S.view.w / 2;
    S.view.y = (from.y + (to.y - from.y) * e) - S.view.h / 2;
    S.tilt = from.tilt + (to.tilt - from.tilt) * e;
    S.yaw = from.yaw + (to.yaw - from.yaw) * e;
    if (window.Sky) Sky.setView(S.yaw / DEG);
    if (e >= 1) { D.fly = 0; Room.centreOn(to.x, to.y, to.w); doomLand(); return; }
    clampView(); invalidate();
    D.fly = requestAnimationFrame(step);
  }
  D.fly = requestAnimationFrame(step);
}
function doomFlyStop() {
  var D = S.doom;
  if (D.fly) { cancelAnimationFrame(D.fly); D.fly = 0; return true; }
  return false;
}
/* arrived (or stopped by a hand): the pit stands up from here, and the
   card is put in the corner of the glass, clear of it */
function doomLand() {
  var D = S.doom;
  if (D.act !== 3 || D.t0) return;
  D.t0 = performance.now();
  queueHash();
  if (D.card) { D.card = false; doomCard(); }
  invalidate();
}
/* ---- AL1: THE HAND ON A LINE ------------------------------------------
   `doomGrab` is asked on every press on the glass in act two, before the
   pan takes it: if the press is on the limb, the line is in the hand and
   the answer is true. `doomAim` turns it with the hand. `doomLetGo` lays
   it.
   AL2 (2026-09-26): THE LIMB ONLY, AND A VERNIER BY REACH. The hand's
   travel ROUND the limb's centre on the glass turns the line: each move's
   change of angle there is turned into bearing through the index's own
   projection (doomPhi), so within DIAL_FINE css px the index stays under
   the finger at any tilt or turn, a hand carried straight out does not
   turn it at all, and beyond DIAL_FINE the turn is times
   (DIAL_FINE / reach)^2. So the same move turns the line a quarter as much at twice
   the reach and a hundredth as much at ten times: ~1 degree a css px on
   the limb, ~0.016 degrees at 230 px out. It is the dial's own arm — the
   further out along it the hand takes it, the finer it goes — and the arm
   is drawn out to the hand while it is beyond the limb. `cx, cy` are the
   hand on the glass, css px.
   AL7 (2026-09-27): no vernier now — the limb is a wheel, cranked; AL9:
   a whole turn of the hand round it moves the line's date a year
   (doomCrank). */
/* the limb's index angle on the glass for bearing `b` (radians) — the
   angle doomDial draws it at — and how fast it turns with the bearing */
function doomPhi(c, o, b) {
  /* AL4: level, at the station's own height, as doomDial draws it */
  var q = S.day.toScreen(c[0] + Math.cos(b) * 3 * C.cw, c[1] + Math.sin(b) * 3 * C.cw, c[0], c[1]);
  return q ? Math.atan2(q[1] - o[1], q[0] - o[0]) : null;
}
function doomGrab(m, cx, cy) {
  var D = S.doom;
  if (!D.on || D.act !== 2 || !m) return false;
  var h = doomHit(m);
  if (h !== 'L' + D.si) return false;
  var G = doomDial();
  if (!G || !isFinite(cx) || !isFinite(cy)) return false;
  var i = D.si, st = D.stn[i];
  D.drag = { i: i, th: Math.atan2(cy - G.cy, cx - G.cx), brg: st.brg };
  st.laid = 0;
  D.hov = h;
  invalidate();
  return true;
}
function doomAim(m, cx, cy) {
  var D = S.doom, g = D.drag;
  if (!g || D.act !== 2 || !isFinite(cx) || !isFinite(cy)) return;
  var G = doomDial();
  if (!G) return;
  var reach = Math.hypot(cx - G.cx, cy - G.cy);
  invalidate();
  if (reach < 4) return;               /* on the pivot: no angle to read */
  var th = Math.atan2(cy - G.cy, cx - G.cx);
  var dth = Math.atan2(Math.sin(th - g.th), Math.cos(th - g.th));
  g.th = th;
  /* AL7: the wheel spins with the hand; the line turns doomCrank (AL9) a whole
     turn, the way the glass shows it turning — the sign of d(index
     angle)/d(bearing) through the projection the index is drawn with, so
     the hand cranked clockwise turns the line clockwise at any tilt or
     turn */
  var st = D.stn[g.i];
  st.wh = (st.wh || 0) + dth;
  var c = D.cells[D.stn[g.i].i], e = 0.25 * Math.PI / 180, b = g.brg * Math.PI / 180;
  var p0 = doomPhi(c, G.o, b - e), p1 = doomPhi(c, G.o, b + e);
  if (p0 === null || p1 === null) return;
  var k = Math.atan2(Math.sin(p1 - p0), Math.cos(p1 - p0));
  g.brg = Math.max(-DOOM_TURN, Math.min(DOOM_TURN, g.brg + (k < 0 ? -1 : 1) * dth / (2 * Math.PI) * doomCrank(g.i, g.brg)));
  doomSet(g.i, g.brg, false);
}
/* AL9: degrees of bearing for one whole turn of the wheel at station i's
   present bearing `deg`: the turn that moves the day the line reaches the
   pit's row by a year, or out past CRANK_YEARS (or with no such day) the
   turn that brings a due-east line in to CRANK_YEARS */
function doomCrank(i, deg) {
  var D = S.doom, c = D.cells[D.stn[i].i], dy = Math.abs(D.cy - c[1]);
  var year = 365.25 / 7 * C.cw, far = Math.max(C.cw, S.fld.front.east + CRANK_YEARS * year - c[0]);
  var farDeg = Math.atan2(dy, far) * 180 / Math.PI;
  if (!(dy > 0)) return farDeg;
  var b = deg * Math.PI / 180, uy = Math.sin(b);
  if (Math.abs(uy) < 1e-9 || (D.cy - c[1]) / uy <= 0) return farDeg;
  var run = (D.cy - c[1]) / Math.tan(b);
  if (!(run > 0) || run > far) return farDeg;
  return Math.min(CRANK_MAX, year * uy * uy / dy * 180 / Math.PI);
}
/* AL10: the bearing, degrees, that lands station i's line on the pit's
   row CRANK_YEARS past the front */
function doomStartBrg(i) {
  var D = S.doom, c = D.cells[D.stn[i].i];
  var far = Math.max(C.cw, S.fld.front.east + CRANK_YEARS * 365.25 / 7 * C.cw - c[0]);
  return Math.atan2(D.cy - c[1], far) * 180 / Math.PI;
}
function doomLetGo() {
  var D = S.doom, g = D.drag;
  if (!g) return false;
  D.drag = null;
  if (D.act === 2) doomSet(g.i, D.stn[g.i].brg, true);
  return true;
}
/* a press that did not travel. Act one: an arrow round the surveyor, and
   the party moves. Act two: the other station's mark, and the instrument
   walks there. Returns true when the ground took it. */
function doomPress(m) {
  var D = S.doom;
  if (!D.on || D.act === 3) return false;
  var h = doomHit(m);
  if (!h || h === 'me') return false;
  if (D.act === 1) {
    var n2 = doomStep(D.days[D.at], h);
    if (!n2) return false;
    if (!doomPace(n2)) return false;
  } else {
    if (h !== 'stn') return false;
    doomWalk(1 - D.si);
  }
  invalidate();
  return true;
}
/* ---- WM2: AND THE SAME GROUND, SAID IN DAYS --------------------------
   The party's own reading, for anything that is not a pointer: which
   seven days the front is, which are levelled, which one the party is
   standing on, and — in act two (AL1) — the two stations with their map
   points and bearings, the pit's centre, and where the lines cross with
   the dates lettered there. It is the whole of what a pair of eyes can
   see out there and not one thing more: the bearing that would bear on
   the throat is NOT given — the eyes have to work it out from the map,
   and so does an agent. */
function doomISO(n) {
  var f = S.fld;
  if (!f || !f.days || !f.days.epochMs) return null;
  return new Date(f.days.epochMs + n * DAY_MS_D).toISOString().slice(0, 10);
}
function doomDay(iso) {
  var f = S.fld;
  if (!f || !f.days || !f.days.epochMs) return NaN;
  var t = Date.parse(String(iso) + 'T00:00:00Z');
  if (!isFinite(t)) return NaN;
  return Math.round((t - f.days.epochMs) / DAY_MS_D);
}
/* ==== AND IT IS NOT CALLED `Room.doom` =================================
   `Room` IS the engine's state object (`window.Room = S`) and `S.doom` is
   the pit's own state, so a method of that name REPLACES the pit — the
   same trap `Room.centre` carries a note about, and it is silent: the
   draw throws `D.paced[0]` of undefined on every frame from the first one
   and the picture stops at `setCommon`. Measured on this box, 2026-09-22.
   The published reading is `Room.pit()`. ============================== */
Room.pit = function () {
  var D = S.doom, i;
  if (!D || !D.ready) return { ready: false, on: false };
  var days = [], paced = [];
  for (i = 0; i < D.days.length; i++) {
    days.push(doomISO(D.days[i]));
    paced.push(!!D.paced[i]);
  }
  var out = { ready: true, on: !!D.on, act: D.act, at: doomISO(D.days[D.at]),
              days: days, paced: paced,
              levelled: paced.filter(Boolean).length, of: days.length,
              centre: [+D.cx.toFixed(1), +D.cy.toFixed(1)], reach: +D.r.toFixed(1),
              precision: DOOM_PREC, done: D.act === 3 };
  if (D.act >= 2) {
    /* AL1: the two stations, the one the instrument is on, each bearing
       (degrees, y down: positive swings south) and whether it is laid,
       and the crossing when there is one */
    out.stations = D.stn.map(function (st, k) {
      var c = D.cells[st.i];
      return { name: st.name, day: doomISO(D.days[st.i]), at: [+c[0].toFixed(1), +c[1].toFixed(1)],
               bearing: +st.brg.toFixed(4), laid: !!st.laid, instrument: D.act === 2 && D.si === k,
               hits: null };
    });
    doomLines();
    /* AL6: the day each line reaches the pit's row, as lettered */
    out.stations.forEach(function (o, k) {
      var H = D.L && D.L[k] && D.L[k].hit;
      o.hits = H ? { date: H.at, lettered: H.text } : null;
    });
    var X = D.X;
    out.crossing = X ? { at: [+X.x.toFixed(1), +X.y.toFixed(1)], date: X.at,
                         from: X.from, to: X.to, lettered: X.text } : null;
  }
  return out;
};
/* the same step a chevron takes, asked for by the day it lands on. It is
   ONE step: the party paces, it does not jump, so a day that is not four
   ways adjacent to the one under its feet is refused. */
Room.doomStep = function (dayISO) {
  var D = S.doom;
  if (!D || !D.on || D.act !== 1) return false;
  var n2 = doomDay(dayISO);
  if (!isFinite(n2)) return false;
  var dirs = ['N', 'S', 'E', 'W'], i, ok = false;
  for (i = 0; i < dirs.length; i++) if (doomStep(D.days[D.at], dirs[i]) === n2) { ok = true; break; }
  if (!ok) return false;
  if (!doomPace(n2)) return false;
  invalidate();
  return true;
};
/* AL1: and the same line a hand turns on its limb and lets go, asked for by station
   and bearing: the instrument walks to that station (`north` or `south`)
   and the line is laid at `deg` degrees (y down, so positive swings
   south; held within DOOM_TURN of east). Laying the second line on the
   throat fixes the pit, exactly as letting go of it does. */
Room.doomBearing = function (station, deg) {
  var D = S.doom;
  if (!D || !D.on || D.act !== 2) return false;
  var nm = String(station || '').toLowerCase(), i = nm === 'north' || nm === 'n' ? 0 : nm === 'south' || nm === 's' ? 1 : -1;
  var d = +deg;
  if (i < 0 || deg === '' || deg === null || deg === undefined || !isFinite(d)) return false;
  D.drag = null;
  doomWalk(i);
  return doomSet(i, d, true);
};
/* the card the record lays down when the bearing is answered: the same
   physical card the voyage presents when she crosses a mark's meridian
   (voyage.js `presentFeature`), put down the same way — the button, or
   the escape — and nothing here to resume when it is. Its date is the
   day the party is standing on, which is the only day the pit has. */
var DOOM_CARD_HEAD = RoomCopy.t('pit.card_head');
var DOOM_CARD_LINE = RoomCopy.t('pit.card_line');
function doomCard() {
  var D = S.doom, f = S.fld;
  if (!(window.Room && Room.cardPresent) || !f || !f.days || !f.days.epochMs) return false;
  /* AL1: the card's date is where the two lines crossed, and its second
     line names the span lettered there; it is held in the corner of the
     glass so the pit can be seen standing up beside it */
  doomLines();
  var X = D.X;
  var date = X ? X.at : new Date(f.days.epochMs + D.days[D.at] * DAY_MS_D).toISOString().slice(0, 10);
  try {
    return Room.cardPresent({ date: date, title: DOOM_CARD_HEAD, story: DOOM_CARD_LINE,
                              detail: RoomCopy.t('pit.card_line_2', { span: X ? X.text : '' }),
                              discovery: true, at: 'corner' });
  } catch (e) { return false; }
}
/* the figure, and the four arrows round it while the hand is there */
var PIT_INK = '#6b6156', PIT_HOT = '#96322a', PIT_PAPER = '#f7efdd';
function chevPainter(th, hot) {
  return function (g) {
    g.rotate(th);
    g.beginPath(); g.moveTo(-2.1, -4.6); g.lineTo(3.4, 0); g.lineTo(-2.1, 4.6);
    g.globalAlpha = 0.75; g.strokeStyle = PIT_PAPER; g.lineWidth = 3.2; g.stroke();
    g.globalAlpha = 1; g.strokeStyle = hot ? PIT_HOT : PIT_INK; g.lineWidth = hot ? 1.5 : 1.25; g.stroke();
  };
}
function dialPainter(G, hot) {
  return function (g) {
    var cx = 0, cy = -DIAL_UP, R = DIAL_R, w = G.wh || 0, i;
    var col = hot ? PIT_HOT : PIT_INK;
    function strokeTwice(path, w, c) {
      g.globalAlpha = 0.7; g.strokeStyle = PIT_PAPER; g.lineWidth = w + 2.6; path(); g.stroke();
      g.globalAlpha = 1; g.strokeStyle = c; g.lineWidth = w; path(); g.stroke();
    }
    /* AL7: the wheel — a rim graduated all round, three spokes to a hub
       and a knob on the rim to crank it by, all turned to the wheel's own
       angle `w` (the hand's, one for one); a turn moves the line's date a
       year (AL9, doomCrank) */
    strokeTwice(function () {
      g.beginPath(); g.arc(cx, cy, R, 0, 2 * Math.PI, false);
      for (i = 0; i < 24; i++) {
        var t = w + i * Math.PI / 12, o = i % 2 ? 3 : 5;
        g.moveTo(cx + Math.cos(t) * R, cy + Math.sin(t) * R);
        g.lineTo(cx + Math.cos(t) * (R - o), cy + Math.sin(t) * (R - o));
      }
      for (i = 0; i < 3; i++) {
        var u = w + i * 2 * Math.PI / 3;
        g.moveTo(cx + Math.cos(u) * 5, cy + Math.sin(u) * 5);
        g.lineTo(cx + Math.cos(u) * (R - 6), cy + Math.sin(u) * (R - 6));
      }
      g.moveTo(cx + 5, cy); g.arc(cx, cy, 5, 0, 2 * Math.PI, false);
    }, hot ? 1.5 : 1.1, col);
    g.globalAlpha = 1; g.fillStyle = col;
    g.beginPath(); g.arc(cx + Math.cos(w) * R, cy + Math.sin(w) * R, hot ? 4 : 3.4, 0, 2 * Math.PI, false);
    g.strokeStyle = PIT_PAPER; g.lineWidth = 1.6; g.stroke(); g.fill();
    /* the index on the limb, where the line of sight crosses it */
    strokeTwice(function () {
      g.beginPath();
      g.moveTo(cx + Math.cos(G.phi) * (R - 9), cy + Math.sin(G.phi) * (R - 9));
      g.lineTo(cx + Math.cos(G.phi) * (R + 7), cy + Math.sin(G.phi) * (R + 7));
    }, 1.6, col);
  };
}
/* AL1: the other station's mark, stood on its day; a press walks there.
   AL3 (2026-09-27): not a trig point any more but THE INSTRUMENT, the red
   level on its tripod the surveyor works (marks.js `camp`), without the
   figure. Tommy: "shouldn't we also use the red alignment machine icon
   instead of the triangle?" Heavier under the hand. */
function stnPainter(hot) {
  return function (g) {
    var w = hot ? 1.55 : 1.15;
    function path(d) { return new Path2D(d); }
    var legs = path('M-5.4 7.2L0 -8.1L5.4 7.2M0 -8.1V7.2'), brace = path('M-3.2 1.4L3.2 1.4');
    var box = path('M-4.5 -12.1H4.5V-8.6H-4.5Z'), barrel = path('M4.5 -10.35H7.6');
    g.lineJoin = 'round'; g.lineCap = 'round';
    g.globalAlpha = 0.75; g.strokeStyle = PIT_PAPER; g.lineWidth = w + 2.2;
    [legs, brace, box, barrel].forEach(function (p) { g.stroke(p); });
    g.globalAlpha = 1; g.strokeStyle = PIT_HOT;
    g.lineWidth = w; g.stroke(legs); g.stroke(barrel); g.stroke(box);
    g.lineWidth = w * 0.8; g.stroke(brace);
    g.fillStyle = PIT_PAPER; g.fill(box); g.lineWidth = w; g.stroke(box);
  };
}
/* AL1: the crossing's dates, lettered in the year figures' own hand
   (lettering.js KIND.yearFig: Goudy, 12 px, tracked a tenth, the
   graticule's sepia, a paper halo), set in capitals like the dates of the
   marks. AL2: lettered BY THE LIMB, at (ox, oy) css px off the station on
   the glass, aligned away from it (`al`: 'left', 'right' or 'center'). */
function fixPainter(text, ox, oy, al) {
  return function (g) {
    g.font = '12px "Sorts Mill Goudy LN", "Sorts Mill Goudy", Georgia, serif';
    try { g.letterSpacing = '1.2px'; } catch (e) { }
    g.textAlign = al; g.textBaseline = 'middle';
    var t = String(text).toUpperCase();
    g.globalAlpha = 0.85; g.strokeStyle = PIT_PAPER; g.lineWidth = 3; g.lineJoin = 'round';
    g.strokeText(t, ox, oy);
    g.globalAlpha = 1; g.fillStyle = '#6a4d2c'; g.fillText(t, ox, oy);
  };
}
/* ==== AL2 (2026-09-26): THE DATES WHERE THE EYE IS. Tommy: "the
   crossover dates are fun but they're written on the location they
   crossover which isn't that helpful as you still need to pan to find them
   and you can already tell the rough date by the location on the map." So
   the span is lettered beside the limb of the station being aimed, and
   changes as the line turns; the red diamond stays where the lines cross.
   With both lines laid it stays by the limb, which is on the station last
   aimed or walked to. It sits across the limb's own line on the glass, on
   the downhill side of the screen, clear of the arc and the figure. ==== */
function doomFixAt(G, r, up) {
  var nx = -Math.sin(G.phi), ny = Math.cos(G.phi);
  if (ny < 0 || (ny === 0 && nx > 0)) { nx = -nx; ny = -ny; }
  if (r === undefined) { r = DIAL_R + 14; up = DIAL_UP; }
  var ox = nx * r, oy = -up + ny * r;
  var al = nx < -0.5 ? 'right' : nx > 0.5 ? 'left' : 'center';
  return [Math.round(ox), Math.round(oy), al];
}
function doomDraw() {
  var D = S.doom;
  if (!D.on || !S.marks || !S.marks.drawDyn) return;
  var c = D.cells[D.at];
  doomSightDraw();
  if ((D.act === 2 || D.act === 3) && S.marks.drawArt && D.stn.length) {
    /* PH1: act two's limb, an upright plate on the station */
    if (D.act === 2) {
      var G = doomDial();
      if (G) {
        var hot = D.hov === 'L' + D.si || (D.drag && D.drag.i === D.si) ? 1 : 0;
        G.wh = D.stn[D.si].wh || 0;
        var art = [Math.round(G.phi * 180 / Math.PI), Math.round(G.wh * 180 / Math.PI) % 360, hot].join(',');
        S.marks.drawArt('pit-dial', art, DIAL_BOX, dialPainter(G, hot), c[0], c[1]);
      }
    }
    /* AL1: the other station, marked */
    var oc = D.cells[D.stn[1 - D.si].i], sh = D.act === 2 && D.hov === 'stn';
    S.marks.drawArt('pit-stn', sh ? 'h' : 'n', STN_BOX, stnPainter(sh), oc[0], oc[1]);
    /* AL2: and the crossing's dates, by the limb (doomFixAt). AL6: each
       line's own date instead — the aimed line's by its limb, the other's
       beside the red instrument, across that line on the glass */
    var Ls = D.L || [], Ha = Ls[D.si] && Ls[D.si].hit, Hb = Ls[1 - D.si] && Ls[1 - D.si].hit;
    /* AL10: a line that never reaches the row says so, in place of a date */
    if (Ls[D.si] && !Ha) Ha = { text: DOOM_NEVER };
    if (Ls[1 - D.si] && !Hb) Hb = { text: DOOM_NEVER };
    var G2 = D.act === 2 && Ha ? doomDial() : null;
    if (D.act === 2 && Hb) {
      var ob = doomGlassAng(oc, D.stn[1 - D.si].brg * Math.PI / 180);
      if (ob !== null) {
        var fb = doomFixAt({ phi: ob }, 20, 3);
        S.marks.drawArt('pit-fix2', Hb.text + '|' + fb.join(','), FIX_BOX, fixPainter(Hb.text, fb[0], fb[1], fb[2]),
                        oc[0], oc[1], 0, null, true);
      }
    }
    if (G2) {
      var fa = doomFixAt(G2);
      /* AL5 (2026-09-27): drawn over the ground. Tommy, at
         #1397,431,2120,34,114: "the alignment date cuts through the
         ground" — the plate stood at the station's depth and is lettered
         on the downhill side of the glass, which is the ground NEARER the
         eye, so that ground hid it. It is a label; it is read on top. */
      S.marks.drawArt('pit-fix', Ha.text + '|' + fa.join(','), FIX_BOX, fixPainter(Ha.text, fa[0], fa[1], fa[2]),
                      D.cells[D.stn[D.si].i][0], D.cells[D.stn[D.si].i][1], 0, null, true);
    }
  }
  S.marks.drawDyn(D.act === 1 ? 'camp' : 'camp2', c[0], c[1]);
  /* D2 (4): the chevrons are ACT ONE's and only act one's */
  if (!D.hov || D.act !== 1 || !S.marks.drawArt) return;
  var dirs = ['N', 'S', 'E', 'W'], i;
  for (i = 0; i < dirs.length; i++) {
    var d = dirs[i];
    if (!doomStep(D.days[D.at], d)) continue;
    var sa = doomArrowScr(d), th = doomArrowAng(d);
    if (!sa) continue;
    /* PA2: anchored on the surveyor's cell, carried out on the glass */
    S.marks.drawArt('pit-chev' + d, Math.round(th * 180 / Math.PI) + (D.hov === d ? 'h' : ''),   /* PA1: a degree, not three: it stepped as the sheet turned */
                    CHEV_BOX, chevPainter(th, D.hov === d), c[0], c[1], 0,
                    [Math.round(sa.off[0] * 2) / 2, Math.round(sa.off[1] * 2) / 2]);
  }
}
/* ==== PH1: THE LINE OF SIGHT, IN THE AIR. From the level, a few css px
   out along the pointer, to the paper's reach past the pit at the height
   of the pit's LIP: a straight line through space, as a sight is, so a
   tilted sheet shows it running out over the scarp and the downs instead
   of lying down their faces. Built on the CPU in clip space (day.js
   toClip) — cut at the camera's plane, widened on the glass to a fixed
   pencil width — and drawn by the smallest program there is, over the
   ground and under the figure.
   AL1: TWO LINES NOW, EACH A THIN WEDGE. Round each centre-line its
   precision (DOOM_PREC) is a faint fill with a hairline either side; the
   centre-line is dashed and running while it is being laid, solid once
   laid, red while the hand is on it. Where the two wedges cross the
   diamond is washed and ruled in the revision red. No cross-hair: the
   throat is found on the map. ==== */
var SIGHT_VS = `#version 300 es
layout(location = 0) in vec4 aP;
layout(location = 1) in vec2 aL;
out vec2 vL;
void main(){ vL = aL; gl_Position = aP; }`;
var SIGHT_FS = `#version 300 es
precision highp float;
in vec2 vL; out vec4 o;
uniform vec4 uSightI;   /* alpha, dash period (css px, 0 solid), core and halo half-widths; core < 0 is a flat wash */
uniform float uSightF;  /* LF1: how far the dashes have run toward the pit, css px */
uniform vec3 uSightC;   /* AL1: the ink */
void main(){
  if (uSightI.z < 0.0) { o = vec4(uSightC * uSightI.x, uSightI.x); return; }
  float y = abs(vL.y);
  float core = 1.0 - smoothstep(uSightI.z - 0.5, uSightI.z + 0.5, y);
  float halo = 1.0 - smoothstep(uSightI.w - 0.5, uSightI.w + 0.5, y);
  if (uSightI.y > 0.0) {
    float on = 1.0 - smoothstep(0.58, 0.64, fract((vL.x - uSightF) / uSightI.y));
    core *= on; halo *= on;
  }
  vec3 c = mix(vec3(0.969, 0.937, 0.867), uSightC, core);
  float a = max(core, halo * 0.5) * uSightI.x;
  o = vec4(c * a, a);
}`;
var SIGHT = { p: null, buf: null, vao: null, f: new Float32Array(6 * 240) };
var SIGHT_FLOW = 21;     /* LF1: css px a second the laying line's dashes run toward the pit */
var SIGHT_INK = [0.42, 0.38, 0.33], SIGHT_HOT = [0.588, 0.196, 0.165];
function doomSightDraw() {
  var D = S.doom, dd = S.day, L = S.layout, gl = S.gl;
  if (!(D.act === 2 || D.act === 3) || !(D.sight > 0.001) || !dd || !dd.toClip || !dd.toScreen || !L || !D.L) return;
  if (!SIGHT.p) SIGHT.p = prog(gl, SIGHT_VS, SIGHT_FS, 'pit-sight');
  var p = SIGHT.p;
  if (!p.u) return;                  /* still linking; the poller asks for the next frame */
  var EPS = 1e-3, E = 3.2, F = SIGHT.f, n = 0, batches = [], pr = DOOM_PREC * Math.PI / 180;
  function px(P) { return [(P[0] / P[3] * 0.5 + 0.5) * L.cw, (0.5 - P[1] / P[3] * 0.5) * L.ch]; }
  function vert(P, ox, oy, al, ac) {
    if (n + 6 > F.length) return;
    F[n++] = P[0] + (2 * ox / L.cw) * P[3]; F[n++] = P[1] + (-2 * oy / L.ch) * P[3];
    F[n++] = P[2]; F[n++] = P[3]; F[n++] = al; F[n++] = ac;
  }
  function batch(I, ink) { batches.push({ at: n / 6, I: I, ink: ink }); }
  function close() { var b = batches[batches.length - 1]; b.n = n / 6 - b.at; }
  /* a pencil line from P to Q on the glass, P in front of the camera; Q
     is cut back to the camera's plane when it is behind it */
  /* AL2: either end may be behind the camera now (a line bends at the
     pit's lip, and the bend can be behind a turned view); `off` carries a
     dash on round the bend; returns the length drawn on the glass */
  function seg(P0, Q0, off) {
    var P = P0.slice(), Q = Q0.slice(), k, t;
    off = off || 0;
    if (!(P[3] > EPS) && !(Q[3] > EPS)) return 0;
    if (!(Q[3] > EPS)) { t = (P[3] - EPS) / (P[3] - Q[3]); for (k = 0; k < 4; k++) Q[k] = P[k] + (Q[k] - P[k]) * t; }
    if (!(P[3] > EPS)) { t = (Q[3] - EPS) / (Q[3] - P[3]); for (k = 0; k < 4; k++) P[k] = Q[k] + (P[k] - Q[k]) * t; }
    var sa = px(P), sb = px(Q), ux = sb[0] - sa[0], uy = sb[1] - sa[1], len = Math.sqrt(ux * ux + uy * uy);
    if (len < 0.5) return 0;
    var nx = -uy / len * E, ny = ux / len * E;
    vert(P, -nx, -ny, off, -E); vert(P, nx, ny, off, E); vert(Q, -nx, -ny, off + len, -E);
    vert(P, nx, ny, off, E); vert(Q, nx, ny, off + len, E); vert(Q, -nx, -ny, off + len, -E);
    return len;
  }
  function lift(P, ox, oy) { P[0] += (2 * ox / L.cw) * P[3]; P[1] += (-2 * oy / L.ch) * P[3]; return P; }
  var lines = [], i;
  for (i = 0; i < 2; i++) {
    var Ln = D.L[i];
    if (!Ln) continue;
    var A = doomAir(Ln, 0);
    if (!(A[3] > EPS)) continue;
    /* the start: on the instrument's station the level, 8 px out along
       the line as the screen shows it; on the other, the trig's top */
    var ox = 0, oy = -8;
    if (D.act === 2 && i === D.si) {
      var o = dd.toScreen(Ln.sx, Ln.sy), q = dd.toScreen(Ln.sx + Ln.ux * 3 * C.cw, Ln.sy + Ln.uy * 3 * C.cw);
      if (o && q) { var phi = Math.atan2(q[1] - o[1], q[0] - o[0]); ox = Math.cos(phi) * 8; oy = -DIAL_UP + Math.sin(phi) * 8; }
    }
    lift(A, ox, oy);
    lines.push({ i: i, A: A, E: doomAir(Ln, Ln.len), Ep: doomAir(Ln, Ln.len, pr), Em: doomAir(Ln, Ln.len, -pr),
                 /* AL2: and where it comes down to the lip's height */
                 M: doomAir(Ln, Ln.lip), Mp: doomAir(Ln, Ln.lip, pr), Mm: doomAir(Ln, Ln.lip, -pr),
                 hot: D.act === 2 && (D.hov === 'L' + i || (D.drag && D.drag.i === i)),
                 laying: D.act === 2 && !D.stn[i].laid });
  }
  /* 1. the wedges' wash: a triangle each, straight in clip space (the GPU
     cuts a triangle at the camera's plane itself) */
  lines.forEach(function (l) {
    batch([0.11 * D.sight, 0, -1, 0], l.hot ? SIGHT_HOT : SIGHT_INK);
    vert(l.A, 0, 0, 0, 0); vert(l.Mp, 0, 0, 0, 0); vert(l.Mm, 0, 0, 0, 0);
    vert(l.Mp, 0, 0, 0, 0); vert(l.Ep, 0, 0, 0, 0); vert(l.Em, 0, 0, 0, 0);
    vert(l.Mp, 0, 0, 0, 0); vert(l.Em, 0, 0, 0, 0); vert(l.Mm, 0, 0, 0, 0);
    close();
  });
  /* 2. (the diamond's wash, in the revision red: not drawn since AL8.
     Tommy: "i find it confusing". Each line carries its own date now
     (AL6); the diamond is still worked out, as the success test) */
  /* 3. the wedges' two hairlines */
  lines.forEach(function (l) {
    batch([0.6 * D.sight, 0, 0.3, 1.1], l.hot ? SIGHT_HOT : SIGHT_INK);
    seg(l.A, l.Mp); seg(l.Mp, l.Ep); seg(l.A, l.Mm); seg(l.Mm, l.Em);
    close();
  });
  /* 4. (the diamond's rule: gone with its wash, AL8) */
  /* 5. the centre-lines: dashed and running while being laid (LF1), solid
     once laid */
  lines.forEach(function (l) {
    batch([0.9 * D.sight, l.laying ? 14 : 0, 0.75, 2.1], l.hot ? SIGHT_HOT : SIGHT_INK);
    seg(l.M, l.E, seg(l.A, l.M));
    close();
  });
  /* 6. (AL2's vernier arm, out to the hand: gone with the vernier, AL7) */
  if (!n) return;
  if (!SIGHT.buf) {
    SIGHT.buf = gl.createBuffer(); SIGHT.vao = gl.createVertexArray();
    gl.bindVertexArray(SIGHT.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, SIGHT.buf);
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 4, gl.FLOAT, false, 24, 0);
    gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 24, 16);
    gl.bindVertexArray(null);
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, SIGHT.buf);
  gl.bufferData(gl.ARRAY_BUFFER, F.subarray(0, n), gl.DYNAMIC_DRAW);
  var depth = gl.isEnabled(gl.DEPTH_TEST);
  gl.disable(gl.DEPTH_TEST);
  gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.depthMask(false);
  gl.useProgram(p);
  gl.bindVertexArray(SIGHT.vao);
  /* LF1 (2026-09-26): the dashes RUN toward the pit while the bearing is
     being laid, SIGHT_FLOW css px a second; solid once it bears, it holds.
     Tommy: "Are we able to make the line move rather than be stationary,
     so it's flowing towards the pit or something?" */
  if (p.u.uSightF) gl.uniform1f(p.u.uSightF, (performance.now() / 1000 * SIGHT_FLOW) % 14);
  batches.forEach(function (b) {
    if (!b.n) return;
    gl.uniform4f(p.u.uSightI, b.I[0], b.I[1], b.I[2], b.I[3]);
    if (p.u.uSightC) gl.uniform3f(p.u.uSightC, b.ink[0], b.ink[1], b.ink[2]);
    gl.drawArrays(gl.TRIANGLES, b.at, b.n);
  });
  gl.bindVertexArray(null);
  gl.depthMask(true); gl.disable(gl.BLEND);
  if (depth) gl.enable(gl.DEPTH_TEST);
}
/* ==== D2: THE HARNESS'S OWN HANDLE, and nothing on the sheet reads it.
   The review round's smoke test walks the seven days and works the
   instrument through the engine's own functions instead of guessing where
   a chevron landed on the glass. `window.Room` is `S` (line 163), so this
   is one object on the public surface and no second path into the state. */
S.__doom = { hit: doomHit, hover: doomHover, press: doomPress, tick: doomTick,
             init: doomInit, cell: doomCell, limbR: limbR,
             /* FR1 (1): where a chevron is printed and how big, so the
                smoke can walk up to one on the GLASS at any attitude
                instead of guessing at a fraction of a cell */
             arrowAt: doomArrowAt, arrowK: doomArrowK,
             /* PH1: the limb on the glass, for the same smoke */
             dial: doomDial,
             /* AL1: a line's ends on the glass, and the hand's three moves */
             grab: doomGrab, aim: doomAim, letGo: doomLetGo, inDiamond: doomInDiamond,
             onThroat: doomOnThroat, lines: doomLines };

/* =================================================================
   LP2 — THE BAKE

   One program, one quad, one draw per change: the terra/mould chain
   written into an offscreen texture over the FIELD'S OWN RECTANGLE, so a
   consumer reads it with `uvOf(s)` — the very function it already uses
   for the bed — and needs no second mapping.

   THE RESOLUTION IS TWICE THE FIELD'S in each axis. The field is a
   2.5 x 3.55 map-unit lattice here (C.cw/ux/upx by C.ch/uy), so the bake
   is 1.25 x 1.77, and the downs' own two lobes are 464 and 202 map units
   across — two hundred samples to a lobe, which is why a bilinear read
   does not soften them. What it does soften is the mask's own hard
   edges: the 0.8-unit backstop past the scarp's foot (which `mouldMaskT`
   never reaches, terraW being 1 there) and the conjecture's row step in
   the forty units west of the front. Both come back as a ramp of about
   one texel instead of a step, which at the fit is about a pixel.

   THE FORMAT IS RG16F — r is terraOf, g is mouldMask — which WebGL2
   filters natively and which `EXT_color_buffer_float` makes renderable.
   Half float carries the mask (0..1) to about 5e-4 and the downs' bed to
   about 1e-3 of a contour interval, a fifth of a map unit of contour
   walk and well under a pixel. Two channels and not four because
   `mouldMaskT` is four instructions on top of the mask: 35 MB on this
   record rather than 69.

   WHEN IT RE-DRAWS. Measured headless: a camera move of any kind does
   not re-bake — the key is a string compare and nothing else happens.
   The funnel standing up (uDoom.w easing through act three) and a day
   being paced (uPace) DO, once per frame for the length of the ease,
   because both are terms of terraOf. That is one extra full-rectangle
   pass of the chain per frame while those two animations run and
   nothing at rest. The rect could be scissored to the funnel's disc and
   the seven pads if it ever shows on a frame budget; it is left whole
   here because a whole rect cannot leave a stale texel and a scissored
   one can.

   AND IT IS CAPPED AT MAX_TEXTURE_SIZE. Five years of record is 4,104
   lattice columns, so twice it is 8,208 — over the 8,192 a modest device
   allows, and the cap simply makes the x density 1.996 instead of 2. The
   mapping is the inverse of `uvOf` at whatever size the texture is, so
   nothing about it depends on the resolution being a round multiple.

   THE UNIT IS 5, and it is bound once a frame and left. Unit 0 is the
   field, 1 the furniture, 2 the ramp, 3 every module's own atlas (the
   marks' families, the lettering's, the rose, the ruler, the rug's
   weave, the books' print) and 4 the books' cover; nothing in the room
   asks for a fifth.
   ================================================================= */
var BAKE_UNIT = 5;
/* set to an array by ensureBake alone: while it is one, setCommon
   collects the values the bake depends on into it, at the one place they
   are worked out, so the key can never drift from the uniforms. */
var BAKE_KEY = null;
var BAKE_VS = `#version 300 es
#undef BAKED
precision highp float;
layout(location = 0) in vec2 a;
void main(){ gl_Position = vec4(a*2.0 - 1.0, 0.0, 1.0); }`;
var BAKE_FS_SRC = null;
function BAKE_FS() {
  return `#version 300 es
#undef BAKED
precision highp float; precision highp sampler2D;
out vec4 o;
uniform vec2 uBakeN;    // the bake's own size in texels
` + FIELD_GLSL + FRONT_GLSL + `
void main(){
  /* uvOf, inverted. gl_FragCoord is the texel's CENTRE, which is the
     point uvOf names, so a consumer reading uvOf(s) reads back exactly
     what was written for s — at any resolution. */
  vec2 uv = gl_FragCoord.xy / uBakeN;
  vec2 s = uLat.xy + (uv*uN - uMarg - 0.5) * uLat.zw;
  /* the target is RG16F; the last two components are dropped */
  o = vec4(terraOf(s), mouldMask(s), 0.0, 0.0);
}`;
}
/* Draw it, if anything it reads has moved since the last one. Called at
   the top of draw(), before the viewport and the clear, so it owns the
   whole of the GL state it touches and hands back nothing draw() does
   not set for itself on the next line. */
function ensureBake(gl) {
  var f = S.fld;
  if (!BAKE_ON || !f || f.stub || !S.layout || !f.W || !f.H) return;
  var p = prog(gl, BAKE_VS, BAKE_FS_SRC || (BAKE_FS_SRC = BAKE_FS()), 'bake');
  if (!p.u) return;                  /* still LINK_ONLY; the next draw has it */
  S.pBake = p;
  var mx = S.maxTex || 4096;
  var w = Math.min(f.W * 2, mx), h = Math.min(f.H * 2, mx);
  gl.useProgram(p);
  BAKE_KEY = [w, h];
  setCommon(gl, p);
  /* PF2: the funnel is not baked — terraOf adds it outside the texture,
     which does not reach the pit (field.js) */
  if (p.u.uDoom) gl.uniform4f(p.u.uDoom, 0, 0, 0, 0);
  var key = BAKE_KEY.join(','); BAKE_KEY = null;
  if (S.bakeW !== w || S.bakeH !== h) {
    /* X6 (c): the last one's texture and framebuffer, through the ledger */
    disposeGL('bake');
    owned('bake', function () {
      S.bakeTex = gl.createTexture(); S.bakeFbo = gl.createFramebuffer();
    });
    gl.activeTexture(gl.TEXTURE0 + BAKE_UNIT);
    gl.bindTexture(gl.TEXTURE_2D, S.bakeTex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RG16F, w, h);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindFramebuffer(gl.FRAMEBUFFER, S.bakeFbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, S.bakeTex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    S.bakeW = w; S.bakeH = h; S.bakeKey = null;
  }
  /* the texture lives on its own unit for the whole frame; setCommon only
     has to tell each program which unit that is */
  gl.activeTexture(gl.TEXTURE0 + BAKE_UNIT);
  gl.bindTexture(gl.TEXTURE_2D, S.bakeTex);
  gl.activeTexture(gl.TEXTURE0);
  if (key === S.bakeKey) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, S.bakeFbo);
  gl.viewport(0, 0, w, h);
  gl.disable(gl.DEPTH_TEST); gl.disable(gl.STENCIL_TEST);
  gl.disable(gl.SCISSOR_TEST); gl.disable(gl.BLEND); gl.disable(gl.CULL_FACE);
  gl.uniform2f(p.u.uBakeN, w, h);
  gl.bindVertexArray(S.vaoQuad);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.bindVertexArray(null);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  S.bakeKey = key;
  S.bakes = (S.bakes || 0) + 1;
}

function setCommon(gl, p) {
  var f = S.fld, L = S.layout;
  function u1(n, a){ if (p.u[n]) gl.uniform1f(p.u[n], a); }
  function u2(n, a, b){ if (p.u[n]) gl.uniform2f(p.u[n], a, b); }
  function u3(n, a, b, c){ if (p.u[n]) gl.uniform3f(p.u[n], a, b, c); }
  function u4(n, a, b, c, d){ if (p.u[n]) gl.uniform4f(p.u[n], a, b, c, d); }
  u4('uLat', f.lat.X0, f.lat.Y0, f.lat.DX, f.lat.DY);
  u4('uLat0', f.lat.DX0 || f.lat.DX, f.lat.DY0 || f.lat.DY, 0, 0);
  u2('uMarg', f.lat.MX || 0, f.lat.MY || 0);
  u2('uN', f.W, f.H);
  u4('uFront', f.front.a0, f.front.e0, f.front.e1, f.front.fade);
  u4('uFrontC', f.front.c0, f.front.c1,
     Math.min(f.front.pA, f.front.pB), Math.max(f.front.pA, f.front.pB));
  u2('uFrontK', f.front.none ? 0 : (f.front.all ? 2 : 1), f.front.east);
  /* ==== R35: THE MOULD IS THE MARGIN'S RECT NOW, not the record's. The
     26-unit skirt (survey-map.js bedAt) brought the ground to the table AT
     THE NEAT LINE because the ground ENDED there; the ground does not end
     there any more — the field carries the record's own continuation out to
     `margin.run` and eases it to the datum over R1's two runs, which is a
     better place to do it (it is a property of the ground, not of a
     shader). So the skirt moves out to the margin's own edge, where the
     field is already at the datum and it has nothing left to do. Inside the
     record every term of mouldMask is 1 either way: the fit cannot move.
     The east is untouched — that is the survey FRONT, and its cut is
     uMouldF.x. ==== */
  /* R41: the west gate's run, in map units (field.js BEY.OPEN_SEA x a
     plate-height). 0 turns it off — `?seamat=0`, the pair. */
  u1('uWestSea', WEST_SEA ? ((f.margin && f.margin.openSea) || 0) : 0);
  var MRUN = (f.margin && f.margin.run) || 0;
  u4('uMould', f.front.west - MRUN, f.front.east, f.front.c0 - MRUN, f.front.c1 + MRUN);
  /* surveyedRow: turned ? (u >= fr+1) : (u <= fr)  — survey-map.js:2125 */
  var rowEdge = C.py + (f.front.turned ? (f.front.row + 1) : f.front.row) * C.ch;
  u4('uMouldF', f.front.e1, rowEdge, f.front.turned ? 1 : 0, C.CONJECTURE_KEEP);
  /* R35: the cut's own x and the run the ground falls over east of it */
  u2('uScarp', f.front.none ? f.front.west : (f.front.all ? f.front.east : f.front.e1),
     (f.margin && f.margin.scarp) || 0);
  /* ==== R40: THE DOWNS EAST OF THE CUT — amp, mean, 1/lobe1, 1/lobe2.
     The two amplitudes are in CONTOUR INTERVALS of the plate's index
     generation (8 x TINT_STEP = sqrt(5)) and the two lobes in
     plate-heights, so the downs are sized by the sheet and not by a
     number somebody liked. amp 0 (`?downs=0`) is the dead plane this
     item replaced, for the pair. ==== */
  var DPA = f.front.c1 - f.front.c0;
  u4('uTerra', DOWNS_AMP * TSTEP, DOWNS_MEAN * TSTEP,
     1 / (F.TERRA.LOBE1 * DPA), 1 / (F.TERRA.LOBE2 * DPA));
  /* ==== D1: the pit east of the front, the days the party has paced and
     the instrument's ray. doomTick is the one place any of the three is
     worked out, and it hands back zeros until there is a record to stand
     on — so a program that never receives them draws what it drew. ==== */
  var DM = doomTick();
  u4('uDoom', DM.cx, DM.cy, DM.on ? DM.r : 0, DM.rise * DM.t);
  /* D2: and the pad's own gate is OFF until a day is actually paced —
     every term it feeds is 0 until then, and the rule's seven-cell loop
     and its derivatives are a cost the record should not pay to draw
     nothing. */
  u2('uPaceK', DM.on && DM.any ? C.cw / 2 : 0, C.ch / 2);
  if (p.u['uPace[0]']) gl.uniform4fv(p.u['uPace[0]'], DM.pace);
  /* D2 (1): the drift's angle, 1/depth (so the shader's own -1..+1 reads
     pit to peak), the ink, and the lattice's period — one cell of the
     record, which is the square the whole sheet is ruled in.
     ==== PT1 2026-09-20: AND THE DRIFT TAKES THE SIGN OF THE RISE. Tommy:
     "it's kind of interesting that the good and bad singularity looks
     identical from flat on ... we can keep the shape for the flat pit but
     maybe we inverse the rotation direction?" At the plan there is no
     relief to tell the two apart, so the HANDEDNESS tells them: the pit
     whirls the way it always did and the peak whirls the other way.
     The sign is worked out HERE and not in the shader because DM.t is the
     signed rise already normalised (uDoom.w is rise x t), so -t is exactly
     -1 or +1 at either end and the pit's angle comes out bit for bit the
     number it was. It is the whole of t and not its sign, so as the
     instrument eases t from -1 to +1 the whirl unwinds through straight
     and winds up the other way instead of snapping round. Handed in with
     a negative rise (the harness's static flip) the two agree, because
     what both read is the sign of rise x t. ==== */
  var dsgn = DM.rise ? -DM.t * (DM.rise < 0 ? -1 : 1) : 0;
  /* PT1: 1/|rise|, so uDoom.w x this is the signed rise itself — the dark
     throat and the lit spire follow uDoom.w's own sign, as D1 says they
     do, whichever side the rise was handed in on. Identical to the bit for
     the engine's own path, where the rise is always positive. */
  u4('uDoomK', DM.spin * dsgn, DM.rise ? 1 / Math.abs(DM.rise) : 0,
     DM.on ? 1 : 0, C.cw);
  /* ==== LP2: THE BAKE'S KEY, TAKEN HERE AND NOWHERE ELSE.
     Everything `terraOf`, `mouldMask` and `mouldMaskT` read is on this
     list — the lattice and its margin, the front, the mould's rect, the
     scarp, the west gate, the downs' two numbers, the funnel and the
     seven pads. `ensureBake` hands in the array, calls this function and
     compares what comes back with the last bake's; a value written above
     that is not collected below could only go wrong silently, which is
     why the collection sits with the writes and not in a second copy of
     the expressions. uFlat, uRelief and the camera are NOT in it: the
     rise is applied outside terraOf and the chain knows nothing of the
     view. ==== */
  if (BAKE_KEY) {
    BAKE_KEY.push(f.lat.X0, f.lat.Y0, f.lat.DX, f.lat.DY, f.W, f.H,
      f.lat.MX || 0, f.lat.MY || 0,
      WEST_SEA ? ((f.margin && f.margin.openSea) || 0) : 0,
      f.front.a0, f.front.e0, f.front.e1, f.front.fade,
      f.front.none ? 0 : (f.front.all ? 2 : 1), f.front.east,
      f.front.west - MRUN, f.front.c0 - MRUN, f.front.c1 + MRUN,
      rowEdge, f.front.turned ? 1 : 0, C.CONJECTURE_KEEP,
      f.front.none ? f.front.west : (f.front.all ? f.front.east : f.front.e1),
      (f.margin && f.margin.scarp) || 0,
      DOWNS_AMP * TSTEP, DOWNS_MEAN * TSTEP, DPA,
      DM.cx, DM.cy, DM.on ? DM.r : 0, DM.rise * DM.t,
      DM.on && DM.any ? C.cw / 2 : 0, C.ch / 2);
    for (var bki = 0; bki < DM.pace.length; bki++) BAKE_KEY.push(DM.pace[bki]);
  }
  /* D2 (2): how far east the paper has to carry */
  u1('uEastK', DM.on ? DM.eastK : 1);
  /* ==== R46: THE OPEN SEA'S FLOOR AND THE SWELL ON IT (`?west=`).
     uSeabed is (amp, mean, 1/lobe1, 1/lobe2) in BED and MAP units, the
     lobes in plate-heights as the downs' are; uSeabed2 carries the third
     lobe and the WEST GATE — the surveyed start and R3 (c)'s OPEN_SEA
     run, the same run the field's own west crossfade uses, so the two
     turn together and nothing at or east of January 2022 can move.
     uSwell is the water plane's ink and is read by water.js alone.
     Every one of them is 0 unless ?west= asked for it. ==== */
  var SB = F.SEABED, WQ = F.WEST, ORUN = (f.margin && f.margin.openSea) || 0;
  u4('uSeabed', WQ.bed ? WQ.amp : 0, WQ.mean,
     1 / (SB.LOBE1 * DPA), 1 / (SB.LOBE2 * DPA));
  u4('uSeabed2', 1 / (SB.LOBE3 * DPA), f.front.west, ORUN, 0);
  u4('uSwell', WQ.swell ? WQ.sp : 0, 1 / SB.OPEN, SB.WID, SB.OP);
  u4('uSwell2', SB.A1, 2 * Math.PI / SB.L1, SB.A2, 2 * Math.PI / SB.L2);
  /* X14 (g): the voyage's ground (field.js voyVec) — the fairway's banks
     from the field's own constants, the marks' foul ground from
     voyage.json when voyage.js hands it over. Gated as the seabed is. */
  if (p.u.uVoyBank || p.u.uVoyFoul) {
    var VG = F.voyGround;
    u4('uVoyBank', VG.rise > 0 ? VG.fair : 0, VG.run, VG.rise, (f.front.c0 + f.front.c1) / 2);
    if (p.u.uVoyFoul) {
      /* OB1 (2026-09-21): the number of slots is FIELD.JS'S, not this
         file's. It was eight here and eight there, written twice, and the
         two unnamed dangers of the long legs needed more of them; a
         second literal is a second place for them to disagree, and the
         one that would go quietly wrong is this one (the shader would
         simply never see the fourteenth dome). `RoomField.VOY.FOULN` is
         the one number now. */
      var FN = (F.VOY && F.VOY.FOULN) || 8;
      if (!S.voyFoulBuf || S.voyFoulBuf.length !== FN * 4) S.voyFoulBuf = new Float32Array(FN * 4);
      var VF = VG.foul || [], vn = Math.min(VF.length, FN);
      for (var vi = 0; vi < FN; vi++) {
        var vo = vi < vn ? VF[vi] : null;
        S.voyFoulBuf[vi * 4] = vo ? vo[0] : 0;
        S.voyFoulBuf[vi * 4 + 1] = vo ? vo[1] : 0;
        S.voyFoulBuf[vi * 4 + 2] = vo ? vo[2] : 0;
        S.voyFoulBuf[vi * 4 + 3] = vo ? vo[3] : 0;
      }
      gl.uniform4fv(p.u.uVoyFoul, S.voyFoulBuf);
      u1('uVoyFoulN', vn);
    }
  }
  /* R36: the marker horizons, in map x. Cut once, when the field lands. */
  if (p.u.uHorzX) {
    /* cut once PER FIELD, not once per page: the first draws happen before
       the worker has landed anything, and a cache keyed on nothing would
       have pinned the empty list the blank sheet was drawn with. */
    if (S.horzFld !== f) {
      S.horzFld = f; S.horzX = new Float32Array(8);
      var HZ = f.horizonsX || [];
      S.horzN = Math.min(HZ.length, 8);
      for (var hzi = 0; hzi < S.horzN; hzi++) S.horzX[hzi] = HZ[hzi].x;
    }
    gl.uniform1fv(p.u.uHorzX, S.horzX);
  }
  u1('uHorzN', S.horzN || 0);
  u3('uSheet', L.ox, L.oy, L.k);
  /* R13 (b): the ground's own reach, for anything set on the ground */
  var GR = S.gRect;
  u4('uGReach', GR ? GR.x : 0, GR ? GR.y : 0, GR ? GR.w : 0, GR ? GR.h : 0);
  if (p.u.uVP) gl.uniformMatrix4fv(p.u.uVP, false, S.VP);
  /* FD2 (a): the paper the window's ink is printed on */
  if (window.RoomObjects && RoomObjects.paperInkUniforms)
    RoomObjects.paperInkUniforms(gl, p, S);
  u4('uView', S.view.x, S.view.y, S.view.w, S.view.h);
  /* the WINDOW, in sheet units — the portal rounded to the device grid
     and read back, so the map lands on exactly the rect 8102's portal is
     laid on and the film is measured over the same box */
  u4('uPlate', (S.portal.l - L.ox) / L.k, (S.portal.t - L.oy) / L.k,
     S.portal.w / L.k, S.portal.h / L.k);
  var rel = reliefOf(S.tilt), kk = S.portal.w / S.view.w / L.k;   // sheet units per map unit
  u3('uAtt', Math.cos(S.tilt), Math.sin(S.tilt), kk);
  u2('uYaw', Math.cos(S.yaw), Math.sin(S.yaw));
  /* relief.js updateCamera: the aim rises a third of the way up the
     highest ground, never by more than a fifth of the window's depth */
  /* ==== E7 (item 5): the aim-lift, settled against the sky and horizon.
     relief.js's figure is an ORTHOGRAPHIC one; under the perspective
     camera the raised ground is magnified toward the eye and the same
     lift left the picture ~25 px low (E1 §4). LIFT_K is the measured
     factor; ?lift=1 is relief.js's own number, for the pair. ==== */
  var lift = liftNow(S.view, rel);
  u1('uLift', lift * kk);
  /* R8: the ruler inverts mapToSheet on the CPU to find where a month's
     meridian cuts the neat line, and the aim-lift is part of that map.
     It is worked out here and nowhere else, so it is published rather
     than recomputed. */
  S.liftS = lift * kk * Math.sin(S.tilt);
  /* JG2: the camera's eye in MAP units and height — mapToSheet undone,
     for the ground fragment's seenField. Only the two ground passes that
     read a level declare it. */
  if (p.u.uEyeM && S.camEye) {
    var E = S.camEye, ctE = Math.cos(S.tilt), stE = Math.sin(S.tilt), cyE = Math.cos(S.yaw), syE = Math.sin(S.yaw);
    var rxE = (E[0] - L.ox) / L.k - ((S.portal.l - L.ox) / L.k + S.portal.w / L.k / 2);
    var vE = (-E[1] - L.oy) / L.k - ((S.portal.t - L.oy) / L.k + S.portal.h / L.k / 2) - S.liftS;
    var upE = E[2] / L.k, ryE = vE * ctE + upE * stE, zE = -vE * stE + upE * ctE;
    u3('uEyeM', S.view.x + S.view.w / 2 + (rxE * cyE + ryE * syE) / kk,
                S.view.y + S.view.h / 2 + (-rxE * syE + ryE * cyE) / kk, zE / kk);
  }
  u1('uRelief', rel);
  /* R4: the reveal's flattening, for every program that samples the field */
  u2('uFlat', 1 - (S.rise === undefined ? 1 : S.rise), f.COAST);
  /* LP2: which unit the baked chain is on. The texture itself was bound
     there by ensureBake at the top of the frame and nothing else in the
     room touches unit 5, so this is one integer and no rebinding. */
  if (p.u.uBake) gl.uniform1i(p.u.uBake, BAKE_UNIT);
}
function reliefOf(t) { var k = clamp(t / MAX_TILT, 0, 1); return 1 - (1 - k) * (1 - k); }
/* ==== THE AIM-LIFT, IN MAP UNITS, IN ONE PLACE.
   relief.js's rule is a figure of the FIELD (a third of the highest
   ground) capped by the WINDOW (a fifth of its depth), magnified by E7's
   measured LIFT_K; R33 (d), on by default since R42 (c), takes it off the
   GROUND UNDER THE HASH'S OWN POINT instead, because neither of the other
   two knows where the reader is looking — zoomed in the cap collapses (at
   zoom 235 the window is 60 map units deep, the cap allows 12 and the
   ground under the aim stands at 49) and the point the hash names walks
   off the top of the plate.

   R44 (b): it was written out FOUR times — setCommon, gridRect,
   portalOnMap and day.js's frame — and only two of them had R33's second
   rule in them. The two that did not are the two that SIZE THE GROUND
   MESH, so under the default aim the mesh was sized for a camera the
   picture was not drawn with. It is one function now, and day.js asks for
   it through the handle. ==== */
function aimLift(v, rel) {
  if (window.RoomDay && RoomDay.heightAt)
    return RoomDay.heightAt(v.x + v.w / 2, v.y + v.h / 2);
  return Math.min(0.34 * S.fld.maxHeight * EXAG * rel, 0.2 * v.h * rel)
       * (window.RoomWater ? RoomWater.LIFT_K : 1);
}
/* ==== THE AIM DOES NOT RIDE THE GROUND UNDER THE HAND.
   Tommy (2026-09-20): "if you start here: #1774,469,1469,53,-6 and then
   you drag slowly in a westerly direction, basically the verticality of
   the camera changes. As soon as Terra Incognita changes to the
   Highlands, it's like your screen rides up it, and then you come over
   the top of that peak. It comes back down the other side."

   aimLift above is R33 (d): the height of the ground under the view's
   CENTRE, so the point a hash names stays on the plate. That is the rule
   for a POSE. Read fresh on every frame of a drag it is the rule for
   whatever is passing under the centre, and the whole picture rode the
   Highlands with it (measured: 0 -> 147 -> 44 -> 84 -> 6 map units of
   aim across one westward drag, every step of it drawn). So the number
   the frame is drawn with is now a STATE, `S.liftCur`, that eases toward
   the target — and while the hand is down the target is HELD at the
   value it had when the hand went down: the camera's height belongs to
   the pose you stop at, not to what passes under you. On release the
   target is read again and the ease takes it there; the wheel reads it
   live and goes through the same ease; a hash, `Room.centreOn`,
   `Room.fit` and `Room.foldAt` are places, not gestures, and land on it
   at once (no ease from zero on load, none when the field arrives).

   One number, still (R44 (b)): setCommon, gridRect, portalOnMap and
   day.js's frame all read `liftNow`, so the mesh is sized for the camera
   the picture is drawn with, at every frame of the ease.

   Taste: LIFT_TAU 350 ms is the ease's time constant (settled to under a
   device pixel in about a second); LIFT_EPS 0.02 map units is where it
   is called settled (0.014 css px at the fault's zoom). The ease is a
   function of TIME, not of frames (a slow frame moves it further, as a
   real thing would), and the clock starts on the first frame that finds
   the aim off its target after a rest — so an idle of any length is not
   a frame's worth of travel. ==== */
var LIFT_TAU = 350, LIFT_EPS = 0.02;
S.liftCur = undefined;            /* the aim the frame is drawn with, map units */
S.liftHold = null;                /* the target, held while a hand is down */
S.liftSnap = true;                /* the next frame lands on the target */
S.liftT = 0; S.liftFld = null; S.liftRest = true;
function liftTarget() {
  return S.liftHold !== null ? S.liftHold : aimLift(S.view, reliefOf(S.tilt));
}
/* the aim the picture is drawn with; before the first frame, the target */
function liftNow(v, rel) { return S.liftCur === undefined ? aimLift(v, rel) : S.liftCur; }
/* one step of the ease, once per draw and before anything reads it */
function liftStep() {
  var tgt = liftTarget(), now = performance.now();
  if (S.liftSnap || S.liftCur === undefined || S.liftFld !== S.fld) {
    S.liftCur = tgt; S.liftFld = S.fld; S.liftSnap = false; S.liftRest = true; return;
  }
  var d = tgt - S.liftCur;
  if (Math.abs(d) <= LIFT_EPS) { S.liftCur = tgt; S.liftRest = true; return; }
  /* at rest until now: this frame starts the clock and draws where it is */
  var dt = S.liftRest ? 0 : now - S.liftT; S.liftT = now; S.liftRest = false;
  S.liftCur += d * (1 - Math.exp(-dt / LIFT_TAU));
  if (Math.abs(tgt - S.liftCur) <= LIFT_EPS) { S.liftCur = tgt; S.liftRest = true; } else invalidate();
}
function holdLift() { if (S.liftHold === null) S.liftHold = aimLift(S.view, reliefOf(S.tilt)); }
function freeLift() { if (S.liftHold !== null) { S.liftHold = null; invalidate(); } }
function snapLift() { S.liftSnap = true; }
/* day.js's frame asks through the handle and gets the picture's number */
S.aimLift = liftNow;
/* the harness's read of the live target (the ground under the centre now,
   held or not) — read-only */
S.aimTarget = function () { return aimLift(S.view, reliefOf(S.tilt)); };
function shadeOf(t) { return clamp(t / (15 * DEG), 0, 1); }

/* the map rect the ground grid must cover so the plate is never bare */
/* ==== R1 (b): AND IT STANDS ON A LATTICE THAT DOES NOT MOVE.
   The height is read in the VERTEX shader, so the surface the fragments
   are painted on is the mesh's PIECEWISE-LINEAR reconstruction of the
   field, not the field. E1 sized this rect straight off the camera —
   half-diagonal of a box whose depth is v.h/cos(tilt) plus twice the
   lifted ground — so a tilt of a tenth of a degree changed the cell size,
   and a pan of one pixel changed the origin: EVERY VERTEX SLID OVER THE
   FIELD ON EVERY FRAME OF EVERY GESTURE, and the facets, and with them
   every contour, tint edge and coast painted on them, re-dealt themselves
   frame by frame. At tilt 56, turned, the cell is 5.1 x 13.6 map units
   against a field lattice of 5 x 3.55 — the surface is a FOUR-TO-ONE
   undersample of the field across the rows, so the re-phasing is not
   subtle. That is the crawl Tommy sees on the curves as he tilts.

   The cure costs nothing: round each cell UP to a power of two of the
   FIELD's own cell and put the origin on a whole number of them. The
   rect still covers what the camera needs; the vertices now sit on fixed
   map points and stay there through a whole gesture, so the reconstructed
   surface is the same surface from frame to frame. It re-phases only when
   a cell crosses an octave — and on that step half the vertices are the
   ones it already had, because the ladder is a power of two.
   ?snap=0 pins E1's floating rect for the pair. ==== */
var SNAP_ON = Q.get('snap') !== '0';
/* the mesh's own resolution — at module scope because gridRect measures
   its cells against the field's lattice */
var GX = +(Q.get('gx') || 512), GY = +(Q.get('gy') || 192);
/* ==== R13 (a), the second half: THE LONGER REACH IS NOT PAID FOR IN
   FACETS. The mesh is a fixed 512 x 192, so a rect that has to be half
   again as wide buys it by DOUBLING THE CELL — and the octave ladder
   doubles it on both axes at once. Measured at `#1182,431,2650,80,30`
   (tilt at the clamp, zoom 2650, turned): the cell goes 10 x 28.4 map
   units to 20 x 56.7, and the front's mould comes back a chunky polygon
   with its dark face gone (room-rev3/R13-after-t80-y30.png against
   R13-before-t80-y30.png at the crop x 900-1450 y 270-430). That is a
   worse picture than the one the item set out to fix.

   So there is a SECOND grid, 1024 x 384, and the cell is halved back on
   an axis ONLY where the reference cell is already four times the field's
   own lattice — which is the (20, 56.7) case and nothing else. Every
   view whose reference cell is at or under the lattice keeps its cell
   and simply covers twice as much ground with it: the vertices still
   sit on the same map points (the origin is floored to a whole cell
   either way, R1 b), so the fit, the flat zooms, tilt 55 and the bearings
   at zoom 2650 are drawn from the SAME lattice and are pixel-identical.
   The four-times mesh is built in an idle slot after the reveal, so the
   load does not pay for it, and until it exists the coarse cell is used
   — a correct picture, one octave down, for the first moment.
   ?fine=0 pins the single grid, for the pair. ==== */
var FINE_ON = Q.get('fine') !== '0';
/* ==== R44 (a): THE OCTAVE IS ENGAGED ONE STEP EARLIER, AND THERE IS A
   MESH THAT ONLY DOUBLES THE COLUMNS.

   Tommy, on the scarp at one zoom and one tilt through a turn:
   "why does it change between these shots? ... the whole mountain shape
   changes too?" (#1371,494,1469,53,98 / 66 / 58). Measured on this branch's
   own base: the cell ALONG the sheet is the lattice (5 map units) at yaw
   98, 90, 80 and 0 and TEN at 72, 66, 62 and 58 — the turned footprint's
   box is wider, rw crosses 512 x DX, and the octave doubles. The scarp's
   run is 14 map units (BEY.SCARP), so at 5 it is drawn from three vertices
   and at 10 from one or two: its silhouette, its toe and the bedding's
   landing move with the bearing, and every hill coarsens with it. R13's
   `big` mesh never came to the rescue because it waits until the cell is
   already FOUR times the lattice, which this case never reaches.

   Two things are fixed here.

   (1) The test is halved: the second mesh is engaged as soon as a cell
   would stand at TWICE the lattice (and, across the rows, at eight times —
   the same two-octave offset R13 measured between the axes, 4 <-> 16 kept
   as 2 <-> 8). At every one of those bearings the cell comes back to 5.

   (2) R13's mesh doubles BOTH axes, and each octave crosses on its own far
   more often than the two cross together — paying four times the vertices
   to halve one axis is the wrong price, and it leaves the cell finer at one
   bearing than at the next, which is the same fault wearing the other hat.
   So there are two more meshes, each twice the vertices and not four: WIDE
   (1024 x 192, the columns halved and the rows exactly as they were) and
   TALL (512 x 384, the other way about). R13's fine 1024 x 384 is kept for
   the views that cross both octaves at once (`#1182,431,2650,80,30` is
   one). The cell is halved on exactly the axis whose count is doubled.

   Every mode returns THE SAME RECT: W = cw x nx and H = chh x ny are the
   512-grid's own figures whichever mesh is used, because the cell is
   halved exactly where the count is doubled. So uGReach, the water plane's
   extent and the record's cut do not move — only the number of vertices
   the same ground is drawn from.
   ==== */
/* ==== R54: THE WIDEST TURNED VIEWS, MEASURED BEFORE THEY WERE BUILT.

   R44 left this in its own "unfixed": at the widest zoom clampView allows
   (2,650) on a turned bearing the cell is still an octave above the
   lattice, because ONE halving is not enough there. R44 guessed the case
   was "twice the lattice on both axes" and costed a 2048 x 768 mesh
   (1.57 M vertices) for it. Measured on this branch over 378 poses
   (3 zooms x 7 tilts x 18 bearings, `room-rev6/R54-sweep.json`), the
   guess is wrong and the true shape is three cases, all of them at zoom
   2,650 and at no other zoom:

     bearings 50-120   30 poses  the COLUMNS are twice the lattice, the
                                 rows already at it  -> 2048 x 384
     bearings 20-30,150 15 poses the ROWS are, the columns are not
                                                     -> 1024 x 768
     bearings 40-45,225,315 20 poses  both are       -> 2048 x 768

   Tommy's two views (`#1182,431,2650,53,50` and `...,53,60`) are in the
   FIRST band: rect 10,240 x 5,445.8, cell 10 x 14.182 — the rows are at
   14.182 already, which is the fit's own row cell. So the card's mesh
   would have paid for twice the rows it needed at exactly the two views
   it was asked to fix.

   So the ladder stops being a list of modes and becomes A PAIR OF COUNTS.
   kx is how many times the column cell must be halved to stand at the
   lattice (DX), ky the same across the rows against the fit's row cell
   (4 x DY — R13's two-octave offset between the axes, kept), each capped
   at two, and the mesh asked for is GX << kx by GY << ky. The rect is
   unchanged whichever mesh answers, exactly as R44 had it: cw is divided
   by the same power the count is multiplied by, so W = cw x nx is the
   512-grid's own figure always and uGReach, the water plane and the
   record's cut cannot move.

   The two-axis, two-octave ladder below is the chosen R54 path. ==== */
/* the meshes, keyed `nx x ny`. The base 512 x 192 is registered at boot;
   everything else arrives in an idle slot and until it does the finest
   mesh that IS there answers — a correct picture, an octave down, for
   the first moment. */
function meshAt(kx, ky) {
  var best = null, bs = -1, M = S.gm || (S.gm = {});
  for (var a = 0; a <= kx; a++) for (var b = 0; b <= ky; b++) {
    var g = M[(GX << a) + 'x' + (GY << b)];
    if (g && a + b > bs) { bs = a + b; best = { kx: a, ky: b, g: g }; }
  }
  return best || { kx: 0, ky: 0, g: null };
}
/* R44's four modes are still reported under their own numbers so its
   report reads against this one; the three R54 meshes carry 4, 5, 6. */
var MODE_N = { '0,0': 0, '1,0': 1, '1,1': 2, '0,1': 3,
               '2,1': 4, '1,2': 5, '2,2': 6, '2,0': 4, '0,2': 5 };
function snapRect(cx, cy, rw, rh) {
  if (!SNAP_ON) return { x: cx - rw / 2, y: cy - rh / 2, w: rw, h: rh };
  var L = S.fld.lat;
  var cw = L.DX * Math.pow(2, Math.ceil(Math.log2(rw / GX / L.DX)));
  var chh = L.DY * Math.pow(2, Math.ceil(Math.log2(rh / GY / L.DY)));
  var nx = GX, ny = GY, kx = 0, ky = 0;
  if (FINE_ON && !REACH_OLD) {
    /* how far above the lattice each axis stands, in octaves. The row
       target is 4 x DY: a square rect always stands two octaves higher
       above the lattice across the rows than along the columns. */
    /* ==== JG1 (2026-09-26): AND ONCE THE GROUND STANDS UP, THE TARGET IS
       FINER THAN THE LATTICE, NOT COARSER. Tommy, of a tilted steep face
       at #1250,627,1018,51,-25: "they're still pretty jaggedy. I'd like
       to see if we could do a much better job." The ink is drawn on the
       smooth field (fieldAt, per fragment); the eye sees each point of a
       line at the height the MESH gives it, and the mesh is linear across
       a cell. So every contour on a steep face is lifted and dropped by
       (mesh - field), with the mesh's period — and at that view the cell
       was 2.5 x 14.18 map units, FOUR lattice rows. Measured over the
       view's ground against the smooth surface, in device px on his
       screen (39.8 px a unit of bed there): p99 8.3 / worst 42 as it was,
       2.6 / 7.8 with the rows at the lattice, 1.0 / 5.0 with the rows at
       the lattice AND the columns at half of it (DX/2) — which at that
       view is R54's own 2048 x 768, the biggest mesh the ladder already
       builds. So under a tilt: columns to DX/2, rows to DY, each capped at
       the ladder's two octaves as before. FLAT, nothing moves: the height
       is multiplied by uRelief = 0 there, the mesh is invisible, and the
       fit keeps its 512-grid cell to the vertex. ==== */
    var up = S.tilt > 1e-4;
    kx = Math.round(Math.log2(cw / (up ? L.DX / 2 : L.DX)));
    ky = Math.round(Math.log2(chh / ((up ? 1 : 4) * L.DY)));
    kx = Math.max(0, Math.min(2, kx));
    ky = Math.max(0, Math.min(2, ky));
    /* R54: the mesh above R44's three is asked for the first time a view
       wants it, not at boot. Until it lands the finest one there answers. */
    if (kx > 1 || ky > 1) wantMesh(kx, ky);
    var m = meshAt(kx, ky);
    kx = m.kx; ky = m.ky;
    nx = GX << kx; ny = GY << ky;
    cw /= (1 << kx); chh /= (1 << ky);
  }
  var W = cw * nx, H = chh * ny;
  var mode = MODE_N[kx + ',' + ky];
  return { x: Math.floor((cx - W / 2) / cw) * cw,
           y: Math.floor((cy - H / 2) / chh) * chh, w: W, h: H,
           big: kx > 0 && ky > 0, mode: mode === undefined ? 0 : mode,
           kx: kx, ky: ky, key: nx + 'x' + ny,
           nx: nx, ny: ny, cw: cw, ch: chh };
}
/* ==== R13 (a): HOW FAR THE GROUND HAS TO REACH, AND WHY THE OLD FIGURE
   WAS TOO SHORT.

   E1 sized this rect on a FLAT-EARTH guess: the view rect is v.h deep, a
   tilt of t lays it back, so the ground behind the window's top edge is
   v.h/2/cos(t) away, and a fat allowance for the height of the land was
   added on both sides. That is the ORTHOGRAPHIC answer, and the camera is
   not orthographic. mapToSheet is affine — the tilt is a shear on the flat
   sheet, not a camera pitch — but the sheet is then drawn on a desk under a
   real 22-degree perspective, and a point `up` units off the paper is drawn
   D/(D - up) times further from the middle of the glass. Far ground has a
   large NEGATIVE up (up = r.y*sin t, and r.y runs away from the eye), so it
   is drawn nearer the middle than the flat-earth figure says: to reach the
   TOP OF THE WINDOW a point must stand much further back than v.h/2/cos t.

   Measured on the branch's own base at `#721,431,2650,75,86` (tilt at the
   75-degree clamp, zoom 2650, a bearing along the record): the flat-earth
   figure is 1,534 map units, the true one 2,822 — the rect fell 400 units
   short of the portal's west top corner and the record's ground simply
   STOPPED there, in a straight line, with the sheet's own paper behind it
   (room-rev3/R13-before-words.png, the window's top-left corner; the
   beyond will not draw inside the record's rect, so what shows through is
   nothing at all).

   So the rect is solved from the window instead of guessed. Everything
   below is one closed form, and the tilt CLAMP is what makes it finite:
   the far row runs to the horizon when tan(tilt) = D/A (82.6 degrees at
   this layout), and MAX_TILT is 75. That is R10 (b)'s rule for the
   beyond's grid — the reach is sized to the clamp, not to the view —
   applied to the ground.

   Three things follow, and each is worth its line:
   - the rect is centred on the FOOTPRINT, not on the view. At a bearing
     the far half is much deeper than the near half, and a rect centred on
     the view spends half its cells behind the camera.
   - at a turned bearing it is the footprint's axis-aligned BOX, not the
     bounding square E1 used. At tilt 55 the box is SMALLER than the old
     square on both axes, so the mesh there gets FINER, not coarser.
   - the near side still carries the land's height (a mountain at the
     bottom edge is drawn from further back), the far side does not: the
     old figure added it twice and paid for it in cell size.
   `?reach=0` is the pair — E1's own rect, for the before picture. ==== */
var REACH_OLD = Q.get('reach') === '0';
/* R44 (b), diagnosis only: ?reachx=N blows the solved box up by N about its
   own centre, to prove whether a blank corner is the RECT or the camera. */
var REACH_X = +(Q.get('reachx') || 1);
/* ==== R19: ?surf=1 — THE THREE SURFACES, FLAT. The ground green, the
   conjecture magenta, the cut face blue, and nothing else in the window at
   all: no sky wash, no water plane, no marks, no lettering, no day. It is
   the only way to COUNT a seam rather than squint at one — every pixel
   inside the window that is none of the three is a pixel of bare paper
   showing between two surfaces that are supposed to meet, and a script can
   add them up (the numbers in REPORT-room-R19.md are all taken this way).
   It is a measuring instrument, not a mode: nothing reads it but the four
   guards below and the three shaders' last lines. ==== */
/* ==== R40: the downs' two numbers, in CONTOUR INTERVALS of the plate's
   index generation — the one the eye has at the fit. `?downs=` is the
   swing either side of the mean and `?downsmean=` how far the mean stands
   over the coast; `?downs=0` is the dead plane Terra Incognita was. ==== */
var TSTEP = 8 * F.TINT_STEP;                       /* sqrt(5) = 2.236068 */
var DOWNS_AMP  = (function (q) { return q === null ? F.TERRA.AMP_STEPS  : Math.max(0, +q || 0); })(Q.get('downs'));
var DOWNS_MEAN = (function (q) { return q === null ? F.TERRA.MEAN_STEPS : (+q || 0); })(Q.get('downsmean'));
var SURF = Q.get('surf') === '1';
/* R41: ?seamat=0 — the ground's material and mould as R39 left them (the
   west sea a band of unsurveyed conjecture under a cut-off skirt), so the
   pair comes off one build. */
var WEST_SEA = Q.get('seamat') !== '0';
/* R28 (diagnosis): one pass at a time, to prove which surface a fault is
   in. R35 left it two: the ground (which is now the record, the margin and
   the front's scarp together) and the water plane. ?iso=noground draws the
   plane alone; ?iso=ground is a no-op kept so a saved URL still loads. */
var ISO = Q.get('iso') || '';
function gridRect() {
  var v = S.view, ct = Math.cos(S.tilt), st = Math.sin(S.tilt);
  var cx = v.x + v.w / 2, cy = v.y + v.h / 2;
  var w = v.w, h = v.h / Math.max(ct, 0.18);
  var lift = S.fld.maxHeight * EXAG * st / Math.max(ct, 0.18);
  h += lift * 2;
  if (REACH_OLD) {
    var d = Math.hypot(w, h) * 0.5 + 8;
    if (Math.abs(S.yaw) > 1e-4) return snapRect(cx, cy, d * 2, d * 2);
    return snapRect(cx, cy, w + 8, h + 8);
  }
  var L = S.layout, P = S.portal, K = L.k, D = (L.ch / 2) / Math.tan(FOV / 2) * (S.frameK || 1);   /* MP2: the world's own camera distance */
  var kk = P.w / v.w / K;                       /* sheet units per map unit */
  var rel = reliefOf(S.tilt);
  var liftS = liftNow(v, rel) * kk;
  /* the depth (sheet units along the bearing, away negative) at which flat
     ground is drawn on the css row `yy`. The plate's own centre row and the
     aim-lift are the only sheet numbers in it; the rest is the camera. */
  var base = L.ch / 2 - P.t - P.h / 2 - K * liftS * st;
  function depthAt(yy) {
    var A = L.ch / 2 - yy, den = K * (D * ct - A * st);
    return D * (base - A) / Math.max(den, 1e-3);
  }
  /* and how far ACROSS the bearing the window's side edges are at that
     depth — the same magnification, the other way round */
  var xMid = P.l + P.w / 2 - L.cw / 2, ax = 0;
  function acrossAt(u) {
    var mu = D / (D - u * st * K);
    var a = Math.abs(((P.l - L.cw / 2) / mu - xMid) / K);
    var b = Math.abs(((P.l + P.w - L.cw / 2) / mu - xMid) / K);
    ax = Math.max(ax, a, b);
  }
  var uFar = depthAt(P.t), uNear = depthAt(P.t + P.h);
  acrossAt(uFar); acrossAt(uNear);
  /* ==== R44 (b): THE ALLOWANCE FOR GROUND THAT DOES NOT LIE ON THE DATUM,
     ON BOTH SIDES, AND ON THE BOX THE CAMERA ACTUALLY HAS.
     Everything solved above is the footprint of the DATUM plane. Ground
     standing above it is drawn as if it were nearer and water below it as
     if it were further, so the near side and the FAR side both have to be
     carried — R13 carried only the near one, and only on its own flat-desk
     solve. R27 then unioned that solve with a ray-cast of the window's four
     corners, for the poses the solve cannot describe; since R17 the pitched
     pose is the DEFAULT, so the ray-cast is the box that wins and neither
     allowance was in it. Two faults followed, both measured on this branch:
     under R33's old flat-aim comparison the near edge wanted
     map y 649 (the ray-cast's 442 plus the relief's 207) and the mesh
     stopped at 567, so the plate's bottom-left corner was bare paper
     (room-rev6/bug-bl-flat.png); and at `#1371,494,1469,53,98` the far SEA
     ended twenty device rows short of the neat line.
     The two figures below are R13's, kept for the solve; the ray-cast now
     cuts all three planes itself (portalOnMap), which is exact. At tilt 0
     both are nought and nothing moves. ==== */
  var above0 = S.fld.maxHeight * EXAG * rel;         /* the top of the relief, map units */
  var below0 = (S.fld.COAST + 0.2) * EXAG * rel;    /* and the sea bed, -COAST exactly */
  var above = above0 * st / Math.max(ct, 0.18);
  /* and the mirror of it, which R13 did not have: ground BELOW the datum —
     the sea, whose bed is at -COAST exactly (field.js: a zero-commit month
     of 2022) — is drawn LOWER on the glass, so the FAR row shows it from
     further away than the datum answer. R13 left the far side to the datum
     ("the near side still carries the land's height, the far side does
     not") because ground standing above the datum is drawn higher and so
     the datum is conservative going away; water is the case that argument
     misses. Measured at `#1371,494,1469,53,98`: without it the far sea ends
     20 device rows short of the neat line and the band above it comes back
     as pale conjecture (room-rev6/R44-b-y98-topcrop.png). */
  var below = below0 * st / Math.max(ct, 0.18);
  var far = uFar / kk - below, near = uNear / kk + above;
  ax /= kk;
  var dh = (near - far) * 0.5, cdep = (near + far) * 0.5;
  var cyw = Math.abs(Math.cos(S.yaw)), syw = Math.abs(Math.sin(S.yaw));
  var hx = ax * cyw + dh * syw, hy = ax * syw + dh * cyw;
  cx += cdep * Math.sin(S.yaw); cy += cdep * Math.cos(S.yaw);
  /* E8's room pose moves the eye off the desk, so the window's box on the
     glass is no longer the portal's css rect and the solve above does not
     describe it. There the old figure is kept as a floor. */
  if (S.cam.room > 0.001 || S.cam.lap > 0.001) {
    var dr = Math.hypot(w, h) * 0.5 + 8;
    hx = Math.max(hx, dr); hy = Math.max(hy, dr);
    cx = v.x + v.w / 2; cy = v.y + v.h / 2;
  }
  /* ==== R27 (3): AND WHEN THE CAMERA IS NOT OVER THE GLASS.
     R13's closed form maps a css row to a depth through D and the plate's
     own centre row; the edge pose's PITCH, its pull-back and R27's AIM are
     none of them in it, so the rect could fall short of the
     window's far row and a block of bare paper showed at the top of the
     plate (R25's OPEN 1; seen at #89,452,1018,64,479, and it survived
     clip=0 and seam=0, so it was never the clip).

     The honest answer needs no new arithmetic about the camera at all: the
     window's four corners are RAY-CAST through the matrix the frame is
     actually drawn with, onto the map's own plane at the datum, and the box
     of the four is UNIONED with the figure above. It is exact for any pose
     — the pitch, the aim, the pull-back and the room move are all in S.VP
     already — and the datum plane is the conservative one for the far side,
     because ground standing above it is drawn HIGHER on the glass than flat
     ground at the same distance, never lower. Four unprojections and one
     4x4 inverse a frame.

     It is a union and it is gated by the camera pose. ==== */
  var solved = { cx: cx, cy: cy, hx: hx, hy: hy }, qBox = null;
  if (S.cam.pitch > 1e-4 || Math.abs(S.cam.aim || 0) > 1e-4) {
    var q = portalOnMap([0, above0, -below0]); qBox = q;
    if (q) {
      var lo0 = cx - hx, hi0 = cx + hx, lo1 = cy - hy, hi1 = cy + hy;
      lo0 = Math.min(lo0, q.x0); hi0 = Math.max(hi0, q.x1);
      lo1 = Math.min(lo1, q.y0); hi1 = Math.max(hi1, q.y1);
      cx = (lo0 + hi0) / 2; cy = (lo1 + hi1) / 2;
      hx = (hi0 - lo0) / 2; hy = (hi1 - lo1) / 2;
    }
  }
  if (REACH_X !== 1) { hx *= REACH_X; hy *= REACH_X; }
  S.gBox = { cx: cx, cy: cy, hx: hx, hy: hy, q: qBox, solved: solved,
             lift: liftS, above: above };
  return snapRect(cx, cy, hx * 2 + 8, hy * 2 + 8);
}

/* the inverse of a 4x4, by cofactors — one a frame, and only when the
   camera has left the glass */
function inv4(m) {
  var o = new Float32Array(16), i;
  o[0]=m[5]*m[10]*m[15]-m[5]*m[11]*m[14]-m[9]*m[6]*m[15]+m[9]*m[7]*m[14]+m[13]*m[6]*m[11]-m[13]*m[7]*m[10];
  o[4]=-m[4]*m[10]*m[15]+m[4]*m[11]*m[14]+m[8]*m[6]*m[15]-m[8]*m[7]*m[14]-m[12]*m[6]*m[11]+m[12]*m[7]*m[10];
  o[8]=m[4]*m[9]*m[15]-m[4]*m[11]*m[13]-m[8]*m[5]*m[15]+m[8]*m[7]*m[13]+m[12]*m[5]*m[11]-m[12]*m[7]*m[9];
  o[12]=-m[4]*m[9]*m[14]+m[4]*m[10]*m[13]+m[8]*m[5]*m[14]-m[8]*m[6]*m[13]-m[12]*m[5]*m[10]+m[12]*m[6]*m[9];
  o[1]=-m[1]*m[10]*m[15]+m[1]*m[11]*m[14]+m[9]*m[2]*m[15]-m[9]*m[3]*m[14]-m[13]*m[2]*m[11]+m[13]*m[3]*m[10];
  o[5]=m[0]*m[10]*m[15]-m[0]*m[11]*m[14]-m[8]*m[2]*m[15]+m[8]*m[3]*m[14]+m[12]*m[2]*m[11]-m[12]*m[3]*m[10];
  o[9]=-m[0]*m[9]*m[15]+m[0]*m[11]*m[13]+m[8]*m[1]*m[15]-m[8]*m[3]*m[13]-m[12]*m[1]*m[11]+m[12]*m[3]*m[9];
  o[13]=m[0]*m[9]*m[14]-m[0]*m[10]*m[13]-m[8]*m[1]*m[14]+m[8]*m[2]*m[13]+m[12]*m[1]*m[10]-m[12]*m[2]*m[9];
  o[2]=m[1]*m[6]*m[15]-m[1]*m[7]*m[14]-m[5]*m[2]*m[15]+m[5]*m[3]*m[14]+m[13]*m[2]*m[7]-m[13]*m[3]*m[6];
  o[6]=-m[0]*m[6]*m[15]+m[0]*m[7]*m[14]+m[4]*m[2]*m[15]-m[4]*m[3]*m[14]-m[12]*m[2]*m[7]+m[12]*m[3]*m[6];
  o[10]=m[0]*m[5]*m[15]-m[0]*m[7]*m[13]-m[4]*m[1]*m[15]+m[4]*m[3]*m[13]+m[12]*m[1]*m[7]-m[12]*m[3]*m[5];
  o[14]=-m[0]*m[5]*m[14]+m[0]*m[6]*m[13]+m[4]*m[1]*m[14]-m[4]*m[2]*m[13]-m[12]*m[1]*m[6]+m[12]*m[2]*m[5];
  o[3]=-m[1]*m[6]*m[11]+m[1]*m[7]*m[10]+m[5]*m[2]*m[11]-m[5]*m[3]*m[10]-m[9]*m[2]*m[7]+m[9]*m[3]*m[6];
  o[7]=m[0]*m[6]*m[11]-m[0]*m[7]*m[10]-m[4]*m[2]*m[11]+m[4]*m[3]*m[10]+m[8]*m[2]*m[7]-m[8]*m[3]*m[6];
  o[11]=-m[0]*m[5]*m[11]+m[0]*m[7]*m[9]+m[4]*m[1]*m[11]-m[4]*m[3]*m[9]-m[8]*m[1]*m[7]+m[8]*m[3]*m[5];
  o[15]=m[0]*m[5]*m[10]-m[0]*m[6]*m[9]-m[4]*m[1]*m[10]+m[4]*m[2]*m[9]+m[8]*m[1]*m[6]-m[8]*m[2]*m[5];
  var det = m[0]*o[0] + m[1]*o[4] + m[2]*o[8] + m[3]*o[12];
  if (!det) return null;
  det = 1 / det;
  for (i = 0; i < 16; i++) o[i] *= det;
  return o;
}

/* the window's four corners, cast onto the map's plane at the datum, as a
   box in MAP units. Returns null if a corner's ray runs parallel to the
   plane or behind the eye — at which point the closed form stands alone. */
function portalOnMap(hs) {
  var L = S.layout, f = S.fld, v = S.view, P = S.portal;
  if (!S.VP || !L || !f || f.stub) return null;
  var M = inv4(S.VP); if (!M) return null;
  var K = L.k, ct = Math.cos(S.tilt), st = Math.sin(S.tilt);
  var kk = P.w / v.w / K;                       /* sheet units per map unit */
  var rel = reliefOf(S.tilt);
  var lift = liftNow(v, rel) * kk;          /* R44 (b): the aim the frame is drawn with */
  var pl = { x: (P.l - L.ox) / K, y: (P.t - L.oy) / K, w: P.w / K, h: P.h / K };
  /* the map's plane, in world: origin, and the two edges r.x and r.y run
     along (mapToSheet, XFORM_GLSL, with h = 0) */
  var O = [L.ox + K * (pl.x + pl.w / 2), -(L.oy + K * (pl.y + pl.h / 2 + lift * st)), 0];
  function un(nx, ny, nz) {
    var x = M[0]*nx + M[4]*ny + M[8]*nz + M[12];
    var y = M[1]*nx + M[5]*ny + M[9]*nz + M[13];
    var z = M[2]*nx + M[6]*ny + M[10]*nz + M[14];
    var w = M[3]*nx + M[7]*ny + M[11]*nz + M[15];
    if (Math.abs(w) < 1e-9) return null;
    return [x / w, y / w, z / w];
  }
  var cy2 = Math.cos(S.yaw), sy2 = Math.sin(S.yaw);
  /* the four rays, once — they do not know which plane they are for */
  var rays = [], i;
  for (i = 0; i < 4; i++) {
    var Ps = S.portalScr || P;                  /* MP2: the corners as they are on the glass */
    var sx = (i & 1) ? Ps.l + Ps.w : Ps.l, sy = (i >> 1) ? Ps.t + Ps.h : Ps.t;
    var nx = sx / L.cw * 2 - 1, ny = 1 - sy / L.ch * 2;
    var a = un(nx, ny, -1), b = un(nx, ny, 1);
    if (!a || !b) continue;
    rays.push({ a: a, d: [b[0] - a[0], b[1] - a[1], b[2] - a[2]] });
  }
  /* ==== R44 (b): AND ON EVERY PLANE THE FIELD CAN STAND ON, NOT ONLY THE
     DATUM. A point h above the datum is mapToSheet's datum plane moved by
     (-h.kk.sin t) down the sheet and (+h.kk.cos t) off it, so the same four
     rays cut it at a different box: land is drawn HIGHER on the glass than
     flat ground at the same distance and water LOWER, so the near row asks
     for ground beyond the datum box at the top of the relief and the far
     row asks for it at the sea bed (-COAST exactly, field.js). R13 carried
     the first as one term added to its own flat-desk solve and did not
     carry the second at all ("the near side still carries the land's
     height, the far side does not") — which is why, once R17 made the
     pitched pose the default and R27's ray-cast became the box that wins,
     the far SEA at `#1371,494,1469,53,98` ended twenty device rows short of
     the neat line. Three planes, twelve intersections, one inverse. ==== */
  var HS = hs || [0], x0 = 1e18, x1 = -1e18, y0 = 1e18, y1 = -1e18, gotDatum = 0;
  for (var hi = 0; hi < HS.length; hi++) {
    var h = HS[hi];
    var Ox = O[0], Oy = O[1] + K * h * kk * st, Oz = K * h * kk * ct, got = 0;
    for (i = 0; i < rays.length; i++) {
      var aa = rays[i].a, d = rays[i].d;
      /* aa + s.d = O + u.ex + w.ey, ex = (K,0,0), ey = (0,-K ct, K st) —
         three equations, three unknowns; the z row is the one that fixes s
         because ex has no z in it */
      var wz = K * st, s2;
      if (Math.abs(wz) > 1e-9) {
        var A = d[1] + (d[2] / wz) * K * ct;
        var B = Oy + (Oz / wz) * K * ct - (aa[2] / wz) * K * ct - aa[1];
        if (Math.abs(A) < 1e-9) continue;
        s2 = B / A;
      } else {
        if (Math.abs(d[2]) > 1e-9) { s2 = (Oz - aa[2]) / d[2]; }
        else { if (Math.abs(d[1]) < 1e-9) continue; s2 = (Oy - aa[1]) / d[1]; }
      }
      if (!(s2 > 0)) continue;
      var wx2 = aa[0] + s2 * d[0], wy2 = aa[1] + s2 * d[1], wz2 = aa[2] + s2 * d[2];
      var rx = (wx2 - Ox) / K;
      var ry = (Math.abs(wz) > 1e-9) ? ((wz2 - Oz) / wz) : ((Oy - wy2) / (K * ct || 1e-9));
      /* r -> map: undo the yaw, then the scale, then the view's centre */
      var dx = rx * cy2 + ry * sy2, dy = -rx * sy2 + ry * cy2;
      var mx = v.x + v.w / 2 + dx / kk, my = v.y + v.h / 2 + dy / kk;
      if (!isFinite(mx) || !isFinite(my)) continue;
      got++;
      if (mx < x0) x0 = mx; if (mx > x1) x1 = mx;
      if (my < y0) y0 = my; if (my > y1) y1 = my;
    }
    if (h === 0) gotDatum = got;
  }
  return gotDatum === 4 ? { x0: x0, x1: x1, y0: y0, y1: y1 } : null;
}

/* ==== R25 (2): THE PLATE'S CLIP IS THE NEAT LINE, IN SHEET SPACE =====

   THE DEFECT. The window in the paper is a RECTANGLE ON THE PAPER; the
   ground, the water, the beyond, the marks and the lettering are all drawn
   through the same view-projection the sheet is drawn with, so at the flat
   rest pose the window projects to an axis-aligned rectangle and E7's
   scissor — the bounding box of the window's four projected corners — is
   that rectangle to the pixel. Pitch the camera and it stops
   being one: the paper's far edge is narrower than its near edge, the
   window on it is a TRAPEZOID, and a bounding box is not a trapezoid. The
   box spills past the printed neat line at the narrow (far) end and falls
   inside it at the wide (near) end — measured on room-rev4/T-edge-combo.png
   at 3x, about 10 device px out at the top-left corner and about 8 in at
   the bottom-left. Tommy saw it as "the map itself isn't slanted so doesn't
   quite fit the paper right": the map's CONTENT was always in the paper's
   perspective; its EDGE was not.

   THE FIX, and why it is a stencil. The honest clip is the neat line in
   SHEET units, and every layer in the window already carries the sheet
   point it is drawing (uPlate is published to all of them by setCommon).
   Written as a fragment discard it would be an edit to eight shaders in
   five files — ground, sky, beyond, seam, water, face, marks, lettering —
   and a per-fragment test paid at every pose. Written as a stencil it is
   ONE extra draw of ONE quad: the window's own rect, on the paper, under
   the same matrix, with the colour mask off. Four vertices, no fragment
   work, and the test itself is free in the raster hardware.

   It is also only reached when the camera has a pitch (or is leaving for
   the room). ==== */
var CLIP_FS = `#version 300 es
precision lowp float;
out vec4 o;
void main(){ o = vec4(0.0); }`;

/* ==== WC1, 2026-09-21: AND THE CUT RIDES THE DRAPE, NOT THE PLANE =====

   THE SECOND DEFECT IN THE SAME EDGE. R25 (2) above put the clip in SHEET
   units, which fixed the trapezoid; it left the clip on the flat plane,
   at uZ = 0, and since DR2 the paper is not on that plane. Every layer
   drawn INTO the window — the ground through `sheetToClip`, the water,
   the sky, the marks, the lettering, the ruler — adds `paperSurf(p)` to
   its own height, so the window's CONTENT rides the drape over the
   journal and the field book. The window's CUT did not. Where the journal
   lifts the paper under the south-east of the sheet, the ground south of
   the neat line projects HIGHER on the glass than the flat stencil edge
   and lands over the printed months: Tommy's picture of 2026-09-21 has
   the map's grid running across NOV.

   It is the same argument R25 (2) made, one term further on. The honest
   clip is the neat line ON THE PAPER, and the paper's face at a station
   is one function — `paperSurf`, out of objects.js, the same chunk every
   other program in the window compiles. So the quad becomes a MESH (four
   vertices cannot carry a curved surface) with that one line in it, taken
   character for character off ruler.js's flat pass, which is the other
   print on this paper that had to come off the plane for the same reason.

   AND IT RUNS AT EVERY POSE. R25 (2)'s gate — some pitch, or leaving for
   the room — was right for a trapezoid, which only exists at a pitch. A
   drape lifts the paper at tilt 0 as well, and the perspective divide
   magnifies lifted content toward the eye by a few pixels there too.

   It was behind `?clip=paper` while it was measured (WC1, screens repo
   room-e9/wc1-0921). Tommy, the same day: "This definitely works from a
   technical viewpoint. I'm fine with becoming the default. I think it's a
   strict improvement." So the flat quad and the switch are gone and this
   is the one clip. ================================================== */
/* the lattice the window's rect is cut into. The ground evaluates
   `paperSurf` PER VERTEX and interpolates across its own cell, so a clip
   coarser than the ground's own pitch would disagree with it by up to a
   cell along the neat line. At the fit the ground's 512 columns fall on
   about 2230 sheet units of window, which is 4.4 sheet units a cell; 192
   x 96 on the window itself is 11.6 x 6.8, and the drape's own feature is
   3.5 book-heights across (objects.js DRAPE_RUN_K), so the chord error is
   far below the pixel. Measured against a 512 x 192 clip at the two book
   corners: 0 pixels different. */
var CLIP_NX = 192, CLIP_NY = 96;
var CLIP_PAPER_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a;
uniform vec4 uRect;    // the window, in sheet units
uniform vec3 uSheet; uniform mat4 uVP;
` + PAPER_INK_GLSL + `
void main(){
  vec2 p = uRect.xy + a*uRect.zw;
  /* ruler.js's own flat line, to the letter: the height is in SHEET units
     and is scaled by uSheet.z with the other two. uCreaseOn is declared
     by undDecl() inside the chunk above (CR2), so it is not redeclared. */
  gl_Position = uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), paperSurf(p)*uSheet.z, 1.0);
}`;
/* built once, permanent (no GLTAG), like the ground's own grids */
function clipMesh(gl) {
  if (S.clipMesh) return S.clipMesh;
  var nx = CLIP_NX, ny = CLIP_NY, i, j;
  var v = new Float32Array((nx + 1) * (ny + 1) * 2), k = 0;
  for (j = 0; j <= ny; j++) for (i = 0; i <= nx; i++) { v[k++] = i / nx; v[k++] = j / ny; }
  var ix = new Uint32Array(nx * ny * 6), m = 0;
  for (j = 0; j < ny; j++) for (i = 0; i < nx; i++) {
    var a0 = j * (nx + 1) + i, b0 = a0 + 1, c0 = a0 + nx + 1, d0 = c0 + 1;
    ix[m++] = a0; ix[m++] = c0; ix[m++] = b0;
    ix[m++] = b0; ix[m++] = c0; ix[m++] = d0;
  }
  var vao = gl.createVertexArray(), vb = gl.createBuffer(), ib = gl.createBuffer();
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, ix, gl.STATIC_DRAW);
  gl.bindVertexArray(null);
  S.clipMesh = { vao: vao, n: m, nx: nx, ny: ny };
  return S.clipMesh;
}
function plateClip(gl) {
  if (!S.stencil || S.clip === false) return false;
  var L = S.layout;
  gl.enable(gl.STENCIL_TEST);
  gl.stencilMask(0xff);
  gl.clearStencil(0);
  gl.clear(gl.STENCIL_BUFFER_BIT);          /* the scissor box only */
  /* MERGE (R20 + R25): the clip owns BIT 1 of the stencil, the record's
     water silhouette (draw(), step 3b) owns BIT 2 — each tests only its
     own bit, so neither undoes the other. */
  gl.stencilFunc(gl.ALWAYS, 1, 0x01);
  gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
  gl.colorMask(false, false, false, false);
  var pp = S.pClipP || (S.pClipP = prog(gl, CLIP_PAPER_VS, CLIP_FS, 'clip-paper'));
  var M = clipMesh(gl);
  gl.useProgram(pp);
  gl.bindVertexArray(M.vao);
  gl.uniformMatrix4fv(pp.u.uVP, false, S.VP);
  gl.uniform3f(pp.u.uSheet, L.ox, L.oy, L.k);
  gl.uniform4f(pp.u.uRect, (S.portal.l - L.ox) / L.k, (S.portal.t - L.oy) / L.k,
               S.portal.w / L.k, S.portal.h / L.k);
  /* the same paper the ground is standing on, off the same uniforms */
  if (window.RoomObjects && RoomObjects.paperInkUniforms)
    RoomObjects.paperInkUniforms(gl, pp, S);
  /* THE CUT MUST NOT LEAVE A FLOOR UNDER THE GROUND. R25's flat quad sat
     at z = 0, below everything the window then drew, so a depth write
     from it could never win; this mesh is AT the ground's own height and
     an equal-or-nearer depth in the buffer would take the surface away
     from itself. The pass reaches here with DEPTH_TEST off (objects.js
     `depthBack` ends the sheet), which is already enough — GL writes no
     depth with the test disabled — and the mask is closed as well so the
     ordering above this line cannot make it matter. */
  gl.depthMask(false);
  gl.drawElements(gl.TRIANGLES, M.n, gl.UNSIGNED_INT, 0);
  gl.depthMask(true);
  gl.colorMask(true, true, true, true);
  gl.stencilMask(0x00);                     /* nothing writes bit 1 again */
  gl.stencilFunc(gl.EQUAL, 1, 0x01);
  return true;
}
function plateUnclip(gl) { gl.disable(gl.STENCIL_TEST); gl.stencilMask(0xff); }

/* ==== R58: THE PRINT, STRUCK ONCE, AT THE MOMENT THE FOLD BEGINS =====
   Tommy, 2026-09-14: "we need to decide for each fold what happens to the
   paper content, it's currently wrong ... 1st fold in progress, right
   side of map disappears ... 2nd fold in progress, immediately the map
   x-axis reappears".

   THE FAULT WAS STRUCTURAL, not a gate in the wrong place. The sheet's
   print rides the paper (it is in uFurn and it folds with the mesh); the
   MAP does not — the ground, the sea, the marks and the lettering are a
   3-D pass drawn into the window in the paper, in the CAMERA's projection
   and not the paper's, so there is no arithmetic that puts them on a
   panel that has been turned. X2 scissored them off at the crease, which
   is exactly what Tommy saw: the map disappearing from the flap. And the
   rulers, being print, were drawn in a second pass IN FRONT of the ground
   while a flap was in the air — which is why the month names came up on
   the back of the packet at fold 2, where only plain paper can be.

   THE PHYSICAL TRUTH IS ONE SENTENCE: the sheet is printed on one side,
   and a fold moves the paper, not the print. So the print is made ONCE,
   flat, the moment the first crease takes an angle — the paper, the
   furniture, the graduated border, the month ruler, the section strip and
   the map's own picture, all of it, in one raster — and for as long as
   there is a fold in the sheet the face's ink is read out of that raster
   at the station it was printed at. Nothing else is drawn: no ground, no
   marks, no lettering, no ruler pass. The back of a panel is plain paper,
   as it has been since X2, and so from the first degree of fold 2 (which
   turns the left half's BACK up) there is nothing printed anywhere.

   IT IS ALSO THE CHEAPER CONSTRUCTION, measured: the fold's frames stop
   paying for the window entirely. The one-off is one extra frame.
   ==================================================================== */
function printFree() {
  if (!S.printSnap) return;
  var gl = S.gl;
  if (S.printSnap.fbo) gl.deleteFramebuffer(S.printSnap.fbo);
  if (S.printSnap.tex) gl.deleteTexture(S.printSnap.tex);
  if (S.printSnap.rb) gl.deleteRenderbuffer(S.printSnap.rb);
  S.printSnap = null;
}
function printSnap() {
  var gl = S.gl, O = window.RoomObjects, L = S.layout, f = S.fld;
  if (!O || !O.drawSheet || !L || !f) return;
  var W = S.cv.width, H = S.cv.height;
  if (!(W > 0 && H > 0)) return;
  var snap = S.printSnap;
  if (!snap || snap.w !== W || snap.h !== H) {
    printFree();
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, W, H, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    /* the depth the sheet and the ground sort against — the canvas has one
       and an FBO does not come with it */
    var rb = gl.createRenderbuffer();
    gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH24_STENCIL8, W, H);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.RENDERBUFFER, rb);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteFramebuffer(fbo); gl.deleteTexture(tex); gl.deleteRenderbuffer(rb);
      return;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    snap = S.printSnap = { tex: tex, fbo: fbo, rb: rb, w: W, h: H, M: new Float32Array(16) };
  }
  /* THE CAMERA THE PRINT IS TAKEN WITH is the one the reader is looking
     through — so the map's own relief is lit and raked exactly as it was
     the frame before the fold began and there is no pop at the first
     degree. It is only ever WIDENED, never narrowed: if the sheet runs
     off the glass (the reader folded while zoomed in) the whole sheet is
     brought inside the raster isotropically, which costs a little of the
     hairlines' width and is the difference between a flap with ink on it
     and a flap with a seam across it. */
  var VP = vp(), ndc = null;
  var lo0 = 1e9, lo1 = 1e9, hi0 = -1e9, hi1 = -1e9, ok = true;
  for (var i = 0; i < 4; i++) {
    var sx = (i & 1) ? f.sheet.w : 0, sy = (i & 2) ? f.sheet.h : 0;
    var wx = L.ox + sx * L.k, wy = -(L.oy + sy * L.k);
    var cw = VP[3] * wx + VP[7] * wy + VP[15];
    if (!(cw > 1e-4)) { ok = false; break; }
    var nx = (VP[0] * wx + VP[4] * wy + VP[12]) / cw;
    var ny = (VP[1] * wx + VP[5] * wy + VP[13]) / cw;
    if (nx < lo0) lo0 = nx; if (nx > hi0) hi0 = nx;
    if (ny < lo1) lo1 = ny; if (ny > hi1) hi1 = ny;
  }
  if (ok && (lo0 < -1 || hi0 > 1 || lo1 < -1 || hi1 > 1)) {
    var bw = Math.max(1e-3, (hi0 - lo0) * 1.04), bh = Math.max(1e-3, (hi1 - lo1) * 1.04);
    var k = Math.min(2 / bw, 2 / bh), cx = (lo0 + hi0) / 2, cy = (lo1 + hi1) / 2;
    ndc = new Float32Array([k, 0, 0, 0,  0, k, 0, 0,  0, 0, 1, 0,  -cx * k, -cy * k, 0, 1]);
    VP = mul(ndc, VP);
  }
  /* a station in SHEET units, straight to that raster's clip space */
  /* FD2, 2026-09-20: AND ITS HEIGHT WITH IT. The z row was 1 because the
     only thing ever handed to this matrix was a nought; the sheet's
     fragment shader asks it at the paper's own face now (vPH), and a
     sheet unit of height is L.k of the world exactly as a sheet unit
     along is. */
  var T = new Float32Array([L.k, 0, 0, 0,  0, -L.k, 0, 0,  0, 0, L.k, 0,  L.ox, -L.oy, 0, 1]);
  snap.M.set(mul(VP, T));

  var t0 = (window.performance || Date).now();
  var keep = O.foldT, keepG = O.foldGrab;
  S.capPrint = 1; S.capNdc = ndc; O.flatCapture = 1; O.foldT = 0; O.foldGrab = 0;
  /* ==== PK1 (2026-10-05): AND WHAT IS SIZED IN PIXELS IS SHRUNK WITH IT.
     Tommy, on both iPhone browsers: "as soon as you start to fold the
     marks on the sheet (like the benchmarks) become a lot bigger". On the
     phone the sheet runs off the glass, so the print brings it inside the
     raster at ndc's k (0.86 at 734 x 337); the marks are plates that stand
     up at a size in DEVICE px, and they were struck at their glass size on
     a sheet k times smaller, so on the flap they came back 1/k larger.
     Every pass that turns pixels into clip space (uVpx: marks, lettering,
     the voyage) reads the raster as 1/k larger while the print is struck,
     which keeps the anchors where they were and takes the plates down by
     k with the sheet. A sheet that fits the glass (k 1) is untouched. ==== */
  S.capK = ndc ? ndc[0] : 1;
  gl.bindFramebuffer(gl.FRAMEBUFFER, snap.fbo);
  /* AND THE STATE THE FRAME EXPECTS TO FIND. draw() is written to run on
     the canvas at the top of a rAF, where nothing is left enabled; called
     here it inherits whatever the frame that asked for the print had on
     when it asked, and one stale mask is a whole pass that does not land. */
  gl.disable(gl.SCISSOR_TEST); gl.disable(gl.STENCIL_TEST); gl.disable(gl.BLEND);
  gl.disable(gl.CULL_FACE); gl.depthMask(true); gl.colorMask(true, true, true, true);
  try { draw(); } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    S.capPrint = 0; S.capNdc = null; S.capK = 1; O.flatCapture = 0; O.foldT = keep; O.foldGrab = keepG;
    gl.viewport(0, 0, S.cv.width, S.cv.height);
  }
  /* PR1: and the zoom the raster was struck through, so a fragment can find
     the texel that was ON ITS OWN PIXEL when the fold began (objects.js) */
  snap.aff = S.lensAff ? S.lensAff.slice() : [1, 0, 0];
  snap.vp = vp();
  snap.ms = (window.performance || Date).now() - t0;
  Room.printSnapMs = snap.ms;
}

/* R58: the raster itself, on the glass — the one way to see what the
   folded paper is being textured with. `Room.printPeek()` in the console,
   or a `shot` step after it. Nothing calls it; it costs nothing. */
Room.printPeek = function (on) {
  S.peek = (on === undefined) ? !S.peek : !!on;
  draw();
  var s = S.printSnap;
  return 'peek ' + S.peek + (s ? ' print ' + s.w + 'x' + s.h + ' in ' + (s.ms || 0).toFixed(2) + ' ms' : ' no print');
};
function printBlit() {
  var gl = S.gl, s = S.printSnap;
  if (!s) return;
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, s.fbo);
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
  gl.blitFramebuffer(0, 0, s.w, s.h, 0, 0, S.cv.width, S.cv.height, gl.COLOR_BUFFER_BIT, gl.NEAREST);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
}

/* =================================================================
   LP4 — THE GROUND'S FOUR PASSES, AND THE TWO BUFFERS THEY WALK

   The cut itself and why the composition is exact are written over
   GROUND_FS. What is here is the plumbing: one framebuffer, two colour
   attachments swapped between the passes, one depth renderbuffer of their
   own, and the rule that a pass whose program is not collected yet is
   simply not drawn this frame.

   THE BUFFERS ARE THE CANVAS'S SIZE, not the portal's. The passes read
   each other at `gl_FragCoord.xy` and the scissor already keeps the work
   inside the window, so a buffer the size of the window would buy nothing
   but an offset to get wrong every time the camera moves the portal.
   RGBA16F where the context has EXT_color_buffer_float (the same
   capability the LP2 bake asks for), RGBA8 where it does not; at Tommy's
   3840x2160 that is 133 MB of colour and 33 MB of depth.

   THE DEPTH IS THEIR OWN AND IT IS CLEARED BEFORE THE FIRST PASS. The
   default framebuffer's depth buffer cannot be attached to a framebuffer
   object, so the three offscreen passes carry a renderbuffer of their
   own: cleared to 1 (inside the scissor, which is all that is drawn),
   written by mat, tested LEQUAL by all three. The fragment that survives
   is therefore the NEAREST one — which is the same fragment the real
   depth buffer keeps when post draws — and that is what makes the texel
   each pass reads its own fragment's colour where the country folds over
   itself. GROUND_VS's `invariant gl_Position` is the other half of it.

   THE STENCIL IS NOT COPIED. plateClip's cut lives in the default
   framebuffer's stencil; the offscreen has none, so the three passes
   shade the whole scissor box and post — which draws into the real
   framebuffer with the real stencil — is what the reader sees clipped.
   The extra fragments are outside the paper and are never read.
   ================================================================= */
var GPREV_UNIT = 6;          /* the unit the previous pass's colour is on */
/* the two colour buffers, their depth and the framebuffer that carries
   them, rebuilt whenever the canvas changes size */
function ensureGroundFbo(gl) {
  var w = S.cv.width, h = S.cv.height;
  if (!(w > 0 && h > 0)) return null;
  var B = S.gpBuf;
  if (B && B.w === w && B.h === h) return B;
  disposeGL('gpass');
  B = owned('gpass', function () {
    var ifmt = S.colorFloat ? gl.RGBA16F : gl.RGBA8;
    var type = S.colorFloat ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE;
    var tex = [null, null];
    for (var i = 0; i < 2; i++) {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, ifmt, w, h, 0, gl.RGBA, type, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      tex[i] = t;
    }
    gl.bindTexture(gl.TEXTURE_2D, null);
    var dep = gl.createRenderbuffer();
    gl.bindRenderbuffer(gl.RENDERBUFFER, dep);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, dep);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex[0], 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { w: w, h: h, fbo: fbo, tex: tex, dep: dep };
  });
  return (S.gpBuf = B);
}
/* ISSUE all four links and collect none of them, in the order at the head
   of GROUND_ISSUE: post first because it is the smallest and nothing is
   on the glass without it, then the material the reader sees first. */
function issueGround(gl, parts) {
  var was = LINK_ONLY; LINK_ONLY = true;
  parts = parts || GROUND_ISSUE;
  try {
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      prog(gl, GROUND_VS, GROUND_FS(part), 'ground-' + part).__part = part;
    }
  } finally { LINK_ONLY = was; }
}
/* the pass's program if the driver has finished with it, and null if it
   has not. Without KHR_parallel_shader_compile there is nothing to ask
   and the collection blocks, which is R6's order and the one that was
   measured faster on a context without the extension. */
function groundPass(gl, part) {
  var name = 'ground-' + part, p = PROGS[name];
  if (!p) {
    var was = LINK_ONLY; LINK_ONLY = true;
    try { p = prog(gl, GROUND_VS, GROUND_FS(part), name); } finally { LINK_ONLY = was; }
  }
  p.__part = part;
  if (p.u) return p;
  if (KHR && !gl.getProgramParameter(p, KHR.COMPLETION_STATUS_KHR)) return null;
  return progFinish(gl, p);
}
/* the ISO arms that draw no ground at all still leave a whole picture */
function markGroundWhole() {
  var d = S.gpDrawn || (S.gpDrawn = {});
  for (var i = 0; i < GROUND_ORDER.length; i++) d[GROUND_ORDER[i]] = d[GROUND_ORDER[i]] || 1;
}
function groundWhole() {
  var d = S.gpDrawn;
  return !!(d && d.mat && d.line && d.feat && d.post);
}
function groundPasses(gl, setU, GB, vao) {
  /* the buffers FIRST, and the mesh after them: a resize disposes the old
     set, and disposeGL unbinds the vertex array on its way past */
  var B = ensureGroundFbo(gl);
  if (!B) return;
  var out = (S.capPrint && S.printSnap) ? S.printSnap.fbo : null;
  gl.bindVertexArray(vao);
  /* the two the picture cannot start without: post puts it on the glass
     and mat is the material under everything else */
  var pPost = groundPass(gl, 'post'), pMat = groundPass(gl, 'mat');
  if (!pPost || !pMat) return;
  var pLine = SK('gline') ? null : groundPass(gl, 'line'), pFeat = SK('gfeat') ? null : groundPass(gl, 'feat');
  var prim = GB.prim || gl.TRIANGLES;
  function pass(p, prev) {
    gl.useProgram(p);
    if (p.u.uField) gl.uniform1i(p.u.uField, 0);
    setU(gl, p);
    if (p.u.uPrev) {
      gl.activeTexture(gl.TEXTURE0 + GPREV_UNIT);
      gl.bindTexture(gl.TEXTURE_2D, prev);
      gl.uniform1i(p.u.uPrev, GPREV_UNIT);
      gl.activeTexture(gl.TEXTURE0);
    }
    gl.drawElements(prim, GB.n, gl.UNSIGNED_INT, 0);
    var d = S.gpDrawn || (S.gpDrawn = {});
    if (!d[p.__part]) {
      d[p.__part] = +(performance.now() - S.tBoot).toFixed(1);
      bootLog('ground pass ' + p.__part + ' drawn', d[p.__part] + ' ms');
    }
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, B.fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, B.tex[0], 0);
  gl.depthMask(true);
  gl.clear(gl.DEPTH_BUFFER_BIT);          /* the scissor box only */
  pass(pMat, null);
  var src = 0, dst;
  if (pLine) {
    dst = 1 - src;
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, B.tex[dst], 0);
    pass(pLine, B.tex[src]); src = dst;
  }
  if (pFeat) {
    dst = 1 - src;
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, B.tex[dst], 0);
    pass(pFeat, B.tex[src]); src = dst;
  }
  /* R58: and back to whatever the frame is drawing INTO — which is the
     canvas on an ordinary frame and the print raster on the one frame
     that strikes it, not null either time by assumption. */
  gl.bindFramebuffer(gl.FRAMEBUFFER, out);
  /* ==== R35: THE GROUND IS BLENDED NOW, and only at the horizon.
     The margin is the same surface and the same program, so what used to
     be the beyond's own ending — "the paper ends where the wash has taken
     it, and nowhere else" (relief.js :5124) — is the ground's last line.
     Inside the neat line every fragment's alpha is exactly 1 and the blend
     is the identity; outside, the far conjecture arrives at the colour
     already painted behind it, which is what lets the SKY through above
     the horizon. One gl.enable, no fragment work, no second pass. ==== */
  gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  pass(pPost, B.tex[src]);
  gl.disable(gl.BLEND);
  /* and nothing of the ground's own is left bound: the next frame renders
     INTO the texture that would still be on the sampler */
  gl.activeTexture(gl.TEXTURE0 + GPREV_UNIT);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.activeTexture(gl.TEXTURE0);
}

/* ==== US1 (2026-10-04): UPRIGHT, THE FOLDED MAP IS AT THE REAL DESK.
   Tommy: "the issue really is just the one of 'folded map has desk full
   screen' which gives you extra desk that disappears when you zoom out to
   the room. we just about get away with it in landscape but in portrait
   it's weird". The stand-in desk is sized to what the frame sees, and the
   tall upright frame sees far past the real desk, so the stand-up shrank
   most of it away in plain view. Upright off the map (RU3's frame, `L.ah`)
   the reader is seated at the real desk in the real room instead:
   objects.js `deskT` and place.js's draws read `cam.seat` beside the
   stand-up. It is ONLY upright because there every way in and out is
   already a cut: the upright folded map is reached only by a turn (the
   fold needs the phone on its side), the stand-up is then only the camera,
   and an unfold gives the glass to the Journal at its release (UF1), when
   `L.ah` goes. So no desk is ever swapped in view. RS2 did this at every
   folded map and the swap came into view (branch poc/room-seat). It is
   set at once, never eased: easing would be the swap seen. A room that is
   not there yet is asked for, and lands as a cut. ==== */
function upSeat() {
  var L = S.layout, P = window.RoomPlace, want = !!(PHONE && L && (L.ah || L.side));   /* US2: and on its side */
  var ready = !!(P && P.ready && P.basePrimed && !P.disabled);
  if (want && !ready && !upSeat.asked && S.needPlace) { upSeat.asked = true; S.needPlace(); }
  S.cam.seat = want && ready ? 1 : 0;
}
Room.seat = function () { return S.cam.seat || 0; };

/* ==== FP3 (2026-10-08): `?fps=2` TIMES EACH PASS OF THE FRAME. Tommy's iPhone sailed at 10 fps (110 ms frames).
   Each mark below waits for the GPU to finish what was asked of it so far (a one-pixel read does that on every
   browser; gl.finish does not on iOS), so the gap between two marks is that pass's own CPU and GPU time. It slows
   the frame it measures, so the readout's fps is not the real one: the SHARES are what it is for. Off without
   the query, where PF is one comparison. ==== */
var SKIP = ',' + (Q.get('skip') || '') + ',';
(Q.get('dbg') || '').split(',').forEach(function (c) { if (c) document.documentElement.classList.add('dbg-' + c); });   /* FP4: ?dbg=nofilter,noshadow for the card's styles */   /* FP4: ?skip=room,desk,... leaves passes out, to time them by their absence */
function SK(n) { return SKIP.indexOf(',' + n + ',') >= 0; }
var PFSYNC = Q.get('fps') === '2', PROF = PFSYNC || Q.get('fps') === '3';   /* fps=3: script time only, no GPU wait */
var pfT = 0, pfAcc = {}, pfN = 0, pfPx = new Uint8Array(4);
function PF(name) {
  if (!PROF) return;
  if (PFSYNC) S.gl.readPixels(0, 0, 1, 1, S.gl.RGBA, S.gl.UNSIGNED_BYTE, pfPx);
  var t = performance.now();
  if (name) pfAcc[name] = (pfAcc[name] || 0) + (t - pfT);
  pfT = t;
}
Room.passTimes = function () { var o = { frames: pfN, ms: pfAcc }; pfAcc = {}; pfN = 0; return o; };
function draw() {
  if (F1_WAIT) return;             /* FW2 (c): frame one's programs are not in yet */
  var gl = S.gl, f = S.fld, L = S.layout;
  if (PROF) { pfN++; PF(''); }
  var tDraw = S.readyMs ? 0 : performance.now();   /* PT1: the draws before ready are named */
  /* LP2: the baked chain, before anything reads it and before the
     viewport and the clear this function sets for itself two dozen lines
     down. It costs one string compare a frame when nothing has moved. */
  ensureBake(gl);
  /* R58: the print is struck on the first frame that has a fold in it and
     thrown away on the first frame that has none */
  var capt = !!S.capPrint;
  /* FD1, 2026-09-20: R80's take-up is gone, so the raster is struck at
     the first degree of the fold, as R47 had it. */
  var paintFold = foldAmt() || foldSettling();   /* CF3: the settle is still the fold's draw */
  if (!capt) {
    if (paintFold > 0) { if (!S.printSnap) printSnap(); }
    else if (S.printSnap) printFree();
  }
  var folded = paintFold > 0;
  upSeat();
  /* ==== R4: FRAME 1 IS A CORRECT PICTURE OF WHAT IS READY. Until the
     worker hands the field over, `S.fld` is the stub — the sheet's own
     geometry, which is arithmetic on the constants and needs no record —
     and everything from the window inwards is simply not a pass that
     happens. Nothing here is drawn and then thrown away. ==== */
  var hasField = !(f.stub);
  liftStep();                      /* the aim this frame is drawn with, before anything reads it */
  S.VP = vp();
  S.vpNow = S.VP;                    /* PR1: the camera before any zoom, to tell the raster's from it */
  S.lensAff = [1, 0, 0];             /* PR1: the glass's own zoom on top of vp(), as k, x, y */
  if (capt && S.capNdc) { S.VP = mul(S.capNdc, S.VP); S.lensAff = [S.capNdc[0], S.capNdc[12], S.capNdc[13]]; }
  else if (S.lens.k > 1.0005 || zGoal.k > 1.0005) {
    /* ZM1: a context left (sat down, stood up, unfolded, the machine
       taken up) lets the glass go back to the eye's own view */
    if (zoomCtx() !== zCtx) { zGoal = { k: 1, x: 0, y: 0 }; zTick(); }
    var zk = S.lens.k;
    if (zk > 1.0005) {
      S.VP = mul(new Float32Array([zk, 0, 0, 0,  0, zk, 0, 0,  0, 0, 1, 0,  S.lens.x, S.lens.y, 0, 1]), S.VP);
      S.lensAff = [zk, S.lens.x, S.lens.y];
    }
  }
  /* R13: ONE rect a frame, worked out before the first pass, because the
     lettering and the marks are cut to it as well as the ground. */
  S.gRect = hasField ? gridRect() : null;
  /* ==== R20: THE TWO NUMBERS THE RECORD'S MATERIAL IS DRAWN WITH, worked
     out ONCE and before any pass, because the CONJECTURE wears that
     material at its first row now (water.js BEY_FS) and is drawn before
     the ground. Read out of the ground's own pass they would have been a
     frame stale — invisible in a still and a lag along the seam through a
     tilt. ==== */
  if (hasField) {
    S.lz = Math.log(fitWidth() / S.view.w) / Math.LN2;
    S.lev = S.levForce ||
      [F.smoothstep(LEV_B - 0.5, LEV_B + 0.5, S.lz), F.smoothstep(LEV_C - 0.5, LEV_C + 0.5, S.lz)];
    S.shade = shadeOf(S.tilt);
  }
  gl.viewport(0, 0, S.cv.width, S.cv.height);
  gl.disable(gl.DEPTH_TEST);
  gl.clearColor(0.114, 0.078, 0.051, 1);
  gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);

  var sun = window.Sky && Sky.screen ? Sky.screen() : null;
  var sv = sun ? [sun.dx || 0.4, -(sun.dy || 0.5), 0.7] : [0.4, -0.5, 0.7];

  /* 0. E8: THE ROOM, when the camera has left the desk. The floor, the
        wall, the window in it and the desk's own edge and legs, all
        BEHIND the desk, which paints over them. At the desk pose this
        line is not reached at all — the room is not a flag inside a
        shader, it is a pass that does not happen. */
  if (window.RoomPlace && !capt && !SK('room')) RoomPlace.drawBack(gl, S);

  /* 1. the desk, the shadows on it and the two books (E4, objects.js) */
  /* R58: and NONE of the desk on the frame that is making the print. The
     raster is only ever read inside the sheet's own deckle, so the oak,
     the books, the lamp and the laptop are work with nowhere to land. */
  var O = window.RoomObjects;
  PF('room'); if (capt || SK('desk')) { /* no oak on the print */ } else if (!(O && O.drawDesk(gl, S))) {
  gl.useProgram(S.pDesk);
  gl.bindVertexArray(S.vaoQuad);
  gl.uniformMatrix4fv(S.pDesk.u.uVP, false, S.VP);
  gl.uniform3f(S.pDesk.u.uSheet, L.ox, L.oy, L.k);
  gl.uniform1f(S.pDesk.u.uZ, -0.5);
  gl.uniform3f(S.pDesk.u.uSun, sv[0], sv[1], sv[2]);
  var big = Math.max(f.sheet.w, f.sheet.h) * 3;
  gl.uniform4f(S.pDesk.u.uRect, f.sheet.w / 2 - big, f.sheet.h / 2 - big, big * 2, big * 2);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
  /* 1b. R27 (2): THE LAMP, a solid standing on the desk — after the oak it
        casts its shadow on and BEFORE the sheet, which cuts it. */
  /* ==== U2: AND IT IS DRAWN AT 1d NOW, AT BOTH POSES — why, in place.js
     above `P.deskLamp`. It had to move PAST the books rather than stay on
     this line, because the books' own pass is what clears the room's spent
     depth for the whole desk (objects.js `depthFore`), and a lamp drawn
     before that clear would have its depth thrown away by it. ==== */
  PF('desk'); if (O && !capt && !SK('books')) O.drawBooks(gl, S);
  /* 1c. X1: THE LAPTOP, which stands on the oak east of the journal. It
        goes in with the books, against the same depth buffer and before
        the sheet, because it is the same kind of thing: a solid on the
        desk. At the fit it is off the glass entirely. */
  PF('books'); if (O && O.drawLaptop && !capt) O.drawLaptop(gl, S);
  /* 1d. U2: THE LAMP, the last of the solids on this desk and the same
        kind of thing as the two before it: one draw, one depth buffer,
        no clear of its own, and before the sheet — so the sheet's own
        pass decides, pixel by pixel, which of the two is in front. At
        the desk pose that is `deskLamp` under `?edgelamp=1`; in the room
        it is `drawFront`, which used to be step 8 and the last thing in
        the frame. */
  if (window.RoomPlace && !capt) {
    if (R17_EDGE_LAMP && RoomPlace.deskLamp) RoomPlace.deskLamp(gl, S);
    RoomPlace.drawFront(gl, S);
  }

  /* 2. the sheet: E4's paper (deckle, creases, the turned corner) or E1's
     flat quad with the furniture laid on it */
  PF('laptop+front'); if (!(O && O.drawSheet(gl, S))) {
  gl.useProgram(S.pSheet);
  gl.bindVertexArray(S.vaoQuad);
  gl.uniformMatrix4fv(S.pSheet.u.uVP, false, S.VP);
  gl.uniform3f(S.pSheet.u.uSheet, L.ox, L.oy, L.k);
  gl.uniform1f(S.pSheet.u.uZ, 0);
  gl.uniform4f(S.pSheet.u.uRect, 0, 0, f.sheet.w, f.sheet.h);
  gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, S.texFurn);
  gl.uniform1i(S.pSheet.u.uFurn, 1);
  gl.uniform1f(S.pSheet.u.uHasFurn, S.furnReady ? 1 : 0);
  /* R42 (a): and frame 1's paper takes the lantern too */
  if (S.pSheet.u.uLampS) {
    var lsh = (window.RoomObjects && RoomObjects.lampSheet) ? RoomObjects.lampSheet(S) : [0, 0, 1, 1, 0];
    gl.uniform4f(S.pSheet.u.uLampS, lsh[0], lsh[1], lsh[2], lsh[3]);
    gl.uniform1f(S.pSheet.u.uLampN, lsh[4]);
  }
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /* 2b. R8: THE MONTH RULERS, on the paper and under everything else.
        They are print, so they go down with the sheet — before the
        window's scissor, before the ground, and under the index card
        that is drawn over the whole desk at step 7. */
  /* ==== R47: AND THEY FOLD. X2 stopped drawing the ruler the moment the
     sheet had any fold in it (its OPEN 1: "the card and the rulers are
     lifted off, they do not fold") — so the graduated border, the month
     names and the day figures all went at the first degree of the first
     turn. They are print on the paper and they go where the paper goes:
     ruler.js compiles objects.js's own fold and turns each graduation
     with the panel it is set on.

     TWO PASSES WHILE THERE IS A FOLD. Print lying flat belongs UNDER the
     ground (the window paints over it, which is what has put the ruler
     here since R8); print on a flap that has left the desk belongs in
     front of it, because the flap is between the eye and the window. So
     pass 1 goes here and pass 2 after the ground at step 3b. Flat, it is
     one draw with `uFoldPass` 0 and this is R8's own line.

     AND PASS 1 IS CUT WHERE THE MAP IS CUT. Print that is still lying
     flat is UNDER the flap that has come over it, and this whole module
     is drawn OVER the sheet's own mesh — so without the cut the month
     names went on being painted on top of the paper that had just
     covered them. The cut is `foldCut`, the map's own scissor, taken on
     the whole canvas instead of on the window's box: the same crease, the
     same arithmetic and the same frame as the ground's. ==== */
  /* ==== R58: AND THERE IS ONLY THE FLAT PASS NOW. R47's two passes were
     the answer to a ruler that had to fold while the map could not: pass
     1 for the graduations still lying down (cut at the crease), pass 2
     for the ones in the air (drawn in front of the ground). Both are
     gone. The ruler is PRINT, it is in the print raster with everything
     else on that piece of paper, and a flap carries it up by being
     textured with it. Pass 2 was also the fault Tommy named — "2nd fold
     in progress, immediately the map x-axis reappears": the month names
     were drawn in front of everything on a packet whose top face is the
     left half's plain BACK. ==== */
  PF('sheet'); if (S.ruler && !folded) S.ruler.draw(0);

  /* 3. the ground — clipped to the window in the paper */
  if (!hasField) {
    /* R12: FRAME 1's CARD — furniture on the desk, outside the window's
       scissor, so it belongs in the picture that has no record in it.
       R12 wrote "before the lamp, which is the last thing on the desk
       (step 8)"; U2 moved the lamp to step 1d, above, which this path
       has already passed through, so there is no second draw here. */
    if (S.card) S.card.draw();
    gl.bindVertexArray(null); S.drawn++; if (tDraw) bootLog('draw ' + S.drawn + ' (frame one)', (performance.now() - tDraw).toFixed(1) + ' ms'); return;
  }
  /* R58: THE WINDOW IS NOT A PASS WHILE THE PAPER IS FOLDED. Everything
     from here to the scissor's end is the map, in the camera's own
     projection; on a folded sheet it lives in the print raster instead,
     and drawing it here is both wrong (it cannot ride a panel) and paid
     for twice. */
  if (!folded) {
  gl.enable(gl.SCISSOR_TEST);
  /* ==== E7: the window's box from its four PROJECTED corners. E1 took it
     from the css rect, which is the right rectangle only while the camera
     is over the glass — with the reader standing in the room the portal moves and the ground was
     clipped to where the window used to be. ==== */
  var box = window.RoomWater ? RoomWater.portalBox(S) : null;
  if (!box) {
    var Pq = S.portalScr || S.portal;           /* MP2 */
    var px = Pq.l * S.dpr, py = Pq.t * S.dpr;
    var pw = Pq.w * S.dpr, ph = Pq.h * S.dpr;
    box = { x: Math.round(px), y: Math.round(S.cv.height - py - ph),
            w: Math.round(pw), h: Math.round(ph) };
  }
  gl.scissor(box.x, box.y, box.w, box.h);
  /* R25 (2): and the neat line itself, in sheet space, when the camera has
     a pitch on it and the box above is no longer the window's own shape */
  /* ==== X2: AND THE MAP IS ONLY ON THE PAPER THAT IS STILL LYING DOWN.
     The ground, the sea, the marks and the lettering are drawn INTO the
     window in the paper; the paper's own print rides the fold in the
     sheet's texture, but these do not, so a panel that has been picked up
     would leave its map painted on the desk behind it. The scissor is cut
     back to the crease that is working: the map is eaten from the east as
     the first two folds take the sheet in to a quarter of its width, and
     from the north when the last one takes it in two. What the reader sees
     is the map going under the paper that is being folded over it, which
     is what folding a map over itself does.

     THE CUT IS THE CREASE'S OWN PROJECTED LINE, not a guess: both ends of
     it are put on the glass by the same projection the sheet is drawn
     with, and the side the sheet's west edge is on is the side that keeps
     the map — so it is right at any bearing, not only north-up. ==== */
  var clipped = plateClip(gl);
  /* ==== E7: the sky wash, behind the horizon and inside the window only */
  PF('section+setup'); if (window.RoomWater && !SURF) RoomWater.drawSky(gl, S);
  PF('sky'); gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL);
  /* ==== R35: AND THERE IS NO SECOND DRAWING TO PUT BEHIND THE FIRST.
     E7 drew the conjecture FIRST and then CLEARED THE DEPTH BUFFER, so a
     guess could never win a pixel from the record — a ruling enforced by
     throwing depth away, which is what left the record's water with
     nothing to be occluded by (R20 c's stencil) and the two meshes with a
     curtain between them (R19). One surface needs none of it: the ground
     occludes itself honestly, the margin is the same mesh at the same
     pitch, and nothing surveyed can be hidden by a guess because the guess
     is the same surface. The beyond's pass, the curtain's pass, the depth
     clear and the record's water stencil are all gone. ==== */
  /* ==== /E7 =========================================================== */
  var g = S.gRect || gridRect();
  /* R44 (a) / R54: the mesh is whichever one snapRect asked for by name.
     It has already halved the cell to match, so the rect is the same rect
     whichever answers, and only the vertex count changes. */
  var GM = (S.gm && g.key) ? S.gm[g.key] : null;
  var GB = GM || S.grid, GVA = GM ? GM.vao : null;
  gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
  var hq = f.hach || { cut0: 0.0022, q1: 0.006, q2: 0.014 };
  var lz = S.lz, lv = S.lev;
  /* LP4: the names are laid out ONCE for the four passes, not once each —
     the layout is the lettering's own work and only one pass reads the
     boxes it leaves. */
  var gp = null;
  if (S.letters) { S.letters.layout(false); gp = S.letters.gaps; }
  /* and the field again, after it: the layout can rebuild an atlas, and
     the four passes below read their heights off unit 0 (2026-09-26) */
  gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
  var lshG = (window.RoomObjects && RoomObjects.lampSheet)
    ? RoomObjects.lampSheet(S) : [0, 0, 1, 1, 0];
  /* LP4: and every one of these is asked for by NAME on the pass being
     drawn. A uniform a pass does not use is not active in it, so its
     location is undefined and the call is a no-op — which is the memo:
     four passes set between them exactly the uniforms the one program
     set, and each one sets only what it can read. */
  function groundU(gl, p) {
    var u = p.u;
    setCommon(gl, p);
    if (u.uGrid) gl.uniform4f(u.uGrid, g.x, g.y, g.w, g.h);
    if (u.uCoast) gl.uniform1f(u.uCoast, f.COAST);
    if (u.uStep) gl.uniform1f(u.uStep, Math.sqrt(5) / 8);
    if (u.uDpr) gl.uniform1f(u.uDpr, S.dpr);
    if (u.uPxPerSheet) gl.uniform1f(u.uPxPerSheet, (S.portalScr || S.portal).w / S.view.w * S.dpr);   /* MP2 */
    if (u.uHach) gl.uniform1f(u.uHach, S.hachMode > 0 ? 1 : 0);
    if (u.uHachC) gl.uniform4f(u.uHachC, hq.cut0, hq.q1, hq.q2, S.hachMode);
    /* THE SIZE THE INK HOLDS: device pixels per sheet unit AT THE FIT. Stroke
       length, stroke pitch and every stroke width are measured in it, so the
       ink is the same physical weight at ratio 1, 1.25 and 2 and at every
       zoom — which is what `calc(Npx / var(--z))` and `patternTransform` are
       for on the sheet, and what the shader gets for nothing. */
    if (u.uInk) gl.uniform1f(u.uInk, (S.portalScr || S.portal).w / fitWidth() * S.dpr);
    if (u.uLev) gl.uniform2f(u.uLev, lv[0], lv[1]);
    if (u.uDayG) gl.uniform4f(u.uDayG, DAY_GRID ? C.cw : 0, C.px, C.ch, C.py);
    /* R9 (a): the grid fades IN over the near band's octave, as it did, and
       then OUT over the next one. Past the near band the contours, the
       ridge-and-furrow and the cell's own size carry the picture and a
       ruling of the cells is just squared paper; the hover's own pencil
       cross is what says which day the hand is on. Peak 0.78 at zoom ~620. */
    if (u.uDayGA)
      gl.uniform1f(u.uDayGA, lv[1] * (1 - F.smoothstep(LEV_C, LEV_C + 1.2, lz)));
    if (u.uNGap) {
      gl.uniform1i(u.uNGap, (gp && LET_CUT) ? gp.n : 0);
      if (gp && gp.n) {
        gl.uniform4fv(u.uGapB, gp.box.subarray(0, gp.n * 4));
        gl.uniform2fv(u.uGapR, gp.rot.subarray(0, gp.n * 2));
        gl.uniform1f(u.uGapF, 1.6 * S.view.w / (S.portalScr || S.portal).w);
      }
    }
    if (u.uReveal) {
      var rv = S.reveal;
      gl.uniform4f(u.uReveal, rv ? rv[0] : 0, rv ? rv[1] : 1, rv ? rv[2] : 0, rv ? rv[3] : 1);
    }
    if (u.uShade) gl.uniform1f(u.uShade, shadeOf(S.tilt));
    if (u.uFilm) gl.uniform1f(u.uFilm, 0.55);
    /* R48: the lantern's temperature on the plate — the SAME pool the sheet,
       the card and frame 1 are warmed on, asked for from the one place that
       works it out, so there is one lamp on the desk and not four. Nought by
       day: lampSheet's fifth number is the night. */
    if (u.uLampS) {
      gl.uniform4f(u.uLampS, lshG[0], lshG[1], lshG[2], lshG[3]);
      gl.uniform1f(u.uLampN, lshG[4]);
    }
    if (u.uSun) gl.uniform3f(u.uSun, sv[0], sv[1], sv[2]);
    if (window.RoomWater) RoomWater.groundUniforms(gl, p, S);
  }
  if (ISO !== 'bey' && ISO !== 'face' && ISO !== 'noground' && !SK('ground'))
    groundPasses(gl, groundU, GB, GVA || S.vaoGrid);
  else markGroundWhole();

  /* ==== E7: the water at the datum, the earth at the front ============= */
PF('ground');   if (window.RoomWater && !SK('water')) {
    /* R35: and the instrument is TAUGHT THE PLANE. R19's ?surf=1 drew the
       three surfaces flat and no water at all, so every pixel of sea bed
       showing between two surfaces under a translucent plane counted as
       bare paper and every pixel the plane covered could not be counted at
       all (R20 OPEN 2 stopped on exactly that). The plane is a surface; it
       is drawn, in its own colour, and the count is honest. */
    RoomWater.drawWater(gl, S, g);
  }
  /* ==== /E7 =========================================================== */

  /* 4. THE MARKS (E5): one instanced draw per family, then the
     buildings, then the landmark sprites and the bench-mark plates */
  PF('water'); if (S.marks && !SURF && !SK('marks')) S.marks.draw();
  if (!SURF) doomDraw();   /* D1: the surveyor, and the arrows round it */

  /* 4b. X5: THE VOYAGE — the boat, her track, the marks of the sea west
        of the record and the soundings she has taken. Only ever present
        at all when `?voyage=1` put the module on the page; one string
        compare on every other frame this engine will ever draw. */
  PF('marks'); if (window.RoomVoyage && !SURF) RoomVoyage.draw();
  /* 5. the lettering — on the glass, over everything the window holds */
  PF('voyage'); if (S.letters && !SURF && !SK('letters')) S.letters.draw();
  /* 6. N2: nothing of the day's is drawn ON the country any more — the
        pencil cross went at R24 and the pinned day's marking pin with
        the lock. The card is an object over the whole desk (7, below). */
  PF('letters'); if (clipped) plateUnclip(gl);
  gl.disable(gl.SCISSOR_TEST);
  gl.disable(gl.DEPTH_TEST);
  }
  /* 3b. R47: and the ruler's SECOND pass — the graduations on the paper
        that has left the desk. They are in the air between the eye and
        the window, so they go over the ground and not under it. Nothing
        is drawn twice: pass 1 took the flat panels and refused these,
        pass 2 takes these and refuses those. */
  /* R58: pass 2 is gone — see the ruler above. */
  /* 7. E6: the card and its shadow are objects lying over the whole
        desk, so they come after the scissor.
        R12: with no record there is no `S.day`, and the stock draws
        itself — frame 1's blank card.
        R47: AND THE CARD IS NOT TAKEN OFF THE DESK ANY MORE. X2 stopped
        drawing it the moment the sheet moved; it is a piece of index
        stock lying on the map and it slides off on to the oak instead
        (day.js, `foldSlide`), which is what a hand folding a map does
        with the card that is lying on it. */
  if (capt) { /* R58: the card is not printed on this paper */ }
  else if (S.day) S.day.drawCard();
  else if (S.card) S.card.draw();
  /* 8. E8: the lamp stood ON the desk and so was the last thing in the
        frame — U2: which is exactly how it came to be painted over a
        raised flap. It is drawn with the other solids at step 1d and
        nothing is drawn here. The card and the note above still come
        after the sheet carrying no depth, so they lie OVER the lamp
        where they meet it on the glass, as a card and a note lying on
        the desk in front of it do. */
  PF('card'); if (capt) { gl.bindVertexArray(null); return; }
  /* N1: the light line is in the print (strikeNote); nothing to lay */
  if (window.RoomPin) RoomPin.lay(S);          /* R23: the corner's hotspot */
  if (window.RoomIndex) RoomIndex.lay(S);      /* R21: the year index, live */
  /* N1: the six marks are on the note under the map, not on the paper:
     nothing of theirs is laid over the sheet any more (progress.js) */
  if (window.RoomHandle) RoomHandle.lay(S);    /* X4: the name in the cartouche */
  /* X1: and the terminal's glass, on the laptop's screen — one call, one
     string compare, and a style write only when the camera has moved. It
     is only ever here at all if somebody asked for the terminal. */
  if (window.RoomTerm) RoomTerm.lay();
  /* ==== R4: NO VERTEX ARRAY IS LEFT BOUND WHEN THE FRAME ENDS.

     A buffer bound to ELEMENT_ARRAY_BUFFER is recorded IN THE VERTEX
     ARRAY that happens to be bound, so any code that builds a mesh while
     someone else's array is still bound silently steals its indices. It
     never mattered while every buffer in the engine was built during
     boot, before the first frame; the moment the grid, the beyond's mesh
     and the marks' instance buffers were moved to AFTER the first frame
     (this item) it mattered at once — the ground's 589,824 indices landed
     in the paper's array and the sheet drew nothing at all, with no error
     and no warning. The frame now leaves the binding where it found it. */
  weatherGrade(gl);                            /* LK3 */
  if (S.peek) printBlit();
  gl.bindVertexArray(null);
  S.drawn++;
  PF('dom+grade'); if (tDraw) bootLog('draw ' + S.drawn, (performance.now() - tDraw).toFixed(1) + ' ms');
  /* LP4: the four passes and the lettering are all behind us now */
  if (S.wantReady && !S.readyMs && groundWhole()) ready();
}

/* =================================================================
   E9 / N1: THE LIGHT LINE — the one piece of the furniture that is not
   fixed print, STRUCK INTO THE PRINT.

   The sheet's last datum note names the light it is drawn under, and
   Sky writes that sentence from the hour, the sun and London's weather.
   E1 rastered it into the furniture texture with the rest of the margin,
   which froze the sentence that happened to be true the night the old
   sheet was dumped; E9 took it out of the SVG and set it as one <p> of
   DOM laid over the paper by the sheet's projection, which put a line
   of type on the plane at any tilt but left it unlit and unfiltered —
   styles.css carried a hand-tuned colour so it would pass for the ink
   above it. N1 (Tommy, 2026-09-20: "We still have a little bit of text
   in the bottom right ... We may as well go to that too"): it is struck
   into the print the way handle.js's section is (S3b) — the sentence
   as an SVG <text> in the frame's own Sorts Mill Goudy, at the datum
   notes' size and in their ink (.sv-datum-note: 11.5px, --ink-3
   #7d6d59 as the texture has it), rastered through an <img> at the
   region's texel size and composited over the pristine print with
   `Room.furnPatch`. It folds with the paper and takes the lamp because
   it IS the paper now; the eight lines of notes are one block of one
   ink, and no DOM is laid.

   The anchor is the note's own place in the sheet's units, unchanged
   from the DOM line: flush right at x 2285 with the datum notes above
   it, baseline y 1112, one line below the marker-horizon note. The
   patch region is a rectangle in those units wide enough for the
   longest sentence Sky.noteText() can write (the lantern under an
   overcast sky, measured at 11.5px in the face) with a margin, and one
   line tall with a margin above the ascenders and below the descenders.

   It is re-struck on Sky.onChange and never otherwise: the strike is
   keyed on the sentence and the region's texel size, so a change that
   leaves the sentence as it was is a no-op, and a raster still in the
   air when a later sentence arrives is dropped on landing (the
   generation). The face is the one the section fetched, or the one
   this fetched for the section — `Room.printFace`, once.
   ================================================================= */
var NOTE_X = 2285, NOTE_Y = 1112;                     /* sheet units: right edge, baseline */
var NOTE_BOX = { x: 1845, y: 1098, w: 448, h: 21 };   /* the patch region, sheet units: 49 west of the longest line, 8 east of the anchor, 14 above the baseline, 7 below */
var noteKey = '', noteGen = 0, noteImg = null, noteLast = null, noteMs = 0, noteBound = false;
function noteMarkup(text, R) {
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + R.rx.toFixed(4) + ' ' +
    R.ry.toFixed(4) + ' ' + R.rw.toFixed(4) + ' ' + R.rh.toFixed(4) +
    '" width="' + R.pw + '" height="' + R.ph + '">' +
    '<style>' + (Room.printFace() || '') +
    'text{font-family:"Sorts Mill Goudy",Georgia,serif;font-size:11.5px;fill:#7d6d59;stroke-width:0;' +
    'font-variant-numeric:lining-nums tabular-nums}</style>' +
    '<text x="' + NOTE_X + '" y="' + NOTE_Y + '" text-anchor="end">' +
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') +
    '</text></svg>';
}
/* the drawFn furnPatch keeps and re-applies after every re-cut of the
   print; a print re-cut at ANOTHER scale (a resize) gets the old raster
   for one frame and a fresh strike straight after, as the section does */
function noteDraw(ctx, R) {
  if (!noteImg) return;
  ctx.drawImage(noteImg, 0, 0);
  if (noteImg.width !== R.pw || noteImg.height !== R.ph) {
    noteKey = ''; setTimeout(strikeNote, 0);
  }
}
function strikeNote() {
  /* 09-20 morning, Tommy: "we killed all the other text, why would we
     keep this?" The light note is not struck any more; the machinery
     stays for the sky's other listeners and for the day it is wanted. */
  return false;
  if (!window.Sky || !Sky.noteText) return false;
  var R = Room.furnRegion(NOTE_BOX.x, NOTE_BOX.y, NOTE_BOX.w, NOTE_BOX.h);
  if (!R) return false;                          /* no print yet: nothing to strike into */
  if (Room.printFace(strikeNote) === null) return false;   /* the face is on its way */
  var text = Sky.noteText();
  var key = text + ':' + R.pw + 'x' + R.ph;
  if (key === noteKey) return true;
  var t0 = (window.performance && performance.now) ? performance.now() : 0;
  noteKey = key;
  var gen = ++noteGen;
  var url = URL.createObjectURL(new Blob([noteMarkup(text, R)], { type: 'image/svg+xml' }));
  var img = new Image();
  img.onload = function () {
    URL.revokeObjectURL(url);
    if (gen !== noteGen) return;                 /* a later sentence is in flight */
    noteImg = img;
    if (!Room.furnPatch(NOTE_BOX.x, NOTE_BOX.y, NOTE_BOX.w, NOTE_BOX.h, noteDraw)) {
      noteKey = ''; return;
    }
    noteMs = t0 ? +(performance.now() - t0).toFixed(2) : 0;
    noteLast = { text: text, texels: [R.px, R.py, R.pw, R.ph], ms: noteMs };
  };
  img.onerror = function () { URL.revokeObjectURL(url); if (gen === noteGen) noteKey = ''; };
  img.src = url;
  return true;
}
/* Sky publishes to a new listener at once and on every change after; the
   listener is bound the first time the print exists (furnUpload), which
   is also E7's condition: nothing here schedules a frame before the
   first picture, because a strike only lands after the print is up. */
function noteBind() {
  if (noteBound || !window.Sky || !Sky.onChange) return;
  noteBound = true;
  Sky.onChange(function () { try { strikeNote(); } catch (e) { console.warn('skyNote', e); } });
}

/* ==== SV1: "SURVEYED TO" IS THE RECORD'S OWN DAY =========================
   Tommy (09-20): "'survey of the works - surveyed to 11th sept 2026' is
   wrong, this should be hard-linked to our latest github data point".
   The date was lettered into v32-frame.svg by hand and never moved. The
   frame now carries the empty element and the line is struck here from
   the field's own `days.surveyedTo` -- the day the card reads at the
   front -- with the print's italic, in the print's ink. Re-struck when
   the record changes (a stranger's handle) and when the print is rebuilt. */
/* N2 (2026-09-20): Tommy — "'surveyed to 18th sept' in the top-left is
   also a little bit small". It was 12 units, about 7 screen px at the
   fit, under a title set at 14. It goes to 15 and the box it is struck
   into grows to hold the taller line: the ascenders now reach y 95 and
   the descenders y 109, between the title's baseline at 89 and the neat
   rule at 115, so the line still stands clear of both. */
var SURV_BOX = { x: 76, y: 93, w: 420, h: 21 };   /* sheet units, around the line at x 79 / baseline 106 */
var survKey = '', survGen = 0, survImg = null, survLast = null;
function survText() {
  var f = S.fld, d = f && f.days && f.days.surveyedTo;
  if (!d) return '';
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return '';
  var MON = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  var date = (+m[3]) + ' ' + MON[+m[2] - 1] + ' ' + m[1];
  return window.RoomCopy ? RoomCopy.t('sheet.surveyed_to', { date: date }) : '';
}
function survMarkup(text, R) {
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + R.rx.toFixed(4) + ' ' +
    R.ry.toFixed(4) + ' ' + R.rw.toFixed(4) + ' ' + R.rh.toFixed(4) +
    '" width="' + R.pw + '" height="' + R.ph + '">' +
    '<style>' + (Room.printFace() || '') +
    'text{font-family:"Sorts Mill Goudy",Georgia,serif;font-size:15px;fill:#7d6d59;stroke-width:0;' +
    'letter-spacing:0.03em;font-style:italic}</style>' +
    '<text x="79" y="106">' + text.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</text></svg>';
}
function survDraw(ctx, R) {
  if (!survImg) return;
  ctx.drawImage(survImg, 0, 0);
  if (survImg.width !== R.pw || survImg.height !== R.ph) { survKey = ''; setTimeout(strikeSurveyed, 0); }
}
function strikeSurveyed() {
  var text = survText();
  if (!text) return false;
  var R = Room.furnRegion(SURV_BOX.x, SURV_BOX.y, SURV_BOX.w, SURV_BOX.h);
  if (!R) return false;
  if (Room.printFace(strikeSurveyed) === null) return false;
  var key = text + ':' + R.pw + 'x' + R.ph;
  if (key === survKey) return true;
  survKey = key;
  var gen = ++survGen;
  var url = URL.createObjectURL(new Blob([survMarkup(text, R)], { type: 'image/svg+xml' }));
  var img = new Image();
  img.onload = function () {
    URL.revokeObjectURL(url);
    if (gen !== survGen) return;
    survImg = img;
    if (!Room.furnPatch(SURV_BOX.x, SURV_BOX.y, SURV_BOX.w, SURV_BOX.h, survDraw)) { survKey = ''; return; }
    survLast = { text: text, texels: [R.px, R.py, R.pw, R.ph] };
  };
  img.onerror = function () { URL.revokeObjectURL(url); if (gen === survGen) survKey = ''; };
  img.src = url;
  return true;
}
Room.surveyedTo = function () { return survLast; };

/* ==== RG1: THE TOP-RIGHT RANGE IS THE VIEW'S ============================
   Tommy (09-20): the line at the top right "says Jan to Sept 2026 and
   that's fixed and unchanging?" ... "not following the view". It was
   lettered by hand into v32-frame.svg. The frame carries the empty
   element now and the months the view actually holds are struck here
   on every settled view, from the same span the section is drawn to. */
var RANGE_BOX = { x: 1960, y: 74, w: 336, h: 22 };   /* sheet units, around the line ending at x 2285 / baseline 89 */
var rangeKey = '', rangeGen = 0, rangeImg = null, rangeLast = null;
var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function rangeText() {
  if (!window.RoomHandle || !RoomHandle.viewDays || !window.RoomCopy) return '';
  var sp = RoomHandle.viewDays();
  if (!sp) return '';
  /* the edges are read four days in: the fit shows a few days of the
     margins either side of the year and the line says the year, not the
     two days of December and January that the paper's edge happens to hold */
  var d0 = new Date(Date.parse('2026-01-01T00:00:00Z') + Math.ceil(sp[0] + 4) * DAY_MS_D);
  var d1 = new Date(Date.parse('2026-01-01T00:00:00Z') + Math.floor(sp[1] - 4) * DAY_MS_D);
  var m0 = MONTHS[d0.getUTCMonth()], y0 = d0.getUTCFullYear();
  var m1 = MONTHS[d1.getUTCMonth()], y1 = d1.getUTCFullYear();
  if (y0 === y1 && m0 === m1) return RoomCopy.t('sheet.range_one', { month: m0, year: y0 });
  if (y0 === y1) return RoomCopy.t('sheet.range', { from: m0, to: m1, year: y1 });
  return RoomCopy.t('sheet.range_years', { from: m0, from_year: y0, to: m1, to_year: y1 });
}
function rangeMarkup(text, R) {
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + R.rx.toFixed(4) + ' ' +
    R.ry.toFixed(4) + ' ' + R.rw.toFixed(4) + ' ' + R.rh.toFixed(4) +
    '" width="' + R.pw + '" height="' + R.ph + '">' +
    '<style>' + (Room.printFace() || '') +
    'text{font-family:"Sorts Mill Goudy",Georgia,serif;font-size:14px;fill:#4a4034;stroke-width:0;letter-spacing:0.02em}</style>' +
    '<text x="2285" y="89" text-anchor="end">' + text.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</text></svg>';
}
function rangeDraw(ctx, R) {
  if (!rangeImg) return;
  ctx.drawImage(rangeImg, 0, 0);
  if (rangeImg.width !== R.pw || rangeImg.height !== R.ph) { rangeKey = ''; setTimeout(strikeRange, 0); }
}
function strikeRange() {
  var text = rangeText();
  if (!text) return false;
  var R = Room.furnRegion(RANGE_BOX.x, RANGE_BOX.y, RANGE_BOX.w, RANGE_BOX.h);
  if (!R) return false;
  if (Room.printFace(strikeRange) === null) return false;
  var key = text + ':' + R.pw + 'x' + R.ph;
  if (key === rangeKey) return true;
  rangeKey = key;
  var gen = ++rangeGen;
  var url = URL.createObjectURL(new Blob([rangeMarkup(text, R)], { type: 'image/svg+xml' }));
  var img = new Image();
  img.onload = function () {
    URL.revokeObjectURL(url);
    if (gen !== rangeGen) return;
    rangeImg = img;
    if (!Room.furnPatch(RANGE_BOX.x, RANGE_BOX.y, RANGE_BOX.w, RANGE_BOX.h, rangeDraw)) { rangeKey = ''; return; }
    rangeLast = { text: text, texels: [R.px, R.py, R.pw, R.ph] };
  };
  img.onerror = function () { URL.revokeObjectURL(url); if (gen === rangeGen) rangeKey = ''; };
  img.src = url;
  return true;
}
Room.rangeStruck = function () { return rangeLast; };
Room.strikeRange = strikeRange;   /* handle.js's first-strike path calls it once the record's calibration is in */

var raf = 0, live = false;
/* ==== E7: NOTHING SCHEDULES A FRAME BEFORE THE FIRST PICTURE. sky.js
   publishes as soon as a listener is registered, and E4's `Sky.onChange`
   put a rAF in the air during the furniture's await — so the one draw the
   plan asks for was arriving as two. The first picture is still the
   direct draw() at the end of boot; every later invalidate is live. ==== */
/* ==== FD1 (2026-10-04): ONE PICTURE A FRAME. Tommy: "unfolding the map
   from the room ... can make my PC severely lag just after we get past the
   half way point". Every animation (the camera's move, the fold, the
   unfold, the turn to the machine, the ruling) is its own requestAnimation-
   Frame loop and each drew the whole scene, so two at once drew it twice a
   frame: an unfold draws two (the unfold and the redraw), and from the room
   three (the camera sits down as the sheet opens), the room at 1.75x being
   the costliest picture there is. Measured on his GPU: frames of 400-480 ms
   at the unfold's end. Now a loop's draw is `frameDraw`: the first in a
   frame draws, the rest of that frame's are skipped (their state is in the
   next frame's picture, one frame on). The frame is the document
   timeline's time, which every rAF callback of one frame shares. Draws
   outside the loops (boot, resize, the print's capture) are `draw` as
   before. ==== */
var frameT = -1;
function frameDraw() {
  var t = document.timeline && document.timeline.currentTime;
  /* FD4 (2026-10-05): a draw asked for after this frame's picture was made
     is PUT OFF to the next frame, never dropped. The timeline's time is the
     last frame's for every task until the next one, so a worker's re-cut
     landing just after a frame (a turn of the phone, a toolbar) asked for
     its draw "in" that frame and lost it: the map lay all brown until a pan
     (Tommy, on his iPhone; production too) */
  if (t != null && t === frameT && !S.capPrint) { invalidate(); return; }
  frameT = t == null ? -1 : t;
  var c0 = performance.now();
  draw();
  S.cpuMs = (S.cpuMs || 0) + performance.now() - c0;   /* FP4: the draw's own script time, for ?fps=1 */
}
function invalidate() { if (!live) return; if (!raf) raf = requestAnimationFrame(function () { raf = 0; frameDraw(); }); }
S.invalidate = invalidate;

/* =================================================================
   THE FURNITURE — the SVG that is in the page today, rastered ONCE
   at device resolution (ROOM-PLAN §3).

   P1. This comment used to say "createImageBitmap decodes OFF the main
   thread, so there is no long task", and it never did. E1 caught it (REPORT-room-E1.md OPEN 3): "`createImageBitmap` never
   works on this sheet ... it rejects every time (`InvalidStateError`)".
   The cause is not the root tag's missing size: Chrome has no SVG decoder
   on the BLOB path at all: the tag as it stands, the tag given width and
   height, each with and without resizeWidth/Height, all four answered
   `InvalidStateError: The source image could not be decoded.` in one
   browser. Nothing writable into sheet-frame.svg changes that, so the
   branch is gone.

   `furnMs` is NOT a long task either: it is wall clock, most of it this
   raster waiting behind the load's own 332-352 ms task for its 73 KB.
   Its own main thread, five cold loads with the calls wrapped: median
   31.8 ms (`drawImage` plus `texImage2D` out of the canvas). The
   off-thread path (an `<img>`, the only thing here that reads SVG, then
   `createImageBitmap(img)` at the TEXTURE's size on the root tag) was
   built, saves ~10 ms cold, and was REJECTED: that rasteriser sets type
   with SUBPIXEL antialiasing, so the cartouche came out fringed orange
   and blue and the small type softer (REPORT-room-P1.md). The canvas
   keeps greyscale AA and the fit stays diff 0.
   ================================================================= */
/* ==== SD1 (2026-09-21): THE PRINT WAITS FOR EVERY MODULE THAT EDITS IT.
   Tommy: "This looks like the old version of the section along the line
   that we used to have, with the newer version drawn over the top of it
   slightly differently ... I tried in incognito mode." It was: the baked
   section AND the live strike, both on the paper. `boot` runs the moment
   engine.js is evaluated (readyState is 'interactive' by then, so the
   DOMContentLoaded branch below is not taken), and handle.js, index.js
   and the rest are DEFERRED scripts that have not executed yet — the
   parser spins the event loop while it waits for each one's bytes, and
   on a load where sheet-frame.svg lands before handle.js the fetch below
   resolved first, `window.RoomHandle` was undefined, `blankSection` was
   never applied, and the print went up with its baked section on it.
   Reproduced headless by holding handle.js 1.5 s (screens repo
   room-e9/sd1-0921/). So the raster waits for DOMContentLoaded as well as
   for the SVG's text — every deferred script has run by then — and the
   fetch still goes out at once, so on a normal load this costs nothing
   (frame 1 was already after DCL in every log Tommy pasted). ==== */
var domSettled = new Promise(function (res) {
  if (document.readyState === 'complete') return res();
  document.addEventListener('DOMContentLoaded', function () { res(); }, { once: true });
  /* a script evaluated after DOMContentLoaded has fired still gets `load` */
  window.addEventListener('load', function () { res(); }, { once: true });
});
/* LD2 (2026-10-02): the sheet's SVG is fetched ONCE and its text shared: the
   furniture raster and Room.printFace (the print's @font-face) each fetched
   it, two requests for the same file on every load. A failed fetch is
   forgotten so the next asker tries again. */
var frameSvgP = null;
function frameSvgText() {
  if (!frameSvgP) frameSvgP = fetch('/img/sheet-frame.svg').then(function (r) { return r.text(); })
    .catch(function (e) { frameSvgP = null; throw e; });
  return frameSvgP;
}
function furniture(w, h) {
  return Promise.all([
    frameSvgText(),
    domSettled
  ]).then(function (a) {
    /* &nbsp; is NOT a defined entity in XML: the whole image fails to load
       with no message beyond an Event (ROOM-PLAN §3). And the margin's type
       is sized `calc(Npx / var(--z))`, so --z has to be the scale the sheet
       is actually laid out at, or the lettering comes out the wrong size. */
    var z = (S.layout ? S.layout.k : 1).toFixed(4);
    var s = a[0].replace(/&nbsp;/g, '\u00a0').replace(/--z:\s*[\d.]+/, '--z: ' + z);
    /* E4: the four shadows, the fold bands and the flat turned corner come
       out of the raster — they are objects in the scene now (objects.js) */
    if (window.RoomObjects) s = RoomObjects.prepFurniture(s);
    /* R23: AND THE PIN COMES OUT OF IT ALTOGETHER. R14 stripped v32's
       rastered pin and laid a live one over the paper in its place, and
       kept the strip conditional so a build without pin.js still showed
       the printed one. There is no pin now, live or printed: the sheet's
       own north-east corner lifts instead (objects.js, pin.js), so the
       group goes unconditionally and nothing can put it back. */
    s = s.replace(/<g class="sv-pin[\s\S]*?<\/g><\/g>/, '');
    /* R21/R79: the bracket is live ink in the sheet pass. The figures,
       ticks and rule are printed into this one-time bitmap; index.js
       re-strikes their cell block only when the filed record's home year
       differs from the serialised SVG. The worker's field is still in
       flight here, but boot has already filed the record text in S.raw. */
    if (window.RoomIndex) {
      var iy = /"year"\s*:\s*(\d{4})/.exec((S.raw && S.raw.record) || '');
      s = RoomIndex.prepFurniture(s, iy ? +iy[1] : 0);
    }
    /* N1: the six marks are not printed on this sheet at all now, so
       there is nothing here to hide and nothing to read back out of the
       string (the drawings are src/_data/glyphs.json, inlined on the note). */
    /* X4: AND THE NAME IN THE CARTOUCHE IS NOT ALWAYS TOMMY LONG. It is
       PRINTED type and must stay printed type — the DOM editor is only up
       while a hand is in it — so a foreign handle re-rasters this string
       with its own name in the cartouche's own face, and the editor blanks
       it for as long as it is standing over it. On the default page this
       returns the string UNTOUCHED, byte for byte, which is why the fit is
       diff 0 (index.js's prepFurniture note: an EDIT to the string moves
       pixels three hundred units away). */
    if (window.RoomHandle) s = RoomHandle.prepFurniture(s);
    /* P1: ONE way in, because it is the only one Chrome has for an SVG. */
    return viaImage(new Blob([s], { type: 'image/svg+xml' }), w, h);
  });
}
/* P1: not a fallback any more — the path. `drawImage` to the texture's
   own size RE-RENDERS the vector at that size (it does not scale the
   292x150 raster an SVG with no intrinsic size measures), which is why
   the lettering is as sharp as the page's own. */
function viaImage(blob, w, h) {
  return new Promise(function (res, rej) {
    var url = URL.createObjectURL(blob), img = new Image();
    img.onload = function () {
      var cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      cv.getContext('2d').drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url); res(cv);
    };
    img.onerror = rej; img.src = url;
  });
}

/* =================================================================
   INPUT — gestures unchanged (ROOM-PLAN §3)
   ================================================================= */
var hashTimer = null;
function queueHash() { clearTimeout(hashTimer); hashTimer = setTimeout(writeHash, 260); }
function writeHash() {
  var v = S.view;
  var h = '#' + Math.round(v.x + v.w / 2) + ',' + Math.round(v.y + v.h / 2) + ',' + Math.round(v.w);
  var t = Math.round(S.tilt / DEG), b = Math.round(S.yaw / DEG);
  if (t || b) h += ',' + t + ',' + b;
  /* ==== N2: AND NO DAY RIDES IN IT ANY MORE. E6 wrote the pinned day
     here as `,d<date>`; there is no pin to write (day.js's head). A link
     carries a day only when the AUTHOR typed one, so nothing this
     function writes can put one in — the grammar below still READS `,d`,
     so every link ever pasted still opens on its day, and the address
     settles to the view alone once the reader moves. ==== */
  /* ==== DL1 (2026-09-23): AND NOTHING ELSE RIDES. Tommy: "Delete the
     carpet arm. Delete the room deep links. Delete the F1 deep link too.
     You can keep your XYW tilt bearing." The address is the view (and a
     day, when an author typed one) and nothing more: E8's `,r1` (the
     camera standing), X9's `,l1` (the laptop, already unwritten since
     TD1) and R47's `,f1` (the folded sheet) are no longer written or
     acted on. applyHash's grammar still ACCEPTS any trailing `,token`
     and ignores it, so every link ever pasted with one of them opens on
     its view, seated, on an open sheet. ==== */
  if (location.hash !== h) history.replaceState(history.state, '', h);
  /* ==== S3: AND THE SECTION UNDER THE MAP IS RE-STRUCK HERE ==========
     A settled view is exactly what this function already is: the hash is
     written 260 ms after the last move and from nowhere else. So the one
     line the sheet's section needed was this one -- not a listener of its
     own on every pointermove, which is the thing it must never be. Never
     on a hidden tab: a tab nobody is looking at has no view to settle. */
  settledStrikes();
}
/* ==== HV1: A VIEW THAT SETTLED ON A HIDDEN TAB IS STRUCK WHEN THE TAB IS
   SEEN. The three strikes above were skipped on a hidden tab and never
   asked for again, so a sheet opened in a background tab (a ctrl-click, a
   link left to load) came up with NO section, no months, no range and no
   surveyed-to line until the first drag settled a new view. Found as a
   "flake" in LF1's reference captures (one headless load in the
   background: 154,488 px of strikes missing); reproduced with a page
   booted behind another tab and activated after READY. The skip is
   remembered and the visibilitychange listener below strikes it. ==== */
function settledStrikes() {
  if (document.hidden) { S.settlePending = true; return; }
  S.settlePending = false;
  if (window.RoomHandle && RoomHandle.onViewSettled) {
    try { RoomHandle.onViewSettled(); } catch (e) {}
  }
  /* RG1: the top-right range and the surveyed-to line, on the same settled view */
  try { strikeRange(); } catch (e) { console.warn('range', e); }
  try { strikeSurveyed(); } catch (e) { console.warn('surveyedTo', e); }
}
/* ==== N2: THE AUTHOR'S DAY, READ ONCE, FROM THE ADDRESS THE PAGE WAS
   OPENED AT. `applyHash()` runs at boot while the field is still in the
   worker and there is no `S.day` to hand a day to, and the first
   `writeHash` after it settles the address to the view alone — so the
   token has to be taken off the address the moment this file is
   evaluated or it is gone before its owner exists. `buildDay` spends it,
   once; a hash EDITED or a link followed inside the page goes through
   applyHash's own parse below. ==== */
var LINK_DAY = (/,d(\d{4}-\d{2}-\d{2})/.exec(location.hash || '') || [])[1] || null;
function applyHash() {
  /* DL1: any trailing `,token` (an old link's `,r1`, `,l1` or `,f1`) is
     accepted and ignored; the view and an authored day are all it reads */
  var m = /^#(-?\d+),(-?\d+),(\d+)(?:,(-?\d+),(-?\d+))?(?:,d(\d{4}-\d{2}-\d{2}))?(?:,[a-z]\w*)*$/
    .exec(location.hash || '');
  if (!m) return false;
  var w = +m[3];
  S.tilt = clamp((+m[4] || 0) * DEG, 0, tiltCap());
  S.yaw = (+m[5] || 0) * DEG;
  /* ==== R56: AND THE SKY IS TOLD THE BEARING. This was the one place a
     bearing was set and sky.js was not: the hand's drag tells it
     (pointermove), Esc tells it, `Room.attitude` tells it, `Room.fit`
     tells it, the voyage tells it — a HASH did not. sky.js turns the
     sun's ground azimuth by the view's bearing to get the SCREEN bearing
     the desk is lit from (its `screenLight`), and everything off the map
     takes its light from that: the oak's grain and its shadows, the
     paper's creases and its drape, the books, the card, the wash in the
     window. So a hash change moved the sheet and left the room lit from
     wherever the bearing had last been *dragged* to — follow a link to
     `…,53,50` in the page and come back to the fit by hash and the desk
     was still lit for 50 degrees, 2,395,841 device px of the frame
     different from the same fit loaded cold (REPORT-room-R54, its one
     ticket, which read it as the furniture being seeded by the pose; it
     is not seeded by anything but `seed 7` and the sheet's own box —
     it was LIT wrong). The map interior was identical throughout, which
     is the tell: the ground takes the sun's own azimuth, not the
     screen's. setView is idempotent under 0.05 degrees, so the boot's
     own two calls below are now belt to this brace. ==== */
  if (window.Sky) Sky.setView(S.yaw / DEG);
  S.view.w = w; S.view.h = w * viewRatio();
  S.view.x = +m[1] - w / 2; S.view.y = +m[2] - S.view.h / 2;
  clampView();
  snapLift();                       /* a link is a place: the aim lands on it at once */
  /* N2: a link that carries a day OPENS showing it, and the pointer
     takes over from the reader's first move across the map. A hash with
     no day says nothing about the card and must not clear it. */
  if (S.day && m[6]) S.day.show(m[6]);
  return true;
}
/* RS1 (2026-09-23): `?unfold=click|both` deleted — the drag is the decided
   gesture and a click on the folded map never opens it. */
function wire() {
  var host = document.getElementById('canvas');
  var pts = new Map(), last = null, base = null, moved = 0;
  /* PH6: where each finger on the glass IS (pts keeps the press), and the
     pinch the first two make: their distance and centre at the last move */
  var fing = new Map(), pinch = null;
  /* PT1: what the two fingers are doing, decided once per touch-down of
     the pair: null until they have moved ~10 px, then 'pinch', 'tilt' or
     'twist' until a finger comes up */
  var two = null;
  /* a finger's place on the SCREEN (phone.js turns the client point into
     the turned box's frame; the screen's up is what the hand means) */
  function onScreen(e) { return window.RoomPhone ? RoomPhone.page(e) : [e.clientX, e.clientY]; }
  var aimId = null;                   /* AL1: the pointer holding a limb, or null */
  var attitudeMoves = new Set(), YAW_SNAP = 1 * DEG, TILT_SNAP = 0.5 * DEG;
  function snapAttitude() {
    var changed = false;
    var turn = Math.round(S.yaw / (Math.PI * 2)) * Math.PI * 2;
    if (Math.abs(S.yaw - turn) <= YAW_SNAP && S.yaw !== 0) {
      S.yaw = 0;
      changed = true;
    }
    if (S.tilt <= TILT_SNAP && S.tilt !== 0) {
      S.tilt = 0;
      changed = true;
    }
    if (!changed) return;
    if (window.Sky) Sky.setView(S.yaw / DEG);
    invalidate();
    queueHash();
  }
  function takeVoyageCamera() {
    var V = Room.voyage;
    if (V && V.cameraTakeover) V.cameraTakeover();
  }
  /* ==== R81: A HAND CAN OPEN THE FOLDED PACKET ======================
     Tommy: "you should be able to drag in reverse to reopen the folded
     map, as well as just clicking it (or maybe instead of the click?)".

     The forward hand is pin.js's empty box over the north-east corner.
     It projects travel on the sheet's north-east-to-south-west diagonal
     and makes three quarters of that diagonal one whole fold. A packet
     has no north-east corner left to put that box on: it is canvas
     geometry, already hit here by `Room.sheetHit`, so its reverse hand
     belongs in this canvas wire. The gearing below is deliberately the
     same projection and the same 0.75 reach. Travel back toward the
     north-east subtracts fold amount; travel the other way clamps at the
     already-folded end and changes nothing.

     Tommy chose drag-only: it is now the default packet gesture.
     (RS1: the `?unfold=click|both` arms are gone; the drag is the
     gesture.) ==== */
  var unfoldPull = null, UNFOLD_REACH = 0.75, UNFOLD_SNAP = 0.5;
  /* THE HOST'S RECT IS READ ONCE, NOT ONCE A MOVE. Every
     getBoundingClientRect inside a pointermove is a forced layout, which
     is exactly what v32 A measured away (301 recalcs over 60 moves). */
  var rect = null;
  function rectOf() { return rect || (rect = host.getBoundingClientRect()); }
  S.dropRect = function () { rect = null; };
  window.addEventListener('scroll', S.dropRect, { passive: true });
  /* THE HAND ON A THING IS NOT THE HAND ON THE GROUND. The books' covers
     and the card's own control are DOM inside the canvas; a pointer on
     one of them is that thing's, not the map's, or a click on `open the
     journal` would also be a click on the paper behind it. */
  function onGlass(e) { return e.target === S.cv || e.target === host; }
  var cardPull = null;                 /* FU3: the hand turning the file card over */
  function packetGrip(e) {
    var O = window.RoomObjects;
    if (!O || !O.foldGeom || foldAmt() < 0.999) return null;
    var r = rectOf(), cx = e.clientX - r.left, cy = e.clientY - r.top;
    if (!Room.sheetHit(cx, cy)) return null;
    var fg = O.foldGeom(), ne = Room.paperPoint(fg.w, 0), sw = Room.paperPoint(0, fg.h);
    if (!ne || !sw) return null;
    var dx = sw[0] - ne[0], dy = sw[1] - ne[1], m = Math.hypot(dx, dy);
    if (m < 40) return null;
    /* MP3: on the phone the run is the sheet's diagonal as before, but never
       more than 0.45 of the glass's own: the packet is the plate's shot now
       whichever view it was folded from, where the whole sheet's diagonal is
       longer than the glass and half of it was a swipe off the screen */
    var run = m * UNFOLD_REACH;
    if (PHONE) run = Math.min(run, Math.hypot(r.width, r.height) * 0.45);
    return { id: e.pointerId, x: e.clientX, y: e.clientY, ux: dx / m, uy: dy / m,
             run: run, t0: O.foldT || 0, active: false };
  }
  function settlePacketPull() {
    var p = unfoldPull, O = window.RoomObjects;
    unfoldPull = null;
    if (!p || !p.active || !O) return false;
    O.foldGrab = 0;
    /* Nearer END state, not nearer crease. Opening goes through R58's
       `B[]` walk, so whatever creases remain open last-first with their
       rests. Only this decision is a commitment to open: `Room.unfold`
       sits the reader down here, never on the first pixel of the pull. */
    if ((O.foldT || 0) < UNFOLD_SNAP) Room.unfold();
    else Room.foldTo(1, 1100);
    return true;
  }
  function dropPacketPull() {
    var had = unfoldPull;
    unfoldPull = null;
    /* Even before the four-pixel threshold this pointer belongs to the
       packet candidate, not to the map that the interrupt is opening. */
    if (had) pts.delete(had.id);
    if (had && had.active && window.RoomObjects) RoomObjects.foldGrab = 0;
  }
  /* Esc and the public `Room.unfold()` can interrupt while the pointer is
     still captured. Drop the grip before that animation starts, so a
     later move from the still-down pointer cannot take the fold back. */
  S.dropUnfoldPull = dropPacketPull;
  host.addEventListener('pointerdown', function (e) {
    /* A discovery card is in the reader's hand. The card's own button
       stops its event; every other press, including one on the chart or
       a book behind it, belongs to neither map nor room until the card is
       put down. */
    var heldCard = Room.cardPresented && Room.cardPresented();
    if (heldCard && heldCard.discovery === true) {
      e.preventDefault();
      return;
    }
    /* The closed field guide's transparent DOM hit box overlaps part of
       the folded packet. Ask the packet once before the DOM gate: only a
       real packet hit may pass through a `.book-cover`, and only while
       that cover's own `.book` explicitly says it is closed. An exposed
       cover and every control in an open book remain the book's hand. */
    var grip = packetGrip(e);
    /* The press lands on whatever the closed book puts under the hand: its
       `.book-cover` button, H's `.book-hit` box, or the `.book` aside's own
       box (which is what the field book presents under the packet's
       south-west; Tommy: "lots of the bottom-left corner isn't covered").
       Any of them, while the book says it is closed, is the packet's. */
    var book = e.target.closest ? e.target.closest('.book') : null;
    var throughClosedCover = !!(grip && book && book.getAttribute('data-open') === 'false');
    if (!onGlass(e) && !throughClosedCover) return;
    /* PH6: on the phone a tap off an open book only puts the book down
       (books.js, which shuts it in a capture listener before this runs,
       so phone.js marks the press); the desk turns back under it, and the
       same tap must not also land on the map as a day */
    if (PHONE && e.__phBook) return;
    /* R60: THE HAND PUT DOWN BESIDE THE MACHINE IS NOT ON THE PAPER. No
       capture, no drag base, no pick. MX1 (2026-09-22): AND IT IS THE WAY
       BACK. Tommy: "We do need a mouse way out of the terminal. You
       should, at the very least, be able to click back on the map to snap
       out." A press anywhere in the picture but the screen (the screen's
       own element stops its pointer events, terminal.js) is looking away
       from the machine — the same turn Esc makes, the terminal kept as it
       was — and the next press is the map's again, wherever the reader
       was: seated, standing, or at the folded sheet. */
    if (Room.lapUp()) {
      S.pickOff = (S.pickOff || 0) + 1;
      e.preventDefault();
      Room.terminal(false);
      return;
    }
    /* EG4: on the phone a second finger before the packet has moved makes
       a pinch of it (the way out), so the grip is let go and the finger
       counted */
    if (unfoldPull && PHONE && !unfoldPull.active) unfoldPull = null;
    /* A second finger cannot replace the packet grip's origin or gearing. */
    if (unfoldPull) { e.preventDefault(); return; }
    /* AL1: a hand on the glass while the view flies to the pit stops it
       there, and the pit stands up where it is */
    if (doomFlyStop()) doomLand();
    /* AL1: A LINE OF SIGHT IS TAKEN BY THE HAND. In act two a press on the
       limb is the line, not the paper (AL2: the limb only): it turns with
       the pointer until the hand comes up, and nothing pans. One pointer
       at a time. */
    if (aimId === null && !pts.size && Room.mapLive() && S.doom.on && S.doom.act === 2 && S.day) {
      var ar = rectOf();
      if (doomGrab(S.day.mapAt(e.clientX - ar.left, e.clientY - ar.top), e.clientX - ar.left, e.clientY - ar.top)) {
        aimId = e.pointerId;
        try { host.setPointerCapture(e.pointerId); } catch (x) { }
        e.preventDefault();
        return;
      }
    }
    cancelHome();                     /* R14: the hand takes the way home */
    try { host.setPointerCapture(e.pointerId); } catch (x) { /* a synthetic pointer has none */ }
    pts.set(e.pointerId, e);
    if (PHONE) { var sp0 = onScreen(e); fing.set(e.pointerId, [e.clientX, e.clientY, sp0[0], sp0[1]]); pinch = null; two = null; }
    holdLift();                       /* the aim belongs to the pose the hand stops at */
    attitudeMoves.delete(e.pointerId);
    last = { x: e.clientX, y: e.clientY };
    /* R62: and the head's own two angles as they stood when the hand went
       down. R68: the look is no longer read from them — it is turned by
       each move's own travel off the clamped angle, so a head at the wall
       comes back the instant the hand does — they are kept as the record
       of where the head started this drag. */
    base = { tilt: S.tilt, yaw: S.yaw, x: e.clientX, y: e.clientY,
             ly: S.cam.lookYaw || 0, lp: S.cam.lookPitch || 0 };
    moved = 0;
    unfoldPull = grip;
    /* FU3: a hand that goes down on the file card beside a folded sheet,
       seated or standing (FU4, as the packet's pull is), TAKES it (Tommy: "i tried dragging it initially", "why
       can't we manually turn it over like the unfolding map?"): the drag
       turns it with the hand, one card's width of travel to the half
       turn, rightward turning the face down; `up` lets it fall to the
       nearer face, and a press that never travelled is still a click. */
    if (cardPull && S.card) S.card.peekSettle(S.card.peekInfo() && S.card.peekInfo().at > 0.5);
    cardPull = null;
    /* PK2 (2026-10-03): THE HAND CARRIES THE CARD UP (day.js `peekTo`).
       Taken where it lies, it follows the hand from its place towards the
       middle of the glass, lifting and turning over on the way; let go past
       half way (or flicked) it comes the rest of the way up and stays,
       short of it it falls back. Up, the hand anywhere takes it back down
       the same way, and a tap puts it down. A tap on it lying gives it a
       small lift and drop, the sign that it moves. Only a LONE finger; the
       second of a pair is a pinch. */
    if (!grip && pts.size === 1 && foldAmt() > 0.5 && S.card && S.card.peekInfo && !Room.lapUp()) {
      var cr0 = rectOf(), pi = S.card.peekInfo();
      var upNow = pi && S.card.peekUp();
      if (pi && (upNow || S.card.hit(e.clientX - cr0.left, e.clientY - cr0.top))) {
        /* FD3: the hand's distance, any way, is how far up it is; the whole is
           the card's way to the glass's middle, as before (see the move handler) */
        var vl = Math.hypot(cr0.width / 2 - pi.rest[0], cr0.height / 2 - pi.rest[1]);
        var span = Math.max(120, Math.min(vl, cr0.height * 0.6));
        cardPull = { id: e.pointerId, x: e.clientX, y: e.clientY, from: upNow ? 1 : 0, span: span,
                     a: upNow ? 1 : 0, hist: [[performance.now(), 0]] };
      }
    }
    e.preventDefault();
  });
  /* ==== E6: THE DAY UNDER THE HAND. One reading a move: the pick, then
     one string to the card. Nothing is measured, nothing is written to
     the document and nothing is read back off the GPU. ==== */
  function readDay(e) {
    if (!S.day || !live || !onGlass(e)) return;
    /* X2: the cross under the hand is the map answering a hand ON it */
    cardHand(e);
    if (!Room.mapLive()) { if (S.day.showing && S.day.showing()) S.day.show(null); doomHover(null); return; }
    var t0 = performance.now();
    var r = rectOf();
    var cx = e.clientX - r.left, cy = e.clientY - r.top;
    /* ==== N2: AND THE CARD IS PART OF THE HOVER. The card is DOM with
       `pointer-events: none` but for its own button, so a pointer over
       it is still a pointer on the glass and this is the one move
       handler that sees it. It is told BEFORE the pick, so `show(null)`
       knows whether the hand has left the day for the card — the reach
       for "read the post" — or left them both. ==== */
    if (S.day.hand) S.day.hand(e.clientX, e.clientY);
    var d = S.day.dayAt(cx, cy);
    S.day.show(d ? d.date : null);
    /* D1: the arrows are a hover and nothing else. The pick is the one
       readDay already made; only act two, whose turn arrows can sit off
       the record altogether, ever asks the ray for a second point. */
    /* D2 (4): the limb stands round the station and its ticks reach past
       the cell, so the doom is handed the MAP POINT whenever it is on */
    doomHover(d || (S.doom.on ? S.day.mapAt(cx, cy) : null));
    var ms = performance.now() - t0;
    S.hoverMs = S.hoverMs || [];
    if (S.hoverMs.length < 400) S.hoverMs.push(+ms.toFixed(3));
  }
  /* ==== R62: THE ONE PLACE THE CURSOR ANSWERS IN THE ROOM. Standing, the
     only thing on the desk that takes a press is the machine (and the
     paper, which is the whole floor of the picture and says nothing), so
     the pointer over the laptop is the whole of the signposting the room
     gets — the same rule the books' covers already follow.
     The style is written only when the answer changes. ==== */
  function roomHand(e) {
    var want = false;
    if (live && onGlass(e) && S.cam.want > 0.5 && !Room.lapUp()) {
      var t0 = performance.now();
      var r = rectOf();
      want = Room.laptopHit(e.clientX - r.left, e.clientY - r.top);
      S.lapHitMs = S.lapHitMs || [];
      if (S.lapHitMs.length < 400) S.lapHitMs.push(+(performance.now() - t0).toFixed(3));
    }
    if (want !== S.__onLap) { S.__onLap = want; host.style.cursor = want ? 'pointer' : S.__onCard ? 'grab' : ''; }
  }
  /* FU1: and the card beside a folded sheet, which a press or a drag
     turns over, is the other thing on the desk the pointer answers over —
     seated or standing (FU4). The cursor is the laptop's OR the card's. */
  function cardHand(e) {
    var g = foldGeom(), want = false;
    if (g && g.t > 0.5 && S.card && S.card.hit && !Room.lapUp()) {
      var r = rectOf();
      want = S.card.hit(e.clientX - r.left, e.clientY - r.top);
    }
    if (want === !!S.__onCard) return;
    S.__onCard = want;
    /* PK2: the card is carried, so the hand over it is an open hand */
    host.style.cursor = want ? 'grab' : S.__onLap ? 'pointer' : '';
  }
  host.addEventListener('pointermove', function (e) {
    if (aimId !== null && e.pointerId === aimId) {
      var am = rectOf();
      if (S.day) doomAim(S.day.mapAt(e.clientX - am.left, e.clientY - am.top), e.clientX - am.left, e.clientY - am.top);
      return;
    }
    if (!pts.has(e.pointerId)) {
      /* A press begun on the compass belongs to its helm until Shift is
         pressed. Voyage hands its last point back here, so the very move
         which changes intent becomes an attitude move with no jump. */
      var handoff = e.shiftKey && (e.buttons & 1) && Room.voyage &&
        Room.voyage.takeCameraPointer && Room.voyage.takeCameraPointer(e.pointerId);
      if (!handoff) { roomHand(e); readDay(e); return; }
      cancelHome();
      try { host.setPointerCapture(e.pointerId); } catch (x) { }
      pts.set(e.pointerId, e);
      holdLift();
      attitudeMoves.delete(e.pointerId);
      last = { x: handoff.x, y: handoff.y };
      base = { tilt: S.tilt, yaw: S.yaw, x: handoff.x, y: handoff.y,
               ly: S.cam.lookYaw || 0, lp: S.cam.lookPitch || 0 };
      moved = 0;
    }
    var t0 = performance.now();
    /* PK2: the card in the hand goes up (or down) with it, and nothing
       else moves */
    if (cardPull && cardPull.id === e.pointerId) {
      moved += Math.abs(e.clientX - last.x) + Math.abs(e.clientY - last.y);
      last = { x: e.clientX, y: e.clientY };
      /* FD3 (2026-10-04): Tommy, of FD2: "I can't lift it up in a rightwards
         motion, only a leftwards motion ... It feels like it's different when
         we're in the room versus the folded map view". Only travel TOWARDS the
         glass's middle lifted it, so which way worked hung on where the card
         lay in that view. Now a drag any way carries it, the card stays under
         the hand, and lifting it off the oak it turns over the way the hand
         goes, left or right (`peekDir`); it goes down the same turn backwards */
      var hx = e.clientX - cardPull.x, hy = e.clientY - cardPull.y, pj = Math.hypot(hx, hy);
      var ca = Math.min(1, Math.max(0, cardPull.from ? 1 - pj / cardPull.span : pj / cardPull.span));
      if (moved > 6 && !cardPull.from && !cardPull.dir && S.card.peekDir && Math.abs(hx) > 3) {
        cardPull.dir = hx > 0 ? 1 : -1; S.card.peekDir(cardPull.dir);
      }
      if (moved > 6) { cardPull.a = ca; S.card.peekTo(ca, [hx, hy], cardPull.from); }
      cardPull.hist.push([performance.now(), pj]);
      if (cardPull.hist.length > 6) cardPull.hist.shift();
      return;
    }
    /* EG4: on the phone a second finger down before the packet has moved
       makes it a pinch (the way out, or back in), not a pull */
    if (unfoldPull && PHONE && fing.size >= 2 && !unfoldPull.active) unfoldPull = null;
    /* Another down pointer cannot borrow this grip's origin or gearing. */
    if (unfoldPull && unfoldPull.id !== e.pointerId) return;
    if (unfoldPull) {
      moved += Math.abs(e.clientX - last.x) + Math.abs(e.clientY - last.y);
      last = { x: e.clientX, y: e.clientY };
      var p = unfoldPull;
      var d = (e.clientX - p.x) * p.ux + (e.clientY - p.y) * p.uy;
      /* Four pixels is the forward corner's own line between a push and
         a pull. The opposite direction remains an inert packet. */
      if (!p.active && d > -4) return;
      p.active = true;
      RoomObjects.foldGrab = 1;
      Room.foldAt(p.t0 + d / p.run);
      return;
    }
    /* ==== PH6: TWO FINGERS PINCH. On the phone the first two fingers down
       zoom about the point between them by the wheel's own path
       (wheelZoom: the same clamp, stops, corner tug and hash), and the
       point between them carries the map as one finger does. A third
       finger is ignored. ==== */
    /* ==== PT1 (2026-10-02): AND TWO FINGERS TILT AND TURN. Tommy: "Is
       there any tilting based on phone, or have we deliberately not
       shipped the 3D tilting model to phone?" The desk tilts and turns
       under a Shift-drag; a phone has no Shift, so it takes the maps
       convention. Two fingers moved together up the SCREEN tilt the
       sheet back (down brings it flat), by the Shift-drag's own gearing,
       clamp and snap; two fingers twisted turn the bearing with them. The
       pair is read once, over its first ~10 px, by whichever motion is
       the largest: the spread changing is a pinch, the pair's middle
       moving up or down the screen with the spread and angle held is a
       tilt, and the angle between them changing is a twist. What it is,
       it stays until a finger lifts. The screen's up is read off the page
       (phone.js turns the client point a quarter round in portrait), so
       up is up however the desk is lying. ==== */
    if (PHONE && fing.has(e.pointerId)) {
      var sp = onScreen(e);
      fing.set(e.pointerId, [e.clientX, e.clientY, sp[0], sp[1]]);
      if (fing.size >= 2) {
        var fi = fing.values(), fa = fi.next().value, fb = fi.next().value;
        var pd = Math.hypot(fb[0] - fa[0], fb[1] - fa[1]);
        var pc = [(fa[0] + fb[0]) / 2, (fa[1] + fb[1]) / 2];
        var pa = Math.atan2(fb[1] - fa[1], fb[0] - fa[0]);
        var sy2 = (fa[3] + fb[3]) / 2;
        moved += 100;                 /* a pinch is never a tap */
        if (!two) two = { mode: null, d: pd, a: pa, sy: sy2, tilt: S.tilt, yaw: S.yaw,
                          fa: fa.slice(), fb: fb.slice() };
        var turnBy = pa - two.a;
        turnBy -= Math.round(turnBy / (2 * Math.PI)) * 2 * Math.PI;
        if (!two.mode) {
          var byScale = Math.abs(pd - two.d), byTwist = Math.abs(turnBy) * pd / 2;
          /* the tilt's travel is the vertical move the two fingers SHARE:
             two fingers going opposite ways are a pinch or a twist */
          var va = fa[3] - two.fa[3], vb = fb[3] - two.fb[3];
          /* PT2 (2026-10-02, his iPhone: "I couldn't get it to work"): a real
             hand's fingers report ONE AT A TIME, each pointermove moving one
             finger while the other still stands where it was. Read over the
             first 10 px, a pair going up together looked like one finger
             moving alone: no shared vertical, a spread change, a pinch, every
             time. (The bench's dispatched gesture moved both in one event.)
             So nothing is decided until BOTH have moved (or the pair has
             travelled 40 px), and the tilt's measure is their mean shared
             move, not the lesser. */
          var am = Math.max(Math.abs(va), Math.abs(fa[2] - two.fa[2])),
              bm = Math.max(Math.abs(vb), Math.abs(fb[2] - two.fb[2]));
          var byTilt = va * vb > 0 ? (Math.abs(va) + Math.abs(vb)) / 2 : 0;
          if (Math.max(byScale, byTwist, byTilt) < 12 ||
              ((am < 4 || bm < 4) && Math.max(am, bm) < 40)) { pinch = { d: pd, c: pc }; return; }
          two.mode = byTilt >= byScale && byTilt >= byTwist ? 'tilt'
                   : byTwist > byScale ? 'twist' : 'pinch';
          /* the gesture starts from where it was decided, not with a jump */
          two.sy = sy2; two.a = pa; two.tilt = S.tilt; two.yaw = S.yaw; turnBy = 0;
        }
        /* ZM1: off the map two fingers are the closer look, whatever the
           pair was read as: the spread magnifies, the pair's middle drags.
           At the eye's own view, seated at the packet, a pinch out is
           still the way up (EG4 below) */
        if (zoomCtx()) {
          if (pinch && pd > 8 && pinch.d > 8) {
            var zf = pd / pinch.d;
            if (two.z0 === undefined) two.z0 = zGoal.k;
            /* a pinch that BEGAN magnified only comes back to the eye */
            if (zf < 1 && zGoal.k <= 1.0005 && S.cam.want < 0.5 && two.z0 <= 1.0005) {
              S.overPinch = Math.max(0, (S.overPinch || 0) + Math.log(pinch.d / pd));
              if (S.overPinch > 0.4) { S.overPinch = 0; wheelOut(); }
            } else {
              S.overPinch = 0;
              zoomPan(pc[0] - pinch.c[0], pc[1] - pinch.c[1]);
              zoomBy(zf, pc[0], pc[1]);
            }
          }
          pinch = { d: pd, c: pc };
          return;
        }
        if (two.mode !== 'pinch') {
          if (Room.mapLive()) {
            takeVoyageCamera();
            if (two.mode === 'tilt') {
              S.tilt = clamp(two.tilt - (sy2 - two.sy) * 0.35 * DEG, 0, tiltCap());
              clampView();            /* AU2: the zoom floor follows the tilt */
            } else {
              S.yaw = two.yaw + turnBy;
            }
            fing.forEach(function (v, id) { attitudeMoves.add(id); });
            if (window.Sky) Sky.setView(S.yaw / DEG);
            invalidate(); queueHash();
          }
          pinch = { d: pd, c: pc };
          return;
        }
        /* EG4: the sheet folded, a pinch out is the wheel's way out: it
           stands the reader up (wheelOut), once, when the spread has
           closed by a third; seated or standing, it moves nothing else */
        if (pinch && pd > 8 && pinch.d > 8 && PHONE && foldAmt() > 0.5) {
          if (S.cam.want < 0.5) {
            S.overPinch = Math.max(0, (S.overPinch || 0) + Math.log(pinch.d / pd));
            if (S.overPinch > 0.4) { S.overPinch = 0; wheelOut(); }
          }
          pinch = { d: pd, c: pc };
          return;
        }
        /* EG3: at the widest view a pinch out goes on into the paper, and
           while the paper is out a pinch in brings it back before it zooms */
        if (pinch && pd > 8 && pinch.d > 8 && PHONE && S.layout.side && S.refit &&
            ((S.wide || 0) > 0 || (pd < pinch.d && S.view.w >= maxViewW() - 0.5))) {
          S.wide = clamp((S.wide || 0) + Math.log(pinch.d / pd) * 2.4, 0, 1);
          S.refit(); queueHash();
          pinch = { d: pd, c: pc };
          return;
        }
        if (pinch && pd > 8 && pinch.d > 8 && Room.mapLive()) {
          var kp = (S.portalScr || S.portal).w / S.view.w;   /* MP2 */
          var pdx = (pc[0] - pinch.c[0]) / kp, pdy = (pc[1] - pinch.c[1]) / kp;
          var pcy = Math.cos(S.yaw), psy = Math.sin(S.yaw);
          S.view.x -= pdx * pcy + pdy * psy; S.view.y -= -pdx * psy + pdy * pcy;
          clampView();
          wheelZoom(Math.log(pinch.d / pd) / 0.0022, pc[0], pc[1]);
          invalidate(); queueHash();
        }
        pinch = { d: pd, c: pc };
        return;
      }
    }
    /* X2: A HAND DRAGGED ACROSS THE ROOM MOVES NOTHING. The travel is
       still counted — that is what tells a press from a drag when the
       hand comes up — and the view, the tilt and the bearing are not
       touched, so there is no draw and no hash. */
    if (!Room.mapLive()) {
      /* R68: where the hand WAS, kept before `last` is moved on — the look
         below is driven by this one move's travel, not by the whole drag */
      var px = last.x, py = last.y;
      moved += Math.abs(e.clientX - last.x) + Math.abs(e.clientY - last.y);
      last = { x: e.clientX, y: e.clientY };
      S.dragOff = (S.dragOff || 0) + 1;
      /* ==== R62: A HAND DRAGGED ACROSS THE ROOM TURNS THE HEAD. Tommy:
         "i think i should be able to free-look via drag around the room?"
         Standing, with the machine down, the drag is a look: the room
         follows the hand exactly as the paper does under a seated drag
         (drag right, the room slides right, the head has turned left).
         No accumulation, no inertia, the head stops when the hand does.
         The map is not touched: this branch is BELOW the gate's refusal
         and the view, the tilt and the bearing are the three numbers it
         never names. No hash: a glance is not a place.

         ==== R68: AND THE HAND NEVER HAS TO UNWIND. Tommy: "if you go
         beyond the max drag you have to undrag the same distance to start
         moving back the other way". R62 read the look ABSOLUTELY from the
         press (base.ly + the drag's whole travel) and clamped the RESULT,
         so every pixel dragged past the wall was still banked in the sum:
         400 px right is 52 degrees, the head shows 22, and the hand owes
         230 px before the head moves at all. A head against its limit
         turns back the moment the hand does — so the clamp is applied to
         the LOOK ITSELF and this move's delta is added to the CLAMPED
         value, never to a running total. Nothing else of R62 changes: the
         gearing, the caps, the sign and the no-hash rule are the same,
         and inside the caps the two forms are arithmetically identical
         (a sum of the deltas is the travel). `base.ly`/`base.lp` stay on
         the press for anything that wants where the head started. ==== */
      /* ZM1: magnified, the head turns as much less as the picture is
         bigger, so the room still follows the hand */
      if (S.cam.want > 0.5 && !Room.lapUp() && base && LOOK.k) {
        var t1 = performance.now();
        var cap = lookCap(), lk = LOOK.k / S.lens.k;
        S.cam.lookYaw = yawIn((S.cam.lookYaw || 0) + (e.clientX - px) * lk * DEG, cap);
        S.cam.lookPitch = clamp((S.cam.lookPitch || 0) + (e.clientY - py) * lk * DEG, -cap.dn, cap.up);
        invalidate();
        S.lookMs = S.lookMs || [];
        if (S.lookMs.length < 400) S.lookMs.push(+(performance.now() - t1).toFixed(3));
      }
      /* ZM1: seated at the packet, magnified, the hand drags the glass */
      else if (S.cam.want < 0.5 && S.lens.k > 1.0005) zoomPan(e.clientX - px, e.clientY - py);
      return;
    }
    if (e.shiftKey) {
      if (e.clientX !== last.x || e.clientY !== last.y) takeVoyageCamera();
      S.tilt = clamp(base.tilt - (e.clientY - base.y) * 0.35 * DEG, 0, tiltCap());
      S.yaw = base.yaw + (e.clientX - base.x) * 0.35 * DEG;
      clampView();                /* AU2: the zoom floor follows the tilt */
      if (e.clientX !== last.x || e.clientY !== last.y) attitudeMoves.add(e.pointerId);
    } else {
      /* PH6: on the phone the paper follows the finger exactly (css px
         to map units is portal/view); the desk's gearing is left as it is */
      var Pg = S.portalScr || S.portal;           /* MP2: on the glass, and the desk's gearing by its window's own fit */
      var k = PHONE ? Pg.w / S.view.w : Pg.w / S.view.w * S.layout.k / (S.frameK || 1);
      var dx = (e.clientX - last.x) / k, dy = (e.clientY - last.y) / k;
      /* HM1: on the phone a finger that wanders under a tap is not a pan, and does not take her camera away. Tommy:
         "It's a bit too easy on the voyage to come out of the voyage, just because you're tapping on the screen" */
      if ((dx || dy) && (!PHONE || moved + Math.abs(e.clientX - last.x) + Math.abs(e.clientY - last.y) > 10)) takeVoyageCamera();
      var cy = Math.cos(S.yaw), sy = Math.sin(S.yaw);
      S.view.x -= dx * cy + dy * sy; S.view.y -= -dx * sy + dy * cy;
      clampView();
    }
    moved += Math.abs(e.clientX - last.x) + Math.abs(e.clientY - last.y);
    last = { x: e.clientX, y: e.clientY };
    if (window.Sky) Sky.setView(S.yaw / DEG);
    S.lastDrawMs = performance.now() - t0;
    S.moveMs = S.moveMs || []; if (S.moveMs.length < 400) S.moveMs.push(+(performance.now() - t0).toFixed(3));
    invalidate(); queueHash();
    /* DP1 (2026-10-07): on the phone a drag is a pan and nothing else; only a tap reads a day. Tommy: "If you merely
       pan the map, it changes the card to the date under where you landed your finger ... the card changes multiple
       times" */
    if (!PHONE) readDay(e);
  });
  function up(e) {
    /* AL1: letting go of a line lays it */
    if (aimId !== null && e.pointerId === aimId) { aimId = null; doomLetGo(); return; }
    var had = pts.has(e.pointerId);
    pts.delete(e.pointerId);
    if (PHONE) phoneLift(e.pointerId);
    if (!pts.size) freeLift();        /* the hand is off: the aim reads the ground again, and eases */
    /* ==== N2: A CLICK ON THE GROUND DOES NOTHING. E6 pinned the day
       under the pointer here and R24 gave that pin a head to push; the
       lock is gone (day.js's head), and a day is read by putting the
       hand on it. A landmark on the plate does not open anything; a
       BENCH MARK opens its essay since BK2 (below), which reverses v32 A's
       "the card's own control is the door" on Tommy's word. What is left in this handler is the way back, the
       packet, the attitude snap and the pit's own arrows. ==== */
    if (!had) return;
    if (unfoldPull) {
      /* Only the hand which took the packet may let it go. */
      if (unfoldPull.id !== e.pointerId) return;
      if (settlePacketPull()) { e.stopPropagation(); return; }
      unfoldPull = null;
      /* Cancellation is not the short, stationary push `both` treats as
         a click. With no R81 candidate, the default legacy path below is
         deliberately unchanged. */
      if (e.type === 'pointercancel') return;
    }
    if (attitudeMoves.has(e.pointerId)) {
      attitudeMoves.delete(e.pointerId);
      if (e.type !== 'pointercancel') snapAttitude();
    }
    if (cardPull && cardPull.id === e.pointerId) {
      var cp = cardPull; cardPull = null;
      if (moved <= 6 || e.type === 'pointercancel') {
        /* a tap: up, it is put down; lying, a small lift and drop */
        if (cp.from) S.card.peekSettle(false);
        else if (e.type !== 'pointercancel') S.card.peekNudge();
        return;
      }
      /* let go: on to the nearer end, a flick carrying it (the last 80 ms
         of the hand, a fifth of a second on) */
      var tn = performance.now(), hl = cp.hist.filter(function (q) { return tn - q[0] <= 100; });
      var vel = 0;
      if (hl.length >= 2) {
        var h0 = hl[0], h1 = hl[hl.length - 1];
        /* and a hand that stopped before it let go has no flick in it */
        vel = tn - h1[0] > 60 ? 0 : (h1[1] - h0[1]) / Math.max(16, h1[0] - h0[0]) / cp.span * (cp.from ? -1 : 1);
      }
      S.card.peekSettle(cp.a + vel * 200 > 0.5);
      return;                              /* PK2: a carry is not a click */
    }
    if (moved > 6) return;
    if (Room.lapUp()) { S.pickOff = (S.pickOff || 0) + 1; return; }   /* R60 */
    /* ==== X2: THE WAY BACK IS THE MAP ITSELF. A press that did not
       travel, made while standing in the room or with the sheet folded,
       is a hand reaching for the thing on the desk — and that is the whole
       of the way back, and it is answered first because in the room a
       press on the paper is not a day. ==== */
    var rr = rectOf();
    if (x2Press(e.clientX - rr.left, e.clientY - rr.top)) return;
    if (!S.day) return;
    var r = rectOf();
    /* TC1: on the phone a tap on the card clipped behind brings it forward, before the map under it is read;
       TP1, NC2: and a tap anywhere on the card is the card (a pair turns over), never the day under it */
    if (PHONE && S.card && S.card.hitCard && S.card.hitCard(e.clientX - r.left, e.clientY - r.top)) {
      if (S.day.turnCard) S.day.turnCard();
      return;
    }
    /* D1: an arrow round the surveyor takes the press and the party
       moves. */
    if (doomPress(S.day.mapAt(e.clientX - r.left, e.clientY - r.top))) return;
    /* ==== BK2 (2026-09-30): A BENCH MARK OPENS ITS ESSAY. Tommy, on the
       card's "read the post": "how can you read the post?" — the card was
       the only door (v32 A, and N2 above), and the hand had HOLD_MS to
       carry the card's hold from the mark to the corner, which it seldom
       could. His ruling now: "Clicking a bench mark opens the post". So a
       press that did not travel, on a bench mark (its drawn symbol, BM1's
       pick, or its day), opens the Journal at that essay, as the card's
       control does; every other cell still takes nothing. ==== */
    var bh = S.day.dayAt(e.clientX - r.left, e.clientY - r.top);
    var bp = bh && (bh.bench || !S.foreign) && window.RoomDay && RoomDay.markOn ? RoomDay.markOn(bh.date) : null;
    if (bp && window.RoomBooks && RoomBooks.openByDate) RoomBooks.openByDate(bp.date, { fly: false });
    /* PH6: A TAP IS THE HOVER. A finger has no hover, so on the phone a
       tap reads the day under it onto the card, as a pointer over it does
       at the desk, and the card keeps it until the next tap (a tap off
       the record puts the card back to the front). */
    /* LY1 (2026-10-04): and on the phone a tap on the paper OFF THE PLATE
       (its head, the section) is off the record: the desk above the paper
       was the only place left to put the card away, and LY1 took it */
    else if (PHONE) {
      var pp = S.portalScr || S.portal, tx = e.clientX - r.left, ty = e.clientY - r.top;   /* MP2 */
      var onPlate = !pp || (tx >= pp.l && tx <= pp.l + pp.w && ty >= pp.t && ty <= pp.t + pp.h);
      if (!(bh && onPlate) && S.day.dropDesk) S.day.dropDesk();   /* UC1 */
      S.day.show(bh && onPlate ? bh.date : null);
    }
  }
  /* PH6: a finger off the glass is out of the pinch, and the one left
     carries on as a drag from where it is, not from where it went down */
  function phoneLift(id) {
    fing.delete(id); pinch = null; two = null;
    /* EG3: the fingers off the glass with the paper half out: it settles
       to the nearer framing, over a fifth of a second */
    if (fing.size < 2 && S.wide > 0 && S.wide < 1 && S.refit) {
      var w0 = S.wide, w1 = w0 > 0.35 ? 1 : 0, t0 = performance.now();
      (function settle() {
        var u = Math.min(1, (performance.now() - t0) / 200);
        S.wide = w0 + (w1 - w0) * u * u * (3 - 2 * u);
        S.refit();
        if (u < 1) requestAnimationFrame(settle);
      })();
    }
    if (fing.size === 1) { var rest = fing.values().next().value; last = { x: rest[0], y: rest[1] }; }
  }
  host.addEventListener('pointerup', up); host.addEventListener('pointercancel', up);
  /* ==== B3: AND EVERY OTHER WAY THE HAND CAN LEAVE THE GLASS =========
     Everything the press above set — the `pts` entry, R81's `unfoldPull`,
     `RoomObjects.foldGrab` — is let go by one event coming back to this
     element, and the press asks for the pointer to be CAPTURED so that it
     does. That capture is asked for inside a try/catch because it can
     simply not be given (a synthetic pointer has none), and it can be
     taken away again afterwards: the window loses focus mid-drag and the
     button is let go in another application, the tab is hidden, another
     element takes the pointer for itself. In each of those the up lands
     somewhere else or never happens at all, and both flags stay set —
     `unfoldPull` makes the next pointerdown return at the head of the
     handler, and `foldGrab` makes `mapLive()` false — so the canvas is
     dead to every hand until the page is reloaded.

     MEASURED before this card (room-rev10/b3-specs): a lost capture with
     the up off the element left the fold stuck at 0.820 with `grab` set,
     and a press-drag-release on the reopened map moved the view 0 units.
     A `pointercancel` did NOT wedge it — `up()` is already wired to that
     one, the only one of the four that was covered.

     So the release is hung on the events that say the hand is gone.
     Each is the RELEASE HALF of `up()` and nothing else: a lost pointer
     is not a click, so it never pins a day and never reaches X2's way
     back, and — exactly as `up()` already has it for a cancel — it does
     not snap the attitude. The packet settles through R81's own
     `settlePacketPull`, so an interrupted pull is put down where a finger
     lifted at that point would have put it. Nothing a normal drag or
     release does changes: each of these does something only while this
     pointer is still held, and after `up()` there is nothing left to let
     go — which is what makes the `lostpointercapture` that follows every
     ordinary release a no-op. None of the four can run per frame. ==== */
  function letGo(id) {
    /* AL1: a line in a hand that has gone is laid where it was left */
    if (aimId !== null && aimId === id) { aimId = null; doomLetGo(); return true; }
    if (cardPull && cardPull.id === id) {
      var cl = cardPull; cardPull = null;
      if (S.card) S.card.peekSettle(cl.a > 0.5);
    }
    if (PHONE) phoneLift(id);
    if (!pts.has(id) && !(unfoldPull && unfoldPull.id === id)) return false;
    pts.delete(id);
    if (!pts.size) freeLift();
    attitudeMoves.delete(id);
    if (unfoldPull && unfoldPull.id === id) settlePacketPull();
    return true;
  }
  function letGoAll() {
    var ids = [];
    pts.forEach(function (v, k) { ids.push(k); });
    if (unfoldPull && ids.indexOf(unfoldPull.id) < 0) ids.push(unfoldPull.id);
    if (aimId !== null && ids.indexOf(aimId) < 0) ids.push(aimId);
    for (var i = 0; i < ids.length; i++) letGo(ids[i]);
  }
  host.addEventListener('lostpointercapture', function (e) { letGo(e.pointerId); });
  /* The up that never came back here. This pair sits on the window in the
     BUBBLE phase, below host's own listeners, so a release that does reach
     the glass is still answered by `up()` first, with its click and its
     stopPropagation, and finds nothing left when it arrives. */
  window.addEventListener('pointerup', function (e) { letGo(e.pointerId); });
  window.addEventListener('pointercancel', function (e) { letGo(e.pointerId); });
  /* A hand that is now somewhere else entirely: the button will be let go
     in another window, or on another tab, and no pointer event of any
     kind is coming back. */
  window.addEventListener('blur', letGoAll);
  document.addEventListener('visibilitychange', function () { if (document.hidden) letGoAll(); });
  host.addEventListener('pointerleave', function (e) {
    /* PH6: a finger lifting is a leave; the tapped day stays on the card */
    if (PHONE && e.pointerType !== 'mouse') return;
    if (S.day) S.day.show(null);
  });
  host.addEventListener('wheel', function (e) {
    e.preventDefault();
    /* A wheel outside the card cannot move the chart, and in particular
       cannot spend the folded-packet wheel bank before mapLive's gate. */
    var heldCard = Room.cardPresented && Room.cardPresented();
    if (heldCard && heldCard.discovery === true) {
      S.wheelOff = (S.wheelOff || 0) + 1;
      return;
    }
    /* ==== X2: THE WHEEL DOES NOT REACH THE MAP FROM THE ROOM. The event
       is still taken off the page (a wheel over a canvas must not scroll
       the document), and then nothing at all happens: no view, no clamp,
       no hash, no draw. `Room.detach()` counts them, which is how the
       report proves it. ==== */
    /* R60: the machine takes the whole hand, first of all. */
    if (Room.lapUp()) { S.wheelOff = (S.wheelOff || 0) + 1; return; }
    /* ZM1: off the map, a wheel in looks closer and a wheel out comes back;
       only at the eye's own view does a wheel out go on to what it did */
    if (zoomCtx() && (e.deltaY < 0 || zGoal.k > 1.0005)) {
      overW = 0; S.wheelOff = (S.wheelOff || 0) + 1;
      zoomBy(Math.exp(-e.deltaY * 0.0022), e.clientX - host.getBoundingClientRect().left,
             e.clientY - host.getBoundingClientRect().top, true);
      return;
    }
    /* X2: standing in the room, the wheel is nothing at all. */
    if (S.cam.want > 0.5) { S.wheelOff = (S.wheelOff || 0) + 1; return; }
    /* ==== R59 (b): AND THE WHEEL OUT OVER A FOLDED SHEET IS THE WAY OUT
       OF IT. Tommy, 2026-09-14: "zoom out to enter room post-fold", and
       "Zoom-out past the fit still enters the room — no, get rid."
       So the bank X2 built against the fit's stop is not spent on an open
       sheet at all any more (see the foot of this handler): it is spent
       HERE, on the packet, where winding out is the only thing left to
       ask for. The order matters — a folded sheet is not live, so this
       has to be read BEFORE `mapLive()` refuses, or the gesture would be
       counted and dropped like any other wheel on dead paper.
       The map still does not move: the view, the clamp, the hash and the
       draw are untouched, and the wheel is counted refused. All that
       accumulates is the bank, and a turn back IN spends it, so a wheel
       that overshoots and comes straight back stands nobody up. ==== */
    if (foldAmt() > 0.5) {
      S.wheelOff = (S.wheelOff || 0) + 1;
      /* Tommy, 2026-09-15: "to zoom out from the folded paper you currently
         need 2 mouse wheel turns, make it 1". The bank is one notch; a
         wheel back in still clears it. */
      if (e.deltaY > 0) {
        overW += e.deltaY;
        if (overW >= 1) { overW = 0; wheelOut(); }
      } else if (e.deltaY < 0) overW = 0;
      return;
    }
    if (!Room.mapLive()) { S.wheelOff = (S.wheelOff || 0) + 1; return; }
    cancelHome();                     /* R14 */
    wheelZoom(e.deltaY, e.clientX, e.clientY);
  }, { passive: false });
  /* PH6: the wheel's zoom, as it was inline in the handler above, so a
     pinch on the phone goes the same way (the same factor per unit of
     deltaY, the same clamp, the same stops and corner tug). */
  function wheelZoom(deltaY, clientX, clientY) {
    var e = { deltaY: deltaY, clientX: clientX, clientY: clientY };
    /* CONTINUOUS: there is no tile lattice to land on any more (§3) */
    var f = Math.exp(e.deltaY * 0.0022);
    var r = host.getBoundingClientRect();
    var Pz = S.portalScr || S.portal;           /* MP2: the pivot under the pointer on the glass */
    var px = (e.clientX - r.left - Pz.l) / Pz.w;
    var py = (e.clientY - r.top - Pz.t) / Pz.h;
    px = clamp(px, 0, 1); py = clamp(py, 0, 1);
    var mx = S.view.x + px * S.view.w, my = S.view.y + py * S.view.h;
    var w0 = S.view.w;
    var w1 = clamp(S.view.w * f, minViewW(), maxViewW());   /* V4 */
    /* ONE WHEEL OUT LIFTS THE CORNER, TWO STAND IT UP. Tommy (2026-09-20):
       "when you're fully zoomed out on the map, I think you have to do two
       out zooms to get the first raised corner and then a third out zoom
       to get the fully raised corner… rather than two and three, we should
       do one and two." The widest view is a quarter wider than the fit and
       one wheel notch is a shade under that (e^0.22), so the first wheel
       from the fit stopped a hair short of the stop and did nothing but
       zoom, and only the next one was AGAINST the stop. A wheel out that
       lands within a notch of the stop is taken to the stop, and a wheel
       that reaches the stop asks the corner up as one against it does. */
    var wMax = maxViewW(), toStop = false;
    if (e.deltaY > 0 && w1 < wMax && w1 * 1.02 >= wMax) w1 = wMax;
    if (e.deltaY > 0 && w1 === wMax && w0 < wMax) toStop = true;
    /* ==== R59: A WHEEL THAT CHANGES NOTHING CHANGES NOTHING. Against
       either stop the clamp gives the width back unaltered, and the
       origin below is `x + px*w - px*w`, which in floating point is the
       same number give or take an ulp — MEASURED: ten wheels against the
       fit's stop moved the sheet by enough ulps to re-ink the turned
       north-east corner, 12 device px at 1/255 (invisible, but not the
       same picture). X2 could not reach this because the first wheel at
       the stop stood the reader up; now that it does not, a reader can
       wind against the stop all day, so the no-op is made a real no-op:
       no origin, no clamp, no hash, no draw. `w0` has been sitting here
       unused since E1 waiting for it. ==== */
    if (w1 === w0) {
      /* ==== B2: EXCEPT THAT THE STOP IS NOW THE WAY IN TO THE FOLD.
         Tommy, 2026-09-19: "it's maybe a little bit hard to discover that
         you can fold up the map… Let's not make it too discoverable…
         zooming out further, tugging the corner up slightly… changing
         the cursor". A reader who has rolled all the way out and keeps
         rolling is asking for something further out than the map, and the
         only thing further out is putting the map away. So the north-east
         corner comes up a few millimetres more (objects.js's rise channel)
         and the cursor over it turns to a grab (pin.js's `is-tug`). The
         wheel itself still does NOTHING here — no view, no clamp, no hash,
         no draw, and no room: R59 (b)'s ruling stands, the way out of a
         flat map is the hand on the corner.

         S-B2: AND IT ASKS IN STAGES. Tommy, review of 2026-09-19: "maybe
         there's a second level of zoom, so it pops out a little bit, and
         as you zoom further, it pulls it even further." Each wheel out
         against the stop asks the corner up a stage (objects.js holds it
         to two, and to one stage a flick); each wheel that MOVES the map
         — which is every wheel back in — lets it down one. ==== */
      if (e.deltaY > 0 && window.RoomObjects && RoomObjects.cornerTug) {
        RoomObjects.cornerTug(true);
      }
      overW = 0; return;
    }
    /* B2: and the moment the map moves again it is no longer at the stop.
       S-B2: down a stage, not out of them all — a wheel in lets the paper
       lie back down at the same rate a wheel out brought it up. */
    if (window.RoomObjects && RoomObjects.cornerTug) RoomObjects.cornerTug(toStop);
    takeVoyageCamera();
    S.view.w = w1;
    S.view.h = S.view.w * viewRatio();
    S.view.x = mx - px * S.view.w; S.view.y = my - py * S.view.h;
    clampView(); invalidate(); queueHash();
    /* ==== R59 (b): AND PAST THE STOP, NOTHING. X2 stood the reader up
       here — a wheel that kept winding against the fit's clamp banked
       `X2_OVER` and then the camera left the desk. Tommy, 2026-09-14:
       "Zoom-out past the fit still enters the room - no, get rid." An
       open map is a thing you are reading; winding out against its stop
       is not an instruction to put it down. The stop is just a stop, and
       the bank now lives on the folded packet at the head of this
       handler. ==== */
    overW = 0;
  }
  window.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    /* The card is the nearest thing. One Esc puts down exactly it; it
       must not also close a book, unfold, reset the view or stop/start a
       boat in the same key event. */
    var heldCard = Room.cardPresented && Room.cardPresented();
    if (heldCard && heldCard.discovery === true) {
      e.preventDefault();
      e.stopImmediatePropagation();
      Room.cardDismiss('escape');
      return;
    }
    /* ESC PEELS ONE THING AT A TIME: an open book first, then the
       laptop, the fold and the room, and only an Esc with none of them
       in force lays the sheet flat. */
    if (window.RoomBooks && RoomBooks.closeOne()) return;
    /* X1: the laptop comes off before the room does — it is the innermost
       of the two camera things and the last one the reader put on. (An Esc
       typed INTO the terminal never reaches here: terminal.js stops its own
       key events on its own element, and handles this itself.) */
    if (S.cam.lapWant > 0.5) { Room.terminal(false); return; }
    /* X2: and a folded sheet is opened before the room is left — it is
       the innermost thing the reader did, and unfolding sits them down
       anyway, so an Esc against a folded map does both in one. */
    if (foldAmt() > 0.0005) { Room.unfold(); return; }
    /* E8: and the room comes off last of the things the reader put on —
       it is the outermost. */
    if (S.cam.want > 0.5) { lookUp(false); return; }
    /* a card the voyage laid down that is not a foreground discovery (a
       discovery is taken at the head of this handler) is put down here,
       silently, exactly as it was before the pin went. */
    if (S.day && S.day.dismiss && S.day.dismiss('escape')) return;   /* CS1: an Esc is a hand */
    S.tilt = 0; S.yaw = 0; if (window.Sky) Sky.setView(0); invalidate(); queueHash();
  });
  /* A foreground discovery has one key control as well as one pointer
     control. Trap Tab on that control and keep map/book shortcuts behind
     the card asleep. Escape is deliberately allowed onward to the window
     listener above; Enter/Space on the button must still make its click. */
  document.addEventListener('keydown', function (e) {
    var heldCard = Room.cardPresented && Room.cardPresented();
    if (!heldCard || heldCard.discovery !== true || e.key === 'Escape') return;
    var onButton = e.target && e.target.closest && e.target.closest('#dc-essay');
    if (onButton && (e.key === 'Enter' || e.key === ' ')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var btn = document.getElementById('dc-essay');
    if (e.key === 'Tab' && btn && btn.focus) btn.focus({ preventScroll: true });
  }, true);
  /* ==== R60: AND THE KEYS, IN ONE PLACE. terminal.js stops the keys that
     START inside the terminal (bubble phase, after its own input has had
     them) — which is the right rule and is unchanged. What it cannot see
     is a key typed with the prompt NOT focused: the reader clicked the
     oak beside the laptop, or a book took the focus, and now `b`, `j`,
     `r`, `0` and the arrows are the map's again while the reader is
     looking at a machine. So the ONE gate answers for the keyboard too,
     at the CAPTURE phase on the document, before any of the page's own
     listeners: while the laptop is up and the key did not start inside
     the terminal, nothing but Esc is allowed through — and
     the key is put back where it belongs by returning the focus to the
     prompt, so the reader's next keystroke types. ==== */
  document.addEventListener('keydown', function (e) {
    if (!Room.lapUp()) return;
    if (e.target && e.target.closest && e.target.closest('.tm')) return;
    if (e.key === 'Escape') return;                       /* turns back */
    if (e.ctrlKey || e.metaKey || e.altKey) return;       /* the browser's, not ours */
    S.keyOff = (S.keyOff || 0) + 1;
    e.stopPropagation();
    if (window.RoomTerm && RoomTerm.focus) RoomTerm.focus();
  }, true);
  /* CT1 (2026-09-22): THERE IS NO KEY TO THE MACHINE. X1's Ctrl+` (this
     file answered it until terminal.js landed, then terminal.js toggled)
     turned the camera to the laptop from anywhere, which skipped every
     stage of the chain before it. Tommy: "You shouldn't be able to just
     accidentally do a keyboard shortcut and skip a large part of the
     Easter egg ... yes delete Ctrl+`". The machine is reached by a hand on
     the lid, or `Room.terminal(true)` for an agent, and left by Esc. */
  window.addEventListener('hashchange', function () {
    if (!applyHash()) return;
    invalidate();
    /* a link edited by hand is a settled view too (found by G's smoke) */
    try { strikeRange(); } catch (e) { console.warn('range', e); }
  });
  var rt = null;
  function refit() {
    layout(); clampView(); reFurnish();
    if (S.letters) S.letters.invalidate();
    if (S.marks) S.marks.invalidate();
    if (window.RoomObjects) RoomObjects.place(S);
    if (S.day) S.day.place();
    else if (S.card) { S.card.place(); S.card.layoutDom(); }
    if (S.dropRect) S.dropRect();
    invalidate();
    /* FD4 (2026-10-05): AND ONCE MORE A FRAME LATER. The first picture after
       a resize lays the ground on the frame before's numbers and it is left
       out, so the plate showed the bare board, all brown, until a hand
       moved it (Tommy, turning his iPhone; production too). A second draw
       on the next frame is what a pan was doing. */
    requestAnimationFrame(function () { invalidate(); });
  }
  S.refit = refit;   /* EG3: the pinch into the paper lays the sheet again at once */
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(refit, 120);
  });
  /* ==== VW1 (2026-10-05): AND THE GLASS ITSELF IS WATCHED. Tommy's iPhone
     screenshots: on a first load the map was laid for a wider glass than
     the one shown (its east end and the corner past the screen's edge, so
     the sheet could not be folded), and back at the bare address for a
     shorter one (a band of board under it). iOS Chrome settles its window
     (the bar, the safe area) after the page has laid itself out and does
     not always say so with a `resize`; a reload worked because the size was
     known from the start. The canvas's own box is what the layout reads, so
     any change to it lays the sheet again, whatever moved it. ==== */
  if (window.ResizeObserver) {
    try {
      new ResizeObserver(function () {
        if (!S.css || (host.clientWidth === S.css.w && host.clientHeight === S.css.h)) return;
        clearTimeout(rt);
        rt = setTimeout(refit, 120);
      }).observe(host);
    } catch (e) { /* older engines: the resize event alone */ }
  }
}


/* X2's `foldCut` stood here: the window's scissor cut back to the crease
   that was working, so the map went under the paper being folded over it.
   R58 stopped drawing the window at all while the paper is folded, and
   the one call was inside that same `!folded` gate, so it never ran again.
   Deleted 2026-09-21 (WC1 found it). */

/* FU2 (2026-09-27): X2's `drawNote` stood here, the scissor that drew the
   note under the map only where the paper was off it. The note is deleted
   (day.js FU2). */

/* S3b: EVERY UPLOAD OF THE FURNITURE GOES THROUGH HERE, and the rasterised
   canvas is KEPT in S.furnCv afterwards instead of dropped -- the pristine
   print, which is the one thing `Room.furnPatch` needs to strike a live
   part back over its own region without re-cutting the sheet. It costs
   what the raster cost to make (it was thrown away a frame later before)
   -- one sheet's worth of RGBA at the texture's size. And every patch
   goes straight back on, in the order it was first struck: a refurnish
   or a resize re-cuts the print under them and must not wipe them. N1:
   there are two (the section's, the light line's) and S.furnLast, which
   kept one, is a list keyed by region -- S.furnPatches. */
function furnUpload(bmp) {
  var gl = S.gl;
  gl.activeTexture(gl.TEXTURE1);   /* its own unit, as furnPatch */
  gl.bindTexture(gl.TEXTURE_2D, S.texFurn);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  furnMips(gl);                    /* AU9 */
  gl.activeTexture(gl.TEXTURE0);
  /* a canvas is kept; an ImageBitmap (none today: viaImage is the only
     path) cannot be, it is closed */
  if (bmp.close) { bmp.close(); S.furnCv = null; } else S.furnCv = bmp;
  S.furnReady = true;
  (S.furnPatches || []).slice().forEach(function (L) {
    try { Room.furnPatch(L.x, L.y, L.w, L.h, L.fn); } catch (e) { console.warn('furnPatch', e); }
  });
  /* N1: the light line's first strike waits for exactly this moment */
  noteBind(); try { strikeNote(); } catch (e) { console.warn('skyNote', e); }
  try { strikeSurveyed(); } catch (e) { console.warn('surveyedTo', e); }
  try { strikeRange(); } catch (e) { console.warn('range', e); }
  /* CR1 (2026-09-20): and the row of FOUND marks in the margin above the
     cartouche, which is print too (progress.js). A reader who earned one
     on an earlier visit has it already in localStorage, so its first
     strike waits for exactly this moment as the light line's does; every
     one after it comes off the module's own knock. */
  try { if (Room.progress && Room.progress.strike) Room.progress.strike(); }
  catch (e) { console.warn('marks', e); }
}

/* ==== AU9 (2026-10-02): THE PRINT IS STRUCK AT TWICE A 1x SCREEN. The
   furniture (the margin notes, the section's scale figures, the year index:
   type of 4-6 px) was rasterised at exactly the device's pixels, then drawn
   through the pitched camera and the drape, so every texel landed between
   two pixels and a 1x screen showed it blurred (the audit's A13). Below 1.5
   it is now struck at 2x and drawn down through mipmaps (furnMips), which is
   a proper filter for the minification; at 1.5 and over it is the device
   ratio as it was. The patches (furnPatch) take their scale off furnPx, so
   they follow. ==== */
function furnScale() { return S.dpr < 1.5 ? 2 : S.dpr; }
function furnMips(gl) {
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
}
function reFurnish() {
  var w = Math.round(S.fld.sheet.w * S.layout.k * furnScale());
  var h = Math.round(S.fld.sheet.h * S.layout.k * furnScale());
  if (S.furnPx && S.furnPx[0] === w && S.furnPx[1] === h) return;
  S.furnPx = [w, h];
  /* 2026-09-20: ONLY THE LATEST RASTER LANDS. Two resizes in flight put
     two rasters on the wire; the one that lands second uploaded a
     texture of ITS size while the kept patches (the section, the range,
     the row) were re-struck at the other's, and GL answered 1281 until
     the next upload. A raster older than the size now asked for is
     dropped unopened. */
  var gen = S.furnGen = (S.furnGen || 0) + 1;
  furniture(w, h).then(function (bmp) {
    if (gen !== S.furnGen) { if (bmp && bmp.close) bmp.close(); return; }
    furnUpload(bmp); invalidate();
  }).catch(function (e) { console.warn('furniture', e); });
}

/* =================================================================
   THE ROOM MOVE — one camera, a different pose (Room.room(1))
   ================================================================= */
/* THE MOVE. 1.2 s, cubic in and out, from wherever the camera is now —
   an interrupted move is picked up from where it stood, not from the
   pose it was leaving, so `room 1` `room 0` `room 1` in quick succession
   never jumps. The view, the tilt and the bearing are NOT touched: the
   sheet is left exactly as the reader had it and is found exactly so on
   the way back. */
/* ==== R62: THE LIMITS OF A LOOK, in degrees, and the hand's gearing.
   MEASURED in this room (room-rev7/r62-probe-*.png), not taken from the
   card's suggestion of +-60 / -25 / +35. The room is a back wall, a
   floor and NOTHING else — no side walls, no ceiling — and the frame's
   own corner is most of the way to where it stops before the head has
   turned at all: sixty degrees of yaw here is thirty degrees of void
   (the sums are `lookCap`'s, below).

   And the card's pitch is the wrong way up here, because the standing
   pose already looks DOWN at a desk: at +18 the window stands whole with
   a hand's width of wall over it and the desk's far edge still in (the
   next probe up, +20, has lost the desk); at -16 the desk, the map and
   the machine are all in over a band of floor (the next probe down, -25,
   is two thirds floorboards). Past those the room has nothing left to
   show — a person could crane further; this room could not answer it.
   `?look=yaw,up,down,degPerPx`; `?look=0,0,0,0` is the before picture. */
/* ==== R71 (c): AND NOW THE HEAD TURNS RIGHT ROUND.
   R62's figures were the LIMITS OF A ROOM THAT WAS ONE WALL: 22 degrees
   of yaw, because at 23 the frame's corner left the plaster for the clear
   colour, and +18/-16 of pitch because past those "the room had nothing
   left to show". It has four walls, a ceiling and a floor now, so there
   is nothing left to run off: the yaw is 180 either way and the pitch
   goes from the floor to the ceiling.

   PITCH IS RELATIVE TO THE DESK-FACING POSE, not the horizon. The measured
   close standing pose looks down atan(550/1100), about 26.6 degrees.
   Its old 62-degree upward offset therefore stopped at just 35.4 degrees
   above the horizon and clipped the ceiling pendant. A 90-degree offset
   reaches 63.4 degrees above the horizon: enough to see the bowl and rose,
   still comfortably short of the straight-up basis singularity.

   THE GEARING is 0.25 degrees per pixel and not R62's 0.13. The head had
   44 degrees to cover and now it has 360: at 0.13 a half-turn is 1,385 px
   of drag, which is a chore and not a look; at 0.25 it is 720, about half
   the width of his screen. `?look=yaw,up,down,degPerPx` still tunes all
   four and `?look=22,18,16,0.13` is R62's head exactly. ==== */
var LOOK = (function () {
  var d = { yaw: 180, up: 90, down: 58, k: 0.25 };
  var v = (Q.get('look') || '').split(',').map(Number);
  if (v.length === 4 && v.every(function (n) { return isFinite(n); }))
    d = { yaw: v[0], up: v[1], down: v[2], k: v[3] };
  return d;
})();

/* ==== R62/R71: AND THE ROOM ITSELF HAS THE LAST WORD ON THE LIMITS.
   Above is a PERSON's range; this was the ROOM's, and the head was given
   the smaller of the two. R62 measured it against a room that was one
   wall and a floor: the wall ended `room.hw` mm either side of the desk
   and stopped `room.high` off it, so sixty degrees of yaw was thirty
   degrees of void and the cap came out at 23.1 left, 26.3 right, 21.5 up.

   R71 CLOSED THE BOX. There are four walls, a ceiling and a floor; a look
   cannot leave the room because the room has no edge to leave by, so the
   room's clause is gone and the person's range is the whole answer. What
   is kept is the CEILING clause — not to stop a hole in the world but
   because the room is only 2700 high and the eye is 1280 up, so a head
   thrown right back is looking at plaster a metre and a half away, which
   is a picture of nothing. Past that the pitch simply stops.  ==== */
function lookCap() {
  var yaw = LOOK.yaw * DEG;
  return { l: yaw, r: yaw, up: LOOK.up * DEG, dn: LOOK.down * DEG };
}
/* ROUND AND ROUND (Tommy, 2026-09-15): "rotate 360 rather than locking at
   180 and then having to go the other way". A full head wraps: past 180
   the yaw comes back in at -180, so a turn carries on through the wall
   behind. A narrower head (`?look=22,...`, R62's) still stops at its cap. */
function yawIn(v, c) {
  if (LOOK.yaw < 180) return clamp(v, -c.r, c.l);
  var t = Math.PI;
  v = (v + t) % (2 * t); if (v < 0) v += 2 * t;
  return v - t;
}

var moveRaf = 0;
function lookUp(on) {
  /* EG4 (2026-10-02): PH5 kept a phone at the desk. Tommy: "Coming out
     into the room, fundamentally, it should work fine ... I don't see why
     we wouldn't be able to rotate around the room model", and of speed,
     "mobile has performed great so far". So a phone stands up as a desk
     does: the sheet folded, and a pinch out (the wheel's way out). */
  /* R4: the room is not in the page until it is asked for. Nothing of the
     camera is touched here — the move starts, from wherever the camera
     stands, when the module lands. */
  if (on && !(window.RoomPlace && ((RoomPlace.ready && RoomPlace.basePrimed) || RoomPlace.disabled))) { needPlace().then(function (P) { if (P) lookUp(true); }); return; }
  var want = on ? 1 : 0;
  if (S.cam.want === want && !moveRaf) {
    S.cam.room = want; markMode();
    if (!want) { S.cam.lookYaw = 0; S.cam.lookPitch = 0; }   /* R62 */
    return;
  }
  S.cam.want = want;
  markMode();
  var from = S.cam.room, t0 = performance.now();
  var span = Math.max(1, Math.abs(want - from)) * 1200;
  if (moveRaf) cancelAnimationFrame(moveRaf);
  moveRaf = requestAnimationFrame(function step(t) {
    var e = clamp((t - t0) / span, 0, 1);
    e = e < 0.5 ? 4 * e * e * e : 1 - Math.pow(-2 * e + 2, 3) / 2;
    /* THE LAST FRAME IS THE POSE ITSELF, to the bit. `room 0` has to
       leave the camera at exactly the numbers it had before `room 1`,
       or the flat picture is not the flat picture (E8 acceptance). */
    S.cam.room = e >= 1 ? want : (from + (want - from) * e);
    /* R62: AND THE HEAD IS STRAIGHT AGAIN WHEN THE CHAIR LANDS. The look
       has been unwinding with `room` all the way down (vp scales it), so
       this changes no pixel of any frame — it is the bookkeeping that
       stops the next stand-up inheriting the last look. Zeroed only at
       the END: a sit interrupted half way and stood back up picks the
       head up exactly where it left it, which is `lookUp`'s own rule for
       the pose. */
    if (e >= 1 && want === 0) { S.cam.lookYaw = 0; S.cam.lookPitch = 0; }
    frameDraw();
    moveRaf = e < 1 ? requestAnimationFrame(step) : 0;
  });
  queueHash();
}
/* X2: AND THE MODE IS WRITTEN WHERE THE DOCUMENT CAN SEE IT. One class on
   the root, toggled only when the answer changes: the paper's own DOM —
   the year index, the light line, the hand on the corner — is laid on the
   sheet by a matrix and stays exactly where it is, but nothing in it takes
   a pointer while the reader is standing up. */
function markMode() {
  var r = document.documentElement, want = S.cam.want > 0.5;
  if (r.__x2room !== want) { r.__x2room = want; r.classList.toggle('room-standing', want); }
}
S.lookUp = lookUp;
Room.inRoom = function () { return S.cam.want > 0.5; };

/* ==== R62: THE LOOK, as the one thing outside vp() that may set it.
   `Room.look()` reads the head in degrees; `Room.look(yaw, pitch)` puts
   it there, clamped, and is what the hand below and the harness both go
   through, so there is exactly one place the limits are applied. It is
   NOT written to the hash: a place is linkable, a glance is not. ==== */
Room.look = function (yaw, pitch) {
  var c = lookCap();
  if (yaw !== undefined) {
    S.cam.lookYaw = yawIn(yaw * DEG, c);
    S.cam.lookPitch = clamp((pitch || 0) * DEG, -c.dn, c.up);
    invalidate();
  }
  return { yaw: +((S.cam.lookYaw || 0) / DEG).toFixed(2),
           pitch: +((S.cam.lookPitch || 0) / DEG).toFixed(2),
           person: [LOOK.yaw, LOOK.up, LOOK.down], k: LOOK.k,
           room: [+(c.l / DEG).toFixed(2), +(c.r / DEG).toFixed(2),
                  +(c.up / DEG).toFixed(2), +(c.dn / DEG).toFixed(2)] };
};

/* =================================================================
   X2 — THE ROOM IS A MODE, AND THE SHEET CAN BE FOLDED

   Tommy (IDEAS #22.3): "As soon as you come out to the room, you should
   detach from the map … you're in map view, or you're in room view …
   Room view is a different beast. You can still see the map, but you're
   not directly controlling it."

   THE TWO STATES.
     `map`   — seated at the desk. The wheel, the hand, the keys, the year
               index and the books all reach the map, exactly as before.
     `room`  — standing up. NOTHING the reader does with the pointer or
               the keyboard reaches the map: the wheel is refused, a drag
               neither pans nor tilts, the pencil cross does not follow the
               hand, the index's cells and bracket
               are inert and the books' contents no longer fly the sheet
               anywhere. The map is still there and still lit; it is a
               thing on a desk you are standing over.

   WHAT STAYS LIVE IN `room`, and why. The PUBLISHED functions — Room.goto,
   Room.zoomWeeks, Room.home, Room.easeTo, Room.terminal, Room.laptop — are
   not the reader's hand: they are the agent's, and X4's MCP session drives
   the map from the terminal while the reader stands and watches (IDEAS
   #23: "you can see your MCP via your agent interacting with the terminal
   in front of you and things changing"). Detaching the HAND from the map
   is the ruling; detaching the API from it would break the showpiece.
   Esc stays too — it is the put-down chain (R33) and it is how you get out
   of anything in this engine.

   THE WAY IN, FOR NOW. Keep zooming out. The view stops at a quarter wider
   than the fit, as it always has; past the stop the wheel has nowhere to
   go, and a reader who keeps turning it is asking for something the sheet
   cannot give — so the camera stands up instead. Tommy (#23.3) has already
   said this is "not quite secret enough … there needs to be some kind of
   physical item to unlock it", and the fold below is the candidate; this
   is the way in until he picks one.

   THE WAY BACK IS A THING IN THE ROOM. You sit down by putting a hand on
   the map. Not a button, not a key, not a corner of the screen: the sheet
   itself, anywhere on it, and if it is folded then the packet the sheet
   has become. That is the same gesture a person makes — you reach for the
   thing you want to look at — and it is the one the card names. (The note
   under a folded map is the second thing you can reach for: putting a hand
   on THAT sits you down and leaves the map folded, because you sat down to
   read the note and not the map.)

   THE LAPTOP POSE IS A THIRD THING AND IS NOT A MODE. X1's turn to the
   machine rides ON TOP of whichever of the two states the reader is in:
   `cam.lap` blends over `cam.room` in vp(), the turn to the machine works
   seated or standing, and Esc peels the laptop first and the room second (R33's
   chain, unchanged). Sitting at the map and turning to the laptop leaves
   you seated when you Esc; standing in the room and turning to the laptop
   leaves you standing. The map's input gate does not care which pose the
   camera is in — it cares which STATE the reader is in — so the terminal
   can be opened and shut from either without the map waking up or going
   to sleep underneath it.
   ================================================================= */
var overW = 0;                 /* wheel banked against the stop */

function foldAmt() { var O = window.RoomObjects; return (O && O.foldOn) ? O.foldT : 0; }
/* CF3: the last leaf has landed and the paper is relaxing back into its
   lie (objects.js O.foldSettle). The map is still the print raster on the
   paper until it has, so nothing may move it. */
function foldSettling() { var O = window.RoomObjects; return (O && O.foldOn && O.foldSettle > 0) ? 1e-4 : 0; }
function foldGeom() { var O = window.RoomObjects; return (O && O.foldGeom) ? O.foldGeom() : null; }

Room.mode = function () { return S.cam.want > 0.5 ? 'room' : 'map'; };
/* THE ONE GATE. Every piece of the reader's input asks this and nothing
   else — engine.js's wheel, drag and pick, index.js's cells and bracket,
   books.js's contents. A folded sheet is as detached as a standing reader:
   it cannot be read, so there is nothing to zoom into. */
/* R60: AND THE MACHINE TAKES THE WHOLE HAND. Tommy: "when in the terminal
   my controls still control the map". terminal.js stops what lands on the
   terminal's own glass; it can say nothing about a wheel, a drag or a key
   that lands on the CANVAS BESIDE the laptop, or on a document that has
   lost the prompt's focus. A reader turned to a machine on the desk is not
   touching the paper at all, so the gate — not the listeners — is where it
   belongs, and the turn counts both ways: 900 ms of camera move is 900 ms
   of hand off the map. */
Room.lapUp = function () {
  return S.cam.lapWant > 0.5 || (S.cam.lap || 0) > 0.0005;
};
Room.mapLive = function () {
  var heldCard = S.day && S.day.presented && S.day.presented();
  if (heldCard && heldCard.discovery === true) return false;
  if (S.cam.want > 0.5) return false;
  if (Room.lapUp()) return false;                /* R60 */
  var O = window.RoomObjects;
  if (O && O.foldOn && (O.foldT > 0.0005 || O.foldGrab || O.foldSettle > 0)) return false;
  return true;
};
Room.folded = foldAmt;
Room.sit = function () { lookUp(false); };
Room.stand = function () { lookUp(true); };

/* ==== B3 / WM2 — WHEELING OUT OF THE FOLDED SHEET IS STANDING UP =====
   Tommy, 2026-09-19: "you should only get the Easter egg for the room
   when you zoom out." X6 struck it on the fold's landing, which is a map
   folded and still in your hands; the room is the thing after it. So it
   is struck HERE and nowhere else — not from `Room.stand`, not from a
   `?room=` deep link, not from `Room.room(on)`, because none of those is
   a reader finding the room.

   WM2 (2026-09-22) took the four lines out of the wheel handler and put
   them here, unchanged, because there is now a second pair of hands: an
   agent's `survey_move out` is THE SAME ACT — a wheel wound out over a
   folded sheet — and it must earn the same mark. Two copies of the act
   would be two places the rule could drift, and the rule is the egg. The
   handler above keeps the BANK (one notch, `overW`) because a bank is a
   gesture's own business; what is in here is the act. A sheet that is not
   folded past the half is not standing anybody up, so the guard is here
   too and a caller that has not folded gets false and no mark.
   ==================================================================== */
/* ==== ZM1 (2026-10-02): A CLOSER LOOK, OFF THE MAP. Tommy, on his
   iPhone: "it's quite difficult to make out detail because it's a small
   screen. I wonder if certain bits, like maybe anything that's not in the
   unfolded map, if you should be able to pinch to zoom to see more detail,
   whether that's on the desk (because you want to look at the hint card
   when you flip it), or whether it's the Stripe/WIP pile. I have no
   objection to being able to scroll the mouse on desktop to be able to do
   the same".
   So where the map is not live and the reader is settled (standing in the
   room, or seated at the folded packet; never with the machine up, which
   has its own view) a pinch or the wheel magnifies the PICTURE towards the
   fingers or the pointer: a lean in, not a move. It is a 2D scale and
   shift of the finished projection (`S.lens`: k the magnification, x and
   y the shift in clip units), on top of vp() the way a capture's NDC is,
   so the eye, the pose and every number vp() makes are untouched, and
   everything that reads S.VP (the hit tests, the card's DOM, the labels)
   reads the magnified glass. It stays inside the eye's own picture (the
   shift is held to k - 1) and goes no further than ZMAX. Zoomed back to
   1, a wheel or a pinch out does what it always did (seated at the packet,
   it stands the reader up). Leaving the context eases it back to 1. ==== */
var ZMAX = 4, zGoal = { k: 1, x: 0, y: 0 }, zRaf = 0, zCtx = '';
S.lens = { k: 1, x: 0, y: 0 };
function zoomCtx() {
  if (Room.lapUp && Room.lapUp()) return '';
  var r = S.cam.room || 0;
  if (S.cam.want > 0.5 && r > 0.98) return 'room';
  /* PK1 (2026-10-03): and not at the folded map any more. Tommy: zooming
     there made the way out to the room "less obvious" and "much easier to
     accidentally zoom out to the room"; the card it was for is peeked
     (day.js), so a pinch or a wheel out at the packet is the way out. */
  return '';
}
function zClamp(z) {
  z.k = clamp(z.k, 1, ZMAX);
  var m = z.k - 1; z.x = clamp(z.x, -m, m); z.y = clamp(z.y, -m, m);
  return z;
}
function zTick() {
  if (zRaf) return;
  zRaf = requestAnimationFrame(function step() {
    zRaf = 0;
    var z = S.lens, g = zGoal;
    z.k += (g.k - z.k) * 0.3; z.x += (g.x - z.x) * 0.3; z.y += (g.y - z.y) * 0.3;
    var done = Math.abs(g.k - z.k) < 0.002 && Math.abs(g.x - z.x) < 0.001 && Math.abs(g.y - z.y) < 0.001;
    if (done) { z.k = g.k; z.x = g.x; z.y = g.y; }
    invalidate();
    if (!done) zRaf = requestAnimationFrame(step);
  });
}
/* by a factor about a point on the glass (css px). `ease`: the wheel's
   notches are walked to (the goal moves, the glass follows); a pinch
   moves the glass itself */
function zoomBy(f, cx, cy, ease) {
  var c = zoomCtx();
  if (!c) return false;
  if (c !== zCtx) { zCtx = c; zGoal = { k: 1, x: 0, y: 0 }; }
  var L = S.layout, ax = cx / L.cw * 2 - 1, ay = 1 - cy / L.ch * 2;
  var z = ease ? zGoal : S.lens, k1 = clamp(z.k * f, 1, ZMAX);
  var n = zClamp({ k: k1, x: ax - k1 * (ax - z.x) / z.k, y: ay - k1 * (ay - z.y) / z.k });
  zGoal = n;
  if (!ease) S.lens = { k: n.k, x: n.x, y: n.y };
  zTick();
  return true;
}
/* the magnified glass dragged by a hand (css px) */
function zoomPan(dx, dy) {
  if (!zoomCtx() || S.lens.k <= 1.0005) return;
  var L = S.layout;
  var n = zClamp({ k: S.lens.k, x: S.lens.x + 2 * dx / L.cw, y: S.lens.y - 2 * dy / L.ch });
  zGoal = n; S.lens = { k: n.k, x: n.x, y: n.y };
  invalidate();
}
Room.closer = function (f, cx, cy) {
  if (f === undefined) return { k: +S.lens.k.toFixed(3), x: +S.lens.x.toFixed(3), y: +S.lens.y.toFixed(3), ctx: zoomCtx() };
  var L = S.layout;
  return zoomBy(f, cx === undefined ? L.cw / 2 : cx, cy === undefined ? L.ch / 2 : cy, true);
};

function wheelOut() {
  if (foldAmt() <= 0.5) return false;
  if (Room.progress) Room.progress.mark('fold');
  lookUp(true);
  return true;
}
Room.wheelOut = wheelOut;

/* ---- THE FOLD, as a move ------------------------------------------
   The hand drives it directly (pin.js pulls the corner across the sheet
   and hands the progress here); this is what happens when the hand lets
   go, and what `Room.foldTo` does for an agent that asks in words. */
var foldRaf = 0;
Room.foldAt = function (t) {
  var O = window.RoomObjects;
  if (!O || !O.fold) return 0;
  snapLift();                       /* a fold put where a token says is a place, not a gesture */
  if (foldRaf) { cancelAnimationFrame(foldRaf); foldRaf = 0; }
  var v = O.fold(t);
  markFold();
  draw();
  /* R47: and the URL, unless it is the HAND that is calling — pin.js
     drives this sixty times a second all the way through the gesture and
     the address bar is not a progress bar. `O.foldGrab` is the hand. */
  if (!O.foldGrab) queueHash();
  return v;
};
Room.foldTo = function (to, ms) {
  var O = window.RoomObjects;
  if (!O || !O.fold) return;
  to = clamp(to, 0, 1);
  var from = O.foldT;
  if (foldRaf) { cancelAnimationFrame(foldRaf); foldRaf = 0; }
  if (to === from) { foldLanded(to); return; }
  /* CF3: A LEAF LET GO SHORT FALLS BACK THROUGH THE LEAVES' OWN PLAN. The
     corner's release short of SNAP (pin.js) and anything else that asks
     for the flat sheet get the unfold's lift, fall, cushion and settle,
     not the fold's cubic run backwards; the card reads the same leaf. */
  if (to === 0 && from > 0) { Room.unfold(); return; }
  unfoldRun = null;
  /* THE CAMERA GOES WITH THE PAPER, NOT AFTER IT. Unfolding sits you down
     as the sheet opens — a person crouches to the desk while their hands
     are still on the map — and folding stands you up only when the last
     crease has landed, because you do not step back until you have let go. */
  if (to < from) lookUp(false);
  if (!live) { O.fold(to); foldLanded(to); return; }
  var span = Math.max(0.2, Math.abs(to - from)) * (ms || 1100), t0 = performance.now();
  foldRaf = requestAnimationFrame(function step(t) {
    var e = clamp((t - t0) / span, 0, 1);
    e = e < 0.5 ? 4 * e * e * e : 1 - Math.pow(-2 * e + 2, 3) / 2;
    O.fold(e >= 1 ? to : from + (to - from) * e);
    markFold();
    frameDraw();
    if (e < 1) { foldRaf = requestAnimationFrame(step); return; }
    foldRaf = 0; foldLanded(to);
  });
};
function foldLanded(to) {
  markFold();
  /* A FOLDED SHEET CANNOT BE READ, so the view has nothing to zoom into
     and there is no reason to be sitting over it (IDEAS #23, Tommy's
     packing-up narrative). The camera stands up on its own. */
  /* X6 (b), SUPERSEDED BY B3: the landing WAS the egg. The card names
     objects.js for this, but objects.js only holds the fold's geometry — `O.fold(t)` is
     set sixty times a second all the way through the gesture and has no
     idea when it is over. THIS is where a fold finishes: the one place
     both the animated `Room.foldTo` and the corner's own release in
     pin.js come to rest. Struck on the way down only (a sheet being
     opened is not an egg), and `mark` is idempotent, so the second fold
     of the session strikes nothing. Nothing marks on load: `foldLanded`
     is not called until something folds. */
  /* ==== R59 (a): AND THE CAMERA DOES NOT STAND UP ON ITS OWN. Tommy,
     2026-09-14: "let's not auto zoom out to the room once the map is
     folded, let's force a zoom out action like mouse scroll out to do
     it." A map that has just been folded is still a thing in your hands
     — you are looking down at the packet you made, not at the room. So
     the last crease lands and the camera STAYS SEATED over it, and the
     way out is the wheel (the handler above), which is a hand doing
     something rather than the page deciding for you.
     The other branch stays exactly as X2 wrote it: opening the map sits
     you down, because a sheet being spread out IS the thing you are
     going to read. ==== */
  /* B3: the landing is NOT the egg any more — the wheel that stands the
     camera up after it is (the wheel handler above). A folded map is not
     the room. */
  if (to < 0.001) lookUp(false);
  /* R47: and the URL says so. Here and nowhere else — a fold that has
     landed is a place; the 60 frames of the gesture are not, and neither
     is a sheet the hand let go of halfway. */
  queueHash();
}
/* ==== R58 (b): A MAP IS OPENED A FOLD AT A TIME ======================
   Tommy, 2026-09-14: "when going back in it unfolds too quickly".
   `foldTo(0, 1100)` ran three creases through one ease-in-out cubic in
   1.1 s, and that cubic is the fault as much as the 1.1 is: objects.js's
   `foldTurns` already gives EACH crease its own smoothstep inside its own
   third of the progress, so a second ease over the whole run smears the
   three moves into one sweep with the middle crease taking most of the
   speed. What a hand does is three moves: turn a leaf back, let go, turn
   the next.
   So the progress walks DOWN through the thirds LINEARLY, one third per
   equal slice of time, and the eases are the creases' own. Each move
   rests for the last seventh of its slice, which is the hand letting go
   before it takes the next leaf. About 2.4 s for a sheet in eight, and
   proportionally less for a sheet that was only half folded — `?unfoldms=`
   is the whole run at three creases. The FOLDING path is untouched: the
   corner's own gesture and `foldTo(1, …)` are what they were. ==== */
/* ==== CF3 (2026-09-23): AND EACH LEAF TAKES ITS OWN TIME ==============
   Tommy: "It's particularly uncanny when you unfold because your sheet
   basically hits the journal, and then the card slides along. It's the
   inverse of the way down, but it's not even the right time." R58's walk
   down through three equal thirds is gone, and with it the `?unfoldms=`
   switch: the timing is the leaves' own now. objects.js `O.unfoldPlan`
   says, per leaf, when a hand lifts it (time as the square root of its
   span), when it passes the vertical and falls on its own (accelerating,
   then the air cushion of the last few degrees), when it lands and its
   one small settle, and when the paper's lie has come back after the
   last landing; this only walks the clock and hands the angles over
   (`O.foldAngles`). About 2.4 s to the last landing for a sheet in eight,
   less for one partly folded (only the engaged leaves are in the plan,
   each from the angle it is at). The FOLDING path is untouched.
   A second call while the leaves are already falling is the same
   gesture, not a new one (Esc, the packet's own release). ==== */
var unfoldRun = null;
/* UF1 (2026-10-04): an unfold under way, let go and running to the flat
   sheet: phone.js gives the upright glass back to the Journal from here,
   not from the last of the settle (the upright camera hung over the open
   sheet for most of a second first) */
Room.unfolding = function () { return !!(unfoldRun && foldRaf); };
Room.unfold = function () {
  var O = window.RoomObjects;
  if (!O || !O.fold) return;
  if (S.dropUnfoldPull) S.dropUnfoldPull();
  if (unfoldRun && foldRaf) return;
  var from = O.foldT;
  if (foldRaf) { cancelAnimationFrame(foldRaf); foldRaf = 0; }
  unfoldRun = null;
  if (!(from > 0.0005) || !O.unfoldPlan) { O.fold(0); foldLanded(0); return; }
  lookUp(false);                       /* the camera sits down as it opens */
  if (!live) { O.fold(0); foldLanded(0); return; }
  var plan = O.unfoldPlan(), t0 = performance.now();
  unfoldRun = plan;
  if (window.RoomPhone && RoomPhone.poke) setTimeout(RoomPhone.poke, 0);   /* UF1: once foldRaf is running */
  foldRaf = requestAnimationFrame(function step(t) {
    var st = O.unfoldAt(plan, t - t0);
    if (st.done) {
      O.foldAngles(null);
      O.fold(0);
    } else O.foldAngles(st.a, st.landed, st.settle);
    markFold();
    frameDraw();
    if (!st.done) { foldRaf = requestAnimationFrame(step); return; }
    foldRaf = 0; unfoldRun = null; foldLanded(0);
  });
};
/* the paper's own DOM — the index, the light line — is not on the paper
   any more once the paper is in eight; it goes with the map it was
   printed beside, and comes back when the sheet is opened */
function markFold() {
  var r = document.documentElement, on = foldAmt() > 0.0005 || foldSettling() > 0;
  if (r.__x2fold === on) return;
  r.__x2fold = on; r.classList.toggle('room-folded', on);
}

/* ---- WHAT THE HAND IS OVER, on the sheet's own plane ----------------
   Three projected corners are an exact affine for a plane, so a point in
   a rectangle OF THE SHEET is a point in a convex quadrilateral on the
   glass: four cross products and a sign. No readback, no pick pass, and
   it works at any tilt, bearing and pose because the projection is the
   one everything else on the paper is laid by. */
function sheetQuad(x0, y0, x1, y1) {
  var p = Room.paperPoint, a = p(x0, y0), b = p(x1, y0), c = p(x1, y1), d = p(x0, y1);
  return (a && b && c && d) ? [a, b, c, d] : null;
}
function inQuad(q, x, y) {
  var n = 0;
  for (var i = 0; i < 4; i++) {
    var a = q[i], b = q[(i + 1) & 3];
    var cr = (b[0] - a[0]) * (y - a[1]) - (b[1] - a[1]) * (x - a[0]);
    if (cr > 0) n++; else if (cr < 0) n--;
  }
  return n === 4 || n === -4;
}
/* the packet a folded sheet has become: a quarter of the width, half of
   the depth, in the sheet's own south-west corner — which is where three
   folds towards the south-west leave it */
function packetQuad() {
  var g = foldGeom();
  if (!g) return null;
  return sheetQuad(0, g.cy, g.c2, g.h);
}
/* ==== R62: THE MACHINE, AS TWO QUADS ON THE GLASS. objects.js owns
   where the laptop is and publishes its lid and its deck (`O.laptop`);
   this projects them with the frame's own matrix, the way
   `Room.screenQuad` projects the display. Two quads and not a hull: they
   ARE the silhouette (the lid leans back over the deck's hinge edge),
   and a hull would claim the wedge of oak behind the screen. ==== */
Room.laptopQuad = function () {
  var O = window.RoomObjects, lp = (O && O.laptop && S.layout && S.VP) ? O.laptop(S) : null;
  if (!lp || !lp.lid) return null;
  function proj(q) {
    var out = [];
    for (var i = 0; i < 4; i++) {
      var p = Room.scenePoint(q[i][0], q[i][1], q[i][2]);
      if (!p) return null;                       /* a corner behind the eye: no quad */
      out.push(p);
    }
    return out;
  }
  return { lid: proj(lp.lid), deck: proj(lp.deck) };
};
Room.laptopHit = function (x, y) {
  var q = Room.laptopQuad();
  if (!q) return false;
  return !!((q.lid && inQuad(q.lid, x, y)) || (q.deck && inQuad(q.deck, x, y)));
};
Room.sheetHit = function (x, y) {
  var g = foldGeom(), q;
  if (g && g.t > 0.5) q = packetQuad();
  else q = sheetQuad(0, 0, g ? g.w : S.fld.sheet.w, g ? g.h : S.fld.sheet.h);
  return !!(q && inQuad(q, x, y));
};

/* ---- A HAND ON THE GLASS, WHEN THE MAP IS NOT LISTENING -------------
   Answers true when it has dealt with the press, which is every press
   made while standing: in the room the map answers no press of its own. */
function x2Press(cx, cy) {
  var g = foldGeom(), folded = !!(g && g.t > 0.5);
  var standing = S.cam.want > 0.5;
  if (!standing && !folded) return false;         /* seated at an open map: E6's */
  /* ==== R62: AND THE MACHINE IS THE FIRST THING ASKED. Tommy: "i should
     be able to click onto the laptop once i'm in the room?" A press on
     the laptop turns to it — `Room.terminal(true)`, the same 900 ms turn
     an agent's call makes, the same Esc back. It is
     answered ABOVE the note and the sheet because the machine stands ON
     the desk and the paper lies under it: where the two quads overlap on
     the glass, the nearer thing is the one the hand meant. Standing only
     — seated, X2's click-to-sit and the note are exactly what they
     were. ==== */
  if (standing && Room.laptopHit(cx, cy)) {
    if (PHONE && window.RoomPhone && RoomPhone.primeKeys) RoomPhone.primeKeys();   /* KB1 */
    Room.terminal(true);
    return true;
  }
  /* FU1 (Tommy, 2026-09-27): the file card lands face up beside the
     folded sheet, "and then you can flip it over to find the hints". A
     press on it turns it over, seated or standing. (FU2: the note under
     the map, which a press from standing sat you down to, is deleted.) */
  /* PK1: and it is not turned by a press any more: the hand held on it
     peeks it (pointerdown); a press on it is still the card's */
  if (folded && S.card && S.card.hit && S.card.hit(cx, cy)) return true;
  if (Room.sheetHit(cx, cy)) {
    /* ==== RS1 (Tommy, 2026-09-23): "from the room view, what is the
       click box to go back to the view where you hover over the folded
       map? Currently, it seems like it's more or less just the note".
       It was: with the click-to-unfold arm off (the drag is the gesture)
       a press on the folded packet from standing did nothing at all. A
       press on the map, folded or open, sits you down to it; seated at a
       folded map a press does nothing, the drag opens it. ==== */
    if (standing) lookUp(false);
    return true;
  }
  return true;                                    /* standing, and not on the map */
}

/* =================================================================
   X1 — THE LAPTOP: THE TERMINAL IS A THING ON THE DESK

   The terminal used to be a strip of phosphor stuck to the bottom of the
   window: the internals showing through the world, and nothing in the
   world at all. It has a BODY now. It is the screen of a laptop standing
   on the oak east of the journal, so opening it is not opening a panel,
   it is turning to look at the machine — and that is a camera move, not
   a piece of UI. There is no button anywhere on the page, and there is
   not going to be one.

   WHAT HAPPENS WHEN IT IS ASKED FOR, in order and only then:
     · `/js/room/terminal.js` is fetched (it is not in the document; see
       base.njk) — 30 KB the reader who never asks never pays for;
     · `/js/room/place.js` is fetched if it is not already there, because
       the laptop pose looks across the desk at the room behind it;
     · the camera turns, over 900 ms, from wherever it stands;
     · the terminal's glass is laid on the screen's four projected
       corners, every frame, by `Room.screenQuad`.
   Esc turns it back to exactly the pose it left (R33's put-down rule).
   ================================================================= */
var termReq = null;
function needTerm() {
  /* TM1 (2026-10-02): PH5 kept the terminal off a phone; it is a portrait
     page there now (phone.js), so it is fetched as at a desk */
  if (termReq) return termReq;
  window.ROOM_TERM_ASKED = 1;
  termReq = new Promise(function (res) {
    if (window.RoomTerm) { res(window.RoomTerm); return; }
    /* TD1: nothing else puts the tag in the document any more (base.njk's
       `?term=1` / `,l1` door is gone); this is the one fetch */
    var t = document.createElement('script');
    t.src = (window.assetUrl ? assetUrl('/js/room/terminal.js') : '/js/room/terminal.js');
    t.onload = function () { res(window.RoomTerm || null); };
    t.onerror = function () { res(null); };
    document.head.appendChild(t);
  });
  return termReq;
}

/* THE TURN. Same shape as lookUp: 900 ms, cubic in and out, picked up
   from wherever it stands if it is interrupted, and the last frame is
   the pose itself to the bit so `lap 0` leaves the camera at exactly the
   numbers it had. */
var lapRaf = 0, lapPending = 0;
function turnTo(on) {
  /* LZ1: THE ONE DEMAND GATE. Every way of asking for the machine comes
     through here — a `,l1` link, the terminal's `laptop` word, a hand on
     the lid, an agent's `Room.laptop.pose` — so the fetch is asked for
     here and nowhere else, and terminal.js needs no line of its own. The
     camera's turn is 900 ms and the anchor already answers every question
     the pose is built from, so the file has that long to land. */
  /* TB1: A TURN ASKED BEFORE THE ENGINE IS LIVE IS KEPT, NOT DROPPED.
     terminal.js is a dynamic tag (base.njk), so it is async whatever its
     `defer` says, and since FW1 boot() is a task AFTER DOMContentLoaded —
     so on a slow enough network the terminal's own boot, and its
     `show(true)` -> `Room.laptop.pose(true)`, runs BEFORE boot has run.
     needPlace() then called `P.init` on a page with no context, the turn
     died in its catch, and nothing ever asked again: the glass was up and
     the camera never left the map (Tommy, ?term=1 on the preview, three
     loads of four; reproduced on 8119 at 60 ms of latency, three of
     four). The ask is kept here and put down at boot's own end. */
  if (!live) { lapPending = on ? 1 : 0; if (on) lapWanted = true; return; }
  if (on) needLaptop(true);
  if (on && !(window.RoomPlace && ((RoomPlace.ready && RoomPlace.basePrimed) || RoomPlace.disabled))) { needPlace().then(function (P) { if (P) turnTo(true); }); return; }
  var want = on ? 1 : 0;
  if (S.cam.lapWant === want && !lapRaf) { S.cam.lap = want; invalidate(); queueHash(); return; }
  S.cam.lapWant = want;
  queueHash();                      /* X9: the laptop rides in the URL */
  var from = S.cam.lap, t0 = performance.now();
  var span = Math.max(1, Math.abs(want - from)) * 900;
  if (lapRaf) cancelAnimationFrame(lapRaf);
  lapRaf = requestAnimationFrame(function step(t) {
    var e = clamp((t - t0) / span, 0, 1);
    e = e < 0.5 ? 4 * e * e * e : 1 - Math.pow(-2 * e + 2, 3) / 2;
    S.cam.lap = e >= 1 ? want : (from + (want - from) * e);
    frameDraw();
    lapRaf = e < 1 ? requestAnimationFrame(step) : 0;
  });
}

Room.terminal = function (on) {
  on = (on === undefined) ? true : !!on;
  if (!on) {
    if (window.RoomTerm) RoomTerm.show(false);
    turnTo(false);
    return Promise.resolve(window.RoomTerm || null);
  }
  turnTo(true);
  return needTerm().then(function (T) { if (T) T.show(true); return T; });
};

/* ==== X6 (e): A MODULE MAY BRING WORDS AFTER THE TERMINAL IS OPEN =====
   `?voyage=1&term=1` opened the terminal on twenty-four commands with
   `sail` unknown, and `?voyage=1` alone with Ctrl+` later opened on
   twenty-eight with `sail` working. Both files are injected as DYNAMIC
   scripts (base.njk), and a dynamic script is ASYNC whatever `defer` says
   — so which of voyage.js and terminal.js runs first is a race between two
   ~35 KB responses, and voyage.js's one handover (`RoomTerm.add`) only
   lands if it lost it.

   This is the meeting place, and it is on the engine because the engine is
   the one file both of them can count on being there: `register` takes the
   words whether or not there is a terminal yet — queueing them where
   terminal.js reads the queue at build, and handing them straight over if
   it is already up. terminal.js drains the same queue on every command it
   is given, so even a module that publishes nothing but the queue is
   answered. Nothing is re-printed; the status line's count is corrected in
   place. ==================================================================== */
Room.terminal.register = function (list) {
  if (!list || !list.length) return (window.ROOM_CMDS || []).length;
  window.ROOM_CMDS = (window.ROOM_CMDS || []).concat(list);
  if (window.RoomTerm && RoomTerm.add) RoomTerm.add(list);
  cmdsIn();                       /* X10: the opening line may now count */
  return window.ROOM_CMDS.length;
};

/* ==== X10: AND THE OPENING LINE DOES NOT COUNT BEFORE THEY ARE ALL IN ==
   X6 (e) made a late word WORK — `run` reads BY_NAME live, and the status
   line's count is corrected in place when one arrives. That was the right
   answer while the voyage was a switch that arrived in the same flight as
   the terminal. X10 moved it a long way back (base.njk: DOMContentLoaded,
   then the record, then idle, then a ~49 KB response), so a count
   corrected in place is now a number the reader WATCHES CHANGE — 23 for
   the best part of a second, then 27. That is R53's fault wearing a
   different hat: two loads of the same url, two different opening blocks.

   R53's rule is the fix: a line that has not been written yet is the only
   line a reader cannot see change. `Room.whenCmds(fn)` is `whenRecord`'s
   shape for the command table — fn runs now if nothing is outstanding, and
   otherwise when the module that was promised has registered. The promise
   is `window.ROOM_VOYAGE_ASKED`, set synchronously by base.njk in the body
   (an inline script, so it is set before any deferred file runs, which is
   why this can be read at the moment the terminal asks).

   It is a QUEUE and not an event, for X6 (e)'s reason: a terminal opened
   by a hand on the lid two minutes in must still be answered. And it is not a
   promise-to-print — terminal.js keeps its own give-up timer, so a
   voyage.js that 404s or never parses cannot leave the screen silent.
   ==================================================================== */
var cmdQ = [];
function cmdsWaiting() { return !!window.ROOM_VOYAGE_ASKED; }
function cmdsIn() {
  if (!cmdQ.length) { window.ROOM_VOYAGE_ASKED = 0; return; }
  window.ROOM_VOYAGE_ASKED = 0;
  var q = cmdQ; cmdQ = [];
  q.forEach(function (fn) { setTimeout(fn, 0); });
}
Room.whenCmds = function (fn) {
  if (typeof fn !== 'function') return !cmdsWaiting();
  if (!cmdsWaiting()) fn();
  else cmdQ.push(fn);
  return !cmdsWaiting();
};

/* WHAT THE LAPTOP PUBLISHES (IDEAS #23: "you can see your MCP via your
   agent interacting with the terminal in front of you"). X4's session
   drives the machine through these three and through nothing private:
     pose(on)   turn to it, or away
     type(text) put text in the prompt a character at a time, and leave
                it there — a promise that settles when the line is typed
     run(cmd)   type it and press Enter; the promise carries the answer
   Both load the terminal first if it is not there, so an agent that
   knows only this name can start from a cold page. */
Room.laptop = {
  pose: function (on) { turnTo(on === undefined ? true : !!on); return S.cam.lapWant > 0.5; },
  type: function (text) {
    return Room.terminal(true).then(function (T) { return T ? T.type(text) : null; });
  },
  run: function (cmd) {
    return Room.terminal(true).then(function (T) { return T ? T.run(cmd) : null; });
  }
};

/* =================================================================
   BOOT
   ================================================================= */
function readJSON(id) { var n = document.getElementById(id); try { return n ? JSON.parse(n.textContent) : null; } catch (e) { return null; } }
function readText(id) { var n = document.getElementById(id); return n ? n.textContent : 'null'; }

/* =================================================================
   THE STUB SHEET (R4)

   The paper's own shape is arithmetic on the constants in field.js —
   the neat lines, the section strip, the foot — and owes the record
   nothing. So the desk, the sheet, its furniture, the books and the
   camera can all be laid out before a single day of the record has
   been read, which is what makes a first frame possible at all.

   `stub: true` is what draw() reads to know the window is not open yet.
   The numbers here are the same expressions field.js returns, so when
   the real field lands the layout does not move by a pixel and the
   furniture rastered against this one does not need re-cutting.
   ================================================================= */
function stubField() {
  var pw = C.cw * C.weeks;
  return {
    stub: true, sheet: F.sheetBox(),
    plate: { x: C.px, y: C.py, w: pw, h: C.ch * (7 + C.EDGE_ROWS * 2) },
    /* `none` is the mould's own "nothing is surveyed" mode, so no shader
       that reads the front can draw a conjecture it has no record of */
    front: { none: true, all: false, west: C.px, east: C.px + pw, row: 0, turned: false,
             a0: 0, e0: 0, e1: 0, fade: 1, c0: 0, c1: 0, pA: 0, pB: 0 },
    COAST: 0, maxHeight: 0
  };
}

/* =================================================================
   BOOT — THE FIRST PICTURE IN TWO FRAMES (R4)

   Tommy, on the load: "you need the desk, paper, etc. and the map can
   draw in". The order here is the answer, and it is an order of WAITS,
   not of features:

     the worker    first thing in the function, before the context, so
                   the 255 ms of field arithmetic starts at ~0 and not
                   at ~150 (the context is the single longest thing the
                   main thread does and the worker used to sit behind it)
     the furniture asked for next — a fetch and an off-thread raster, so
                   it lands while the context is still being made; frame
                   1 waits for it rather than flash bare paper
     the fonts     asked for in the same breath and waited for by NOBODY:
                   the first frame has no lettering in it, and the atlas
                   is cut from the face only once the face is in
     the context   and the three programs frame 1 actually draws with
     FRAME 1       the desk, the sheet, its furniture, the books, and the
                   DOM the engine lays becomes visible in this frame
     the compiles  the other eleven programs, issued now, collected when
                   the driver says it is done — they overlap the worker
     FRAME 2       the field lands: the ground, the sea, the marks, the
                   card. If it was already there at frame 1, frame 1 IS
                   the whole picture and there is no second frame.

   Nothing is drawn and thrown away: frame 1 is a true picture of a sheet
   whose window has not been opened yet, and frame 2 opens it.
   ================================================================= */
function boot() {
  var tEnter = performance.now();
  var host = document.getElementById('canvas');
  if (!host) return;
  /* the head script marked a touch-first device: the plain page stands, no engine */
  if (/\bnogl\b/.test(document.documentElement.className)) return;
  var tBoot = S.tBoot = performance.now();
  var T = S.boot = { script: +T0.toFixed(1), enter: +tEnter.toFixed(1) };
  bootLog('boot entered');
  function at() { return +(performance.now() - tBoot).toFixed(1); }

  /* ---- 1. THE WORKER, FIRST --------------------------------------
     THE RECORD GOES OVER AS TEXT. Parsed, it is five years of objects and
     the structured clone to the worker is main-thread work for no reason;
     as text it is one string and the worker does the parsing. */
  var raw = {
    record: readText('survey-data'), history: readText('survey-history'),
    events: readText('survey-events'), horizons: readText('survey-horizons'),
    topo: readText('survey-toponymy'), posts: readText('survey-posts'),
    /* R5 (b): the work event's line — name (default) | track | rule */
    eventsMode: Q.get('events') || 'name',
    /* R9 (b): ?seams=old puts the ruled column seam back, for the pair */
    seams: Q.get('seams') || '',
    /* R26: ?ladder=demo multiplies every day's count by three, for the
       capture that shows the ladder's top rungs. Nothing else reads it. */
    ladder: Q.get('ladder') || '',
    /* R35: ?margin=0 — the field without its continuation, for the pair */
    margin: Q.get('margin') || '',
    /* R39: ?seawest=0 — the west sea as review 6 found it (the north/south
       sink carried on past January 2022), for the pair */
    seawest: Q.get('seawest') || '',
    /* LX1: ?upx=N — the lattice upsampled N times across (default 2; 1 = as built) */
    upx: Q.get('upx') || '',
    /* the scale the sheet will be laid on the desk at. The lettering
       reserves ground in map units at a fixed apparent size, so the
       placement needs the fit before layout() has run — it is the same
       arithmetic layout() does, off the host's measured shape. */
    k: (function () {
      var sb = F.sheetBox();
      var cw = host.clientWidth || 1440, ch = host.clientHeight || 900;
      var m = clamp(Math.round(Math.min(cw, ch) * 0.045), 48, 120);
      var mS = Math.max(m, BOOK_SHOW + 24);
      return Math.min(Math.max(80, cw - m * 2) / sb.w, Math.max(80, ch - m - mS) / sb.h);
    })()
  };
  /* X4: the boot's own `raw` is what the worker eats, and `Room.load` has
     to hand the worker the SAME bag with one record swapped out of it. */
  S.raw = raw;
  var input = null;
  function parsed() {
    if (input) return input;
    function J(t) { try { return JSON.parse(t); } catch (e) { return null; } }
    var evd = J(raw.events) || {}, hzd = J(raw.horizons) || {};
    input = { record: J(raw.record), history: J(raw.history) || {},
              events: evd.events || {}, horizons: hzd.horizons || [],
              bankHolidays: evd.bankHolidays || {}, topo: J(raw.topo) || {}, k: raw.k,
              posts: J(raw.posts) || [], eventsMode: raw.eventsMode,
              seams: raw.seams, ladder: raw.ladder, margin: raw.margin,
              seawest: raw.seawest, upx: raw.upx };
    return input;
  }
  var FLD = null;                       /* the field, the instant it lands */
  var fieldReady = new Promise(function (res) {
    try {
      /* the thread was started by base.njk's inline script the moment the
         record's JSON was in the page, so it has run its importScripts by
         now and the record goes into a worker that is already waiting;
         `new Worker` here is only for a page without that script */
      var w = window.__fieldWorker || new Worker(window.workerUrl ? workerUrl() : '/js/room/worker.js');
      window.__fieldWorker = null;
      if (w.__failed) throw new Error('the field worker failed before boot');
      w.onmessage = function (e) {
        /* the worker's first word is that it has the record and is building: the gap
           between the post and this line is the thread waiting for a core */
        if (e.data && e.data.building) { T.building = at(); bootLog('field worker building', T.building + ' ms after boot'); return; }
        FLD = e.data; T.worker = at(); bootLog('field worker answered', T.worker + ' ms after boot'); issueSecondWave(); res(e.data); w.terminate();
      };
      w.onerror = function () { FLD = window.RoomField.build(parsed()); res(FLD); };
      w.postMessage(raw); bootLog('field worker posted');
    } catch (e) { FLD = window.RoomField.build(parsed()); res(FLD); }
  });
  T.post = at();

  /* ---- 2. THE CANVAS, THE STUB SHEET AND THE LAYOUT ---------------
     All of it arithmetic, none of it the record's, and the furniture
     needs the fit before it can be rastered at the right size. */
  /* ==== R4: THE CANVAS IS NOT IN THE PAGE UNTIL IT HAS THE PICTURE IN IT.

     A webgl2 context taken with `alpha: false` has an OPAQUE BLACK backing
     store until something is drawn into it, and the element is the top
     layer of the desk — so from the moment boot appended it until the
     first draw the reader was shown a black rectangle over the oak.
     Measured on a cold load (R4-before-cast-0300.jpg): black from ~230 ms
     to ~700 ms, with the books' labels floating on it.

     WebGL does not need the element to be in the document, so it is made,
     measured, laid out and DRAWN detached, and appended in the same task
     as the first frame. `preserveDrawingBuffer` keeps that frame on the
     glass until the compositor takes it. Before it lands the page is the
     body's own oak (styles.css, html.room body) and nothing else. ==== */
  var cv = document.createElement('canvas');
  cv.className = 'room-canvas'; cv.id = 'room-canvas';
  S.cv = cv;
  S.fld = stubField();
  /* R8: THE EPOCH BEFORE THE RECORD. The month rulers are drawn from
     the view and need to know which Sunday column 0 is — which is the
     home year and nothing else of the record. Parsing 100 KB of JSON on
     the main thread to learn one integer is exactly the long task R4
     took out, so the year is READ OUT OF THE TEXT and the epoch built
     from it (field.js's own expression), and the ruler is in frame 1. */
  S.fld.epochMs = (function (txt) {
    var m = /"year"\s*:\s*(\d{4})/.exec(txt || '');
    var y = m ? +m[1] : new Date().getUTCFullYear();
    var c = new Date(Date.UTC(y, 0, 1));
    c.setUTCDate(c.getUTCDate() - (C.WEEK_START === 'sunday' ? c.getUTCDay() : (c.getUTCDay() + 6) % 7));
    c.setUTCHours(0, 0, 0, 0);
    return c.getTime();
  })(raw && raw.record);
  S.rise = 1;
  layout();
  S.view = { x: S.fld.plate.x, y: S.fld.plate.y, w: S.fld.plate.w, h: 0 };
  clampView();
  applyHash();
  var vq = Q.get('view');
  if (vq) {
    /* THE SAME VIEW, TO THE FLOAT, FOR A CAPTURE BESIDE 8102 (E1). */
    var vp0 = vq.split(',').map(Number);
    S.view.w = vp0[2]; S.view.h = vp0.length > 3 ? vp0[3] : vp0[2] * viewRatio();
    S.view.x = vp0[0] - S.view.w / 2; S.view.y = vp0[1] - S.view.h / 2;
  }
  if (Q.get('tilt')) S.tilt = clamp(+Q.get('tilt') * DEG, 0, tiltCap());
  if (Q.get('bearing')) S.yaw = +Q.get('bearing') * DEG;

  /* ---- 3. THE FURNITURE, ASKED FOR NOW ----------------------------
     It used to be asked for AFTER the field came back, which put a 72 KB
     fetch and a ~55 ms raster in a straight line behind 400 ms that had
     nothing to do with it. Both are off the main thread; both now run
     under the worker. */
  var fw = Math.round(S.fld.sheet.w * S.layout.k * furnScale());   /* AU9 */
  var fh = Math.round(S.fld.sheet.h * S.layout.k * furnScale());
  S.furnPx = [fw, fh];
  S.tFurn0 = performance.now();
  var furnBmp = furniture(fw, fh).then(function (bmp) {
    T.furn = at(); S.furnMs = +(performance.now() - S.tFurn0).toFixed(1);
    return bmp;
  }).catch(function (e) { console.warn('furniture', e); return null; });

  /* ---- 4. THE FACE, ASKED FOR AND NOT WAITED FOR ------------------
     The glyph atlas is cut from the page's own Sorts Mill Goudy; in the
     room the old DOM sheet is hidden, so nothing else on the page asks
     for the woff2 and the browser would not fetch it until the atlas
     wanted it — and an atlas cut before the face arrives is cut in
     Georgia, for good. E1 asked for the faces here and then made the
     FIRST PICTURE wait for them, which put a font on the critical path
     of a frame with no letter in it. Asked for here, waited for by the
     lettering alone: the names are the last thing inked. */
  var fontsReady = (document.fonts && document.fonts.load)
    ? Promise.all([document.fonts.load('32px "Sorts Mill Goudy LN"'),
                   document.fonts.load('italic 32px "Sorts Mill Goudy LN"')])
        .then(function () { S.fontsIn = true; T.fonts = at(); }).catch(function () { S.fontsIn = true; })
    : Promise.resolve();
  S.fontsReady = fontsReady;

  /* ---- 5. THE CONTEXT --------------------------------------------- */
  var tC = performance.now();
  /* R20: A STENCIL, for the record's water clipped to the record's own
     silhouette (see draw(), step 3b); R25 (2) uses the same stencil for the
     window's outline on the paper when the edge pose is on. DEPTH24_STENCIL8
     is the same 32 bits a WebGL2 depth-only default framebuffer already
     allocates as D24X8, so this asks the driver for no more memory;
     measured on this box, gl.getParameter(STENCIL_BITS) 0 -> 8 and the
     reported GPU memory unchanged. (Merge of R20 and R25: R20 needs it
     always, so it is always on.) */
  var gl = cv.getContext('webgl2', { antialias: false, alpha: false, depth: true, stencil: true, preserveDrawingBuffer: true });
  if (!gl) {
    /* no WebGL2: the plain page (index.njk #plain) stands in for the sheet */
    console.warn('no webgl2');
    document.documentElement.className = document.documentElement.className.replace(/\broom\b/, '') + ' nogl';
    return;
  }
  S.gl = gl;
  trackGL(gl);
  /* ==== CL1 (2026-09-24): AND THE CONTEXT CAN BE TAKEN AWAY. Tommy came
     back to a tab left on a finished voyage and the sheet was a sad face,
     the console full of `voy-spr.vs: null` — every info log is null once
     the context is gone. A GPU reset (a game, the driver, sleep) or the
     GPU process dying LOSES a WebGL context, and a page that does not
     preventDefault the loss is never given one back. Rebuilding every
     program, texture and buffer in place is a second boot; a reload IS
     the boot, and all that matters survives it — the view in the hash,
     the chart, the progress token and the active clock (saved on
     pagehide). So: ask for the context back, and reload when it comes, or
     three seconds after the tab is looked at if it never does. Once a
     minute at most, so a GPU that keeps dying is not reloaded in a loop. */
  var lostT = 0;
  function reviveByReload() {
    if (!S.glLost || S.glRevived) return;
    S.glRevived = true;
    var last = 0;
    try { last = +sessionStorage.getItem('tl-gl-revive') || 0; sessionStorage.setItem('tl-gl-revive', String(Date.now())); } catch (e) { }
    if (Date.now() - last < 60000) return;
    location.reload();
  }
  function reviveLater() {
    if (!S.glLost || document.hidden) return;
    clearTimeout(lostT); lostT = setTimeout(reviveByReload, 3000);
  }
  cv.addEventListener('webglcontextlost', function (e) {
    e.preventDefault();
    S.glLost = true;
    reviveLater();
  });
  cv.addEventListener('webglcontextrestored', reviveByReload);
  document.addEventListener('visibilitychange', reviveLater);
  S.stencil = !!(gl.getContextAttributes() || {}).stencil;
  /* R25: `?clip=0` is the pair — E7's screen-space bounding box, which is
     what the window was clipped to before this item. It is also how the
     clip's own frame cost is measured, because `Room.clip = false` can be
     flipped inside ONE page load and `Room.perf` run either side of it. */
  S.clip = Q.get('clip') !== '0';
  S.floatLinear = !!gl.getExtension('OES_texture_float_linear');
  S.colorFloat = !!gl.getExtension('EXT_color_buffer_float');
  /* ==== LP2: and that is what decides whether the terra/mould chain is
     baked. It has to be known HERE, before the first `shaderSource`,
     because `BAKED` is injected into every source (see zeroDecl) and a
     program compiled one way cannot be re-read the other. RGBA16F is
     renderable with this extension and filterable in WebGL2 core.
     MAX_TEXTURE_SIZE is asked for in the same breath — the bake is twice
     the field's lattice and a small device has to be allowed to cap it.
     Both are read before any compile or upload is queued, so the flush
     they cost is the empty one. ==== */
  BAKE_ON = S.colorFloat;
  S.bakeOn = BAKE_ON;
  S.maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096;
  KHR = gl.getExtension('KHR_parallel_shader_compile');
  S.parallelCompile = !!KHR;
  T.ctx = +(performance.now() - tC).toFixed(1);
  /* ==== LP3 (a): THE GROUND'S LINK IS ISSUED THE MOMENT THE CONTEXT
     EXISTS. Measured on Tommy's card (RTX 3070 Ti, ANGLE D3D11, 2026-09-22):
     the ground is the last program READY waits for, ~3.4 s of Direct3D
     compile with no single feature to cut, and until this line it was
     issued after frame one, at ~1.3 s. With KHR_parallel_shader_compile
     the driver compiles on its own threads from the instant of the link,
     so issuing it here costs the main thread nothing and the poller
     collects it when it is done. Only with the extension: without it the
     first query of any program pays for everything queued (R6's finding),
     and the old order stands. ==== */
  /* ==== FW2 (b): THE OTHER TEN, IN TWO WAVES. Measured on Tommy's Chrome
     (16 threads, 2026-09-22): with all fifteen links in the driver's pool
     at once the field worker is starved — it waited up to 660 ms for a
     core and its 320 ms build ran 0.6-2.7 s, once 6 s — and it is the
     lettering's link that is the critical path to the whole picture
     (1.3 s alone, 1.5-1.9 s in the pool), so holding the whole set until
     the worker had answered freed the worker and cost 0.5-0.9 s of READY.
     So the LONG links go at the context, right after frame one's five
     (section 6) so the pool takes those first: the bake, the two ground
     passes that take a second or more, and the lettering's. The SHORT
     ones — the other two passes, the marks' three and the paper's clip,
     under a second each — wait for the worker's answer, or for frame one
     if the worker is slower than the oak. Both idempotent. */
  function issueFirstWave() {
    if (!KHR || S.issuedWave1) return;
    S.issuedWave1 = true;
    var wasLO = LINK_ONLY; LINK_ONLY = true;
    try {
      /* the bake first — every program after it reads its texture */
      if (BAKE_ON) S.pBake = prog(gl, BAKE_VS, BAKE_FS_SRC || (BAKE_FS_SRC = BAKE_FS()), 'bake');
      issueGround(gl, ['line', 'mat']);
      if (window.RoomLettering && RoomLettering.precompile) RoomLettering.precompile(modH());
    } catch (e) { console.warn('first wave', e); }
    finally { LINK_ONLY = wasLO; }
    T.wave1 = at();
  }
  function issueSecondWave() {
    if (!KHR || S.issuedWave2) return;
    S.issuedWave2 = true;
    issueFirstWave();
    var wasLO = LINK_ONLY; LINK_ONLY = true;
    try {
      issueGround(gl, ['feat', 'post']);
      if (window.RoomMarks && RoomMarks.precompile) RoomMarks.precompile(modH());
      prog(gl, CLIP_PAPER_VS, CLIP_FS, 'clip-paper');   /* memoised by name; plateClip finishes it */
      /* and the boat's two, which blocked the frame at the edge lamp for 400 ms */
      if (window.RoomVoyage && RoomVoyage.precompile) RoomVoyage.precompile(modH());
    } catch (e) { console.warn('second wave', e); }
    finally { LINK_ONLY = wasLO; }
    T.wave2 = at();
    bootLog('second wave issued', Object.keys(PROGS).length + ' programs linking');
  }

  /* ---- 6. THE PROGRAMS FRAME 1 DRAWS WITH ------------------------
     and no others. The plate, the water, the marks and the lettering are
     not in this frame and their compiles are not in front of it (R4 third
     pass, SHADER ORDER). R12 added a fourth, `day-card`: the card's stock
     is furniture, placed from the layout alone, so it belongs with the
     desk and the books. Its type still waits for the field. */
  var tP = performance.now();
  /* ==== FW2: FRAME ONE'S PROGRAMS ARE ISSUED, NOT WAITED FOR. Measured
     on Tommy's Chrome (2026-09-22, Direct3D): `prog()` here blocked on
     each link in turn — the oak's alone 0.9-1.2 s, all five 1.2 s — inside
     boot's own task, so the field worker's answer (built by then) and the
     window it opens sat behind the compile, and the first picture was the
     desk alone with the map 0.9 s after it. With the extension the five
     are linked in parallel and collected by `whenFrameOne` below, while
     the furniture is rastered and the window opened beside them; the
     first draw then finds every program it wants finished and is the
     whole picture. R12 measured the opposite on swiftshader, which has no
     parallel compile: without the extension the blocking order stands. */
  var wasF1 = LINK_ONLY; F1_ISSUE = true; if (KHR) LINK_ONLY = true;
  try {
    /* LD3: `linked` collects a program the driver has finished without
       waiting for one it has not; `khr` says whether it can */
    if (window.RoomObjects) RoomObjects.init(gl, S, { prog: prog, FLAT_VS: FLAT_VS, khr: !!KHR,
      linked: function (p) { if (p.u) return true; if (!KHR || !gl.getProgramParameter(p, KHR.COMPLETION_STATUS_KHR)) return false; progFinish(gl, p); return true; } });
    /* Fall back to the engine's flat desk and paper only if the object
       module could not provide its programs. */
    if (!(window.RoomObjects && RoomObjects.pDesk)) S.pDesk = prog(gl, FLAT_VS, DESK_FS, 'desk');
    if (!(window.RoomObjects && RoomObjects.pSheet)) S.pSheet = prog(gl, FLAT_VS, SHEET_FS, 'sheet');
    /* R4 OPEN 2, closed: the card's paper. `?card=0` off, `?day=0` off.
       Compiled HERE, with the other three — 11-14 ms, which is what the
       card in frame 1 costs. R12 tried to hide it: linked FIRST and
       LINK-ONLY, so the driver could build it beside the desk's, the
       sheet's and the book's and the card's own draw could collect it.
       It was worse — frame 1 157 -> 275 ms warm, `day-card` 120-530 ms to
       collect instead of 6-14. On this driver the compiles serialise
       whatever the order, and KHR only helps a program nothing waits on. */
    if (DAY_ON && window.RoomDay && RoomDay.card)
      S.card = RoomDay.card({ gl: gl, S: S, prog: prog });
    var qb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, qb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    S.vaoQuad = gl.createVertexArray(); gl.bindVertexArray(S.vaoQuad);
    gl.bindBuffer(gl.ARRAY_BUFFER, qb); gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    S.texField = gl.createTexture();
    S.texFurn = gl.createTexture();
    /* R8: the ruler is FURNITURE and the furniture is in frame 1, so its
       program is issued with the other three rather than after the field. */
    buildRuler();
  } finally { LINK_ONLY = wasF1; F1_ISSUE = false; }
  issueFirstWave();
  T.programs = +(performance.now() - tP).toFixed(1);
  T.sync = +(performance.now() - tEnter).toFixed(1);

  /* FW2: frame one draws when every program it wants has been collected
     (12 ms turns on COMPLETION_STATUS, nothing blocks), and the field is
     landed the moment it arrives, so the first picture is the whole one
     wherever the worker beats the oak. Eight seconds is the backstop for
     a link that never completes; the draw then blocks as it always did. */
  function linked(name) {
    var p = PROGS[name]; if (!p) return true;
    if (p.u) return true;
    if (!gl.getProgramParameter(p, KHR.COMPLETION_STATUS_KHR)) return false;
    progFinish(gl, p); return true;
  }
  /* the programs the window's opening draws with — the lettering's and the
     marks' — collected without a wait; the cut blocked the thread 0.5-0.8 s
     on the letter's link otherwise (measured). Eight seconds is the backstop. */
  function windowReady() { return linked('letter') && linked('mark-fam') && linked('mark-bld') && linked('mark-spr'); }
  function whenWindow(cb) {
    if (!KHR) return cb();
    var t0 = performance.now();
    (function tick() {
      if (windowReady() || performance.now() - t0 > 8000) return cb();
      setTimeout(tick, 12);
    })();
  }
  function whenFrameOne(cb) {
    if (!KHR) return cb();
    var t0 = performance.now();
    F1_WAIT = true;
    (function tick() {
      var left = 0, k, p;
      for (k in PROGS) {
        p = PROGS[k]; if (!p.__f1 || p.u) continue;
        if (gl.getProgramParameter(p, KHR.COMPLETION_STATUS_KHR)) progFinish(gl, p); else left++;
      }
      /* the window opens here only once the programs its opening draws
         with — the lettering's and the marks' — can be collected without
         a wait; otherwise the cut would block the thread 0.5-0.8 s on the
         letter's link (measured) while the oak sat finished. If they are
         not ready when the five are, frame one is the desk and the map
         follows it, as before. */
      if (FLD && S.fld.stub && windowReady()) { openWindow(FLD); S.rise = 1; }
      if (!left) { F1_WAIT = false; return cb(); }
      if (performance.now() - t0 > 8000) { bootLog('frame one on the backstop', left + ' programs uncollected'); F1_WAIT = false; return cb(); }
      setTimeout(tick, 12);
    })();
  }

  /* ---- 7. FRAME 1 -------------------------------------------------- */
  furnBmp.then(function (bmp) { whenFrameOne(function () {
    issueSecondWave();                 /* the worker was slower than the oak: issue now */
    if (bmp) furnUpload(bmp);
    /* THE WHOLE PICTURE IN ONE FRAME WHERE IT CAN BE. On a warm cache or
       a fast machine the field is already here, and then there is no
       second frame and no reveal: one draw, everything in it, exactly as
       before this item. */
    if (FLD && S.fld.stub && windowReady()) { openWindow(FLD); S.rise = 1; }
    /* R56: AND FRAME 1 IS LIT AT ITS OWN BEARING. `?bearing=` is read
       after applyHash and openWindow only runs here on a warm cache, so
       on the cold path the FIRST picture of a turned link used to be
       drawn with the sky still at north — the desk's light then jumped a
       whole bearing on frame 2. One idempotent call, before the draw. */
    if (window.Sky) Sky.setView(S.yaw / DEG);
    var t0 = performance.now();
    draw();
    S.firstDrawMs = +(performance.now() - t0).toFixed(2);
    host.appendChild(cv);            /* the picture and the element together */
    T.frame1 = at();
    S.frame1Ms = T.frame1;
    bootLog('frame 1 painted', 'draw ' + S.firstDrawMs + ' ms');
    S.drawsAtFrame1 = S.drawn;
    live = true;
    wire();
    if (window.Sky) { Sky.setView(S.yaw / DEG); Sky.onChange(function () { invalidate(); }); }
    /* THE DOM COMES WITH THE PICTURE, NOT BEFORE IT. Everything the
       engine lays over the canvas — the books' labels, and with frame 2
       the card's typing — is `visibility: hidden` until the class this
       adds says there is something under it (styles.css). Never
       display:none: that would relayout the moment the class landed. */
    document.documentElement.classList.add('room-ready');
    if (window.RoomBooks) RoomBooks.init({ S: S, posts: readJSON('survey-posts') || [] });
    if (window.RoomPin) RoomPin.init(S);
    if (window.RoomIndex) RoomIndex.init(S);
    if (window.RoomHandle) RoomHandle.init(S);   /* X4 */
    if (window.RoomObjects) RoomObjects.place(S);
    /* P3 (P2's OPEN 1): R6's note stood here — "the oak is compiled before
       the first picture" — over a `RoomObjects.buildWood()` that has been
       a no-op for as long as `O.init` has called `buildWood` itself. It
       does: inside boot's own synchronous task, and P2's stack probe found
       it there, 37.7 ms at objects.js:4199 in task 1, in front of frame 1.
       R6's sentence is true and its call was not: this one always returned
       false and its `draw()` never ran. The truth now lives over the build
       in objects.js `O.init`, where the compile is. */

    /* LZ1: AND THE MACHINE ON THE DESK IS ASKED FOR HERE, not by a tag in
       the head. The first picture is on the glass; the fetch that follows
       cannot be in flight against anything it was waiting for.
       LD4 (2026-10-02): and not here either — its two links were in the
       driver's pool beside the ground's, on the way to READY, for a
       machine outside the fit's frame. It is asked for in `lateCompile`,
       after READY; a demand (`turnTo`) still asks at once. */
    /* TB1: and a turn asked for before this line could take it (an
       agent's `Room.laptop.pose` before the first picture) is put down now */
    if (lapPending) { lapPending = 0; turnTo(true); }

    startRuling();                     /* LD6: the empty box, ruled while it waits */
    /* ---- 8. THE OTHER ELEVEN PROGRAMS, WHILE THE WORKER WORKS ---- */
    precompile();
    pollLinks();
    if (S.fld.stub) {
      fieldReady.then(function (f) {
        if (!S.fld.stub) return;       /* opened by whenFrameOne after all */
        whenWindow(function () { openWindow(f); startReveal(); });
      });
      return;
    }
    /* FW2: the window opened beside frame one's compile, so the first
       picture was the whole one and there is nothing to reveal. READY
       still waits for every ground pass to have drawn. */
    readyWhenWhole();
  }); });
}

/* ==== LD6 (2026-10-02): THE EMPTY BOX IS RULED WHILE THE GROUND COMPILES.
   Tommy: "Agree about the loading screen made of objects." A load whose
   land is drawn within RULE_AFTER of frame 1 (any warm cache, any fast
   machine) never starts this and sees no change. Otherwise the plate's
   week and month lines are inked west to east over RULE_MS (ruler.js, the
   program frame 1 already has), and they fade over RULE_FADE once the
   ground's post pass has put the land on the glass over them. ==== */
var RULE_AFTER = 300, RULE_MS = 1400, RULE_FADE = 300;
function startRuling() {
  if (Q.get('rule') === '0' || S.ruling) return;
  setTimeout(function () {
    if (S.gpDrawn && S.gpDrawn.post) return;           /* the land beat it: nothing to rule */
    var t0 = performance.now(), landAt = 0;
    S.ruling = { p: 0, a: 1, t0: +(t0 - S.tBoot).toFixed(1) };
    bootLog('ruling the empty box', 'the ground is still compiling');
    (function step() {
      var now = performance.now(), e = Math.min(1, (now - t0) / RULE_MS);
      S.ruling.p = 1 - (1 - e) * (1 - e);
      if (!landAt && S.gpDrawn && S.gpDrawn.post) landAt = now;
      S.ruling.a = landAt ? Math.max(0, 1 - (now - landAt) / RULE_FADE) : 1;
      if (S.ruler && S.ruler.invalidate) S.ruler.invalidate();
      frameDraw();
      if (S.ruling.a > 0) requestAnimationFrame(step);
      else { S.rulingMs = +(now - S.tBoot).toFixed(1); S.ruling = null; if (S.ruler) S.ruler.invalidate(); draw(); }
    })();
  }, RULE_AFTER);
}

/* the helper bag every field-reading module is attached with */
function modH() {
  return { gl: S.gl, S: S, prog: prog, setCommon: setCommon, EXAG: EXAG,
           /* a deferred program's uniforms, on demand: the first caller
              that draws before the poller has collected it pays here
              instead of throwing (a tilted hash on load reached
              RoomWater.drawSky during the reveal with e7sky unfinished
              and every frame after it aborted — a blank plate) */
           finish: function (p) { return progFinish(S.gl, p); },
           fitWidth: fitWidth, smoothstep: F.smoothstep, LEV_B: LEV_B, LEV_C: LEV_C,
           reliefOf: reliefOf, invalidate: invalidate, queueHash: queueHash,
           upToCamera: LET_UP, only: Q.get('marks') || '',
           chunks: { FIELD: FIELD_GLSL, FRONT: FRONT_GLSL, XFORM: XFORM_GLSL } };
}

/* ==== LZ1: THE MACHINE ON THE DESK, FETCHED (C0's OPEN 2) =============
   desk-laptop.js is 21 KB gz — the two programs, the UK keyboard, the
   legend atlas, the mesh and the draw — for a machine that stands outside
   the fit's frame. As a `<script defer>` in base.njk it was downloaded,
   parsed and run before engine.js booted, on the path to a first picture
   it cannot be in. It is fetched now, with `needFurniture`'s own
   discipline (place.js R71 f): FETCHED, NOT TAGGED, because a `<script
   src>` for a file that is not there is a 404 AND a console error, and a
   fetch is silent — a 200 is run, anything else is simply not a machine.
   What it needs to exist before it lands is `RoomObjects.laptop(S)`, and
   that is the ANCHOR, which is in objects.js and is a desk fact.

   TWO GATES, WHICHEVER COMES FIRST:
     · the idle slot after the first picture (boot's own `.then`, beside
       `precompile`), which is where it lands on an ordinary page;
     · DEMAND — `turnTo()`, which every way of asking for the machine goes
       through: a `,l1` link or a hash edited in the bar (`Room.terminal`),
       the terminal's own `laptop` word (terminal.js -> `Room.terminal`),
       `Room.laptop.pose` for an agent, and a hand on the lid
       (`x2Press` -> `Room.terminal`). No file but this one knows.
   `want` is that demand: it says the reader is on their way, so the piece
   takes its idle slot the moment the text arrives instead of waiting.
   ==================================================================== */
var lapAsked = false, lapSrc = null, lapWanted = false;
function needLaptop(want) {
  if (want) lapWanted = true;
  /* the file is already in the page: nothing to fetch, and its `late` has
     been run by `O.precompile` in the burst. Nothing on the shipped page
     puts it there; LZ1's A/B arm did (`?lz=tag`, a `document.write` in
     base.njk, removed with the measurement), and a page that decides to
     carry the machine in its head again would. */
  if (window.RoomObjects && RoomObjects.lapFile) { if (lapWanted) lapFinish(); return; }
  if (lapAsked) { lapLand(); return; }
  lapAsked = true;
  window.__lapAsk = +performance.now().toFixed(1);
  fetch((window.assetUrl ? assetUrl('/js/room/desk-laptop.js') : '/js/room/desk-laptop.js'), { credentials: 'same-origin' }).then(function (res) {
    if (!res.ok) return null;
    var ct = res.headers.get('content-type') || '';
    if (ct.indexOf('javascript') < 0 && ct.indexOf('ecmascript') < 0) return null;
    return res.text();
  }).then(function (src) { lapSrc = src || null; })
    .catch(function () { /* not served: the desk is drawn without the machine */ })
    .then(function () { lapLand(); });
}
/* THE EVALUATION, AND ITS COMPILE UNDER THE HOUSE'S OWN DISCIPLINE.
   Registering calls `O.piece`, which runs the piece's `late` hook on the
   spot when the idle slot has already been (objects.js, LZ1) — so the two
   programs are asked for inside this call and `LINK_ONLY` has to be up
   around the WHOLE of it, the append included.

   WHY, measured (P2 section 1, P3 part 1): on ANGLE/d3d12 every
   synchronous GL query flushes the command stream and waits for the GPU
   process. Collected here and now, the laptop's two programs cost
   `lapProgMs` **1,109–1,357 ms** across five cold loads and put TWO new
   long tasks in the load. Issued and left for `pollLinks`, they cost the
   issue alone and are collected on a turn the driver says is ready.
   `pollLinks` has stopped by now — it stops rescheduling the moment
   nothing is pending — so it is started again for them. */
function lapLand() {
  if (lapSrc) {
    var src = lapSrc; lapSrc = null;
    var t = document.createElement('script');
    t.textContent = src + '\n//# sourceURL=/js/room/desk-laptop.js';
    var was = LINK_ONLY; LINK_ONLY = true;
    try {
      document.head.appendChild(t);
      /* and for the case where the text beat the first picture: the piece
         registered before `O.precompile` had ever run, so its `late` hook
         has not been called and the idle slot must call it. */
      if (window.RoomObjects && RoomObjects.precompile && S.grid) RoomObjects.precompile(modH());
    } catch (e) { console.warn('laptop late', e); }
    finally { LINK_ONLY = was; }
    window.__lapIn = +performance.now().toFixed(1);
    pollLinks();
    invalidate();
  }
  if (lapWanted) lapFinish();
}
/* AND WHEN A READER IS ACTUALLY ON THEIR WAY TO IT, the programs are
   collected at once rather than on the poller's next turn: `turnTo` has
   900 ms of camera to hide the stall in, and this is a gesture, not the
   load. Idempotent — `progFinish` returns on a program that has its
   uniforms. */
function lapFinish() {
  var O = window.RoomObjects;
  if (!O || !O.pLap || !O.pLapG || !S.gl) return;
  progFinish(S.gl, O.pLap); progFinish(S.gl, O.pLapG);
  invalidate();
}

/* ISSUE EVERY REMAINING COMPILE, COLLECT NONE OF THEM. Each module hands
   back the same programs its own attach will ask for, so this is not a
   second set: PROGS memoises by name and the attach finds them made. */
function precompile() {
  var gl = S.gl, H = modH(), t0 = performance.now();
  gl.bindVertexArray(null);          /* see the note at the end of draw() */
  LINK_ONLY = true;
  try {
    /* LP2 first: every other program below reads its texture, so its link
       wants to be the one already in the driver's hands. */
    if (BAKE_ON) S.pBake = prog(gl, BAKE_VS, BAKE_FS_SRC || (BAKE_FS_SRC = BAKE_FS()), 'bake');
    issueGround(gl);
    /* R7: the paper's own second program, which frame 1 did not want */
    if (window.RoomObjects && RoomObjects.precompile) RoomObjects.precompile(H);
    if (window.RoomWater && RoomWater.precompile) RoomWater.precompile(gl, S, H);
    if (window.RoomMarks && RoomMarks.precompile) RoomMarks.precompile(H);
    if (window.RoomLettering && RoomLettering.precompile) RoomLettering.precompile(H);
    if (window.RoomRuler && RoomRuler.precompile) RoomRuler.precompile(H);
    if (window.RoomDay && RoomDay.precompile) RoomDay.precompile(H);
  } catch (e) { console.warn('precompile', e); }
  LINK_ONLY = false;
  /* the grid the ground is drawn on: 98,000 vertices of arithmetic that
     the first frame has no use for either */
  var tG = performance.now();
  S.gm = S.gm || {};
  S.grid = gridBuffer(gl, GX, GY);
  S.boot.grid = +(performance.now() - tG).toFixed(1);
  S.vaoGrid = gl.createVertexArray(); gl.bindVertexArray(S.vaoGrid);
  gl.bindBuffer(gl.ARRAY_BUFFER, S.grid.vb); gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, S.grid.ib);
  gl.bindVertexArray(null);
  S.boot.issued = +(performance.now() - t0).toFixed(1);
  buildFineGrid();
}

/* R13 (a): the four-times mesh, asked for in an idle slot. It is 393,216
   vertices and 2.35 M indices of pure arithmetic that nothing on the way
   to the first picture wants, and it is never bound until a view asks for
   a cell four times the field's lattice — the clamp's own attitude at a
   zoom wider than the fit. Built once; `?fine=0` never builds it.

   R54: and the three bigger ones are NOT asked for here. They answer
   sixty-five poses out of three hundred and seventy-eight, every one of
   them at the widest zoom the clamp allows, and the biggest of them is
   25.2 MB of buffer. So they are built THE FIRST TIME A VIEW ASKS FOR ONE
   (snapRect's `wantMesh`), which means a reader who never opens the view
   wider than the fit pays nothing at all for them: the cold load is
   lighter than R44's, not heavier. Until the mesh arrives the finest one
   that IS there answers, which is R44's own picture. */
/* R54: AND NO MESH IS BUILT IN ONE PIECE. Measured on this box, the
   whole-mesh builder R44 left cost 117.7 ms for 2048 x 384, 78.6 ms for
   1024 x 768 and 250.7 ms for 2048 x 768 — and R13's own 1024 x 384 came
   in at 243.8 ms on one of these runs against the 6.9 ms R44 reported, so
   the long task was already there and only sometimes visible. A reader
   reaches these views by DRAGGING, so the freeze lands in the middle of
   the gesture. So the buffers are allocated in a slot each and then
   written a band of rows at a time, one idle slot per band: `?band=N` is
   the indices per band (default a million). Measured after: no slot over
   13 ms on any mesh but the largest, whose worst slot is 41 ms, and the
   whole of 2048 x 768 is 53 ms over ten slots against 250.7 in one. The mesh is not registered — and so cannot be drawn —
   until the last band is in, so a half-built buffer is never bound.
   The stages run 0 (size the vertices), 4 (size the indices), 1 (write
   the vertices), 2 (write the indices), 3 (done). */
var BAND = +(Q.get('band') || 1e6);
function meshBuild(gl, nx, ny, done) {
  var spent = 0, worst = 0, slots = 0;
  var vb = null, ib = null;
  var vRows = Math.max(1, Math.floor(BAND / ((nx + 1) * 2)));
  /* R54: AND THE BIG MESHES ARE STRIPS. A row of quads drawn as
     TRIANGLES is nx x 6 indices; as ONE TRIANGLE_STRIP it is
     (nx + 1) x 2 and a restart — a third of them — and at 2048 x 768 that
     is 12.6 MB of index instead of 37.7 MB, which is the whole of what
     made this mesh dear to build. The strip is [a0, c0, a1, c1, ...] down
     the row (a = this row, c = the next), and GL's own winding rule for
     an odd triangle in a strip makes that THE SAME TWO TRIANGLES IN THE
     SAME ORDER as gridBuffer's (a,c,b) then (b,c,d) — the same vertices,
     the same anti-diagonal, the same order under the ground's blend. So
     it is not a different surface, only a shorter way of asking for it. */
  var iRow = (nx + 1) * 2 + 1;
  var iRows = Math.max(1, Math.floor(BAND / iRow));
  var vBand = null, iBand = null, vy = 0, iy = 0, stage = 0;
  function work() {
    var t = performance.now(), k, x, y;
    if (stage === 0) {
      /* the buffers are SIZED and nothing is written: a bufferData with a
         byte count is the driver's allocation, not a copy — but the index
         allocation is 37.7 MB at 2048 x 768 and the spec makes it zero
         it, so it gets a slot of its own. */
      vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
      gl.bufferData(gl.ARRAY_BUFFER, (nx + 1) * (ny + 1) * 8, gl.STATIC_DRAW);
      vBand = new Float32Array(Math.min(vRows, ny + 1) * (nx + 1) * 2);
      stage = 4;
    } else if (stage === 4) {
      ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, ny * iRow * 4, gl.STATIC_DRAW);
      iBand = new Uint32Array(Math.min(iRows, ny) * iRow);
      stage = 1;
    } else if (stage === 1) {
      var n = Math.min(vRows, ny + 1 - vy); k = 0;
      for (y = vy; y < vy + n; y++) for (x = 0; x <= nx; x++) { vBand[k++] = x / nx; vBand[k++] = y / ny; }
      gl.bindBuffer(gl.ARRAY_BUFFER, vb);
      gl.bufferSubData(gl.ARRAY_BUFFER, vy * (nx + 1) * 8, vBand, 0, n * (nx + 1) * 2);
      vy += n; if (vy > ny) stage = 2;
    } else {
      var m = Math.min(iRows, ny - iy); k = 0;
      for (y = iy; y < iy + m; y++) {
        for (x = 0; x <= nx; x++) { var a1 = y * (nx + 1) + x; iBand[k++] = a1; iBand[k++] = a1 + nx + 1; }
        iBand[k++] = 0xFFFFFFFF;
      }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
      gl.bufferSubData(gl.ELEMENT_ARRAY_BUFFER, iy * iRow * 4, iBand, 0, m * iRow);
      iy += m; if (iy >= ny) stage = 3;
    }
    var el = performance.now() - t; spent += el; slots++;
    if (el > worst) worst = el;
    if (stage !== 3) { idleSlot(work); return; }
    var G = { vb: vb, ib: ib, n: ny * iRow, nx: nx, ny: ny,
              prim: gl.TRIANGLE_STRIP,
              bytes: (nx + 1) * (ny + 1) * 8 + ny * iRow * 4,
              ms: +spent.toFixed(1), worst: +worst.toFixed(1), slots: slots };
    G.vao = gl.createVertexArray(); gl.bindVertexArray(G.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, G.vb); gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, G.ib);
    gl.bindVertexArray(null);
    (S.gm || (S.gm = {}))[nx + 'x' + ny] = G;
    S.boot['grid' + nx + 'x' + ny] = G.ms;
    S.boot['worst' + nx + 'x' + ny] = G.worst;
    S.boot['slots' + nx + 'x' + ny] = G.slots;
    S.boot['bytes' + nx + 'x' + ny] = G.bytes;
    done(G);
    invalidate();
  }
  work();
}
function idleSlot(fn) {
  var idle = window.requestIdleCallback ||
             function (f) { return setTimeout(function () { f(); }, 1200); };
  idle(fn, { timeout: 4000 });
}
/* R54: one idle chain per mesh, at most one in flight per key. */
var meshPend = {};
function askMesh(nx, ny, done) {
  var key = nx + 'x' + ny;
  if (meshPend[key] || (S.gm && S.gm[key]) || !S.gl) return;
  meshPend[key] = 1;
  idleSlot(function () {
    var gl = S.gl; if (!gl || (S.gm && S.gm[key])) return;
    meshBuild(gl, nx, ny, done || function () {});
  });
}
function wantMesh(kx, ky) { askMesh(GX << kx, GY << ky); }
function buildFineGrid() {
  if (!FINE_ON || !S.gl) return;
  /* R44 (a): the WIDE mesh first. It is half the arithmetic of the fine
     one (196,608 vertices against 393,216) and it is the one nearly every
     turned bearing asks for, so it is the one worth having soonest. */
  askMesh(GX * 2, GY, function (G) { S.gridWide = G; S.vaoWide = G.vao; S.boot.gridWide = G.ms; });
  askMesh(GX, GY * 2, function (G) { S.gridTall = G; S.vaoTall = G.vao; S.boot.gridTall = G.ms; });
  askMesh(GX * 2, GY * 2, function (G) { S.gridFine = G; S.vaoFine = G.vao; S.boot.gridFine = G.ms; });
}

/* ==== R53: AND ANYTHING THAT READS THE RECORD ONCE MUST BE TOLD WHEN IT
   IS IN. The field is built in a worker, so everything the RECORD knows —
   the day under the view, the marks, the names — is unanswerable for the
   first few hundred ms of a load and answerable after. A reader that asks
   every frame never notices; a reader that asks ONCE races the worker.
   terminal.js's opening `status` line is that reader: it printed
   `view 53.0w at 2026-07-01` on one load and `view 53.0w` on the next two,
   the three-line block rewrapped, and 17,781 px moved between two loads of
   the same url (REPORT-room-R50 §5).

   `Room.whenRecord(fn)` is the answer, and it is a QUEUE and not an event
   for the reason X6 (e) wrote the command queue: a module that asks after
   the record has landed must still be answered, or the fix is a second
   race. fn runs now if the record is in, and otherwise at the end of
   openWindow() — after buildDay(), which is the first instant
   `Room.dayAtMap` can answer, and after clampView(), so the view the line
   names is the view that was drawn. ============================== */
/* R70: the filed record's own two numbers. A module-local and NOT a key
   on `S` — `window.Room = S`, so anything put there is public API, and
   `S.home` / `S.scale` would have shadowed `Room.home` and `Room.scale`
   (measured: the first cut of this item posted `Room.home` to the worker
   and the key printed NaN). */
var homePair = null;
var recordQ = [];
function recordIn() { return !!(S.fld && !S.fld.stub && S.fld.days); }
Room.whenRecord = function (fn) {
  if (typeof fn !== 'function') return recordIn();
  if (recordIn()) fn();
  else recordQ.push(fn);
  return recordIn();
};
/* NOT INSIDE THE FRAME THAT OPENS THE WINDOW. Everything queued here is
   somebody else's work — a DOM line, a re-read — and openWindow is on the
   critical path of the first whole picture, so the queue is drained on the
   next turn of the loop and the record's own frame pays nothing for it. */
function tellRecord() {
  if (!recordQ.length) return;
  setTimeout(function () {
    var q = recordQ; recordQ = [];
    q.forEach(function (fn) {
      try { fn(); } catch (e) { console.warn('whenRecord', e); }
    });
  }, 0);
}

/* =================================================================
   FRAME 2 — THE WINDOW IS OPENED

   Everything from here in is the record's: the field texture, the tint
   ramp, the marks, the names, the sea and the day. The view, the tilt
   and the bearing are whatever frame 1 left them at, so a reader who
   panned or zoomed in the meantime keeps what they did.
   ================================================================= */
function openWindow(f) {
  var gl = S.gl, T = S.boot, t0 = performance.now();
  gl.bindVertexArray(null);          /* see the note at the end of draw() */
  S.fld = f;
  /* SV1: the record's own day is known now; the print may or may not be */
  try { strikeSurveyed(); } catch (e) { console.warn('surveyedTo', e); }
  try { strikeRange(); } catch (e) { console.warn('range', e); }
  /* R36: AND THE KEY'S OWN FIGURE IS THE RECORD'S. The field book says a
     directed day "now stands at <n> commits, read off the record filed
     with this sheet rather than printed once and left to go stale" — and
     the span had been printing an em dash since v32's own filler went
     (nothing in the engine ever wrote it). The level is re-struck against
     the datum horizon in this item, so it is written here, where it lands.
     `key-settled`, one leaf further on, is still empty: that one wants
     v32's settlement SENTENCE and not a number (OPEN). */
  (function () {
    var el = document.getElementById('datum-mhw');
    if (el && f.days && f.days.newSeries) el.textContent = f.days.newSeries;
  })();
  /* ==== R70: THE FILED RECORD IS THE LADDER, so its own two numbers are
     kept the first time they land and handed to the worker with every
     record that arrives afterwards. They are taken from the BOOT field
     and never re-taken: whatever is filed with the page is what the sheet
     is printed for, and a stranger is placed on that. Nothing is
     computed here on his own sheet — `f.scale` is null on it — which is
     why the fit is 0 px by construction as well as by arithmetic. */
  if (!homePair && !S.foreign && f.days && f.days.newSeries && f.days.peak)
    homePair = { m: f.days.newSeries, p: f.days.peak };
  /* the key's figures and its level line are the record's, so they are
     re-struck when a record lands — never at boot, where paginateBook has
     already written them off the printed ladder. */
  if (S.loads && window.RoomBooks && RoomBooks.rescale) RoomBooks.rescale(f.scale || null);
  S.workerMs = +(t0 - S.tBoot).toFixed(1);
  T.fieldWait = S.workerMs;
  bootLog('field landed, opening the window');
  /* R16F, uploaded as FLOAT. HALF_FLOAT raises INVALID_OPERATION in this
     Chrome and the texture reads back as zeros (ROOM-PLAN §1). R32F is
     the default (?r32=0 for E1's R16F): half float quantises the level
     set into dashes at x8 (E1). */
  gl.bindTexture(gl.TEXTURE_2D, S.texField);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  var fmt = (S.floatLinear && Q.get('r32') !== '0') ? gl.R32F : gl.R16F;
  gl.texImage2D(gl.TEXTURE_2D, 0, fmt, f.W, f.H, 0, gl.RED, gl.FLOAT, f.bed);
  /* P3 (P2's plan item 4): `S.texErr = gl.getError()` STOOD ON THIS LINE
     and it was the most expensive line of the load — 838.2-851.6 ms,
     measured twice. Not because asking for an error code is expensive: on
     this back end every synchronous GL query flushes the command stream
     and waits for the GPU process to drain it, and this was the FIRST
     query after the context bring-up, eighteen compiles, a 3 MB R32F
     upload and a 2688x1512 first draw, so it paid for all of them (P2
     section 1). The reading is moved a dozen lines down, on to the back of
     `prog('ground')` — a query that has to be made, whose collection
     cannot be avoided, and which has already flushed the same stream. The
     error flag is sticky until it is read, so nothing is lost: what it
     reports is still the first error of the field's upload.
     `Room.state().texErr` is unchanged.

     MEASURED, six interleaved cold-load pairs with the reading put back by
     a temporary hook so the box's drift could not decide it: task 3 934.7
     vs 950.6 ms, task 4 576.4 vs 699.9, task 5 278.3 vs 115.0, `sum50`
     2,148 vs 2,339. Every one of those is inside this box's +/-25%, and
     they do not agree on a winner — which is P2's finding about its own
     fix A, arrived at from the other side: deleting the query does not
     delete the flush, it hands it to the next query. What this buys is not
     milliseconds. It is that the line which pays for the whole boot is now
     one the picture actually needs, and not a diagnostic. */
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  if (!S.grid) precompile();          /* a warm load reaches here first */
  /* THE PROGRAMS ARE COLLECTED HERE. precompile() only ISSUED them; this
     asks the engine's memo for the same ones by name, and prog() stalls
     on whichever the driver has not finished. Every one the poller has
     already collected costs nothing. */
  var tw = performance.now();
  function step(name) { var n = performance.now(); (T.openSteps = T.openSteps || {})[name] = +(n - tw).toFixed(1); tw = n; }
  step('field upload');
  /* ==== LP4: AND THE GROUND IS NOT WAITED FOR HERE ANY MORE. This line
     was the longest wait on a cold Windows load — 3.4 to 5 s of Direct3D
     compile with the map held off the glass behind it. The four passes are
     issued (memoised: on any path that has already issued them this costs
     nothing) and the frame draws whichever of them the poller has
     collected, so the sheet fills in instead of sitting white. Without
     KHR_parallel_shader_compile there is nothing to poll and the first
     question pays for everything queued (R6), so there the four are
     collected here, in the order they are drawn, exactly as the one
     program was. ==== */
  issueGround(gl);
  if (!KHR) for (var gpi = 0; gpi < GROUND_ORDER.length; gpi++) groundPass(gl, GROUND_ORDER[gpi]);
  step('ground programs');
  /* P3: and the two uploads' error code, off the back of that collection's
     flush rather than in front of it — see the note beside the field's
     `texImage2D` above. */
  S.texErr = gl.getError();
  step('getError flush');
  buildMarks();
  step('marks');
  /* ==== E7 ==== */
  if (window.RoomWater) RoomWater.init(gl, S, {
    prog: prog, setCommon: setCommon, EXAG: EXAG,
    finish: function (p) { return progFinish(gl, p); },
    chunks: { FIELD: FIELD_GLSL, FRONT: FRONT_GLSL, XFORM: XFORM_GLSL }
  });
  step('water');
  /* ==== /E7 ==== */
  layout();
  clampView();
  buildDay();
  step('layout, day');
  if (window.RoomObjects) RoomObjects.place(S);
  if (window.Sky) Sky.setView(S.yaw / DEG);
  step('place');
  T.open = +(performance.now() - S.tBoot).toFixed(1);
  S.openMs = +(performance.now() - t0).toFixed(1);
  bootLog('window opened', S.openMs + ' ms of work: ' + JSON.stringify(T.openSteps));
  tellRecord();                      /* R53: the record is in, and askable */
  /* THE NAMES ARE INKED LAST. The atlas is cut from the real face or it
     is cut in Georgia for good, so the lettering waits for the woff2 —
     and for nothing else to wait on it. */
  if (S.fontsIn) {
    /* the face is already in — on a warm load it is in the http cache and
       lands before the field does — so the names are cut into THIS frame
       and there is nothing to fade */
    var tl0 = performance.now();
    buildLettering();
    S.letterCutMs = +(performance.now() - tl0).toFixed(1);
    bootLog('lettering cut in the frame', S.letterCutMs + ' ms');
  } else (S.fontsReady || Promise.resolve()).then(function () {
    if (S.letters || !S.fld || S.fld.stub) return;
    var tl = performance.now();
    buildLettering();
    S.letterCutMs = +(performance.now() - tl).toFixed(1);
    S.letterLateMs = +(performance.now() - S.tBoot).toFixed(1);
    bootLog('lettering cut', S.letterCutMs + ' ms');
    if (S.letters) { S.letterFade = 0; S.letterT0 = performance.now(); fadeNames(); }
    invalidate();
  });
}

/* the names come up over a quarter of a second, once */
function fadeNames() {
  var e = clamp((performance.now() - S.letterT0) / 260, 0, 1);
  S.letterFade = e * e * (3 - 2 * e);
  draw();
  if (e < 1) requestAnimationFrame(fadeNames);
}

/* =================================================================
   THE REVEAL (R4, Tommy's third pass)

   `rise` — the default, and his own proposal: the field is held at the
   datum and let up over 400 ms. The contours and the tint are derived
   from the SCALED height every frame, so the levels sweep up the land as
   it comes and it reads as the survey being drawn rather than a picture
   being pasted. `ink` walks a soft edge west to east over bare paper;
   `fade` washes the window in; `none` cuts. The cost of a reveal frame
   is the cost of an ordinary frame — nothing extra is drawn.
   ================================================================= */
function startReveal() {
  var mode = Q.get('reveal') || 'rise';
  S.revealMode = mode;
  if (mode === 'none') { S.rise = 1; S.markFade = 1; S.reveal = null; readyWhenWhole(); draw(); return; }
  var span = mode === 'rise' ? 400 : 300, t0 = performance.now();
  var v = S.view;
  S.reveal = mode === 'ink' ? [1, 0, v.x, v.w * 0.10] : (mode === 'fade' ? [2, 0, 0, 1] : null);
  if (mode === 'rise') S.rise = 0;
  /* the marks come up WITH the ground whichever reveal is running, so a
     farm is never standing on bare paper or on the sea */
  S.markFade = 0;
  readyWhenWhole();
  (function step() {
    var e = clamp((performance.now() - t0) / span, 0, 1);
    var ee = e < 0.5 ? 2 * e * e : 1 - Math.pow(-2 * e + 2, 2) / 2;
    if (mode === 'rise') S.rise = ee;
    else if (mode === 'ink') { S.reveal[1] = ee; S.reveal[2] = v.x + v.w * ee * 1.1; }
    else S.reveal[1] = ee;
    S.markFade = ee;
    draw();
    if (e < 1) requestAnimationFrame(step);
    else { S.rise = 1; S.markFade = 1; S.reveal = null;
           S.revealMs = +(performance.now() - t0).toFixed(1); draw(); }
  })();
}

/* ==== LP4: AND THE PICTURE IS NOT WHOLE UNTIL EVERY PASS HAS DRAWN.
   The ground used to be collected inside openWindow, so by the time this
   was called the whole map was on the glass. It is four programs now and
   three of them may still be in the driver's hands, so READY waits for the
   frame in which all four have drawn — and, because the check is made at
   the END of draw(), for the lettering of that frame with them. The
   backstop is there because a reader whose context refuses the offscreen
   would otherwise never be told the picture had arrived; it is the one
   path on which READY is a timer and it says so on the console. ==== */
function readyWhenWhole() {
  if (S.readyMs || S.wantReady) return;
  S.wantReady = +(performance.now() - S.tBoot).toFixed(1);
  if (groundWhole()) return ready();
  /* twenty seconds, not four: the ground's own compile is 3.4 to 5 s on
     Tommy's card and the four passes are issued together, so anything
     under that would make the backstop the ordinary path and READY a
     timer. This is the broken-context case and nothing else. */
  setTimeout(function () {
    if (S.readyMs) return;
    bootLog('READY on the backstop: a pass never drew', JSON.stringify(S.gpDrawn || {}));
    ready();
  }, 20000);
  invalidate();
}
/* the picture is whole: what waited for it can go */
function ready() {
  if (S.readyMs) return;
  S.readyMs = +(performance.now() - S.tBoot).toFixed(1);
  S.boot.ready = S.readyMs;
  S.drawsAtReady = S.drawn;
  window.__ready = true;
  bootLog('READY: the picture is whole', S.drawn + ' draws');
  setTimeout(bootSummary, 0);
  /* and on the warm path, where there was no second frame and precompile()
     was never reached: after the picture, not in front of it (R7) */
  if (window.RoomObjects && RoomObjects.precompile) RoomObjects.precompile(modH());
  /* the card's typing belongs to the frame that draws the card */
  document.documentElement.classList.add('room-map');
  /* ==== E7 (item 7): the weather is asked for only now — AFTER the first
     picture. The picture was drawn with the last known sky; when the
     answer lands the wash EASES to it. ==== */
  if (window.RoomWater) RoomWater.afterFirstPicture();
  /* Room code starts only after a presentation opportunity.  The same lane
     serves the edge lamp and speculative room work. */
  afterMapPaint(function () {
    startRoomWarm(false);
    needEdgeLamp();
    lateCompile();
  });
}
/* ==== LD4 (2026-10-02): WHAT IS NOT IN THE FIRST PICTURE COMPILES AFTER IT.
   The loading investigation (S4): before this, the laptop's two programs,
   the boat's two and the sky's were linked on the way to READY, about
   270 KB of the 1.38 MB of GLSL the driver had in hand while the ground
   (the last thing READY waits for) was still compiling. Now: the laptop
   is fetched and issued here, the sky is issued here (unless the page
   booted tilted into it), and the boat attaches on the next frame. All
   link-only; the poller collects them, and the boat's and the sky's
   draws skip until theirs are in, so the first voyage frame does not
   stall (FW3's 400 ms). ==== */
function lateCompile() {
  if (S.lateCompiled) return;
  S.lateCompiled = true;
  needLaptop();
  var was = LINK_ONLY; LINK_ONLY = true;
  try { if (window.RoomWater && RoomWater.precompile) RoomWater.precompile(S.gl, S, modH(), true); }
  catch (e) { console.warn('late compile', e); }
  finally { LINK_ONLY = was; }
  pollLinks();
  invalidate();
}

/* =================================================================
   THE ROOM IS FETCHED AND WARMED AFTER THE MAP (R4)

   place.js is 33 KB of a room the reader may never look up at, and its
   four programs are four compiles. Tommy: "i'm not sure how much we have
   to build/render initially there for a 1% easter egg." It is not a
   script tag any more.  Once the map is painted it is fetched and warmed
   incrementally; the first `Room.room(1)` (the corner drag, the WebMCP tools) promotes any
   work still outstanding and the move starts when its base is ready.
   ================================================================= */
function needEdgeLamp() {
  if (!R17_EDGE_LAMP || S.edgeLampAsked) return;
  S.edgeLampAsked = 1;
  roomWarmLamp = true;
  startRoomWarm(false);
}

/* ==== THE ROOM WARMS BEHIND A LIVE MAP — AND SINCE WL1 IT SPRINTS =====

   One scheduler owns every speculative room task. WHAT WAS HERE: a tick
   ran on `requestIdleCallback({timeout: 900})`, did ONE bounded step, and
   refused to run at all while any pointer input was within 140 ms
   (`inputPending`). On a page that draws while the mouse moves that is one
   step per 900 ms at best and nothing at all while the hand is on the
   mouse — Tommy, on a gaming PC, 2026-09-18: "the fireplace popped in
   after like 30 seconds and the beaureau after like 2 more", and on the
   fix: "do we need to be so conservative once we've done first paint?
   background work shouldn't mean pausing everytime something is
   happening? ... we should SPRINT for first paint, interactivity working,
   etc. and then just get on with the rest of the stuff."

   WHAT IS HERE NOW. Nothing before `ready()` changes. After it a tick is
   a macrotask (a MessageChannel message: no idle callback, no 4 ms nesting
   clamp) that runs `warmStep` until WARM_BUDGET_MS have gone — 8 ms, so a
   60 fps frame keeps its draw between ticks — or a step says the driver is
   busy (a number: come back in that many ms) or the room is whole. Input
   is never consulted. A hidden document still does nothing (no GPU work
   for a tab nobody is looking at) and resumes on visibilitychange. A
   single step can overrun the budget (a mesh is built whole); that is
   measured in `S.roomWarm.worstStepMs`, not hidden. There is one speed:
   `promote` survives only as the stamp `S.roomWarm.promoted` (when a real
   room request first arrived), because place.js now issues every link the
   moment it can whether or not anyone is waiting.
   ==================================================================== */
var placeReq = null, placeLoadFailed = false, roomWarmTimer = 0;
var roomWarmQueued = false, roomWarmGen = 0, roomWarmPost = null, roomWarmLamp = false;
var WARM_BUDGET_MS = 8;
function afterMapPaint(fn) {
  requestAnimationFrame(function () { requestAnimationFrame(fn); });
}
document.addEventListener('visibilitychange', function () {
  /* TB1: a PROMOTED warm (a real request for the room — `Room.room(1)`, a turn to
     the machine) parked by a hidden tab before READY resumes too; it used
     to resume only after READY, and `whenBasePrimed`'s waiters (the turn
     to the laptop among them) stood for the life of the page. */
  if (!document.hidden && (S.readyMs || (S.roomWarm && S.roomWarm.promoted))) startRoomWarm(false);
  /* HV1: the settled strikes, whether or not the skip was recorded — the
     section's own face is only asked for by the first strike that is not
     on a hidden tab, and the per-frame poll that would ask again fires
     every thirtieth draw of a sheet that draws about once a second */
  if (!document.hidden && S.readyMs && typeof settledStrikes === 'function') settledStrikes();   /* typeof: PT1's vm */
});
function loadPlace(retry) {
  if (window.RoomPlace) return Promise.resolve(window.RoomPlace);
  if (placeLoadFailed && !retry) return Promise.resolve(null);
  if (retry) placeLoadFailed=false;
  if (placeReq) return placeReq;
  var t0 = performance.now();
  placeReq = new Promise(function (res) {
    var t = document.createElement('script');
    t.src = (window.assetUrl ? assetUrl('/js/room/place.js') : '/js/room/place.js');
    t.onload = function () {
      if(!window.RoomPlace){placeLoadFailed=true;placeReq=null;if(S.roomWarm)S.roomWarm.loadError='not registered';res(null);return;}
      if (S.roomWarm) S.roomWarm.fetchMs = +(performance.now() - t0).toFixed(1);
      res(window.RoomPlace);
    };
    t.onerror = function () {
      console.warn('place.js'); placeLoadFailed=true; placeReq=null;
      if(S.roomWarm)S.roomWarm.loadError=true;
      res(null);
    };
    document.head.appendChild(t);
  });
  return placeReq;
}
/* the next macrotask, with no nesting clamp: a MessageChannel message.
   Where there is none (the test harness) a zero timeout will do. */
function roomWarmYield(fn) {
  if (typeof MessageChannel === 'undefined') { setTimeout(fn, 0); return; }
  if (!roomWarmPost) {
    var ch = new MessageChannel();
    roomWarmPost = { port: ch.port2, fn: null };
    ch.port1.onmessage = function () { var f = roomWarmPost.fn; roomWarmPost.fn = null; if (f) f(); };
  }
  roomWarmPost.fn = fn; roomWarmPost.port.postMessage(0);
}
function scheduleRoomWarm(delay) {
  if (roomWarmQueued || document.hidden) return;
  roomWarmQueued = true;
  var gen = roomWarmGen;
  var run = function () { roomWarmQueued = false; roomWarmTimer = 0; if (gen !== roomWarmGen) return; roomWarmTick(); };
  if (delay > 0) roomWarmTimer = setTimeout(run, delay);
  else roomWarmYield(run);
}
function startRoomWarm(promote) {
  if (!S.gl) return;
  if (!S.roomWarm) S.roomWarm = { queued: +(performance.now() - S.tBoot).toFixed(1), steps: 0, ticks: 0,
                                  totalMs: 0, worstMs: 0, worstStepMs: 0, budgetMs: WARM_BUDGET_MS };
  if (promote) {
    if (!S.roomWarm.promoted) S.roomWarm.promoted = +(performance.now() - S.tBoot).toFixed(1);
    /* a real request does not sit behind a queued GPU wait */
    if (roomWarmTimer) { clearTimeout(roomWarmTimer); roomWarmTimer = 0; }
    roomWarmGen++; roomWarmQueued = false;
  }
  scheduleRoomWarm(0);
}
function roomWarmWake() { scheduleRoomWarm(0); }
function roomWarmTick() {
  if (document.hidden) return;
  var W = S.roomWarm, t0 = performance.now();
  if (!window.RoomPlace) {
    loadPlace().then(function (P) { if(P){P._wake=roomWarmWake;scheduleRoomWarm(0);} });
    return;
  }
  RoomPlace._wake=roomWarmWake;
  if (!RoomPlace.warmStep) return;
  var mk = roomProgramTools(), done=false, delay=0, steps=0;
  try {
    if (roomWarmLamp && RoomPlace.initLamp) { RoomPlace.initLamp(S.gl, S, mk); roomWarmLamp = false; }
    do {
      var ts = performance.now();
      var more = RoomPlace.warmStep(S.gl, S, mk);
      var dt = performance.now() - ts; steps++;
      if (dt > W.worstStepMs) { W.worstStepMs = +dt.toFixed(1); W.worstStep = RoomPlace.lastStep || '?'; }
      if (more === false) { done=true; break; }
      if (typeof more === 'number') { delay = more; break; }
    } while (performance.now() - t0 < WARM_BUDGET_MS);
  } catch (e) {
    /* ==== B2: A WARM-TICK FAILURE IS NOT THE END OF THE ROOM ==========
       This catch used to call `failWarm` and return. `failWarm` rejected
       every `whenBasePrimed` waiter, the rejection was permanent, and
       `lookUp`'s `if (P) lookUp(true)` never fired again: one GPU fence
       answering WAIT_FAILED left the reader at the desk for the life of
       the page, with nothing said. place.js now answers with what it can
       still do, and the blocking `RoomPlace.init` path — the bring-up the
       staged warm-up replaced — carries the room the next time it is
       actually asked for. Only a failure in THAT path is terminal.
       ================================================================ */
    console.warn('room warm', e); W.error = String(e);
    W.recover = (window.RoomPlace&&RoomPlace.failWarm) ? RoomPlace.failWarm(e) : 'terminal';
    /* the base room was already up and a later step fell over: the lane has
       more furniture to do and place.js has dropped the piece that threw. */
    if (W.recover === 'primed') scheduleRoomWarm(40);
    return;
  }
  if (RoomPlace.lampReady && !S.boot.edgeLamp) {
    S.boot.edgeLamp = +(performance.now() - S.tBoot).toFixed(1);
    /* PT1: `typeof`, because test-room-warm-scheduler.js runs this block
       on its own in a vm with no bootLog in it */
    if (typeof bootLog === 'function') bootLog('edge lamp lit');
    invalidate();
  }
  var tick = performance.now() - t0;
  W.ticks++; W.steps += steps; W.totalMs = +(W.totalMs + tick).toFixed(1);
  W.worstMs = +Math.max(W.worstMs, tick).toFixed(1);
  if(done){if(!W.error||W.recover==='primed'){W.complete=+(performance.now()-S.tBoot).toFixed(1);if (typeof bootLog === 'function') bootLog('room warm complete', W.ticks + ' ticks, worst ' + W.worstMs + ' ms');}return;}
  scheduleRoomWarm(delay);
}

function needPlace(lampOnly) {
  if (!lampOnly) startRoomWarm(true);
  return loadPlace(true).then(function (P) {
    if (!P) return null;
    var t0 = performance.now(), mk = roomProgramTools();
    if (lampOnly) {
      P.initLamp(S.gl, S, mk);
      S.lampMs = +(performance.now() - t0).toFixed(1);
    } else {
      if (P.promote) P.promote();
      P.init(S.gl, S, mk);
      S.placeMs = +(performance.now() - t0).toFixed(1);
    }
    scheduleRoomWarm(0);
    if(!lampOnly && P.whenBasePrimed) return P.whenBasePrimed();
    return P;
  }).catch(function(e){console.warn('place init',e);return null;});
}
S.needPlace = needPlace;

/* the measuring hooks the harness uses */
  /* R13: the ground mesh's own rect, published so the harness can ask how
     far the drawn ground reaches without reading a shader. */
  S.gridRect = gridRect;
  S.perf = function (n) {
    var gl = S.gl, px = new Uint8Array(4);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
    var t0 = performance.now();
    for (var i = 0; i < n; i++) draw();
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
    var t1 = performance.now();
    return { frames: n, ms: +(t1 - t0).toFixed(2), perFrame: +((t1 - t0) / n).toFixed(4),
             px: S.cv.width * S.cv.height,
             mpxPerMs: +((S.cv.width * S.cv.height * n / 1e6) / (t1 - t0)).toFixed(1) };
  };
  S.state = function () {
    return { view: S.view, tilt: S.tilt / DEG, yaw: S.yaw / DEG, draws: S.drawn,
             workerMs: S.workerMs, fieldMs: S.fld && S.fld.ms, furnMs: S.furnMs,
             /* DR1, 2026-09-21: which drape the sheet is on (`?drape=`).
                DR2, 2026-09-21: Tommy picked ("i like drape=paper"), so
                the switch, the other two drapes and `RoomObjects.DRAPE`
                are deleted and this line with them. There is one drape. */
             deskCompileMs: window.RoomObjects ? RoomObjects.deskCompileMs : null,
             woodCompileMs: window.RoomObjects ? RoomObjects.woodCompileMs : null,
             placeMs: S.placeMs,
             firstDrawMs: S.firstDrawMs, readyMs: S.readyMs, texErr: S.texErr,
             floatLinear: S.floatLinear, dpr: S.dpr, layout: S.layout, portal: S.portal,
             ortho: ORTHO, boot: S.boot, drawsAtReady: S.drawsAtReady,
             plate: S.fld && S.fld.plate, front: S.fld && S.fld.front,
             letters: S.letters ? S.letters.stats() : null,
             letteringMs: S.letteringMs,
             /* SL1: the section's strike, which is drawn on any frame the
                span moves now -- the last one's ms and the count since
                load, so a drag's cost is two samples apart. */
             sectionMs: window.RoomHandle ? RoomHandle.sectionMs() : null,
             sectionStrikes: window.RoomHandle ? RoomHandle.sectionStrikes() : null,
             /* R4: the two frames, and what each cost */
             frame1Ms: S.frame1Ms, drawsAtFrame1: S.drawsAtFrame1,
             openMs: S.openMs, revealMode: S.revealMode, revealMs: S.revealMs,
             letterCutMs: S.letterCutMs, placeMs: S.placeMs,
             roomWarm: S.roomWarm,
             /* B2: the fence, the fallback and whether either failed, in
                text — the room itself never shows a reader an error. */
             roomPlace: (window.RoomPlace && RoomPlace.warmFence) ? RoomPlace.warmFence() : null,
             parallelCompile: S.parallelCompile, progMs: S.progMs,
             /* LP4: when each of the ground's four passes first drew */
             groundPasses: S.gpDrawn, groundWhole: groundWhole(),
             renderer: (function () { var d = S.gl.getExtension('WEBGL_debug_renderer_info');
               return d ? S.gl.getParameter(d.UNMASKED_RENDERER_WEBGL) : '?'; })() };
  };

/* =================================================================
   THE LETTERING (E3). The placement came back with the field, from the
   worker; everything here is the atlas, the per-zoom layout and one
   instanced draw. Kept to this hook and one call in draw() so the module
   is a module.
   ================================================================= */
function buildLettering() {
  disposeGL('letters');                /* X6 (c): the last load's atlas and buffers */
  if (!LET_ON || !window.RoomLettering || !S.fld.lettering) { S.letters = null; return; }
  S.letters = owned('letters', function () { return window.RoomLettering.attach({
    gl: S.gl, S: S, prog: prog, setCommon: setCommon, EXAG: EXAG,
    fitWidth: fitWidth, smoothstep: F.smoothstep, LEV_B: LEV_B, LEV_C: LEV_C,
    upToCamera: LET_UP,
    chunks: { FIELD: FIELD_GLSL, FRONT: FRONT_GLSL, XFORM: XFORM_GLSL }
  }); });
  owned('letters', function () { S.letters.setLabels(S.fld.lettering.labels); });
  S.letteringMs = S.fld.lettering.ms;
}

/* THE MONTH RULERS (R8). No placement: the graduations are arithmetic
   on the view and the epoch, so the module is attached with the three
   programs frame 1 draws with and needs nothing from the worker. */
function buildRuler() {
  if (Q.get('ruler') === '0' || !window.RoomRuler) { S.ruler = null; return; }
  /* THE COMPILE IS PAID HERE, IN FULL, AND IT IS THE SECOND-BEST OPTION.
     R4's idiom — issue the link, collect it after the picture — was tried
     and is WORSE for this one: with `LINK_ONLY` nothing asks after the
     link, the driver does not start on it, and the whole compile lands
     inside the first draw (measured: frame 1 736-816 ms, progMs.ruler
     522-602). Polling COMPLETION_STATUS_KHR from boot did not reliably
     start it either (one run in three came back at 306 ms, the others at
     736+). Compiled here it is a flat 12 ms, and frame 1 is 303 ms
     against 266 with `?ruler=0` — the price of the graduated border
     being in the first picture rather than arriving 750 ms later. */
  /* R18: NO `letOn`. It was `LET_ON && !!window.RoomLettering`, and this
     runs synchronously in boot where base.njk has not reached lettering.js
     yet, so it was false on every load and the ruler never once took the
     branch it gated. The ruler asks for RoomLettering live instead: it
     sets frame 1 from its own small atlas and hands over to E3's the frame
     that one is cut (ruler.js atlas()). */
  S.ruler = window.RoomRuler.attach({
    gl: S.gl, S: S, prog: prog
  });
}
Room.rulerStats = function () { return S.ruler ? S.ruler.stats() : null; };

/* THE MARKS (E5). The placement came back with the field, from the
   worker; everything here is the atlas, the instance buffers and the
   draws. Kept to this hook and one call in draw() so the module is a
   module. `?marks=0` draws none of them (the pair for the measurement);
   `?marks=fam` draws the land families and no sprites. */
function buildMarks() {
  disposeGL('marks');                  /* X6 (c): the last load's atlas, families and sprites */
  if (Q.get('marks') === '0' || !window.RoomMarks || !S.fld.marks) { S.marks = null; return; }
  S.marks = owned('marks', function () { return window.RoomMarks.attach({
    gl: S.gl, S: S, prog: prog, setCommon: setCommon, EXAG: EXAG,
    fitWidth: fitWidth, smoothstep: F.smoothstep, LEV_B: LEV_B, LEV_C: LEV_C,
    reliefOf: reliefOf, only: Q.get('marks') || '', rose: Q.get('rose'),
    chunks: { FIELD: FIELD_GLSL, FRONT: FRONT_GLSL, XFORM: XFORM_GLSL }
  }); });
  owned('marks', function () { S.marks.setMarks(S.fld.marks); });
  S.marksMs = S.fld.marks.ms;
  S.marksBytes = S.fld.marks.bytes;
  S.marksCounts = S.fld.marks.counts;
}
/* THE DAY (E6). The pick, the pencil cross, the card and everything
   typed on it. One hook here, two calls in draw(), and the input in
   wire(); the module is a module. */
function buildDay() {
  disposeGL('day');                    /* X6 (c): the last load's quad and its array */
  if (!DAY_ON || !window.RoomDay || !S.fld.days) { S.day = null; return; }
  S.day = owned('day', function () { return window.RoomDay.attach({
    gl: S.gl, S: S, prog: prog, EXAG: EXAG, card: S.card,
    invalidate: invalidate, queueHash: queueHash
  }); });
  /* ==== R24 / N2: A DATED LINK ARRIVES ON ITS DAY. `applyHash()` runs
     at boot, while the field is still in the worker and there is no
     `S.day` to take the token — so the day is handed over here, the
     frame the day module exists, from `LINK_DAY` (read off the address
     at evaluation; see it above) and nowhere else. It is spent once: the
     reader's first move over the map rules from then on. ==== */
  if (LINK_DAY) { S.day.show(LINK_DAY); LINK_DAY = null; }
}
Room.dayStats = function () { return S.day ? S.day.stats() : null; };
/* THE DAY UNDER A POINT OF THE CANVAS, in css pixels (ROOM-PLAN §3). */
Room.dayAt = function (x, y) { return S.day ? S.day.dayAt(x, y) : null; };
/* ==== FP2 (2026-10-07): `?fps=1` PAINTS THE FRAME RATE. Tommy: "I can't quite figure out if my phone is stuttering
   ... Are we able to temporarily paint a frame rate onto the screen?" A trial readout, top left, once a second:
   the browser's own frames a second (its requestAnimationFrame), the engine's draws a second (it draws only when
   something moved, so at rest this is nought and that is right), the longest gap between two frames in that
   second and how many gaps were over 25 ms (a dropped frame at 60 Hz). Off without the query. ==== */
if (Q.get('fps') === '1' || PROF) (function () {
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;left:calc(env(safe-area-inset-left) + 6px);top:calc(env(safe-area-inset-top) + 6px);' +
    'z-index:2147483647;font:11px/1.3 ui-monospace,Menlo,monospace;color:#fff;background:rgba(0,0,0,.65);' +
    'padding:3px 6px;border-radius:3px;pointer-events:none;white-space:pre';
  document.body.appendChild(el);
  var SPIN = Q.get('spin') === '1';
  var n = 0, t0 = performance.now(), last = t0, worst = 0, slow = 0, d0 = S.drawn || 0, c0 = S.cpuMs || 0;
  (function tick(t) {
    var g = t - last; last = t; n++;
    if (g > worst) worst = g;
    if (g > 25) slow++;
    if (t - t0 >= 1000) {
      var dr = (S.drawn || 0) - d0;
      el.textContent = Math.round(n * 1000 / (t - t0)) + ' fps  ' + Math.round(dr * 1000 / (t - t0)) + ' draws\n' +
        'worst ' + Math.round(worst) + ' ms  ' + slow + ' slow\n' +
        'draw js ' + (dr ? ((S.cpuMs || 0) - c0) / dr : 0).toFixed(1) + ' ms';
      if (PROF) {   /* FP3: the passes, ms a frame, biggest first */
        var pt = Room.passTimes(), ks = Object.keys(pt.ms).sort(function (a, b) { return pt.ms[b] - pt.ms[a]; });
        if (pt.frames) el.textContent += '\n' + ks.slice(0, 9).map(function (k) { return k + ' ' + (pt.ms[k] / pt.frames).toFixed(1); }).join('\n');
        window.__pf = el.textContent;
      }
      n = 0; t0 = t; worst = 0; slow = 0; d0 = S.drawn || 0; c0 = S.cpuMs || 0;
    }
    if (SPIN) invalidate();   /* FP4: ?spin=1 draws every frame, a bench with nothing moving */
    requestAnimationFrame(tick);
  })(t0);
})();
Room.mapAt = function (x, y) { return S.day ? S.day.mapAt(x, y) : null; };
Room.dayAtMap = function (mx, my) { return S.day ? S.day.dayAtMap(mx, my) : null; };
/* X5: THE BED, in the field's own units (0 is the coast, -COAST the open
   sea's floor). The lead line reads this and nothing else. */
Room.bedAt = function (mx, my) { return S.day && S.day.bedAt ? S.day.bedAt(mx, my) : 0; };
Room.probe = function (x, y, n) { return S.day ? S.day.probe(x, y, n) : null; };
Room.toScreen = function (mx, my) { return S.day ? S.day.toScreen(mx, my) : null; };
Room.dayShow = function (d) { if (S.day) S.day.show(d || null); };
Room.cardPresent = function (record) { return S.day && S.day.present ? S.day.present(record) : false; };
Room.cardDismiss = function (reason) { return S.day && S.day.dismiss ? S.day.dismiss(reason) : false; };
Room.cardPresented = function () { return S.day && S.day.presented ? S.day.presented() : null; };
Room.cellOf = function (d) { return S.day ? S.day.cellOf(d) : null; };

/* =================================================================
   THE PUBLISHED FACE OF THE ENGINE (E6)

   The terminal drives these and nothing else — the same rule the old
   one kept: a command goes through a published function or the answer
   is to publish one, never to poke at a private (terminal.js's own
   header). They are the sheet's own gestures, named.
   ================================================================= */
Room.fitWidth = fitWidth;
/* THE VIEW'S CENTRE, as the hash writes it. It is NOT called `view`:
   `Room` IS the engine's state object and `S.view` is the view rect, so a
   method of that name shadowed the rect until boot replaced it — and the
   terminal's first `status`, which runs 400 ms in, read `S.view.w` off a
   FUNCTION and printed `view NaNw`. Measured, and renamed. */
Room.centre = function () { return { x: S.view.x + S.view.w / 2, y: S.view.y + S.view.h / 2,
                                     w: S.view.w, h: S.view.h }; };
Room.windowWeeks = function () { return S.view.w / C.cw; };
Room.tiltDeg = function () { return S.tilt / DEG; };
Room.yawDeg = function () { return S.yaw / DEG; };
Room.attitude = function (tilt, yaw) {
  if (tilt !== undefined && tilt !== null) { S.tilt = clamp(tilt * DEG, 0, tiltCap()); clampView(); }
  if (yaw !== undefined && yaw !== null) S.yaw = yaw * DEG;
  if (window.Sky) Sky.setView(S.yaw / DEG);
  invalidate(); queueHash();
};
Room.centreOn = function (mx, my, w) {
  if (w) S.view.w = clamp(w, minViewW(), maxViewW());   /* V4 */
  S.view.h = S.view.w * viewRatio();
  S.view.x = mx - S.view.w / 2; S.view.y = my - S.view.h / 2;
  snapLift();
  clampView(); invalidate(); queueHash();
};
function fitPose() {
  var w = fitWidth(), h = w * viewRatio(), P = S.fld.plate;
  return { x: P.x, y: P.y + P.h / 2 - h / 2, w: w, h: h };
}
function pauseVoyageForHome() {
  var V = Room.voyage;
  if (V && V.pauseForHome) V.pauseForHome();
}
Room.fit = function () {
  pauseVoyageForHome();
  cancelHome();
  var f = fitPose();
  S.tilt = 0; S.yaw = 0;
  if (window.Sky) Sky.setView(0);
  S.view.w = f.w; S.view.h = f.h; S.view.x = f.x; S.view.y = f.y;
  snapLift();
  clampView(); invalidate(); queueHash();
};

/* =================================================================
   R14: IS THE SHEET AT HOME, AND THE WAY BACK TO IT

   v32's isAtFit (survey-map.js :7954) in the engine's own terms: flat,
   north-up, at the home pan and zoom, and — the engine's one addition —
   on the desk rather than up in the room. Anything else (a pan, a turn of
   the wheel, a degree of tilt, a swing of the bearing, the room move) and
   the sheet is not home, so the pin has something to undo.

   THE TOLERANCE IS THE HASH'S. A view that came back off a link is only
   good to the integer the hash wrote, so a link to the fit has to READ as
   the fit or the pin would be inked on the picture it belongs to. v32's
   0.002 of the width is 4.2 px at this sheet — eight times the half-unit
   the rounding can be out by, and a thousandth of a screen off the fit,
   which is not a pan anybody made.
   ================================================================= */
Room.atFit = function () {
  if (!S.layout || !S.fld) return false;
  if (S.cam.want > 0.5 || S.cam.room > 0.01) return false;
  if (Math.abs(S.tilt) > 0.4 * DEG) return false;
  var b = S.yaw - Math.round(S.yaw / (2 * Math.PI)) * 2 * Math.PI;
  if (Math.abs(b) > 0.4 * DEG) return false;
  var f = fitPose(), tol = f.w * 0.002;
  return Math.abs(S.view.x - f.x) < tol && Math.abs(S.view.y - f.y) < tol &&
         Math.abs(S.view.w - f.w) < tol;
};

/* HOME IS A MOVE, NOT A CUT. The pin is pushed and the sheet is laid back
   out — a hand does that in front of you, it does not teleport. The same
   cubic the room move is eased with (lookUp), over 620 ms, with the ZOOM
   TAKEN GEOMETRICALLY: a linear ramp on the width crawls at the wide end
   and snaps at the near one, because what the eye reads is the ratio.
   The bearing takes the short way round — a sheet turned to 400 degrees
   is a sheet turned to 40 — and the last frame is the fit itself, to the
   bit, so `atFit` answers yes and the pin goes blank on arrival.
   Any gesture during the move takes it: the hand wins. */
var homeRaf = 0;
function cancelHome() { if (homeRaf) { cancelAnimationFrame(homeRaf); homeRaf = 0; } }
Room.home = function (ease) {
  pauseVoyageForHome();
  if (S.cam.want > 0.5) lookUp(false);
  if (!ease || !live) { Room.fit(); return; }
  cancelHome();
  var f = fitPose();
  var b0 = S.yaw - Math.round(S.yaw / (2 * Math.PI)) * 2 * Math.PI;
  S.yaw = b0;
  var from = { x: S.view.x + S.view.w / 2, y: S.view.y + S.view.h / 2,
               w: S.view.w, t: S.tilt, b: b0 };
  var to = { x: f.x + f.w / 2, y: f.y + f.h / 2, w: f.w };
  var ratio = to.w / from.w, t0 = performance.now();
  homeRaf = requestAnimationFrame(function step(t) {
    var e = clamp((t - t0) / 620, 0, 1);
    e = e < 0.5 ? 4 * e * e * e : 1 - Math.pow(-2 * e + 2, 3) / 2;
    if (e >= 1) { homeRaf = 0; Room.fit(); return; }
    S.tilt = from.t * (1 - e); S.yaw = from.b * (1 - e);
    S.view.w = from.w * Math.pow(ratio, e); S.view.h = S.view.w * viewRatio();
    S.view.x = (from.x + (to.x - from.x) * e) - S.view.w / 2;
    S.view.y = (from.y + (to.y - from.y) * e) - S.view.h / 2;
    clampView();
    if (window.Sky) Sky.setView(S.yaw / DEG);
    invalidate();
    homeRaf = requestAnimationFrame(step);
  });
};

/* =================================================================
   R21: THE SAME MOVE, TO SOMEWHERE THAT IS NOT HOME

   A year clicked on the index is travelled to, not jumped to — v32's
   flyToYear (survey-map.js :5882) eased the pan and the zoom and left the
   attitude alone, because going to 2023 is not the same gesture as laying
   the sheet back out flat. This is `home`'s stepper with the attitude
   taken out of it: the same 620 ms, the same cubic, the same geometric
   ramp on the width, and the same `homeRaf`, so a gesture cancels it and
   a push of the pin overrides it exactly as it overrides a home.
   ================================================================= */
Room.easeTo = function (cx, cy, w) {
  cancelHome();
  if (!live) { Room.centreOn(cx, cy, w); return; }
  w = clamp(w || S.view.w, minViewW(), maxViewW());   /* V4 */
  var from = { x: S.view.x + S.view.w / 2, y: S.view.y + S.view.h / 2, w: S.view.w };
  var ratio = w / from.w, t0 = performance.now();
  homeRaf = requestAnimationFrame(function step(t) {
    var e = clamp((t - t0) / 620, 0, 1);
    e = e < 0.5 ? 4 * e * e * e : 1 - Math.pow(-2 * e + 2, 3) / 2;
    if (e >= 1) { homeRaf = 0; Room.centreOn(cx, cy, w); return; }
    S.view.w = from.w * Math.pow(ratio, e); S.view.h = S.view.w * viewRatio();
    S.view.x = (from.x + (cx - from.x) * e) - S.view.w / 2;
    S.view.y = (from.y + (cy - from.y) * e) - S.view.h / 2;
    clampView(); invalidate();
    homeRaf = requestAnimationFrame(step);
  });
};
/* a hand on the index's bracket is a gesture, and a gesture stops a move
   — the canvas's own pointerdown does this, and the bracket's does not
   reach the canvas (it stops there, or the sheet would pan under it) */
Room.stopMove = cancelHome;

/* =================================================================
   R21: A THING LAID ON THE PAPER (E9's projection, R14's matrix)

   The sheet is a PLANE, so three projected points are a complete affine:
   one CSS matrix puts an element exactly on the paper at any tilt, pan,
   bearing or room pose, with no draw, no texture and no rebuild. It was
   written twice — once for the light line, once for the pin — and the
   year index is the third, so it lives here.

   `sx, sy` are the sheet coordinate the element's origin sits at, and `u`
   is how many SHEET UNITS one CSS pixel of the element is worth (the
   margin's furniture is drawn in the over layer's units, which are
   1.297476 sheet units each). Null when the point is behind the camera.
   ================================================================= */
Room.onPaper = function (sx, sy, u) {
  var L = S.layout, m = S.VP;
  if (!L || !m) return null;
  function proj(x, y) {
    var wx = L.ox + x * L.k, wy = -(L.oy + y * L.k);
    var cx = m[0] * wx + m[4] * wy + m[12], cy = m[1] * wx + m[5] * wy + m[13],
        cw = m[3] * wx + m[7] * wy + m[15];
    if (cw <= 0.0001) return null;
    return [(cx / cw * 0.5 + 0.5) * L.cw, (0.5 - cy / cw * 0.5) * L.ch];
  }
  var o = proj(sx, sy), px = proj(sx + u, sy), py = proj(sx, sy + u);
  if (!o || !px || !py) return null;
  return 'matrix(' + (px[0] - o[0]).toFixed(5) + ',' + (px[1] - o[1]).toFixed(5) + ',' +
         (py[0] - o[0]).toFixed(5) + ',' + (py[1] - o[1]).toFixed(5) + ',' +
         o[0].toFixed(2) + ',' + o[1].toFixed(2) + ')';
};

/* =================================================================
   R30: THE SAME PLANE, WITH THE PERSPECTIVE ROW KEPT

   `Room.onPaper` above drops it. Three projected points ARE a complete
   affine — for a plane parallel to the screen. The desk's edge pose is
   not: it pitches the board 22 degrees, so the sheet and the covers on
   it are TRAPEZOIDS on the screen, and an affine plate laid by three of
   their corners misses the fourth. Measured at the shipped pose, 1536 x
   864 at devicePixelRatio 1.75: the year index 2.80 device px out at its
   far corner, the sky note 2.06, the corner's hotspot 1.13, and a book's
   cover — whose own transform was an origin and ONE scale, not even the
   affine — 27.43.

   A css `matrix3d` carries the row. The homography that maps the
   element's own box to the four projected corners is Heckbert's, and
   because a css transform divides by w the fourth corner lands where the
   camera actually puts it: the residual is zero at all four, not at
   three. `transform-origin: 0 0` and the element's own w, h are the
   whole of the contract.

   `Room.onPaper` is UNTOUCHED and still used where an affine is exact
   enough (nothing on the desk, now) or where the plate is a point.
   ================================================================= */
/* Preserve WebGL's trivial frustum rejection for projected DOM. A quad
     wholly beyond one clip plane paints no pixels in GL, but a CSS
     homography made from its remote screen coordinates can acquire an
     internal pole and fold back across the viewport. Partly off-screen
     quads remain valid and are left for the browser to clip normally.
     Both transform paths use this: cover labels default to the affine. */
function domQuadOutside(q) {
  var cq = q.map(function (p) { return p && p.__clip; });
  if (cq.every(Boolean)) {
    var outside = function (test) { return cq.every(test); };
    if (outside(function (c) { return c[0] < -c[3]; }) ||
        outside(function (c) { return c[0] >  c[3]; }) ||
        outside(function (c) { return c[1] < -c[3]; }) ||
        outside(function (c) { return c[1] >  c[3]; }) ||
        outside(function (c) { return c[2] >  c[3]; })) return true;
  }
  return false;
}
Room.quadMat = function (q, w, h, tol) {
  if (!q[0] || !q[1] || !q[2] || !q[3] || !w || !h || domQuadOutside(q)) return null;
  var x0 = q[0][0], y0 = q[0][1], x1 = q[1][0], y1 = q[1][1],
      x2 = q[2][0], y2 = q[2][1], x3 = q[3][0], y3 = q[3][1];
  /* A CSS homography cannot clip a quad through the eye the way WebGL
     clips triangles. Callers reject those corners before they get here;
     this last guard keeps a bad projection from becoming an enormous
     but syntactically valid matrix. */
  if (![x0, y0, x1, y1, x2, y2, x3, y3].every(Number.isFinite)) return null;
  /* q is (0,0), (w,0), (w,h), (0,h) — the box walked round */
  var sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
  var ax1 = x1 - x2, ax2 = x3 - x2, ay1 = y1 - y2, ay2 = y3 - y2;
  /* AND A PLANE THAT IS NEARLY PARALLEL TO THE SCREEN GETS THE 2D MATRIX
     BACK, to the same five decimals it had before. `hypot(sx, sy)` IS the
     affine's error at the fourth corner, exactly, so this test is the
     fault it is here to fix: under a quarter of a css pixel there is
     nothing to carry, and a matrix3d would also hand the element its own
     compositing layer and raster it afresh.

     Nearly flat planes keep the 2D matrix; measured, the index card's
     residual decides when the perspective road is required. */
  var res = Math.hypot(sx, sy);
  if (res < (tol === undefined ? 0.25 : tol)) {
    return 'matrix(' + ((x1 - x0) / w).toFixed(5) + ',' + ((y1 - y0) / w).toFixed(5) + ',' +
           ((x3 - x0) / h).toFixed(5) + ',' + ((y3 - y0) / h).toFixed(5) + ',' +
           x0.toFixed(2) + ',' + y0.toFixed(2) + ')';
  }
  var g = 0, hh = 0, den = ax1 * ay2 - ax2 * ay1;
  if (Math.abs(den) < 1e-12) return null;
  g = (sx * ay2 - ax2 * sy) / den;
  hh = (ax1 * sy - sx * ay1) / den;
  var a = (x1 - x0 + g * x1) / w, d = (y1 - y0 + g * y1) / w,
      b = (x3 - x0 + hh * x3) / h, e = (y3 - y0 + hh * y3) / h;
  g /= w; hh /= h;
  /* css matrix3d is column-major: the first four numbers are the first
     column, and the fourth row is the one that does the dividing */
  return 'matrix3d(' + a.toFixed(6) + ',' + d.toFixed(6) + ',0,' + g.toFixed(9) + ',' +
         b.toFixed(6) + ',' + e.toFixed(6) + ',0,' + hh.toFixed(9) + ',0,0,1,0,' +
         x0.toFixed(2) + ',' + y0.toFixed(2) + ',0,1)';
};

/* AND THE BEST AFFINE, when a matrix3d costs more than it buys.

   `Room.onPaper`'s affine is exact at three corners and piles the whole
   projective residual on the fourth. This one is the LEAST-SQUARES fit
   over all four — for a rectangle mapped to a quad the bilinear term
   drops straight out, so it is an average of the two edge vectors and a
   centroid, six lines — and it spreads that residual evenly: a quarter
   of it at each corner instead of all of it at one. Measured on a book's
   cover at the shipped pose, 1.75: 5.61 device px at the fourth corner
   the three-point way, 1.40 the four-point way, with the same 2D matrix
   and therefore the same crisp raster.
   ================================================================= */
Room.quadAff = function (q, w, h) {
  if (!q[0] || !q[1] || !q[2] || !q[3] || !w || !h || domQuadOutside(q)) return null;
  /* q is (0,0), (w,0), (w,h), (0,h) */
  var ax = ((q[1][0] - q[0][0]) + (q[2][0] - q[3][0])) / (2 * w),
      ay = ((q[1][1] - q[0][1]) + (q[2][1] - q[3][1])) / (2 * w),
      cx = ((q[3][0] - q[0][0]) + (q[2][0] - q[1][0])) / (2 * h),
      cy = ((q[3][1] - q[0][1]) + (q[2][1] - q[1][1])) / (2 * h);
  var mx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4,
      my = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
  return 'matrix(' + ax.toFixed(5) + ',' + ay.toFixed(5) + ',' +
         cx.toFixed(5) + ',' + cy.toFixed(5) + ',' +
         (mx - ax * w / 2 - cx * h / 2).toFixed(2) + ',' +
         (my - ay * w / 2 - cy * h / 2).toFixed(2) + ')';
};

/* the sheet's own plane, in the units `Room.onPaper` takes: the element's
   origin at sheet (sx, sy), one css pixel of it worth `u` sheet units,
   and its box w x h css px. Null when a corner is behind the camera. */
/* a point of the paper (sheet units, h = 0) on the screen, in css px, or
   null behind the eye — the projection onPaperQuad lays the furniture by.
   index.js's drag takes the rule's direction from two of these (Tommy,
   2026-09-14: "dragging the red date index box seems to blank out the map
   and give NaN in the URL" — it parsed `matrix(a,b,…)` out of a transform
   that has been `matrix3d(…)` since R30 (a)). */
/* R43: AND THE POINT IS ON THE PAPER'S TOP FACE, not on the plane the
   paper is lying on. X3 measured the difference on the 780 x 1410 canvas:
   the year index's rule printed at device y 1316 and this function saying
   1321.9, the six marks' row 1352 against 1360.2 — because the margins of
   a sheet that has been folded in eight and laid over two books are NOT at
   z = 0, and a steep pose turns that height into a visible slip of the DOM
   off its own print. `RoomObjects.paperHeight` is the sheet vertex
   shader's `paperH` in JS, in sheet units; k puts it in the world's css
   pixels, the third column of VP does the rest, and inside the neat rule
   it is nought to the last decimal, so the map window does not move.
   `?ph=0` is the projection as it was, for the before picture. */
/* A DOM plane has to be wholly in front of the camera's near plane.
   WebGL clips geometry there; CSS matrix3d does not, and feeding a corner
   on either side of that plane to quadMat turns a small physical label
   into a screen-spanning homography. Keep off-screen points valid — only
   the camera/near-plane crossing is refused. */
function domWorldPoint(x, y, z) {
  var L = S.layout, m = S.VP;
  if (!L || !m) return null;
  var cx = m[0]*x + m[4]*y + m[8]*z + m[12],
      cy = m[1]*x + m[5]*y + m[9]*z + m[13],
      cz = m[2]*x + m[6]*y + m[10]*z + m[14],
      cw = m[3]*x + m[7]*y + m[11]*z + m[15];
  var guard = 1e-4 * Math.max(1, Math.abs(cw));
  if (![cx, cy, cz, cw].every(Number.isFinite) || cw <= 1e-4 || cz + cw <= guard)
    return null;
  var sx = (cx / cw * 0.5 + 0.5) * L.cw,
      sy = (0.5 - cy / cw * 0.5) * L.ch;
  if (!Number.isFinite(sx) || !Number.isFinite(sy)) return null;
  var out = [sx, sy];
  /* The quad helpers consume this only when all four corners came through
     the shared world projection; ordinary callers' two-number points
     retain their established behavior. */
  out.__clip = [cx, cy, cz, cw];
  return out;
}
Room.paperPoint = function (x, y) {
  var L = S.layout;
  if (!L) return null;
  var O = window.RoomObjects;
  var wz = (PAPER_H && O && O.paperHeight) ? O.paperHeight(S, x, y) * L.k : 0;
  return domWorldPoint(L.ox + x * L.k, -(L.oy + y * L.k), wz);
};
/* R47: THE SAME PROJECTION, AT A HEIGHT THAT IS NOT THE PAPER'S.
   `Room.paperPoint` is for things laid ON the sheet and carries the
   sheet's own surface (R43). A thing on the DESK — the note under the
   map, a card the fold has slid off on to the oak — is in the same sheet
   units and at its own flat z, and asking paperPoint for it puts it on
   the paper that is lying over it, which is the one place it is not.
   Three lines, and the same four multiplies. */
Room.deskPoint = function (x, y, z) {
  var L = S.layout;
  if (!L) return null;
  var wx = L.ox + x * L.k, wy = -(L.oy + y * L.k), wz = (z || 0) * L.k;
  return domWorldPoint(wx, wy, wz);
};
/* =================================================================
   X1: THE SAME PROJECTION, FOR A THING THAT IS NOT ON THE PAPER

   `Room.paperPoint` takes SHEET units and puts the point on the sheet's
   own plane (z = 0). The laptop's screen is not on that plane and is not
   in those units: it stands up off the desk, east of the journal, leaning
   fifteen degrees back. So this is the same four lines with the z row
   kept — a point of the ROOM, in the world's own css pixels (x east,
   y north, z up: the units `S.camEye` and RoomPlace are in) — and with
   it any solid in the scene can have DOM laid on one of its faces by
   `Room.quadMat`, exactly as the year index is laid on the paper.
   ================================================================= */
Room.scenePoint = function (x, y, z) {
  return domWorldPoint(x, y, z);
};
/* and the laptop's screen as a matrix, which is the one thing anybody
   asks the two of them for together */
Room.screenQuad = function () {
  var O = window.RoomObjects, lp = O && O.laptop && S.layout && S.VP ? O.laptop(S) : null;
  if (!lp) return null;
  /* LZ1: AND NO GLASS WITHOUT A MACHINE BEHIND IT. The anchor answers where
     the screen is before desk-laptop.js has landed — it has to, because
     place.js builds the `,l1` camera out of it — but this function is what
     DOM is laid on, and a terminal laid on a machine that is not drawn is a
     rectangle of type floating over bare oak. Caught on a capture with the
     file deliberately blocked (`LZ1-blocked-lap-night-175.png`): no laptop,
     and the terminal's own glow on the wood under it. `machine` is false
     until the mesh exists; both callers (terminal.js and the passive screen
     in desk-laptop.js) ask every frame, so the glass lays itself the moment
     it is true, and on a page where the file never arrives it never lies.
     `Room.laptopQuad`/`laptopHit` are deliberately NOT gated: the machine is
     physically on the desk, a hand may reach for it, and that reach is one
     of the things that asks for the fetch. */
  if (lp.machine === false) return null;
  /* DOM belongs to the front glass. Leave no projected terminal behind
     when a future room bearing sees the aluminium back of the lid. */
  var e = S.camEye, f = lp.face, n = lp.normal;
  if (!e || (e[0] - f[0])*n[0] + (e[1] - f[1])*n[1] + (e[2] - f[2])*n[2] <= 0.01)
    return null;
  var q = lp.screen.map(function (p) { return Room.scenePoint(p[0], p[1], p[2]); });
  var qm = Room.quadMat(q, lp.px[0], lp.px[1], 0.25);
  return qm ? { m: qm, w: lp.px[0], h: lp.px[1], q: q } : null;
};

/* R43: A DOM BOX IS A PLANE, AND THE PAPER UNDER IT IS NOT.

   Taking the height at the box's own four corners is the obvious thing
   and it is wrong wherever the box straddles a crease. Measured, the year
   index at the 780 x 1410 fit: the paper is flat under the whole of the
   printed rule (the index lies on the field book's cover, 30 sheet units
   up) and then falls off the board's edge in the last tenth of the box —
   so a quad hung on the corners tips the WHOLE element, and the rule came
   out sloping five device px across a print that does not slope at all,
   with the west year figures sheared for good measure. The error at the
   east end was 4.0 px, worse than two of the points it had just fixed.

   So the element is laid on the BEST PLANE through the paper it covers,
   not on the plane through its four corners: 25 heights on a 5 x 5 grid
   over the box and a least-squares fit (a regular grid is orthogonal in x
   and y, so it is three sums and no matrix). A box on the flat map gets
   z = 0 exactly as before; a box on the lie's long swell gets the swell;
   a box over the board's arris gets the cover it is mostly lying on and
   leaves the last centimetre of fall to the paper, which is the only
   thing one matrix can do about a crease. Measured after: the rule's
   error along its whole length is at most 1.1 device px.

   `Room.paperPoint` keeps the EXACT height — a point is not a plane, and
   the drag's direction, the hit quads and the map's own scissor all want
   the paper where it actually is. ================================== */
/* the grid, each way. `?pq=0` takes the height at the box's own four
   corners instead — the obvious way, kept so the two can be photographed
   against each other (the numbers are in REPORT-room-R43 §4). */
var PQ_N = (function () { var v = parseInt(Q.get('pq'), 10); return isFinite(v) && v >= 0 ? v : 5; })();
function paperProj(x, y, z) {
  var L = S.layout, m = S.VP;
  var wx = L.ox + x * L.k, wy = -(L.oy + y * L.k), wz = z * L.k;
  var cx = m[0] * wx + m[4] * wy + m[8] * wz + m[12],
      cy = m[1] * wx + m[5] * wy + m[9] * wz + m[13],
      cw = m[3] * wx + m[7] * wy + m[11] * wz + m[15];
  if (cw <= 0.0001) return null;
  return [(cx / cw * 0.5 + 0.5) * L.cw, (0.5 - cy / cw * 0.5) * L.ch];
}
Room.onPaperQuad = function (sx, sy, u, w, h) {
  if (!S.layout || !S.VP) return null;
  var W = w * u, H = h * u;
  /* ==== R47: AND NOT WHILE THE PAPER UNDER IT HAS GONE ================
     A DOM box is one plane and the fold turns whole panels, so a box on a
     panel that has been picked up cannot be laid at all, and a box on a
     panel that is still flat must be laid exactly as it always was — and
     then taken away when the flap comes down ON TOP of it, because that
     is what the flap does to the print underneath.

     `RoomObjects.foldOpen` already answers both: it is the paper still
     lying uncovered over the desk, in sheet units, and it is the same
     rectangle the map's own scissor is cut to. A box wholly inside it is
     laid; anything else hides itself, which every caller here already
     does with a null (index.js, progress.js, handle.js all share the one
     line). Before this, styles.css hid the year index at the first
     degree of the first turn, whatever it was lying on — and the index
     lies on the ONE panel of the eight that never moves. It now stays
     printed at the foot of the sheet until the east half lands over it,
     which is the second that a person watching the fold would expect to
     lose sight of it. ==== */
  /* 2026-09-20: A BOX THAT LAPS THE SHEET'S EDGE IS ON THE PAPER IT LAPS.
     The clip's barrel stands 22 units over the head (pin.js CLIP.y is
     -22), so the box's own top was north of any open rectangle and the
     clip hid at the first degree of every fold: Tommy, "it totally
     disappears during folding, which is wrong". What is tested is the
     part of the box that lies on the sheet at all. */
  var O0 = window.RoomObjects;
  if (O0 && O0.foldOn && O0.foldT > 0 && O0.foldOpen) {
    var op = O0.foldOpen(), sh = S.fld && S.fld.sheet;
    var bx0 = sh ? Math.max(sx, 0) : sx, by0 = sh ? Math.max(sy, 0) : sy;
    var bx1 = sh ? Math.min(sx + W, sh.w) : sx + W, by1 = sh ? Math.min(sy + H, sh.h) : sy + H;
    if (bx0 < op.x - 0.5 || by0 < op.y - 0.5 ||
        bx1 > op.x + op.w + 0.5 || by1 > op.y + op.h + 0.5) return null;
  }
  var O = window.RoomObjects, ph = PAPER_H && O && O.paperHeight ? O.paperHeight : null;
  var z00 = 0, z10 = 0, z11 = 0, z01 = 0;
  if (ph && PQ_N < 2) {
    z00 = ph(S, sx, sy); z10 = ph(S, sx + W, sy);
    z11 = ph(S, sx + W, sy + H); z01 = ph(S, sx, sy + H);
  } else if (ph) {
    var n = PQ_N, i, j, zx = 0, zy = 0, zs = 0, zz;
    /* the fit's own denominator: sum (i - mid)^2 over the grid, times n */
    var mid = (n - 1) / 2, den = 0;
    for (i = 0; i < n; i++) den += (i - mid) * (i - mid);
    den *= n;
    for (i = 0; i < n; i++) {
      for (j = 0; j < n; j++) {
        zz = ph(S, sx + W * i / (n - 1), sy + H * j / (n - 1));
        zs += zz; zx += (i - mid) * zz; zy += (j - mid) * zz;
      }
    }
    /* z = mean + bx*(x - xmid) + by*(y - ymid), in the grid's own steps */
    var mean = zs / (n * n), bx = zx / den, by = zy / den;
    z00 = mean - bx * mid - by * mid;
    z10 = mean + bx * mid - by * mid;
    z11 = mean + bx * mid + by * mid;
    z01 = mean - bx * mid + by * mid;
  }
  return Room.quadMat([paperProj(sx, sy, z00), paperProj(sx + W, sy, z10),
                       paperProj(sx + W, sy + H, z11), paperProj(sx, sy + H, z01)], w, h);
};

Room.zoomWeeks = function (n) {
  var mx = S.view.x + S.view.w / 2, my = S.view.y + S.view.h / 2;
  Room.centreOn(mx, my, n * C.cw);
};
Room.goto = function (date) {
  if (!S.day) return false;
  var c = S.day.cellOf(date);
  if (!c) return false;
  Room.centreOn(c[0], c[1], Math.min(S.view.w, fitWidth() * 0.30));
  return true;
};
Room.years = function () {
  var f = S.fld, out = [], y0;
  if (!f || f.stub) return out;          /* R4: before the record lands */
  y0 = +f.today.slice(0, 4);
  for (var y = C.EARLIEST_YEAR; y <= y0; y++) out.push(y);
  return out;
};
Room.notableDates = function () {
  var out = [], p = readJSON('survey-posts') || [];
  p.forEach(function (x) { out.push(x.date); });
  if (S.fld && !S.fld.stub) out.push(S.fld.today);
  return out;
};
Room.room = function (on) { lookUp(!!on); };
/* X2: WHAT THE MODE IS DOING, for the report and for an agent. `wheelOff`
   and `dragOff` are the events the map REFUSED since the page loaded —
   the proof that the mode detaches is that they go up while the view's
   five numbers do not move at all. */
Room.detach = function () { return Room.x2(); };
Room.x2 = function () {
  var g = foldGeom();
  return { mode: Room.mode(), live: Room.mapLive(), fold: +foldAmt().toFixed(4),
           foldOn: !!(g && g.on), grab: !!(window.RoomObjects && RoomObjects.foldGrab),
           turns: g ? g.a.map(function (r) { return +(r * 180 / Math.PI).toFixed(1); }) : null,
           creases: g ? [+g.c1.toFixed(1), +g.c2.toFixed(1), +g.cy.toFixed(1)] : null,
           lap: +(S.cam.lap || 0).toFixed(3), over: overW,
           lapWant: +(S.cam.lapWant || 0), lapUp: Room.lapUp(),
           upCos: +(S.upCos || 0).toFixed(5), upFix: S.upFix || 0,
           /* R62: the head, in degrees, and whether the hand is on the machine */
           look: [+((S.cam.lookYaw || 0) / DEG).toFixed(2),
                  +((S.cam.lookPitch || 0) / DEG).toFixed(2)],
           onLap: !!S.__onLap,
           wheelOff: S.wheelOff || 0, dragOff: S.dragOff || 0,
           pickOff: S.pickOff || 0, keyOff: S.keyOff || 0,
           view: [+S.view.x.toFixed(2), +S.view.y.toFixed(2), +S.view.w.toFixed(2),
                  +(S.tilt / DEG).toFixed(3), +(S.yaw / DEG).toFixed(3)] };
};
/* ==== X5: THE HOST, PUBLISHED ======================================
   Every module of this engine that draws (`marks`, `lettering`, `water`,
   `ruler`) is handed the same small object by `buildX()` — the context,
   the state, the program cache, the shared GLSL and the two or three
   functions a drawing module needs. The voyage is the first module that
   is NOT part of the boot (it is behind `?voyage=1` and arrives whenever
   the network brings it), so it cannot be handed anything by a `buildX`:
   it asks for the host instead, once, on the first frame it exists. The
   object is the same one, built the same way, so there is exactly one
   contract and not two. ==== */
Room.host = function () {
  if (!S.gl || !S.fld) return null;
  var RT = roomProgramTools();
  return { gl: S.gl, S: S, prog: prog, setCommon: setCommon, EXAG: EXAG,
           fitWidth: fitWidth, smoothstep: F.smoothstep, reliefOf: reliefOf,
           /* FW3: a module that arrives after the picture (the boat) issues
              its links with `issue`, draws once `complete` says so and asks
              `poll` to collect them — the ground passes' own pattern, so
              its compile is never a stall on a frame */
           issue: RT.issue, complete: RT.complete, poll: pollLinks,
           chunks: { FIELD: FIELD_GLSL, FRONT: FRONT_GLSL, XFORM: XFORM_GLSL } };
};
/* and the one way to ask for another frame from outside. `invalidate` has
   always been private because nothing outside decides whether to redraw
   (ROOM-PLAN section 6); a boat that is moving is the one thing that has
   to. It coalesces, so calling it twice in a frame costs nothing. */
Room.repaint = function () { invalidate(); };
Room.pose = function () { return { room: S.cam.room, want: S.cam.want, eye: S.camEye }; };

/* =================================================================
   X4 — THE SHEET IS DRAWN FROM A RECORD, AND THE RECORD CAN BE SWAPPED

   Tommy (IDEAS #22.2): "letting you change the works of Tommy Long (the
   Tommy Long part) to someone else's GitHub handle and having it redraw.
   Maybe if you click it, it's just editable."

   The engine has always been able to do this and has never been asked to:
   boot() builds a bag of TEXT, hands it to the worker, and the field that
   comes back opens the window. `Room.load` is that bag with one record
   taken out of it and another put in — the same worker, the same
   arithmetic, the same openWindow. Nothing is re-fetched, nothing is
   re-compiled, and the camera, the tilt and the bearing are exactly
   where the reader left them.

   WHAT CHANGES FOR A RECORD THAT IS NOT HIS, and why (REPORT-room-X4.md
   carries the reasoning and what would flip it):

     THE TITLE        the cartouche names the SURVEY'S SUBJECT, so the name
                      is re-PRINTED — the furniture is rastered again with
                      the new name in the cartouche's own face. The eyebrow
                      ("A SURVEY OF THE WORKS OF") stands: it is still
                      true.
     THE HORIZONS     KEPT. A marker horizon is a MODEL'S RELEASE — Opus
                      4.5, GPT o1 — which is a date in the world and not a
                      date in his life; the whole interest of a foreign
                      record is whether THEIR ground rises at the same ash
                      layer. Nothing about them is his.
     THE DIARY        GONE: events (his work trips, his planning days, his
                      leave), the bank holidays (his country's), the
                      toponymy (bays named for where HE took his leave) and
                      the posts (his essays, which are the bench marks).
                      We do not know these things about anybody else, and a
                      landing named "Vilnius" on a stranger's June would be
                      a lie. So a foreign sheet has ground, water, contours,
                      hachures, settlement and horizons, and no diary.
     THE KEY'S FIGURE the level of a directed day is the MEDIAN working day
                      since the datum horizon, read off whatever record is
                      loaded — openWindow writes it, so it follows by
                      itself and says something true about the new record.
                      Sea level (five commits) is a CONSTANT and does not
                      move: it is what makes two sheets comparable, which
                      is the field book's own reason for it.
     THE YEAR INDEX   unchanged, and that is the honest answer: the strip
                      is 2022 to date whoever is on it, so an account that
                      only opened in 2024 correctly shows two years of
                      Precambrian Sea in the west. The index's figures are
                      years, not his years.
     THE SURVEYOR     unchanged. The field book's "the surveyor" leaf is
                      about who DREW the sheet and under what light; the
                      room, the desk, the journal and the books are still
                      Tommy's. You have laid somebody else's survey out on
                      his desk; you have not become him.
   ================================================================= */
/* the payload /api/record/<handle> answers with, a bare year record, or
   null to put his own record back */
Room.load = function (payload) {
  if (!S.raw || !live) return Promise.reject(new Error('the sheet is not up yet'));
  var raw = {}, k;
  for (k in S.raw) raw[k] = S.raw[k];
  var name = null, foreign = false;

  if (!payload) {
    /* HIS OWN RECORD IS STILL IN THE DOCUMENT and is never re-fetched:
       the page was printed with it. */
    raw.record = readText('survey-data'); raw.history = readText('survey-history');
    raw.events = readText('survey-events'); raw.topo = readText('survey-toponymy');
    raw.posts = readText('survey-posts');
    raw.home = null;                   /* R70: his own record IS the ladder */
  } else if (payload.years) {
    var home = String(payload.home || Math.max.apply(null, Object.keys(payload.years).map(Number)));
    var rec = payload.years[home];
    if (!rec) return Promise.reject(new Error('the record has no home year'));
    rec = { year: +home, total: rec.total, contributions: rec.contributions,
            today: payload.today, surveyedTo: payload.surveyedTo };
    var hist = {};
    Object.keys(payload.years).forEach(function (y) {
      if (y !== home) hist[y] = { year: +y, total: payload.years[y].total,
                                  contributions: payload.years[y].contributions };
    });
    raw.record = JSON.stringify(rec); raw.history = JSON.stringify(hist);
    raw.events = '{}'; raw.topo = '{}'; raw.posts = '[]';
    raw.home = homePair;               /* R70 */
    name = payload.name || payload.handle || null;
    foreign = true;
  } else if (payload.contributions) {
    raw.record = JSON.stringify(payload); raw.history = '{}';
    raw.events = '{}'; raw.topo = '{}'; raw.posts = '[]';
    raw.home = homePair;               /* R70 */
    name = payload.name || null;
    foreign = true;
  } else return Promise.reject(new Error('not a record'));

  S.loading = true;
  S.loads = (S.loads || 0) + 1;        /* X6 (c): what Room.glStats().loads counts */
  return new Promise(function (res, rej) {
    var done = false;
    function land(f) { if (done) return; done = true; res(f); }
    try {
      var w = new Worker(window.workerUrl ? workerUrl() : '/js/room/worker.js');
      /* ==== HX1 (Tommy, 2026-09-23: "I tried the GitHub handle in
         production, and it didn't work, i tried 'knadh' and everything
         is frozen"). FW2 gave the worker a first message, `{building:
         true}`, so the engine can log when the thread got its core; the
         boot's own handler (bootField) skips it and this one did not, so
         the stranger's FIELD was that one-word note, `S.fld.lat` and
         `S.fld.sheet` were gone, and every frame after it died in
         bedAt. Broken on production from FW2 (2026-09-22) to this. ==== */
      w.onmessage = function (e) {
        if (e.data && e.data.building) return;
        land(e.data); w.terminate();
      };
      w.onerror = function () {
        function J(t) { try { return JSON.parse(t); } catch (x) { return null; } }
        var evd = J(raw.events) || {}, hzd = J(raw.horizons) || {};
        land(window.RoomField.build({
          record: J(raw.record), history: J(raw.history) || {}, events: evd.events || {},
          horizons: hzd.horizons || [], bankHolidays: foreign ? {} : (evd.bankHolidays || {}),
          topo: J(raw.topo) || {}, k: raw.k, posts: J(raw.posts) || [],
          eventsMode: raw.eventsMode, seams: raw.seams, ladder: raw.ladder,
          margin: raw.margin, seawest: raw.seawest, upx: raw.upx,
          home: raw.home               /* R70 */
        }));
      };
      w.postMessage(raw);
    } catch (e) { rej(e); }
  }).then(function (f) {
    S.loading = false;
    S.foreign = foreign;
    /* ==== PF1 (Tommy, 2026-09-23): "We disable the voyage during someone
       else's GitHub handle ... but we keep pacing the frontier active
       ... We should disable it the same for consistency reasons. It also
       potentially doesn't make a lot of sense currently." The party's
       days are HIS front's, read once at doomInit, so on a stranger's
       sheet they paced a column of someone else's ground and could
       strike the doom mark there. `S.doom.on` is the one flag every
       reader of the pacing goes through — the tick, the draw, the hit,
       the hover, the press, the card's hint and the tools' state — so it
       is put down while a foreign record is up and stood back up when
       his own returns, the act and the paced days untouched, exactly as
       the voyage keeps her state and loses only her clock. ==== */
    if (S.doom && S.doom.ready) { S.doom.on = foreign ? 0 : 1; S.doom.hov = 0; }
    if (window.RoomHandle) {
      RoomHandle.setName(foreign ? name : null);
      RoomHandle.setSubject(foreign ? payload : null);
      /* the key's own figure — the median working day since the datum
         horizon — is the FIELD's and not the wire's, so it goes over now,
         before the margin is re-struck with it */
      if (foreign && f.days) RoomHandle.setLevel(f.days.newSeries);
    }
    /* H (2026-09-19): the voyage and the card find out NOW, not on the
       next ordinary frame — the frames that follow are the reveal's and
       the card west would keep the old record's marks list until the
       pointer moved. `off()` runs voyage.js's own check; `restrike`
       re-strikes the punch card from it. */
    try { if (Room.voyage && Room.voyage.off) Room.voyage.off(); } catch (e) {}
    try { if (S.day && S.day.restrike) S.day.restrike(); } catch (e) {}
    openWindow(f);
    /* the new survey is DRAWN IN, the way the first one was: the field is
       held at the datum and let up. A sheet that cuts to another person's
       country between two frames reads as a bug. */
    S.revealMs = 0; S.readyMs = S.readyMs || 1;
    startReveal();
    return Room.refurnish().then(function () { return f; });
  }).catch(function (e) { S.loading = false; throw e; });
};

/* THE FURNITURE, RASTERED AGAIN. Same fetch (the SVG is in the http cache),
   same off-thread decode, same texture — only the string it is cut from has
   changed. Measured cost is in the report. */
Room.refurnish = function () {
  if (!S.gl || !S.furnPx) return Promise.resolve(false);
  var gl = S.gl, t0 = performance.now();
  return furniture(S.furnPx[0], S.furnPx[1]).then(function (bmp) {
    if (!bmp) return false;
    furnUpload(bmp);
    S.refurnMs = +(performance.now() - t0).toFixed(1);
    invalidate();
    return true;
  }).catch(function () { return false; });
};

/* S3b: A LIVE PART OF THE PRINT, STRUCK INTO THE TEXTURE IN PLACE.
   `furnRegion(x, y, w, h)` -- ROOT sheet units (sheet-frame.svg's
   0 0 2364 1216) -- is that rectangle snapped OUTWARD to whole texels of
   the furniture texture: `px py pw ph` the texel rect, `k` texels per
   root unit, `rx ry rw rh` the same rect back in root units, which is the
   viewBox a caller rasters its own SVG at (width pw, height ph) so its
   pixels land on the texture's one for one. Null until the print exists.
   `furnPatch(x, y, w, h, drawFn)` copies the PRISTINE print of that
   region out of the kept raster into one scratch canvas, calls
   `drawFn(ctx2d, region)` with the identity transform (0,0 is px,py) to
   draw the live part over it, and puts the region back into S.texFurn
   with `texSubImage2D` under the same unpack state the whole upload uses
   (premultiplied, no flip -- the texture is the canvas, top row first).
   No shader change, no pass: the print is already under the card, folds
   with the paper and takes the lamp. Every patch is remembered, one per
   region (S.furnPatches, keyed x,y,w,h: a re-strike of the same region
   replaces its entry, keeps its place), and all are re-applied in order
   by furnUpload when the print is re-cut. */
Room.furnRegion = function (x, y, w, h) {
  if (!S.gl || !S.furnReady || !S.furnCv || !S.furnPx || !S.fld) return null;
  var k = S.furnPx[0] / S.fld.sheet.w;
  var px = Math.max(0, Math.floor(x * k)), py = Math.max(0, Math.floor(y * k));
  var pw = Math.min(S.furnPx[0], Math.ceil((x + w) * k)) - px;
  var ph = Math.min(S.furnPx[1], Math.ceil((y + h) * k)) - py;
  if (!(pw > 0 && ph > 0)) return null;
  return { k: k, px: px, py: py, pw: pw, ph: ph, rx: px / k, ry: py / k, rw: pw / k, rh: ph / k };
};
Room.furnPatch = function (x, y, w, h, drawFn) {
  var R = Room.furnRegion(x, y, w, h);
  if (!R) return false;
  var gl = S.gl, cv = S.furnScratch || (S.furnScratch = document.createElement('canvas'));
  if (cv.width !== R.pw || cv.height !== R.ph) { cv.width = R.pw; cv.height = R.ph; }
  var ctx = cv.getContext('2d');
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, R.pw, R.ph);
  ctx.drawImage(S.furnCv, R.px, R.py, R.pw, R.ph, 0, 0, R.pw, R.ph);
  ctx.save();
  try { drawFn(ctx, R); } finally { ctx.restore(); }
  /* on the furniture's own unit: on whatever was active it could land on 0,
     where the ground reads the field (lettering.js setBand, 2026-09-26) */
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, S.texFurn);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, R.px, R.py, gl.RGBA, gl.UNSIGNED_BYTE, cv);
  furnMips(gl);                    /* AU9: the patch reaches every level */
  gl.activeTexture(gl.TEXTURE0);
  var P = S.furnPatches || (S.furnPatches = []), key = x + ',' + y + ',' + w + ',' + h, i;
  for (i = 0; i < P.length && P[i].key !== key; i++) { }
  P[i] = { key: key, x: x, y: y, w: w, h: h, fn: drawFn };
  invalidate();
  return true;
};
/* THE FRAME'S OWN FACE, FETCHED ONCE FOR EVERY STRIKE. An SVG decoded
   through an <img> sees no stylesheet of the page's, so the Sorts Mill
   Goudy the printed figures are set in rides inside each strike's
   markup: the @font-face cut from sheet-frame.svg's <style> (the http
   cache already holds the file). The section (handle.js) and the light
   line both need it and neither fetches it twice: `printFace(cb)` is the
   face when it is in, null while it is on its way (cb runs when it
   lands), '' if the fetch failed (the strike then falls back to the
   page's face). */
var printFace = null, printFaceReq = 0, printFaceWait = [];
Room.printFace = function (cb) {
  if (printFace !== null) return printFace;
  if (cb) printFaceWait.push(cb);
  if (!printFaceReq) {
    printFaceReq = 1;
    var land = function (f) {
      printFace = f;
      var w = printFaceWait; printFaceWait = [];
      w.forEach(function (fn) { try { fn(f); } catch (e) { console.warn('printFace', e); } });
    };
    frameSvgText().then(function (t) {
      var m = /@font-face\{[^}]*font-style:normal[^}]*\}/.exec(t);
      land(m ? m[0] : '');
    }).catch(function () { land(''); });
  }
  return null;
};
/* FOR THE HARNESS: the light line's last strike -- the sentence, its
   texel rect and the whole strike's ms (markup, raster, composite and
   upload) -- as RoomHandle.sectionSpan() is for the section. */
Room.skyNote = function () { return noteLast; };
/* FOR THE HARNESS: the region READ BACK out of the texture through a
   framebuffer -- a hash, the count of inked texels and gl.getError() --
   which is the only proof that what is live is in the print and not on
   the page. Costs a readPixels; never called by the room itself. */
Room.furnPeek = function (x, y, w, h) {
  var R = Room.furnRegion(x, y, w, h);
  if (!R) return null;
  var gl = S.gl, fb = gl.createFramebuffer(), out = null;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, S.texFurn, 0);
  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE) {
    var px = new Uint8Array(R.pw * R.ph * 4), hsh = 0, ink = 0, i;
    gl.readPixels(R.px, R.py, R.pw, R.ph, gl.RGBA, gl.UNSIGNED_BYTE, px);
    for (i = 0; i < px.length; i += 4) {
      hsh = (Math.imul(hsh, 31) + px[i] + px[i + 1] + px[i + 2] + px[i + 3]) >>> 0;
      if (px[i + 3] > 8) ink++;
    }
    out = { texels: [R.px, R.py, R.pw, R.ph], hash: hsh, ink: ink, err: gl.getError() };
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.deleteFramebuffer(fb);
  invalidate();
  return out;
};

/* R70: the ladder the sheet is plotted on — null when it is his own
   record, which IS the printed ladder. `to`/`from` are commits <-> sheet
   commits, the way every printed figure comes back to the record's own. */
Room.sheetScale = function () { return (S.fld && S.fld.scale) || null; };
Room.sheetLadder = function () { return F.ladder(Room.sheetScale()); };

/* whose sheet is on the desk. null when it is his own. */
Room.subject = function () {
  return window.RoomHandle ? RoomHandle.subject() : null;
};

Room.marksStats = function () {
  if (!S.marks) return null;
  var st = S.marks.stats();
  return { counts: S.marksCounts, draws: st.draws, atlas: st.atlas,
           placeMs: S.marksMs, buildMs: st.buildMs, bytes: S.marksBytes,
           sprites: st.sprites, kinds: st.kinds, rose: st.rose };
};

/* boot() runs in a task of ITS OWN. Chrome executes every deferred script
   that has already arrived in ONE task, and this file is the last big one,
   so a synchronous boot here sat inside that batch: the field worker's
   start (a main-thread task, queued by the head script) waited for the
   parse of twenty scripts AND the boot before its thread ran — measured
   on Tommy's Chrome (2026-09-22): the worker started 15 ms after
   DOMContentLoaded, 1.7 s after it was asked for. A timer task lets the
   batch end first; the boot itself loses nothing but that boundary.
   BR3 (2026-10-02): readyState is already 'interactive' while the deferred
   scripts run, so testing 'loading' let the timer fire between them when a
   later script had not arrived yet: water.js, webmcp.js and visit.js were
   not yet run, the line program compiled with the waterLining stub (the
   sea's waterlines gone for the whole visit, and a shader-cache miss). 3 of
   15 cold loads. Boot waits for DOMContentLoaded unless it has truly fired. */
(function () {
  var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  var fired = nav ? nav.domContentLoadedEventStart > 0 : document.readyState === 'complete';
  if (fired) setTimeout(boot, 0);
  else document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 0); });
})();
})();
